import React, { useEffect, useState, useCallback, useMemo, useRef, useImperativeHandle } from 'react';
import {
  Card, Table, Switch, Typography, Tag, message, Space, Statistic, Row, Col,
  Tabs, Input, InputNumber, Button, Modal, Form, Select, DatePicker, Popconfirm, Badge, Descriptions,
  Drawer, Spin, Alert, Layout, Menu, Divider, Tooltip, Empty, theme, Collapse, Image, Dropdown, Avatar, Checkbox,
  Radio, Segmented, Grid,
} from 'antd';
import {
  UserOutlined, TeamOutlined, MedicineBoxOutlined, SettingOutlined,
  BarChartOutlined, DatabaseOutlined, FileSearchOutlined, BellOutlined,
  DollarOutlined, GiftOutlined, RobotOutlined, DashboardOutlined,
  SafetyCertificateOutlined, ExportOutlined, LogoutOutlined, KeyOutlined,
  LinkOutlined, EditOutlined, CheckOutlined, CloseOutlined, ReloadOutlined,
  PlusOutlined, DeleteOutlined, SearchOutlined, EyeOutlined, MinusCircleOutlined,
  CodeOutlined, TrophyOutlined, DownloadOutlined, FileTextOutlined, PictureOutlined,
  SaveOutlined, AccountBookOutlined, UpOutlined, CopyOutlined, HolderOutlined,
  MenuFoldOutlined, MenuUnfoldOutlined, DownOutlined, ThunderboltOutlined,
  ExperimentOutlined, MailOutlined, CrownOutlined, SwapOutlined, CloudUploadOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { adminAPI, adminAuthAPI, clearAdminToken, siteAPI, covariateAPI } from '../services/api';
import FileUploader from '../components/FileUploader';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { COVARIATE_REGISTRY, buildEffectiveRegistry, type CustomCovariateDefinition } from '../config/covariateRegistry';
import { formatCN } from '../utils/dateFormat';
import { formatErrorMessage, safeErrorMessage } from '../utils/errorHandler';
import { getCovariateTagStyle, getDrugTagStyle, getPopulationTagColor } from '../utils/clinicalTagPalette';
import {
  AUC_UNIT_OPTIONS,
  CONCENTRATION_UNIT_OPTIONS,
  DOSE_MASS_UNIT_OPTIONS,
  displayUnitLabel,
  getDoseDisplayUnit,
  unitConversionFactor,
} from '../utils/quantityUnits';
import {
  getFixedPkpdTargetUnit,
  getPkpdTargetUnitHelp,
  getPkpdTargetUnitOptions,
  normalizePkpdTargetUnit,
} from '../utils/pkpdTargetUnits';
import { CompactStatusGlyph, CompactStatusLegend, type CompactStatusTone } from '../components/CompactStatus';
import {
  SAFETY_METRIC_OPTIONS,
  SAFETY_OPERATOR_OPTIONS,
  SAFETY_ROUTE_OPTIONS,
  SAFETY_SEVERITY_OPTIONS,
  buildLegacySafetyConstraints,
  createSafetyRule,
  formatSafetyRuleSummary,
  getSafetyRuleUnitOptions,
  normalizeSafetyRules,
  serializeSafetyRule,
  type SafetyMetric,
} from '../utils/safetyRules';
import { DRUG_NAME_MAP, CALC_TYPE_LABELS as _CALC_TYPE_LABELS, getDrugLabel } from '../constants/drugs';
import { POPULATION_CATEGORIES } from '../constants/populations';
import {
  COVARIATE_SCOPE_OPTIONS,
  getCovariateScopeLabel,
  POPULATION_GROUP_OPTIONS,
  getPopulationGroupLabel,
  type CovariateScopeGroup,
  type PopulationGroup,
} from '../constants/populationGroups';
import {
  DERIVATION_CONDITION_FIELD_GROUPS,
  DERIVATION_CONDITION_PRESETS,
  editorRulesToApiRules,
  formatRuleConditionSummary,
  getConditionFieldConfig,
  getConditionOperatorOptions,
  newConditionRow,
  newEditorRule,
  newParamRow,
  ruleToEditorRule,
  type EditableConditionRow,
  type EditableDerivationRule,
  type EditableParamRow,
} from '../utils/derivationRuleEditor';
import ModelWizard from './ModelWizard';
import ModelCovariateBindingEditor from '../components/ModelCovariateBindingEditor';
import BoundaryConditionManager from '../components/BoundaryConditionManager';
import ModelValidationCenter from '../components/ModelValidationCenter';
import CandidatePackagesTab from '../components/CandidatePackagesTab';
import ModelScriptHealthPanel from '../components/ModelScriptHealthPanel';
import AIInterpretationProfileEditor, { normalizeAIInterpretationConfig } from '../components/AIInterpretationProfileEditor';
import ModelScriptUpgradeModal from '../components/ModelScriptUpgradeModal';
import AppDetailModal from '../components/AppDetailModal';
import BannersEditor from '../components/LandingPageCMS/BannersEditor';
import HeroSlidesEditor from '../components/LandingPageCMS/HeroSlidesEditor';
import TrustBarEditor from '../components/LandingPageCMS/TrustBarEditor';
import FeaturesEditor from '../components/LandingPageCMS/FeaturesEditor';
import WorkflowEditor from '../components/LandingPageCMS/WorkflowEditor';
import TestimonialsEditor from '../components/LandingPageCMS/TestimonialsEditor';
import ResourcesEditor from '../components/LandingPageCMS/ResourcesEditor';
import NavBarEditor from '../components/LandingPageCMS/NavBarEditor';
import FooterProductsEditor from '../components/LandingPageCMS/FooterProductsEditor';
import ContentPagesTab from '../components/ContentPagesTab';
import { useSiteBranding } from '../hooks/useSiteBranding';
import {
  BRAND_LOGO_SCOPE_OPTIONS,
  normalizeBrandLogoAssets,
  resolvePublicAssetUrl,
  type BrandLogoAsset,
  type BrandLogoScope,
} from '../utils/siteConfig';
import { buildSidebarMenuItems, type SidebarSection } from '../utils/sidebarNav';
import type {
  AdminModelMeta,
  DrugCovariateScopeCandidate,
  DrugCovariateScopeMatrixResponse,
  DrugCovariateScopeMatrixRow,
} from '../types';


const { Title, Text, Paragraph } = Typography;
const { Search } = Input;
const ADMIN_REPLAY_DRAFT_STORAGE_PREFIX = 'precidose_admin_replay_draft:';
const { RangePicker } = DatePicker;
const { Sider, Content, Header } = Layout;

// ================================================================
// Main Admin Panel
// ================================================================
type AdminNavSection = SidebarSection & {
  accent: string;
};

type DashboardTabProps = {
  adminUser: any;
  siteName: string;
  onSelectTab: (key: string) => void;
};

type TenantJoinRequestRow = {
  id: string;
  tenant_id: string;
  tenant_name: string;
  user_id: string;
  email: string;
  requested_role: string;
  status: string;
  source?: string;
  created_at?: string | null;
};

type DataQueryDrugOption = {
  value: string;
  label: string;
  rawLabel: string;
};

/** System-admin review queue for self-service tenant membership requests. */
const TenantJoinRequestsTab: React.FC = () => {
  const [rows, setRows] = useState<TenantJoinRequestRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [decisionRow, setDecisionRow] = useState<TenantJoinRequestRow | null>(null);
  const [decisionAction, setDecisionAction] = useState<'approve' | 'reject'>('approve');
  const [decisionSubmitting, setDecisionSubmitting] = useState(false);
  const [decisionForm] = Form.useForm<{ note?: string }>();

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminAPI.listTenantJoinRequests(statusFilter);
      setRows(Array.isArray(response) ? response : []);
    } catch (error) {
      message.error(formatErrorMessage(error, '加入申请加载失败'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { void loadRequests(); }, [loadRequests]);

  const openDecision = (row: TenantJoinRequestRow, action: 'approve' | 'reject') => {
    decisionForm.resetFields();
    setDecisionAction(action);
    setDecisionRow(row);
  };

  const submitDecision = async () => {
    if (!decisionRow) return;
    const values = await decisionForm.validateFields();
    setDecisionSubmitting(true);
    try {
      await adminAPI.decideTenantJoinRequest(decisionRow.id, {
        action: decisionAction,
        note: String(values.note || '').trim() || undefined,
      });
      message.success(decisionAction === 'approve' ? '加入申请已批准' : '加入申请已拒绝');
      setDecisionRow(null);
      await loadRequests();
    } catch (error) {
      message.error(formatErrorMessage(error, '处理加入申请失败'));
    } finally {
      setDecisionSubmitting(false);
    }
  };

  const columns = [
    { title: '机构', dataIndex: 'tenant_name', key: 'tenant_name', ellipsis: true },
    { title: '申请人邮箱', dataIndex: 'email', key: 'email', ellipsis: true },
    { title: '申请角色', dataIndex: 'requested_role', key: 'requested_role', width: 100 },
    {
      title: '来源', dataIndex: 'source', key: 'source', width: 110,
      render: (value?: string) => value === 'self_request' ? '自主申请' : (value || '-'),
    },
    {
      title: '申请时间', dataIndex: 'created_at', key: 'created_at', width: 170,
      render: (value?: string | null) => value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 90,
      render: (value: string) => <Tag color={value === 'approved' ? 'green' : value === 'rejected' ? 'red' : 'gold'}>{value}</Tag>,
    },
    {
      title: '操作', key: 'actions', width: 170,
      render: (_: unknown, row: TenantJoinRequestRow) => row.status === 'pending' ? (
        <Space size={4}>
          <Button type="primary" size="small" onClick={() => openDecision(row, 'approve')}>批准</Button>
          <Button danger size="small" onClick={() => openDecision(row, 'reject')}>拒绝</Button>
        </Space>
      ) : <Text type="secondary">已处理</Text>,
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card
        title="机构加入申请"
        extra={(
          <Space>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: 'pending', label: '待审批' },
                { value: 'approved', label: '已批准' },
                { value: 'rejected', label: '已拒绝' },
                { value: 'all', label: '全部' },
              ]}
              style={{ width: 110 }}
            />
            <Button icon={<ReloadOutlined />} onClick={() => void loadRequests()} loading={loading}>刷新</Button>
          </Space>
        )}
      >
        <Table<TenantJoinRequestRow>
          rowKey="id"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={rows}
          scroll={{ x: 900 }}
          pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
          locale={{ emptyText: statusFilter === 'pending' ? '暂无待审批申请' : '暂无申请记录' }}
        />
      </Card>

      <Modal
        open={Boolean(decisionRow)}
        title={decisionAction === 'approve' ? '批准加入申请' : '拒绝加入申请'}
        okText={decisionAction === 'approve' ? '批准' : '拒绝'}
        okButtonProps={{ danger: decisionAction === 'reject', loading: decisionSubmitting }}
        cancelText="取消"
        onOk={() => void submitDecision()}
        onCancel={() => setDecisionRow(null)}
        destroyOnHidden
      >
        {decisionRow ? (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Text>{decisionRow.tenant_name} · {decisionRow.email}</Text>
            <Form form={decisionForm} layout="vertical">
              <Form.Item
                name="note"
                label={decisionAction === 'reject' ? '拒绝原因' : '审核备注'}
                rules={decisionAction === 'reject' ? [{ required: true, whitespace: true, message: '请输入拒绝原因' }] : []}
              >
                <Input.TextArea rows={3} maxLength={500} showCount placeholder="可选，备注将发送给申请人" />
              </Form.Item>
            </Form>
          </Space>
        ) : null}
      </Modal>
    </Space>
  );
};

const ADMIN_NAV_GROUPS: AdminNavSection[] = [
  {
    key: 'section-user',
    label: '用户租户',
    icon: <TeamOutlined />,
    accent: '#1677FF',
    children: [
      { key: 'users', icon: <UserOutlined />, label: '用户' },
      { key: 'tenants', icon: <TeamOutlined />, label: '租户' },
      { key: 'tenant-join-requests', icon: <CheckOutlined />, label: '加入审批' },
    ],
  },
  {
    key: 'section-clinical',
    label: '核心资产',
    icon: <DatabaseOutlined />,
    accent: '#1890FF',
    children: [
      { key: 'drugs', icon: <MedicineBoxOutlined />, label: '药物' },
      { key: 'covariates', icon: <DatabaseOutlined />, label: '协变量' },
      { key: 'models', icon: <SafetyCertificateOutlined />, label: '模型' },
      { key: 'model-validation', icon: <ExperimentOutlined />, label: '临床证据中心' },
    ],
  },
  {
    key: 'section-stats',
    label: '数据统计',
    icon: <BarChartOutlined />,
    accent: '#2453D4',
    children: [
      { key: 'workload', icon: <BarChartOutlined />, label: '排行榜' },
      { key: 'data', icon: <DatabaseOutlined />, label: '查询' },
      { key: 'audit', icon: <FileSearchOutlined />, label: '审计' },
    ],
  },
  {
    key: 'section-biz',
    label: '商业运营',
    icon: <DollarOutlined />,
    accent: '#F6AD55',
    children: [
      { key: 'plans', icon: <DollarOutlined />, label: '套餐' },
      { key: 'coins', icon: <AccountBookOutlined />, label: '金币' },
      { key: 'activation', icon: <KeyOutlined />, label: '激活码' },
      { key: 'promo', icon: <GiftOutlined />, label: '优惠码' },
      { key: 'tasks', icon: <TrophyOutlined />, label: '发榜' },
      { key: 'ai', icon: <RobotOutlined />, label: 'AI' },
    ],
  },
  {
    key: 'section-finance',
    label: '财务',
    icon: <AccountBookOutlined />,
    accent: '#0F6CBD',
    children: [
      { key: 'orders', icon: <DollarOutlined />, label: '订单' },
      { key: 'invoices', icon: <FileTextOutlined />, label: '发票' },
      { key: 'revenue', icon: <AccountBookOutlined />, label: '收支' },
    ],
  },
  {
    key: 'section-cms',
    label: 'CMS',
    icon: <PictureOutlined />,
    accent: '#4F6EF7',
    children: [
      { key: 'cms-landing', icon: <PictureOutlined />, label: '首页' },
      { key: 'cms-pages', icon: <FileTextOutlined />, label: '内容' },
    ],
  },
  {
    key: 'section-sys',
    label: '系统设置',
    icon: <SettingOutlined />,
    accent: '#0F766E',
    children: [
      { key: 'announcements', icon: <BellOutlined />, label: '公告' },
      { key: 'config', icon: <SettingOutlined />, label: '配置' },
    ],
  },
];

const ADMIN_SECTION_KEYS = ADMIN_NAV_GROUPS.map((group) => group.key);

const ADMIN_MENU_ITEMS = buildSidebarMenuItems(
  ADMIN_NAV_GROUPS,
  { key: 'dashboard', icon: <DashboardOutlined />, label: '总览' },
  'admin-shell__menu-popup',
);

const SECTION_BY_TAB = ADMIN_NAV_GROUPS.reduce<Record<string, string>>((acc, group) => {
  group.children.forEach((item) => {
    acc[item.key] = group.key;
  });
  return acc;
}, {});

const ADMIN_TAB_META = ADMIN_NAV_GROUPS.reduce<Record<string, { label: string; groupLabel: string; accent: string }>>(
  (acc, group) => {
    group.children.forEach((item) => {
      acc[item.key] = { label: item.label, groupLabel: group.label, accent: group.accent };
    });
    return acc;
  },
  {
    dashboard: { label: '总览', groupLabel: '工作台', accent: '#1677FF' },
  },
);

const ADMIN_TAB_ICONS = ADMIN_NAV_GROUPS.reduce<Record<string, React.ReactNode>>(
  (acc, group) => {
    group.children.forEach((item) => {
      acc[item.key] = item.icon;
    });
    return acc;
  },
  {
    dashboard: <DashboardOutlined />,
  },
);

const ADMIN_QUICK_ACTION_GROUP_KEYS = ['grp-user', 'grp-clinical', 'grp-biz', 'grp-cms'];
const ADMIN_FEATURE_GROUP_KEYS = ['grp-clinical', 'grp-finance', 'grp-sys'];

const ADMIN_SURFACE_STYLE: React.CSSProperties = {
  background: '#FFFFFF',
  borderRadius: 8,
  border: '1px solid #E4E8EF',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
};

const ADMIN_PANEL_STYLES = `
  .admin-shell {
    background: #F6F8FA;
    color: #152238;
    font-variant-numeric: tabular-nums;
  }

  .admin-shell__sider {
    box-shadow: none;
    border-right: 1px solid rgba(255, 255, 255, 0.08);
    z-index: 10;
  }

  .admin-shell__sider .ant-layout-sider-children {
    display: flex;
    flex-direction: column;
    background: #0B172A;
  }

  .admin-shell > .ant-layout {
    min-width: 0;
  }

  .admin-shell .ant-layout,
  .admin-shell .ant-layout-content {
    min-width: 0;
  }

  .admin-shell__menu.ant-menu-dark {
    background: transparent;
    border-inline-end: none;
  }

  .admin-shell__menu.ant-menu-dark .ant-menu-item {
    height: 40px;
    line-height: 40px;
    margin: 3px 10px;
    width: calc(100% - 20px);
    border-radius: 8px;
    color: rgba(255, 255, 255, 0.78);
  }

  .admin-shell__menu.ant-menu-dark .ant-menu-item:hover {
    color: #FFFFFF;
    background: rgba(24, 144, 255, 0.18);
  }

  .admin-shell__menu.ant-menu-dark .ant-menu-item-selected {
    color: #FFFFFF;
    background: #1D4ED8;
    box-shadow: none;
  }

  .admin-shell__menu.ant-menu-dark .ant-menu-item-divider {
    background: rgba(255, 255, 255, 0.08);
    margin: 12px 18px 6px;
  }

  .admin-shell__menu.ant-menu-dark .ant-menu-submenu {
    margin-top: 2px;
  }

  .admin-shell__menu.ant-menu-dark .ant-menu-submenu-title {
    height: 40px;
    line-height: 40px;
    margin: 3px 10px;
    width: calc(100% - 20px);
    border-radius: 8px;
    color: rgba(255, 255, 255, 0.78);
  }

  .admin-shell__menu.ant-menu-dark .ant-menu-submenu-title:hover {
    color: #FFFFFF;
    background: rgba(255, 255, 255, 0.08);
  }

  .admin-shell__menu.ant-menu-dark .ant-menu-submenu-open > .ant-menu-submenu-title,
  .admin-shell__menu.ant-menu-dark .ant-menu-submenu-selected > .ant-menu-submenu-title {
    color: #FFFFFF;
    background: rgba(24, 144, 255, 0.14);
  }

  .admin-shell__menu.ant-menu-dark .ant-menu-sub {
    background: transparent !important;
    padding-bottom: 6px;
  }

  .admin-shell__menu.ant-menu-dark .ant-menu-sub .ant-menu-item {
    margin-left: 24px;
    width: calc(100% - 36px);
    color: rgba(255, 255, 255, 0.72);
  }

  .admin-shell__menu-popup.ant-menu-submenu-popup > .ant-menu {
    min-width: 200px;
    padding: 6px;
    border-radius: 8px;
    background: #0B172A;
  }

  .admin-shell__menu-popup.ant-menu-submenu-popup .ant-menu-item {
    border-radius: 8px;
  }

  .admin-shell__header {
    background: #FFFFFF;
    height: 60px;
    padding: 0 20px;
    border-bottom: 1px solid #E4E8EF;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    position: sticky;
    top: 0;
    z-index: 9;
    box-shadow: none;
  }

  .admin-shell__header-left {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
    flex: 1;
  }

  .admin-shell__header-title {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .admin-shell__title-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
  }

  .admin-shell__title-copy {
    min-width: 0;
  }

  .admin-shell__title-text {
    color: #152238;
    font-size: 16px;
    font-weight: 650;
    line-height: 1.2;
  }

  .admin-shell__title-meta {
    color: #8C8C8C;
    font-size: 12px;
    margin-top: 4px;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .admin-shell__header-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    flex-shrink: 0;
  }

  .admin-shell__ghost-btn.ant-btn {
    height: 36px;
    padding-inline: 12px;
    border-radius: 8px;
    border: 1px solid #E4E8EF;
    background: #FFFFFF;
    color: #475467;
    box-shadow: none;
  }

  .admin-shell__ghost-btn.ant-btn:hover,
  .admin-shell__ghost-btn.ant-btn:focus {
    background: #F2F6FB !important;
    color: #1F2937 !important;
    border-color: #D9E4F2 !important;
  }

  .admin-shell__user-trigger {
    height: 36px;
    padding: 0 12px 0 8px;
    border-radius: 8px;
    border: 1px solid #E4E8EF;
    background: #FFFFFF;
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    transition: background 0.2s ease, border-color 0.2s ease;
  }

  .admin-shell__user-trigger:hover {
    background: #F2F6FB;
    border-color: #D9E4F2;
  }

  .admin-shell__user-name {
    color: #262626;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    white-space: nowrap;
  }

  .admin-shell__content {
    background: transparent;
    padding: 20px 20px 14px;
    min-width: 0;
  }

  .admin-shell__content-inner {
    min-height: calc(100vh - 148px);
    width: 100%;
    max-width: min(1680px, 100%);
    min-width: 0;
    margin: 0 auto;
  }

  .admin-shell__footer {
    color: #8C8C8C;
    font-size: 12px;
    text-align: center;
    padding: 16px 0 8px;
    max-width: 1680px;
    margin: 0 auto;
  }

  .admin-shell .ant-card {
    border-radius: 8px !important;
  }

  .admin-shell .ant-card-head {
    min-height: 48px;
    padding: 0 16px;
    border-bottom-color: #E4E8EF;
  }

  .admin-shell .ant-card-body {
    min-width: 0;
  }

  .admin-shell .ant-card-small > .ant-card-body {
    padding: 12px 14px;
  }

  .admin-shell .ant-btn,
  .admin-shell .ant-input,
  .admin-shell .ant-input-affix-wrapper,
  .admin-shell .ant-picker,
  .admin-shell .ant-select-selector {
    border-radius: 8px !important;
  }

  .admin-shell .ant-btn-primary {
    background: #1D4ED8;
    box-shadow: none;
  }

  .admin-shell .ant-btn-primary:hover,
  .admin-shell .ant-btn-primary:focus {
    background: #1E40AF !important;
  }

  .admin-shell .ant-tabs > .ant-tabs-nav {
    margin-bottom: 16px;
  }

  .admin-shell .ant-tabs-top > .ant-tabs-nav::before {
    border-bottom-color: #E4E8EF;
  }

  .admin-shell .ant-tabs-tab {
    padding: 9px 4px 11px;
    color: #667085;
    font-weight: 500;
  }

  .admin-shell .ant-tabs-tab + .ant-tabs-tab {
    margin-left: 24px;
  }

  .admin-shell .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    color: #1D4ED8;
    font-weight: 650;
  }

  .admin-shell .ant-tabs-ink-bar {
    height: 2px !important;
    border-radius: 2px 2px 0 0;
    background: #1D4ED8;
  }

  .admin-shell .ant-form-item {
    margin-bottom: 18px;
  }

  .admin-shell .ant-form-item .ant-form-item-label > label {
    color: #344054;
    font-size: 13px;
    font-weight: 600;
  }

  .admin-shell .ant-table-wrapper {
    min-width: 0;
    max-width: 100%;
  }

  .admin-shell .ant-table {
    color: #1D2939;
  }

  .admin-shell .ant-table-thead > tr > th {
    background: #F8FAFC !important;
    color: #475467;
    font-weight: 600;
  }

  .admin-shell .ant-table-tbody > tr > td {
    border-bottom-color: #EEF2F6;
  }

  .admin-shell .ant-table-tbody > tr:hover > td {
    background: #F8FAFC !important;
  }

  .admin-shell .ant-pagination .ant-pagination-item {
    border-radius: 6px;
    border-color: #E4E8EF;
  }

  .admin-shell .ant-pagination .ant-pagination-item-active {
    border-color: #1D4ED8;
  }

  .admin-shell .ant-pagination .ant-pagination-item-active a {
    color: #1D4ED8;
    font-weight: 600;
  }

  .admin-shell .ant-drawer-header {
    min-height: 58px;
    border-bottom-color: #E4E8EF;
  }

  .admin-shell .ant-drawer-title {
    color: #152238;
    font-weight: 650;
  }

  .drug-detail-modal .app-detail-modal__body {
    background: #F8FAFC;
    padding: 14px 16px 22px;
  }

  .drug-detail-modal .drug-detail-drawer__section {
    margin-bottom: 12px;
    padding: 12px 14px 8px;
    border: 1px solid #E4E8EF;
    border-radius: 8px;
    background: #FFFFFF;
  }

  .drug-detail-modal .drug-detail-drawer__section:last-child { margin-bottom: 0; }

  .drug-detail-modal .drug-detail-drawer__section-title {
    display: flex;
    align-items: center;
    gap: 7px;
    margin-bottom: 8px;
    color: #152238;
    font-size: 13px;
    font-weight: 650;
  }

  .drug-detail-modal .ant-descriptions-title { display: none; }
  .drug-detail-modal .ant-descriptions-item-label { color: #667085; font-size: 12px; }
  .drug-detail-modal .ant-descriptions-item-content { color: #1D2939; font-size: 12px; }

  .admin-shell .ant-statistic-title {
    color: #667085;
    font-size: 12px;
    margin-bottom: 5px;
  }

  .admin-shell .ant-statistic-content {
    color: #152238;
    font-size: 22px;
    font-weight: 650;
    line-height: 1.2;
  }

  .model-management-surface {
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 0;
  }

  .model-management__summary {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 8px;
  }

  .model-management__metric.ant-card {
    min-width: 0;
    border-color: #E4E8EF !important;
    box-shadow: none;
  }

  .model-management__metric .ant-card-body {
    min-height: 60px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 14px;
  }

  .model-management__metric-label {
    display: flex;
    align-items: center;
    gap: 7px;
    color: #667085;
    font-size: 12px;
    line-height: 1.3;
    white-space: normal;
  }

  .model-management__metric-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex: 0 0 auto;
    opacity: 0.86;
  }

  .model-management__metric .ant-statistic-content {
    margin-top: 0;
    font-size: 23px;
    font-variant-numeric: tabular-nums;
  }

  .model-management__metric .ant-statistic {
    flex: 0 0 auto;
    line-height: 1;
  }

  .model-management__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    padding: 9px 10px;
    border: 1px solid #E4E8EF;
    border-radius: 8px;
    background: #FFFFFF;
  }

  .model-management__notice.ant-alert {
    border-radius: 8px !important;
  }

  .model-management__notice.ant-alert-warning {
    border-color: #E7D9B8 !important;
    background: #FFFCF5 !important;
  }

  .model-management__table .ant-table-container {
    border: 1px solid #E4E8EF;
    border-radius: 8px;
    overflow: hidden;
  }

  .model-management__table .ant-pagination {
    margin: 10px 0 4px !important;
  }

  .model-management__table .ant-pagination-total-text {
    margin-right: auto;
  }

  .model-management__orphan-collapse.ant-collapse {
    border-color: #E4E8EF;
    background: #FFFFFF;
  }

  .model-management__orphan-collapse .ant-collapse-header {
    min-height: 36px;
    align-items: center !important;
    padding: 7px 12px !important;
    color: #475467 !important;
    font-size: 12px;
  }

  .model-management__table .ant-table-thead > tr > th {
    letter-spacing: 0 !important;
    white-space: nowrap;
    padding: 8px 8px !important;
  }

  .model-management__table .ant-table-tbody > tr > td {
    vertical-align: middle;
    padding: 6px 8px !important;
    font-size: 12px;
  }

  .model-lifecycle,
  .model-operations {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }

  .model-management-surface .ant-tag:not(.clinical-entity-tag) {
    margin-inline-end: 0;
    border-color: #D9E1E9 !important;
    border-radius: 4px;
    background: #F8FAFC !important;
    color: #526174 !important;
    font-size: 11px;
    line-height: 18px;
    padding-inline: 5px;
  }

  .model-management-surface .ant-tag:not(.clinical-entity-tag):hover {
    border-color: #BCC8D5 !important;
  }

  .model-management__table .ant-table-tbody > tr:hover > td {
    background: #F8FAFC !important;
  }

  .model-script-summary,
  .model-boundary-summary {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;
    min-width: 0;
  }

  .model-script-summary__name {
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .model-script-summary__meta {
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
  }

  .model-script-summary__meta > .ant-typography {
    flex: 0 0 auto;
    font-size: 11px;
  }

  .model-script-summary__meta .ant-space {
    min-width: 0;
    flex-wrap: nowrap !important;
  }

  .model-script-summary .ant-tag {
    margin-inline-end: 0;
  }

  .model-script-summary .ant-space,
  .model-management__table .ant-space {
    row-gap: 3px !important;
  }

  .model-boundary-summary {
    color: #475467;
    font-size: 12px;
    line-height: 1.45;
  }

  @media (max-width: 1100px) {
    .model-management__summary {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  @media (max-width: 760px) {
    .model-management__summary {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .model-management__toolbar {
      align-items: stretch;
    }

    .model-management__toolbar > .ant-space {
      width: 100%;
    }
  }

  .admin-data-query__shell > .ant-card .ant-space {
    max-width: 100%;
    row-gap: 8px;
  }

  .admin-data-query__shell > .ant-card .ant-picker-range {
    max-width: 100%;
  }

  .admin-data-query__shell > .ant-card .ant-table-wrapper {
    overflow-x: auto;
  }

  .admin-interactive-card {
    transition: box-shadow 0.18s ease, border-color 0.18s ease, background 0.18s ease;
  }

  .admin-interactive-card:hover {
    background: #FCFDFF;
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
    border-color: rgba(29, 78, 216, 0.22) !important;
  }

  .admin-covariate-item {
    width: 100%;
    border: 1px solid #E4E8EF;
    border-radius: 8px;
    padding: 8px 10px;
    background: #FFFFFF;
    cursor: pointer;
    transition: all 0.18s ease;
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) minmax(150px, 0.9fr);
    grid-template-areas:
      'index identity side';
    align-items: start;
    column-gap: 12px;
    row-gap: 6px;
    text-align: left;
  }

  .admin-covariate-item:hover {
    border-color: #B6D4FE;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.05);
  }

  .admin-covariate-item--selected {
    border-color: #1677FF;
    background: #F7FAFF;
    box-shadow: none;
  }

  .admin-covariate-item__index {
    grid-area: index;
    color: #98A2B3;
    font-size: 11px;
    white-space: nowrap;
    align-self: start;
    padding-top: 2px;
  }

  .admin-covariate-item__identity {
    grid-area: identity;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      'label unit'
      'meta unit';
    align-items: start;
    column-gap: 10px;
    row-gap: 3px;
    min-width: 0;
  }

  .admin-covariate-item__label {
    grid-area: label;
    color: #101828;
    font-size: 13px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .admin-covariate-item__meta,
  .admin-covariate-item__unit {
    color: #667085;
    font-size: 11px;
  }

  .admin-covariate-item__meta {
    grid-area: meta;
    min-width: 0;
    line-height: 1.4;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .admin-covariate-item__unit {
    grid-area: unit;
    justify-self: end;
    align-self: start;
    padding: 1px 8px;
    border-radius: 999px;
    background: #F5F8FC;
    border: 1px solid #E7EDF4;
    line-height: 18px;
  }

  .admin-covariate-item__side {
    grid-area: side;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
    min-width: 0;
  }

  .admin-covariate-item__summary {
    width: 100%;
    min-width: 0;
    display: flex;
    justify-content: flex-end;
  }

  .admin-covariate-item__summary .ant-space {
    width: 100%;
    justify-content: flex-end;
    flex-wrap: wrap !important;
    gap: 4px !important;
  }

  .admin-covariate-item__summary .ant-tag {
    margin-inline-end: 0;
    white-space: nowrap;
  }

  .admin-covariate-item__actions {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    white-space: nowrap;
    justify-self: end;
  }

  .admin-drug-covariate-editor__topbar {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
  }

  .admin-drug-covariate-editor__title {
    display: block;
    color: #101828;
    font-size: 18px;
    line-height: 1.2;
  }

  .admin-drug-covariate-editor__meta {
    display: inline-flex;
    gap: 8px;
    margin-top: 6px;
    flex-wrap: wrap;
    color: #667085;
    font-size: 12px;
  }

  .admin-drug-covariate-editor__summary-strip {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .admin-drug-covariate-editor__summary-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    border-radius: 999px;
    background: var(--scope-bg);
    color: var(--scope-accent);
    border: 1px solid rgba(15, 23, 42, 0.08);
    font-size: 12px;
    line-height: 1;
  }

  .admin-drug-covariate-editor__summary-pill em {
    font-style: normal;
    opacity: 0.78;
  }

  .admin-drug-covariate-editor__layout {
    align-items: stretch;
  }

  .admin-drug-covariate-editor__list-card.ant-card,
  .admin-drug-covariate-editor__detail-card.ant-card {
    border-radius: 8px;
    overflow: hidden;
  }

  .admin-drug-covariate-editor__caption {
    display: block;
    margin-bottom: 8px;
    font-size: 12px;
  }

  .admin-drug-covariate-editor__list-scroll {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 560px;
    overflow-y: auto;
    padding-right: 2px;
  }

  .admin-drug-covariate-editor__detail-header {
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid #E4E8EF;
    background: #FFFFFF;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .admin-drug-covariate-editor__detail-title {
    color: #101828;
    font-size: 16px;
  }

  .admin-drug-covariate-editor__detail-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 6px;
  }

  .admin-drug-covariate-editor__detail-tags .ant-tag,
  .admin-drug-covariate-editor__source-inline .ant-tag {
    margin-inline-end: 0;
    border-radius: 999px;
  }

  .admin-drug-covariate-editor__scope-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .admin-drug-covariate-editor__scope-card.ant-card {
    border-radius: 8px;
    border: 1px solid #E4E8EF;
    box-shadow: none;
  }

  .admin-drug-covariate-editor__scope-card--locked.ant-card {
    opacity: 0.7;
    background: #FCFCFD;
  }

  .admin-drug-covariate-editor__scope-card .ant-card-head {
    min-height: auto;
    padding: 8px 10px 0;
  }

  .admin-drug-covariate-editor__scope-card .ant-card-body {
    padding: 8px 10px 10px;
  }

  .admin-drug-covariate-editor__scope-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .admin-drug-covariate-editor__scope-name {
    font-size: 13px;
    font-weight: 600;
  }

  .admin-drug-covariate-editor__scope-note {
    color: #98A2B3;
    font-size: 11px;
    white-space: nowrap;
  }

  .admin-drug-covariate-editor__scope-radio.ant-radio-group {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 6px;
    width: 100%;
  }

  .admin-drug-covariate-editor__scope-radio .ant-radio-button-wrapper {
    border-radius: 10px;
    text-align: center;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding-inline: 0;
    font-size: 12px;
    height: 32px;
  }

  .admin-drug-covariate-editor__scope-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .admin-drug-covariate-editor__scope-foot .ant-typography {
    font-size: 12px;
  }

  .admin-drug-covariate-editor__source-inline {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    min-height: 24px;
  }

  .admin-drug-covariate-editor__add-intro {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .admin-drug-covariate-editor__add-search {
    width: 100%;
  }

  .admin-drug-covariate-editor__add-search .ant-input-affix-wrapper {
    min-height: 42px;
    border-radius: 8px;
  }

  .admin-drug-covariate-editor__add-panel {
    width: 100%;
    border: 1px solid #E4E7EC;
    border-radius: 8px;
    background: #FCFCFD;
    overflow: hidden;
  }

  .admin-drug-covariate-editor__add-list {
    display: flex;
    flex-direction: column;
    max-height: 360px;
    overflow-y: auto;
  }

  .admin-drug-covariate-editor__add-item {
    width: 100%;
    border: 0;
    border-bottom: 1px solid #EEF2F6;
    background: transparent;
    padding: 12px 14px;
    text-align: left;
    cursor: pointer;
    transition: background 0.16s ease;
  }

  .admin-drug-covariate-editor__add-item:hover {
    background: #F8FAFC;
  }

  .admin-drug-covariate-editor__add-item:last-child {
    border-bottom: 0;
  }

  .admin-drug-covariate-editor__add-item--selected {
    background: #F4F8FF;
  }

  .admin-drug-covariate-editor__candidate-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-width: 0;
  }

  .admin-drug-covariate-editor__candidate-main {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .admin-drug-covariate-editor__candidate-label {
    color: #101828;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.25;
    word-break: break-word;
  }

  .admin-drug-covariate-editor__candidate-meta {
    color: #667085;
    font-size: 11px;
    line-height: 1.2;
    word-break: break-all;
  }

  .admin-drug-covariate-editor__candidate-unit {
    flex-shrink: 0;
    color: #475467;
    font-size: 11px;
    padding: 3px 8px;
    border-radius: 999px;
    background: #F2F4F7;
    border: 1px solid #E4E7EC;
    white-space: nowrap;
  }

  .admin-drug-covariate-editor__add-footer {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .admin-drug-covariate-editor__add-selected {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .admin-drug-covariate-editor__add-selected .ant-tag {
    margin-inline-end: 0;
    border-radius: 999px;
  }

  .admin-drug-covariate-editor__add-count {
    padding: 10px 14px;
    border-top: 1px solid #EEF2F6;
    background: #FFFFFF;
  }

  .admin-ai-usage__hero {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border-bottom: 1px solid #E4E8EF;
    background: #FFFFFF;
  }

  .admin-ai-usage__compact-title {
    color: #152238;
    font-size: 18px;
    font-weight: 650;
    line-height: 1.2;
  }

  .admin-ai-usage__controls {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 10px;
  }

  .admin-ai-usage__metrics {
    padding: 12px 16px 16px;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
  }

  .admin-ai-usage__metric {
    min-width: 0;
    padding: 12px 14px;
    border-radius: 8px;
    border: 1px solid #E4E8EF;
    background: #FFFFFF;
    box-shadow: none;
  }

  .admin-ai-usage__metric-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
    color: #486581;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0;
    text-transform: uppercase;
  }

  .admin-ai-usage__metric-chip {
    display: inline-flex;
    width: 10px;
    height: 10px;
    border-radius: 999px;
    flex-shrink: 0;
    box-shadow: 0 0 0 4px rgba(22, 119, 255, 0.10);
  }

  .admin-ai-usage__metric-value {
    color: #102A43;
    font-size: 24px;
    font-weight: 700;
    line-height: 1.1;
    word-break: break-word;
  }

  .admin-ai-usage__metric-helper {
    margin-top: 10px;
    color: #7B8794;
    font-size: 12px;
    line-height: 1.6;
  }

  .admin-ai-usage__section-card.ant-card {
    border-radius: 8px;
    border-color: #E4E8EF;
    box-shadow: none;
  }

  .admin-ai-usage__section-card .ant-card-head {
    min-height: 56px;
    padding: 0 18px;
  }

  .admin-ai-usage__section-card .ant-card-body {
    padding: 14px 18px 18px;
  }

  .admin-ai-usage__signal-list {
    display: grid;
    gap: 12px;
  }

  .admin-ai-usage__signal {
    padding: 14px 15px;
    border-radius: 8px;
    border: 1px solid #E4E8EF;
    background: #FFFFFF;
  }

  .admin-ai-usage__signal-label {
    color: #6B778C;
    font-size: 12px;
  }

  .admin-ai-usage__signal-value {
    margin-top: 8px;
    color: #102A43;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.35;
  }

  .admin-ai-usage__signal-helper {
    margin-top: 4px;
    color: #7B8794;
    font-size: 12px;
    line-height: 1.6;
  }

  @media (max-width: 960px) {
    .admin-drug-covariate-editor__candidate-option {
      flex-direction: column;
      align-items: flex-start;
      gap: 6px;
    }
  }

  @media (max-width: 1400px) {
    .admin-covariate-item {
      grid-template-columns: auto minmax(0, 1fr);
      grid-template-areas:
        'index identity'
        'index side';
    }

    .admin-covariate-item__side {
      align-items: flex-start;
    }

    .admin-covariate-item__summary,
    .admin-covariate-item__summary .ant-space {
      justify-content: flex-start;
    }
    .admin-drug-covariate-editor__scope-grid { grid-template-columns: 1fr; }
  }

  @media (max-width: 960px) {
    .admin-covariate-item__identity {
      grid-template-columns: minmax(0, 1fr);
      grid-template-areas:
        'label'
        'meta'
        'unit';
    }

    .admin-covariate-item__unit {
      justify-self: start;
    }

    .admin-drug-covariate-editor__topbar,
    .admin-drug-covariate-editor__detail-header {
      flex-direction: column;
      align-items: stretch;
    }

    .admin-drug-covariate-editor__scope-radio.ant-radio-group {
      grid-template-columns: 1fr;
    }

    .admin-ai-usage__hero {
      flex-direction: column;
    }

    .admin-ai-usage__controls {
      width: 100%;
      justify-content: flex-start;
    }

    .admin-ai-usage__metrics {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  .admin-data-query__shell {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
    width: 100%;
    overflow-x: clip;
  }

  .admin-data-query__topbar.ant-card {
    overflow: hidden;
  }

  .admin-data-query__topbar .ant-card-body {
    padding: 16px 18px;
    background: #FFFFFF;
  }

  .admin-data-query__topbar-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
  }

  .admin-data-query__title-block {
    min-width: 0;
  }

  .admin-data-query__section-title {
    color: #152238;
    font-size: 21px;
    font-weight: 650;
    line-height: 1.2;
    letter-spacing: 0;
    margin-bottom: 6px;
  }

  .admin-data-query__actions {
    display: flex;
    gap: 10px;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
  }

  .admin-data-query__actions .ant-btn {
    height: 36px;
    border-radius: 8px;
  }

  .admin-data-query__summary-strip {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 10px;
    margin-top: 16px;
  }

  .admin-data-query__summary-pill {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid #E4E8EF;
    background: #FFFFFF;
  }

  .admin-data-query__summary-pill--muted {
    background: #F7FAFC;
  }

  .admin-data-query__summary-pill-label {
    color: #64748B;
    font-size: 12px;
    line-height: 1;
  }

  .admin-data-query__summary-pill-value {
    color: #102A43;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.15;
  }

  .admin-data-query__workspace {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto minmax(0, 1fr);
    gap: 14px;
    width: 100%;
    min-width: 0;
  }

  .admin-data-query__filters-card.ant-card,
  .admin-data-query__results-card.ant-card {
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  .admin-data-query__filters {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
  }

  .admin-data-query__filter-layout {
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 0;
  }

  .admin-data-query__filter-main {
    min-width: 0;
    display: block;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid #E4E8EF;
    background: #FFFFFF;
  }

  .admin-data-query__filter-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(176px, 100%), 1fr));
    gap: 10px;
    width: 100%;
  }

  .admin-data-query__filter-field {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .admin-data-query__filter-label {
    color: #5B6B80;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.1;
  }

  .admin-data-query__filter-main .ant-select,
  .admin-data-query__filter-toolbar .ant-select,
  .admin-data-query__date-range.ant-picker {
    min-width: 0;
    width: 100%;
  }

  .admin-data-query__filter-toolbar {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(240px, 100%), 1fr));
    gap: 10px;
    align-items: end;
    min-width: 0;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid #E4E8EF;
    background: #FFFFFF;
  }

  .admin-data-query__filter-rail {
    min-width: 0;
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
  }

  .admin-data-query__filter-actions {
    display: flex;
    justify-content: flex-end;
    width: 100%;
    min-width: 0;
  }

  .admin-data-query__filter-hint {
    display: flex;
    min-width: 0;
    padding: 0 2px 2px;
  }

  .admin-data-query__filters-card .ant-card-head,
  .admin-data-query__results-card .ant-card-head {
    min-height: 52px;
    padding: 0 16px;
    min-width: 0;
  }

  .admin-data-query__filters-card .ant-card-body {
    padding: 12px 14px 14px;
    min-width: 0;
  }

  .admin-data-query__results-card .ant-card-body {
    padding: 12px 16px 14px;
  }

  .admin-data-query__filters .ant-input,
  .admin-data-query__filters .ant-input-affix-wrapper,
  .admin-data-query__filters .ant-picker,
  .admin-data-query__filters .ant-select-selector {
    min-height: 36px;
    min-width: 0;
    border-radius: 8px !important;
  }

  .admin-data-query__filters .ant-picker-input {
    min-width: 0;
  }

  .admin-data-query__filters .ant-picker-input > input {
    min-width: 0;
    text-overflow: ellipsis;
  }

  .admin-data-query__filters .ant-select-selector {
    padding-top: 3px !important;
    padding-bottom: 3px !important;
  }

  .admin-data-query__active-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 0;
    min-width: 0;
    flex: 1 1 100%;
  }

  .admin-data-query__filter-buttons {
    display: inline-grid;
    grid-auto-flow: column;
    grid-auto-columns: minmax(88px, max-content);
    gap: 8px;
    max-width: 100%;
  }

  .admin-data-query__filter-buttons .ant-btn {
    width: auto;
    min-height: 36px;
    border-radius: 8px;
    padding-inline: 16px;
  }

  .admin-data-query__active-tags .ant-tag {
    margin: 0;
    border-radius: 8px;
    padding-inline: 10px;
    border-color: #DCE8F7;
    background: #F8FBFF;
    color: #2453D4;
  }

  .admin-data-query__table-summary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 10px;
  }

  .admin-data-query__table-card .ant-card-head {
    border-bottom: 1px solid #EAF0F6;
  }

  .admin-data-query__table .ant-table {
    border-radius: 8px;
    overflow: hidden;
  }

  .admin-data-query__table .ant-table-container {
    border: 1px solid #E4E8EF;
    border-radius: 8px;
    overflow: hidden;
  }

  .admin-data-query__table .ant-table-thead > tr > th {
    background: #F7FAFC;
    color: #334155;
    font-weight: 600;
    font-size: 12px;
    padding-top: 12px;
    padding-bottom: 12px;
  }

  .admin-data-query__table .ant-table-tbody > tr > td {
    vertical-align: top;
    padding-top: 12px;
    padding-bottom: 12px;
  }

  .admin-data-query__table .ant-table-tbody > tr:hover > td,
  .admin-data-query__table .ant-table-cell-fix-left,
  .admin-data-query__table .ant-table-cell-fix-right {
    background: #FFFFFF;
  }

  .admin-data-query__cell-stack {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
  }

  .admin-data-query__cell-title {
    color: #0F172A;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.2;
  }

  .admin-data-query__cell-subtitle {
    color: #64748B;
    font-size: 12px;
    line-height: 1.5;
    word-break: break-word;
  }

  .admin-data-query__cell-inline {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .admin-data-query__cell-inline .ant-tag {
    margin: 0;
  }

  .admin-data-query__metric-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    color: #0F172A;
    font-size: 12px;
    line-height: 1.5;
  }

  .admin-data-query__drawer .ant-drawer-body {
    padding: 16px;
    background: #F8FBFF;
  }

  .admin-data-query__drawer-title {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .admin-data-query__drawer-content {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .admin-data-query__drawer-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
  }

  .admin-data-query__info-card {
    padding: 12px 14px;
    border-radius: 8px;
    border: 1px solid #E4E8EF;
    background: #FFFFFF;
    box-shadow: none;
  }

  .admin-data-query__json-block {
    margin: 0;
    padding: 12px 14px;
    border-radius: 8px;
    background: #0F172A;
    color: #DDE7F5;
    font-size: 12px;
    line-height: 1.6;
    overflow: auto;
  }

  @media (max-width: 1200px) {
    .admin-shell__content {
      padding: 20px;
    }

    .admin-data-query__summary-strip {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  @media (max-width: 1024px) {
    .admin-shell__header {
      padding: 0 16px;
    }

    .admin-shell__content {
      padding: 16px;
    }

    .admin-shell__user-meta {
      display: none;
    }

    .admin-shell__header {
      gap: 10px;
    }

    .admin-shell__header-actions {
      gap: 8px;
    }

    .admin-ai-usage__metrics {
      padding: 18px 18px 20px;
    }

    .admin-data-query__topbar-row {
      flex-direction: column;
    }

    .admin-data-query__actions {
      width: 100%;
      justify-content: flex-start;
    }

    .admin-data-query__drawer-grid,
    .admin-data-query__filter-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(min(180px, 100%), 1fr));
    }

    .admin-data-query__filter-toolbar {
      grid-template-columns: repeat(auto-fit, minmax(min(240px, 100%), 1fr));
    }

    .admin-data-query__filter-buttons {
      display: grid;
      grid-template-columns: repeat(2, minmax(88px, 1fr));
      width: min(100%, 240px);
    }
  }

  @media (max-width: 640px) {
    .admin-shell__content {
      padding: 12px 12px 16px;
    }

    .admin-shell__header {
      padding: 0 10px;
    }

    .admin-shell__title-meta {
      display: none;
    }

    .admin-shell__title-text {
      max-width: 96px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .admin-shell__header-actions .admin-shell__ghost-btn {
      display: none;
    }

    .admin-ai-usage__hero {
      padding: 18px;
    }

    .admin-ai-usage__metrics {
      grid-template-columns: 1fr;
      gap: 12px;
    }

    .admin-data-query__section-title {
      font-size: 20px;
    }

    .admin-data-query__summary-strip,
    .admin-data-query__filter-grid,
    .admin-data-query__drawer-grid,
    .admin-data-query__filter-layout {
      display: grid;
      grid-template-columns: 1fr;
    }

    .admin-data-query__filter-main,
    .admin-data-query__filter-toolbar {
      padding: 14px;
    }

    .admin-data-query__date-range.ant-picker {
      min-width: 0;
    }

    .admin-data-query__filter-toolbar,
    .admin-data-query__filter-rail,
    .admin-data-query__filter-actions {
      width: 100%;
      justify-content: stretch;
    }

    .admin-data-query__filter-buttons {
      width: 100%;
      display: grid;
      grid-template-columns: 1fr;
    }

    .admin-data-query__filter-buttons .ant-btn {
      width: 100%;
    }

    .admin-data-query__shell > .ant-card:nth-child(2) .ant-space {
      display: grid !important;
      grid-template-columns: 1fr;
      width: 100%;
    }

    .admin-data-query__shell > .ant-card:nth-child(2) .ant-space-item,
    .admin-data-query__shell > .ant-card:nth-child(2) .ant-input,
    .admin-data-query__shell > .ant-card:nth-child(2) .ant-input-affix-wrapper,
    .admin-data-query__shell > .ant-card:nth-child(2) .ant-select,
    .admin-data-query__shell > .ant-card:nth-child(2) .ant-picker,
    .admin-data-query__shell > .ant-card:nth-child(2) .ant-btn {
      width: 100% !important;
    }
  }

  .drug-admin {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .drug-admin__summary {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    border: 1px solid #E4E8EF;
    border-radius: 8px;
    background: #FFFFFF;
    overflow: hidden;
  }

  .drug-admin__metric {
    min-height: 62px;
    padding: 11px 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    border-right: 1px solid #E4E8EF;
  }

  .drug-admin__metric:last-child {
    border-right: 0;
  }

  .drug-admin__metric span {
    color: #667085;
    font-size: 12px;
  }

  .drug-admin__metric strong {
    color: #152238;
    font-size: 22px;
    font-weight: 650;
  }

  .drug-admin__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 9px 10px;
    border: 1px solid #E4E8EF;
    border-radius: 8px;
    background: #FFFFFF;
  }

  .drug-admin__table .ant-table-container {
    border: 1px solid #E4E8EF;
    border-radius: 8px;
    overflow: hidden;
  }

  .drug-admin__table .ant-table-thead > tr > th,
  .drug-admin__table .ant-table-tbody > tr > td {
    padding: 9px 10px !important;
  }

  .drug-admin__identity {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    cursor: pointer;
  }

  .drug-admin__avatar.ant-avatar {
    flex: 0 0 auto;
    border-radius: 7px;
    background: #EAF2FF;
    color: #1D4ED8;
  }

  .drug-admin__identity-copy {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .model-admin__identity,
  .covariate-admin__identity {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .model-admin__avatar.ant-avatar,
  .covariate-admin__avatar.ant-avatar {
    flex: 0 0 auto;
    border-radius: 7px;
    background: #EEF5FF;
    color: #2453D4;
  }

  .model-admin__identity-copy,
  .covariate-admin__identity-copy {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .model-admin__identity-title,
  .covariate-admin__identity-title {
    color: #152238;
    font-size: 13px;
    font-weight: 650;
    line-height: 1.35;
  }

  .model-admin__identity-meta,
  .covariate-admin__identity-meta {
    max-width: 100%;
    color: #667085;
    font-size: 11px;
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .model-admin__drug-population,
  .model-admin__status-stack,
  .covariate-admin__classification,
  .covariate-admin__units,
  .covariate-admin__links {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    min-width: 0;
  }

  .model-admin__scope-line {
    color: #667085;
    font-size: 11px;
    line-height: 1.45;
  }

  .model-admin__status-stack {
    gap: 6px;
  }

  .covariate-admin__links-row {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #667085;
    font-size: 11px;
  }

  .covariate-admin__description {
    display: -webkit-box;
    overflow: hidden;
    color: #475467;
    font-size: 12px;
    line-height: 1.5;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  .drug-editor-modal .ant-modal-header {
    margin-bottom: 8px;
    padding-bottom: 10px;
    border-bottom: 1px solid #E8ECF2;
  }

  .drug-editor-modal .ant-modal-title {
    color: #152238;
    font-size: 17px;
    font-weight: 650;
  }

  .drug-editor-modal .ant-modal-content {
    border: 1px solid #DDE5EE;
    border-radius: 12px;
    box-shadow: 0 22px 58px rgba(11, 37, 69, 0.18);
  }

  .drug-editor-modal__toolbar {
    min-height: 36px;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .drug-editor-tabs {
    min-height: 68vh;
  }

  .drug-editor-tabs > .ant-tabs-nav {
    width: 184px;
    margin: 0;
    padding: 4px 12px 4px 0;
    border-right: 1px solid #E8ECF2;
  }

  .drug-editor-tabs > .ant-tabs-nav .ant-tabs-tab {
    min-height: 40px;
    margin: 0 0 3px !important;
    padding: 8px 10px;
    border-radius: 6px;
  }

  .drug-editor-tabs > .ant-tabs-nav .ant-tabs-tab-active {
    background: #F2F6FA;
    box-shadow: inset 3px 0 0 #0B2545;
  }

  .drug-editor-tabs > .ant-tabs-nav .ant-tabs-tab-btn {
    color: #526174;
    font-size: 12px;
    font-weight: 600;
  }

  .drug-editor-tabs > .ant-tabs-nav .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: #0B2545;
    font-weight: 700;
  }

  .drug-editor-tabs > .ant-tabs-content-holder {
    max-height: 72vh;
    padding: 2px 4px 24px 18px;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .drug-editor-tabs .ant-form-item {
    margin-bottom: 13px;
  }

  .drug-editor-tabs .ant-form-item-label {
    padding-bottom: 4px;
  }

  .drug-editor-tabs .ant-form-item-label > label {
    color: #344054;
    font-size: 12px;
    font-weight: 550;
  }

  .covariate-editor-modal .ant-modal-body,
  .model-wizard-modal .ant-modal-body {
    min-width: 0;
  }

  .covariate-editor-modal .drug-editor-tabs > .ant-tabs-content-holder {
    max-height: 72vh;
  }

  .model-wizard-modal .drug-editor-tabs > .ant-tabs-content-holder {
    max-height: 65vh;
  }

  .model-wizard__toolbar {
    min-height: 42px;
    margin-bottom: 8px;
    padding: 6px 8px 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    border-bottom: 1px solid #E8ECF2;
  }

  .model-wizard__status,
  .model-wizard__actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    min-width: 0;
  }

  .model-wizard__notice.ant-alert {
    margin: 0 4px 10px;
    padding-block: 6px;
    border-color: #DCE8F7;
    background: #F8FBFF;
  }

  .model-wizard__content {
    min-height: 62vh;
    padding: 2px 4px 20px;
  }

  .model-workflow-summary {
    overflow: hidden;
    border: 1px solid #D9E2EC;
    border-radius: 8px;
    background: #FFFFFF;
  }

  .model-workflow-summary--process {
    border-color: #C9DAF2;
    background: #F8FBFF;
  }

  .model-workflow-summary--error {
    border-color: #F0C8C8;
    background: #FFF9F8;
  }

  .model-workflow-summary--finish {
    border-color: #C8E6D5;
    background: #F8FCFA;
  }

  .model-workflow-summary__main {
    min-height: 88px;
    padding: 14px 16px 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
  }

  .model-workflow-summary__copy {
    display: flex;
    flex: 1 1 auto;
    min-width: 0;
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }

  .model-workflow-summary__title {
    color: #172B4D;
    font-size: 15px;
    line-height: 1.4;
  }

  .model-workflow-summary__description {
    max-width: 780px;
    line-height: 1.55;
  }

  .model-workflow-summary__action {
    flex: 0 0 auto;
  }

  .model-workflow-summary__details {
    border-top: 1px solid #E6ECF2;
  }

  .model-workflow-summary__details > .ant-collapse-item > .ant-collapse-header {
    padding: 7px 16px !important;
    color: #526174;
    font-size: 12px;
  }

  .model-workflow-summary__details .ant-collapse-content-box {
    padding: 8px 16px 14px !important;
  }

  .admin-editor-section {
    margin-bottom: 18px;
    padding-bottom: 4px;
    border-bottom: 1px solid #EEF2F6;
  }

  .admin-editor-section__title {
    margin-bottom: 12px;
    color: #24364B;
    font-size: 14px;
    font-weight: 650;
  }

  .admin-ai-interpretation-editor {
    min-height: 66vh;
    display: grid;
    grid-template-columns: 218px minmax(0, 1fr);
    border: 1px solid #E4E8EF;
    border-radius: 8px;
    overflow: hidden;
    background: #FFFFFF;
  }

  .admin-ai-interpretation-editor__sidebar {
    min-width: 0;
    padding: 14px 10px;
    display: flex;
    flex-direction: column;
    border-right: 1px solid #E4E8EF;
    background: #F8FAFC;
  }

  .admin-ai-interpretation-editor__sidebar-title {
    padding: 0 8px 10px;
    display: flex;
    align-items: center;
    gap: 7px;
    color: #24364B;
    font-size: 13px;
    font-weight: 650;
  }

  .admin-ai-interpretation-editor__profile-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .admin-ai-interpretation-editor__profile {
    width: 100%;
    min-height: 54px;
    padding: 8px 10px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;
    border: 1px solid transparent;
    border-radius: 6px;
    background: transparent;
    color: #344054;
    text-align: left;
    cursor: pointer;
  }

  .admin-ai-interpretation-editor__profile:hover {
    background: #FFFFFF;
    border-color: #DCE3EC;
  }

  .admin-ai-interpretation-editor__profile.is-active {
    background: #FFFFFF;
    border-color: #B8D3F5;
    color: #174A7C;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  }

  .admin-ai-interpretation-editor__profile strong {
    font-size: 13px;
    line-height: 18px;
  }

  .admin-ai-interpretation-editor__profile span {
    max-width: 100%;
    color: #667085;
    font-size: 11px;
    line-height: 16px;
    overflow-wrap: anywhere;
  }

  .admin-ai-interpretation-editor__version.ant-tag {
    width: max-content;
    margin: auto 8px 0;
  }

  .admin-ai-interpretation-editor__canvas {
    min-width: 0;
    padding: 16px 18px 24px;
    background: #FFFFFF;
  }

  .admin-ai-interpretation-editor__profile-header {
    min-height: 54px;
    margin-bottom: 14px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
  }

  .admin-ai-interpretation-editor__profile-header .ant-typography {
    margin: 0;
  }

  .admin-ai-interpretation-editor__section {
    padding: 14px 0 16px;
    border-top: 1px solid #EEF2F6;
  }

  .admin-ai-interpretation-editor__section-title {
    min-height: 28px;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 7px;
    color: #24364B;
    font-size: 13px;
    font-weight: 650;
  }

  .admin-ai-interpretation-editor__section-title--actions {
    justify-content: space-between;
    gap: 12px;
  }

  .admin-ai-interpretation-editor__settings-grid {
    display: grid;
    grid-template-columns: minmax(220px, 0.8fr) minmax(320px, 1.2fr);
    gap: 12px;
  }

  .admin-ai-interpretation-editor__settings-grid label,
  .admin-ai-interpretation-editor__field {
    display: flex;
    flex-direction: column;
    gap: 5px;
    color: #475467;
    font-size: 12px;
  }

  .admin-ai-interpretation-editor__field {
    margin-top: 12px;
  }

  .admin-ai-interpretation-editor__variables {
    position: sticky;
    top: -2px;
    z-index: 3;
    background: rgba(255, 255, 255, 0.98);
  }

  .admin-ai-interpretation-editor__variables .admin-ai-interpretation-editor__section-title {
    justify-content: space-between;
  }

  .admin-ai-interpretation-editor__variables .ant-typography {
    font-size: 11px;
    font-weight: 400;
  }

  .admin-ai-interpretation-editor__token-list {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
  }

  .admin-ai-interpretation-editor__token-list .ant-btn {
    height: 26px;
    padding-inline: 8px;
    font-family: Consolas, monospace;
    font-size: 11px;
  }

  .admin-ai-interpretation-editor__template-list,
  .admin-ai-interpretation-editor__custom-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .admin-ai-interpretation-editor__template-row,
  .admin-ai-interpretation-editor__custom-block {
    padding: 11px 12px;
    border: 1px solid #E4E8EF;
    border-radius: 6px;
    background: #FCFDFE;
  }

  .admin-ai-interpretation-editor__template-head,
  .admin-ai-interpretation-editor__custom-toolbar {
    margin-bottom: 9px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  .admin-ai-interpretation-editor__template-name {
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 7px;
  }

  .admin-ai-interpretation-editor__template-name .ant-tag {
    margin-inline-end: 0;
  }

  .admin-ai-interpretation-editor__order {
    width: 24px;
    color: #667085;
    font-variant-numeric: tabular-nums;
    text-align: center;
  }

  .admin-ai-interpretation-editor__template-row .ant-input,
  .admin-ai-interpretation-editor__template-row .ant-input-textarea,
  .admin-ai-interpretation-editor__custom-block .ant-input-textarea {
    width: 100%;
  }

  .admin-ai-interpretation-editor__custom-toolbar {
    display: grid;
    grid-template-columns: minmax(220px, 1fr) 170px auto 36px;
  }

  .admin-ai-interpretation-editor__empty {
    min-height: 96px;
    display: grid;
    place-items: center;
    border: 1px dashed #D6DCE5;
    border-radius: 6px;
    color: #98A2B3;
    background: #FAFBFC;
  }

  @media (max-width: 1100px) {
    .drug-editor-tabs > .ant-tabs-nav {
      width: auto;
      padding: 0 0 8px;
      border-right: 0;
    }

    .drug-editor-tabs > .ant-tabs-content-holder {
      padding: 8px 2px 22px;
    }

    .admin-ai-interpretation-editor {
      grid-template-columns: 1fr;
    }

    .admin-ai-interpretation-editor__sidebar {
      border-right: 0;
      border-bottom: 1px solid #E4E8EF;
    }

    .admin-ai-interpretation-editor__profile-list {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .admin-ai-interpretation-editor__version.ant-tag {
      margin-top: 10px;
    }
  }

  @media (max-width: 760px) {
    .admin-ai-interpretation-editor__settings-grid,
    .admin-ai-interpretation-editor__custom-toolbar {
      grid-template-columns: 1fr;
    }

    .admin-ai-interpretation-editor__profile-header,
    .admin-ai-interpretation-editor__section-title--actions,
    .admin-ai-interpretation-editor__template-head {
      align-items: stretch;
      flex-direction: column;
    }
  }

  .drug-calc-section-heading {
    min-height: 38px;
    margin: 18px 0 12px;
    padding: 8px 11px;
    display: flex;
    align-items: center;
    gap: 8px;
    color: #24364B;
    font-size: 14px;
    font-weight: 650;
    line-height: 20px;
    background: #F4F7FA;
    border: 1px solid #E1E7EE;
    border-radius: 6px;
  }

  .drug-calc-section-heading--first {
    margin-top: 4px;
  }

  .drug-calc-section-heading .anticon {
    color: #315B7D;
    font-size: 15px;
  }

  .drug-remedial {
    border: 1px solid #E4E8EF;
    border-radius: 8px;
    background: #FFFFFF;
    overflow: hidden;
  }

  .drug-remedial__status {
    min-height: 64px;
    padding: 12px 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    background: #F8FAFC;
    border-bottom: 1px solid #E4E8EF;
  }

  .drug-remedial__section {
    padding: 12px 14px 2px;
    border-bottom: 1px solid #EEF2F6;
  }

  .drug-remedial__section-title {
    margin-bottom: 10px;
    color: #344054;
    font-size: 12px;
    font-weight: 650;
  }

  .drug-remedial .ant-form-item {
    margin-bottom: 12px;
  }

  .drug-remedial > .ant-collapse {
    border-radius: 0;
  }

  .drug-loading-profile-row {
    flex-wrap: nowrap;
  }

  .drug-safety-rule {
    margin-bottom: 12px;
    border: 1px solid #DDE5EE;
    border-radius: 10px;
    background: #FFFFFF;
    box-shadow: 0 4px 12px rgba(11, 37, 69, 0.035);
    overflow: hidden;
  }

  .drug-safety-rules__intro {
    min-height: 46px;
    margin-bottom: 12px;
    padding: 0 2px 10px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    border-bottom: 1px solid #E8EEF4;
  }

  .drug-safety-rules__intro > div {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .drug-safety-rules__intro .ant-typography-strong {
    color: #24364B;
    font-size: 14px;
  }

  .drug-safety-rules__intro .ant-typography-secondary {
    color: #7A869A;
    font-size: 11px;
  }

  .drug-safety-rules__intro .ant-tag {
    margin-inline-end: 0;
    border: 1px solid #DCE5EF;
    border-radius: 999px;
    background: #F7FAFC;
    color: #667085;
    font-size: 11px;
  }

  .drug-safety-rule__header {
    min-height: 42px;
    margin: 0;
    padding: 8px 14px;
    border-bottom: 1px solid #E8EEF4;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .drug-safety-rule__header--dose {
    background: #FAFCFE;
  }

  .drug-safety-rule__header--exposure {
    background: #FAFCFE;
  }

  .drug-safety-rule__header--administration {
    background: #FAFCFE;
  }

  .drug-safety-rule__header .ant-tag {
    margin-inline-end: 0;
    padding-inline: 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
  }

  .drug-safety-rule > .ant-row {
    padding: 14px 14px 2px;
  }

  .drug-safety-rule > .ant-row + .ant-row {
    padding-top: 0;
    padding-bottom: 2px;
  }

  .drug-safety-rule .ant-form-item {
    margin-bottom: 11px;
  }

  .drug-safety-rule .ant-form-item-label > label {
    color: #526174;
    font-size: 11px;
    font-weight: 650;
  }

  .drug-safety-rule .ant-input,
  .drug-safety-rule .ant-input-number,
  .drug-safety-rule .ant-select-selector {
    border-color: #D8E2EC !important;
    border-radius: 7px !important;
    box-shadow: none !important;
  }

  .drug-safety-rule .ant-input-number,
  .drug-safety-rule .ant-input,
  .drug-safety-rule .ant-select-single .ant-select-selector {
    min-height: 34px;
  }

  .drug-safety-rule .ant-switch {
    margin-top: 2px;
  }

  .drug-safety-rule ~ .ant-btn-dashed {
    min-height: 38px;
    border-color: #C8D6E5;
    border-style: solid;
    border-radius: 8px;
    color: #315B7D;
    background: #FBFCFE;
  }

  .drug-safety-rule ~ .ant-btn-dashed:hover {
    border-color: #8FA9C2;
    color: #0B2545;
    background: #F5F8FB;
  }

  @media (max-width: 760px) {
    .drug-safety-rules__intro {
      align-items: stretch;
      flex-direction: column;
      gap: 8px;
    }

    .drug-safety-rules__intro .ant-tag {
      align-self: flex-start;
    }

    .drug-safety-rule > .ant-row,
    .drug-safety-rule > .ant-row + .ant-row {
      padding-left: 12px;
      padding-right: 12px;
    }
  }

  @media (max-width: 760px) {
    .drug-admin__summary {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .drug-admin__metric:nth-child(2) {
      border-right: 0;
    }

    .drug-admin__metric:nth-child(-n + 2) {
      border-bottom: 1px solid #E4E8EF;
    }

    .drug-admin__toolbar,
    .drug-remedial__status,
    .model-wizard__toolbar {
      align-items: stretch;
      flex-direction: column;
    }

    .model-wizard__actions .ant-btn {
      flex: 1 1 auto;
    }

    .drug-loading-profile-row {
      flex-wrap: wrap;
    }

    .model-workflow-summary__main {
      align-items: stretch;
      flex-direction: column;
      gap: 12px;
    }

    .model-workflow-summary__action .ant-btn {
      width: 100%;
    }
  }
`;

const buildAdminActionDescription = (groupLabel: string, label: string) =>
  `进入${groupLabel}中的${label}模块，快速处理相关后台事务。`;

const buildAdminFeatureDescription = (group: AdminNavSection) => {
  const labels = group.children.map((item) => item.label);
  if (labels.length === 1) return `集中处理${labels[0]}相关后台动作。`;
  if (labels.length === 2) return `覆盖${labels[0]}与${labels[1]}两类核心入口，减少跳转。`;
  return `整合${labels.slice(0, -1).join('、')}与${labels[labels.length - 1]}等模块，保持后台操作连贯。`;
};

const formatServiceLabel = (key: string) => {
  const labelMap: Record<string, string> = {
    api: 'API 服务',
    database: '数据库',
    r_service: 'R 计算引擎',
  };
  return labelMap[key] || key;
};

const formatSystemLabel = (key: string) => {
  const labelMap: Record<string, string> = {
    platform: '运行平台',
    python_version: 'Python 版本',
  };
  return labelMap[key] || key;
};

const pickAdminGroups = (keys: string[]) =>
  keys
    .map((key) => ADMIN_NAV_GROUPS.find((group) => group.key === key))
    .filter((group): group is AdminNavSection => Boolean(group));

const AdminSectionHeader: React.FC<{ title: string; extra?: React.ReactNode }> = ({ title, extra }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12 }}>
    <Text style={{ color: '#262626', fontSize: 16, fontWeight: 600 }}>{title}</Text>
    {extra}
  </div>
);

const AdminMetricCard: React.FC<{
  accent: string;
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  helper?: React.ReactNode;
}> = ({ accent, icon, label, value, helper }) => (
  <div
    className="admin-interactive-card"
    style={{
      ...ADMIN_SURFACE_STYLE,
      padding: '22px 24px',
      height: '100%',
      display: 'flex',
      alignItems: 'flex-start',
      gap: 16,
    }}
  >
    <div
      style={{
        width: 48,
        height: 48,
        borderRadius: 8,
        background: `${accent}12`,
        color: accent,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 20,
        flexShrink: 0,
      }}
    >
      {icon}
    </div>
    <div style={{ minWidth: 0, flex: 1 }}>
      <div style={{ fontSize: 13, color: '#8C8C8C', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 30, lineHeight: 1.1, fontWeight: 700, color: '#262626', letterSpacing: 0 }}>
        {value}
      </div>
      {helper ? (
        <div style={{ marginTop: 10, fontSize: 12, color: '#595959', lineHeight: 1.6 }}>
          {helper}
        </div>
      ) : null}
    </div>
  </div>
);

const AdminShortcutCard: React.FC<{
  accent: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}> = ({ accent, icon, title, description, onClick }) => (
  <div
    className="admin-interactive-card"
    onClick={onClick}
    style={{
      ...ADMIN_SURFACE_STYLE,
      padding: '18px 20px',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      cursor: 'pointer',
    }}
  >
    <div
      style={{
        width: 42,
        height: 42,
        borderRadius: 8,
        background: `${accent}12`,
        color: accent,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 18,
        flexShrink: 0,
      }}
    >
      {icon}
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 15, color: '#262626', fontWeight: 600, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 12, color: '#8C8C8C', lineHeight: 1.6 }}>{description}</div>
    </div>
  </div>
);

const AdminFeatureCard: React.FC<{
  accent: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  tags: string[];
}> = ({ accent, icon, title, description, tags }) => (
  <div
    className="admin-interactive-card"
    style={{
      ...ADMIN_SURFACE_STYLE,
      padding: '22px 24px',
      height: '100%',
    }}
  >
    <div
      style={{
        width: 52,
        height: 52,
        borderRadius: 8,
        background: `${accent}14`,
        color: accent,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 22,
        marginBottom: 16,
      }}
    >
      {icon}
    </div>
    <div style={{ fontSize: 16, color: '#262626', fontWeight: 600, marginBottom: 8 }}>{title}</div>
    <div style={{ fontSize: 13, color: '#595959', lineHeight: 1.6, marginBottom: 14 }}>{description}</div>
    <Space size={[8, 8]} wrap>
      {tags.map((tag) => (
        <Tag
          key={tag}
          style={{
            border: '1px solid #D9E6F5',
            color: '#4F5D75',
            background: '#F7FAFD',
            borderRadius: 999,
            marginInlineEnd: 0,
            paddingInline: 10,
          }}
        >
          {tag}
        </Tag>
      ))}
    </Space>
  </div>
);

const AdminPanel: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [clinicalValidationModelId, setClinicalValidationModelId] = useState<string | undefined>();
  const [adminUser, setAdminUser] = useState<any>(null);
  const [verifying, setVerifying] = useState(true);
  const [siderCollapsed, setSiderCollapsed] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [brandFullFailed, setBrandFullFailed] = useState(false);
  const [brandIconFailed, setBrandIconFailed] = useState(false);
  const {
    siteName,
    slogan,
    copyright,
    adminWorkspaceLogoUrl,
    adminWorkspaceIconUrl,
    brandMonogram,
  } = useSiteBranding();

  useEffect(() => {
    adminAuthAPI.me()
      .then((u) => { setAdminUser(u); setVerifying(false); })
      .catch(() => { navigate('/admin/login', { replace: true }); });
  }, [navigate]);

  useEffect(() => {
    setBrandFullFailed(false);
  }, [adminWorkspaceLogoUrl]);

  useEffect(() => {
    setBrandIconFailed(false);
  }, [adminWorkspaceIconUrl]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 640px)');
    const syncSiderForViewport = () => setSiderCollapsed(mediaQuery.matches);

    syncSiderForViewport();
    mediaQuery.addEventListener('change', syncSiderForViewport);
    return () => mediaQuery.removeEventListener('change', syncSiderForViewport);
  }, []);

  const activeSectionKey = SECTION_BY_TAB[activeTab];

  useEffect(() => {
    if (siderCollapsed) {
      setOpenKeys([]);
      return;
    }
    setOpenKeys(activeSectionKey ? [activeSectionKey] : []);
  }, [activeSectionKey, siderCollapsed]);

  const handleAdminLogout = () => {
    clearAdminToken();
    navigate('/admin/login', { replace: true });
  };

  const openClinicalValidation = (modelId: string) => {
    setClinicalValidationModelId(modelId);
    setActiveTab('model-validation');
  };

  const consumeClinicalValidationTarget = useCallback(() => {
    setClinicalValidationModelId(undefined);
  }, []);

  if (verifying) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#F5F7FA' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!adminUser) return null;

  const activeMeta = ADMIN_TAB_META[activeTab] || ADMIN_TAB_META.dashboard;
  const activeIcon = ADMIN_TAB_ICONS[activeTab] || <DashboardOutlined />;
  const adminDisplayName = adminUser.real_name || adminUser.email || 'System Admin';
  const userMenuItems = [
    { key: 'open-site', icon: <LinkOutlined />, label: '打开前台' },
    { type: 'divider' as const },
    { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', danger: true },
  ];

  const tabContentMap: Record<string, React.ReactNode> = {
    dashboard: <DashboardTab adminUser={adminUser} siteName={siteName} onSelectTab={setActiveTab} />,
    users: <UsersTab currentUserId={adminUser?.id} />,
    tenants: <TenantsTab />,
    'tenant-join-requests': <TenantJoinRequestsTab />,
    drugs: <DrugsTab />,
    models: <ModelsTab onOpenClinicalValidation={openClinicalValidation} />,
    'model-validation': (
      <ModelValidationCenter
        initialModelId={clinicalValidationModelId}
        onInitialModelConsumed={consumeClinicalValidationTarget}
      />
    ),
    covariates: <CovariatesTab />,
    tasks: <TasksTab />,
    workload: <WorkloadDashboardTab />,
    data: <DataQueryV2Tab />,
    audit: <AuditSearchTab />,
    announcements: <AnnouncementsTab />,
    'cms-landing': <LandingPageTab />,
    'cms-pages': <ContentPagesTab />,
    plans: <PlansTab />,
    coins: <CoinManagementTab />,
    activation: <ActivationCodesUnifiedTab />,
    promo: <PromoTab />,
    ai: <AIUsageV2Tab />,
    invoices: <InvoicesTab />,
    revenue: <RevenueTab />,
    config: <ConfigTab />,
  };

  return (
    <Layout className="admin-shell" style={{ minHeight: '100vh' }}>
      <style>{ADMIN_PANEL_STYLES}</style>
      <Sider
        collapsed={siderCollapsed}
        trigger={null}
        width={240}
        collapsedWidth={72}
        theme="dark"
        className="admin-shell__sider"
      >
        <div
          style={{
            minHeight: 86,
            padding: siderCollapsed ? '0 12px' : '16px 20px 14px',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            display: 'flex',
            alignItems: siderCollapsed ? 'center' : 'flex-start',
            justifyContent: siderCollapsed ? 'center' : 'flex-start',
          }}
        >
          {siderCollapsed ? (
            adminWorkspaceIconUrl && !brandIconFailed ? (
              <img
                src={adminWorkspaceIconUrl}
                alt={`${siteName} icon`}
                style={{ width: 34, height: 34, objectFit: 'contain', display: 'block' }}
                onError={() => setBrandIconFailed(true)}
              />
            ) : (
              <span style={{ color: '#FFFFFF', fontWeight: 700 }}>{brandMonogram}</span>
            )
          ) : (
            <div style={{ minWidth: 0, flex: 1 }}>
              {adminWorkspaceLogoUrl && !brandFullFailed ? (
                <img
                  src={adminWorkspaceLogoUrl}
                  alt={`${siteName} admin`}
                  style={{
                    display: 'block',
                    maxWidth: '100%',
                    height: 30,
                    objectFit: 'contain',
                    objectPosition: 'left center',
                  }}
                  onError={() => setBrandFullFailed(true)}
                />
              ) : (
                <div style={{ color: '#FFFFFF', fontWeight: 600, fontSize: 16, lineHeight: 1.2 }}>{siteName}</div>
              )}
              <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, marginTop: 8, lineHeight: 1.4 }}>
                {slogan}
              </div>
            </div>
          )}
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '10px 0 12px' }}>
          <Menu
            mode="inline"
            theme="dark"
            selectedKeys={[activeTab]}
            openKeys={siderCollapsed ? [] : openKeys}
            onClick={({ key }) => setActiveTab(String(key))}
            onOpenChange={(keys) =>
              setOpenKeys((keys as string[]).filter((key) => ADMIN_SECTION_KEYS.includes(key)).slice(-1))
            }
            items={ADMIN_MENU_ITEMS}
            className="admin-shell__menu"
          />
        </div>
      </Sider>
      <Layout>
        <Header className="admin-shell__header">
          <div className="admin-shell__header-left">
            <Button
              type="text"
              icon={siderCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setSiderCollapsed((prev) => !prev)}
              className="admin-shell__ghost-btn"
            />
            <div className="admin-shell__header-title">
              <div
                className="admin-shell__title-icon"
                style={{ background: `${activeMeta.accent}12`, color: activeMeta.accent }}
              >
                {activeIcon}
              </div>
              <div className="admin-shell__title-copy">
                <div className="admin-shell__title-text">{activeMeta.label}</div>
                <div className="admin-shell__title-meta">
                  管理工作台 · {activeMeta.groupLabel} · {dayjs().format('YYYY-MM-DD')}
                </div>
              </div>
            </div>
          </div>
          <div className="admin-shell__header-actions">
            <Button
              type="text"
              icon={<LinkOutlined />}
              onClick={() => window.open('/', '_blank', 'noopener,noreferrer')}
              className="admin-shell__ghost-btn"
            >
              前台首页
            </Button>
            <Dropdown
              trigger={['click']}
              menu={{
                items: userMenuItems,
                onClick: ({ key }) => {
                  if (key === 'open-site') window.open('/', '_blank', 'noopener,noreferrer');
                  if (key === 'logout') handleAdminLogout();
                },
              }}
            >
              <div className="admin-shell__user-trigger">
                <Avatar style={{ background: '#1677FF' }} icon={<UserOutlined />} />
                <div className="admin-shell__user-meta admin-shell__user-name">{adminDisplayName}</div>
                <DownOutlined style={{ fontSize: 11, color: '#98A2B3' }} />
              </div>
            </Dropdown>
          </div>
        </Header>
        <Content className="admin-shell__content">
          <div className="admin-shell__content-inner">
            {tabContentMap[activeTab] || <Empty description="页面不存在" />}
          </div>
          <div className="admin-shell__footer">
            {copyright}
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

// ================================================================
// Dashboard Tab
// ================================================================
const DashboardTab: React.FC<DashboardTabProps> = ({ adminUser, siteName, onSelectTab }) => {
  const [stats, setStats] = useState<any>(null);
  const [monitor, setMonitor] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      adminAPI.stats().catch(() => null),
      adminAPI.monitor().catch(() => null),
    ]).then(([s, m]) => { setStats(s); setMonitor(m); }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const serviceEntries = useMemo(() => Object.entries(monitor?.services || {}) as Array<[string, boolean]>, [monitor]);
  const systemEntries = useMemo(() => Object.entries(monitor?.system || {}) as Array<[string, string]>, [monitor]);
  const healthyServices = serviceEntries.filter(([, value]) => Boolean(value)).length;
  const allServicesHealthy = serviceEntries.length > 0 && healthyServices === serviceEntries.length;
  const partialHealthy = healthyServices > 0 && healthyServices < serviceEntries.length;
  const monitorTone = allServicesHealthy ? 'success' : partialHealthy ? 'warning' : 'error';
  const monitorText = serviceEntries.length > 0 ? `${healthyServices}/${serviceEntries.length} 项服务在线` : '监控数据待刷新';
  const latestTrend = stats?.daily_trend?.[stats.daily_trend.length - 1];
  const adminDisplayName = adminUser?.real_name || adminUser?.email || '管理员';

  const metricCards = useMemo(() => ([
    {
      key: 'users',
      label: '用户总数',
      value: stats?.total_users ?? '--',
      icon: <UserOutlined />,
      accent: '#1677FF',
      helper: `租户 ${stats?.total_tenants ?? 0} 个`,
    },
    {
      key: 'cases',
      label: '病例总数',
      value: stats?.total_cases ?? '--',
      icon: <FileTextOutlined />,
      accent: '#1890FF',
      helper: `已收录模型 ${stats?.total_models ?? 0} 个`,
    },
    {
      key: 'calculations',
      label: '累计计算',
      value: stats?.total_calculations ?? '--',
      icon: <BarChartOutlined />,
      accent: '#2453D4',
      helper: latestTrend ? `最近更新 ${latestTrend.date}` : '近 7 日暂无趋势数据',
    },
    {
      key: 'monitor',
      label: '服务状态',
      value: serviceEntries.length > 0 ? `${healthyServices}/${serviceEntries.length}` : '--',
      icon: <SafetyCertificateOutlined />,
      accent: monitorTone === 'success' ? '#52C41A' : monitorTone === 'warning' ? '#FAAD14' : '#FF4D4F',
      helper: monitorText,
    },
  ]), [healthyServices, latestTrend, monitorText, monitorTone, serviceEntries.length, stats]);

  const quickActions = useMemo(
    () =>
      pickAdminGroups(ADMIN_QUICK_ACTION_GROUP_KEYS).map((group) => {
        const target = group.children[0];
        return {
          key: target.key,
          title: target.label,
          description: buildAdminActionDescription(group.label, target.label),
          icon: target.icon,
          accent: group.accent,
        };
      }),
    [],
  );

  const featureCards = useMemo(
    () =>
      pickAdminGroups(ADMIN_FEATURE_GROUP_KEYS).map((group) => ({
        key: group.key,
        title: group.label,
        description: buildAdminFeatureDescription(group),
        icon: group.children[0]?.icon || <DashboardOutlined />,
        accent: group.accent,
        tags: group.children.map((item) => item.label),
      })),
    [],
  );

  if (loading && !stats) {
    return <Card loading bordered={false} style={ADMIN_SURFACE_STYLE} />;
  }

  if (!stats) {
    return (
      <Alert
        showIcon
        type="error"
        message="后台概览加载失败"
        description="未能读取后台统计数据，请稍后重试。"
        action={<Button type="link" icon={<ReloadOutlined />} onClick={fetchAll}>重新加载</Button>}
        style={{ ...ADMIN_SURFACE_STYLE }}
      />
    );
  }

  return (
    <div>
      <Alert
        showIcon
        type="info"
        message={`欢迎使用 ${siteName} 管理后台`}
        description={`${adminDisplayName}，当前已接入 ${stats.total_models ?? 0} 个 PK 模型，${monitorText}。${latestTrend ? `最近一次趋势记录日期为 ${latestTrend.date}。` : ''}`}
        action={(
          <Space size={8} wrap>
            <Tag color="blue" style={{ margin: 0, borderRadius: 999 }}>模型 {stats.total_models ?? 0}</Tag>
            <Button type="link" icon={<ReloadOutlined />} onClick={fetchAll}>刷新</Button>
          </Space>
        )}
        style={{
          ...ADMIN_SURFACE_STYLE,
          marginBottom: 24,
          background: '#EAF4FF',
          borderColor: '#D4E9FF',
        }}
      />

      <AdminSectionHeader title="指标统计" />
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        {metricCards.map(({ key, ...item }) => (
          <Col xs={24} sm={12} xl={6} key={key}>
            <AdminMetricCard {...item} />
          </Col>
        ))}
      </Row>

      <AdminSectionHeader title="快捷操作" />
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        {quickActions.map((item) => (
          <Col xs={24} sm={12} xl={6} key={item.key}>
            <AdminShortcutCard
              accent={item.accent}
              icon={item.icon}
              title={item.title}
              description={item.description}
              onClick={() => onSelectTab(item.key)}
            />
          </Col>
        ))}
      </Row>

      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} xl={8}>
          <Card
            bordered={false}
            style={{ ...ADMIN_SURFACE_STYLE, height: '100%' }}
            title={<Text style={{ color: '#262626', fontWeight: 600 }}>运行状态</Text>}
            extra={<Button type="link" icon={<ReloadOutlined />} onClick={fetchAll}>刷新</Button>}
          >
            <Space size={[8, 8]} wrap style={{ marginBottom: 18 }}>
              {serviceEntries.map(([key, value]) => (
                <Tag
                  key={key}
                  color={value ? 'success' : 'error'}
                  style={{ margin: 0, borderRadius: 999, paddingInline: 10 }}
                >
                  {formatServiceLabel(key)} · {value ? '正常' : '异常'}
                </Tag>
              ))}
            </Space>
            <div style={{ display: 'grid', gap: 12 }}>
              {systemEntries.map(([key, value]) => (
                <div
                  key={key}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                    paddingBottom: 12,
                    borderBottom: '1px solid #F0F4F8',
                  }}
                >
                  <Text style={{ color: '#8C8C8C' }}>{formatSystemLabel(key)}</Text>
                  <Text style={{ color: '#262626', fontWeight: 600 }}>{String(value)}</Text>
                </div>
              ))}
            </div>
          </Card>
        </Col>
        <Col xs={24} xl={16}>
          <Card
            bordered={false}
            style={{ ...ADMIN_SURFACE_STYLE, height: '100%' }}
            title={<Text style={{ color: '#262626', fontWeight: 600 }}>近期计算趋势</Text>}
          >
            <Table
              dataSource={stats.daily_trend || []}
              columns={[
                { title: '日期', dataIndex: 'date', key: 'date' },
                { title: '计算次数', dataIndex: 'count', key: 'count' },
              ]}
              rowKey="date"
              pagination={false}
              size="small"
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无趋势数据" /> }}
            />
          </Card>
        </Col>
      </Row>

      <AdminSectionHeader title="核心功能区" />
      <Row gutter={[24, 24]}>
        {featureCards.map((item) => (
          <Col xs={24} lg={12} xl={8} key={item.key}>
            <AdminFeatureCard
              accent={item.accent}
              icon={item.icon}
              title={item.title}
              description={item.description}
              tags={item.tags}
            />
          </Col>
        ))}
      </Row>
    </div>
  );
};

// ================================================================
// Users Tab
// ================================================================
interface AdminUserTenantMembership {
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  tenant_status: string;
  role: string;
  is_default: boolean;
  is_primary_owner: boolean;
}

interface AdminUserDetail {
  id: string;
  email: string;
  username?: string | null;
  real_name?: string | null;
  phone?: string | null;
  is_active: boolean;
  is_verified: boolean;
  is_super_admin: boolean;
  created_at?: string | null;
  last_login_at?: string | null;
  tenants?: AdminUserTenantMembership[];
}

interface AdminUserEditValues {
  email: string;
  username?: string;
  real_name?: string;
  phone?: string;
  is_active: boolean;
  is_verified: boolean;
  is_super_admin: boolean;
}

const UsersTab: React.FC<{ currentUserId?: string }> = ({ currentUserId }) => {
  const [users, setUsers] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [detailUser, setDetailUser] = useState<AdminUserDetail | null>(null);
  const [editingUser, setEditingUser] = useState(false);
  const [userEditForm] = Form.useForm<AdminUserEditValues>();
  const [emailUser, setEmailUser] = useState<AdminUserDetail | null>(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailForm] = Form.useForm<{ subject: string; content: string }>();
  const [resetModal, setResetModal] = useState<string | null>(null);
  const [newPwd, setNewPwd] = useState('');
  const [createModal, setCreateModal] = useState(false);
  const [createForm] = Form.useForm();
  const [coinModal, setCoinModal] = useState<string | null>(null);
  const [coinAmount, setCoinAmount] = useState<number>(0);
  const [coinReason, setCoinReason] = useState('');
  const [authorizeUserModal, setAuthorizeUserModal] = useState(false);
  const [authorizeUserForm] = Form.useForm();
  const [authorizeTenantOptions, setAuthorizeTenantOptions] = useState<any[]>([]);
  const [authorizeUserSaving, setAuthorizeUserSaving] = useState(false);

  const fetchUsers = useCallback(async (s?: string) => {
    setLoading(true);
    try {
      const res = await adminAPI.listUsers(0, 200, s);
      setUsers(res.items); setTotal(res.total);
    } catch { message.error('加载用户失败'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleToggle = async (userId: string, field: string, value: boolean) => {
    try {
      await adminAPI.updateUser(userId, { [field]: value });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, [field]: value } : u));
      message.success('已更新');
    } catch (err: any) { message.error(err?.response?.data?.detail || '操作失败'); }
  };

  const handleResetPwd = async () => {
    if (!resetModal || !newPwd) return;
    try {
      await adminAPI.resetPassword(resetModal, newPwd);
      message.success('密码已重置'); setResetModal(null); setNewPwd('');
    } catch (err: any) { message.error(err?.response?.data?.detail || '重置失败'); }
  };

  const showDetail = async (userId: string) => {
    try {
      setDetailUser(await adminAPI.getUserDetail(userId));
      setEditingUser(false);
    }
    catch { message.error('加载详情失败'); }
  };

  const startEditUser = () => {
    if (!detailUser) return;
    userEditForm.setFieldsValue({
      email: detailUser.email,
      username: detailUser.username || undefined,
      real_name: detailUser.real_name || undefined,
      phone: detailUser.phone || undefined,
      is_active: detailUser.is_active,
      is_verified: detailUser.is_verified,
      is_super_admin: detailUser.is_super_admin,
    });
    setEditingUser(true);
  };

  const handleUserSave = async () => {
    if (!detailUser) return;
    try {
      const values = await userEditForm.validateFields();
      await adminAPI.updateUser(detailUser.id, values);
      message.success('用户资料已更新');
      await Promise.all([showDetail(detailUser.id), fetchUsers(search)]);
    } catch (err: any) {
      message.error(err?.response?.data?.detail || '更新失败');
    }
  };

  const openEmailComposer = (user: AdminUserDetail) => {
    emailForm.resetFields();
    setEmailUser(user);
  };

  const handleSendEmail = async () => {
    if (!emailUser) return;
    try {
      const values = await emailForm.validateFields();
      setEmailSending(true);
      await adminAPI.sendUserEmail(emailUser.id, values);
      message.success('邮件已发送');
      setEmailUser(null);
      emailForm.resetFields();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (detail) message.error(typeof detail === 'string' ? detail : detail.message || '发送失败');
    } finally {
      setEmailSending(false);
    }
  };

  const handleAdjustCoin = async () => {
    if (!coinModal || !coinAmount) return;
    try {
      await adminAPI.adjustCoin(coinModal, coinAmount, coinReason);
      setUsers(prev => prev.map(u => u.id === coinModal ? { ...u, coin_balance: (u.coin_balance || 0) + coinAmount } : u));
      message.success('金币已调整');
      setCoinModal(null); setCoinAmount(0); setCoinReason('');
    } catch (err: any) { message.error(err?.response?.data?.detail || '调整失败'); }
  };

  const handleCreateUser = async () => {
    try {
      const values = await createForm.validateFields();
      await adminAPI.createUser(values);
      message.success('用户已创建'); setCreateModal(false); createForm.resetFields(); fetchUsers();
    } catch (err: any) { message.error(err?.response?.data?.detail || '创建失败'); }
  };

  const openAuthorizeUser = async () => {
    if (!detailUser) return;
    authorizeUserForm.resetFields();
    setAuthorizeUserModal(true);
    try {
      const response = await adminAPI.listTenants(0, 200, undefined, 'active');
      const existing = new Set((detailUser.tenants || []).map((item) => item.tenant_id));
      setAuthorizeTenantOptions((response.items || []).filter((tenant: any) => !existing.has(tenant.id)));
    } catch {
      message.error('加载可授权租户失败');
    }
  };

  const handleAuthorizeUser = async () => {
    if (!detailUser) return;
    try {
      const values = await authorizeUserForm.validateFields();
      setAuthorizeUserSaving(true);
      await adminAPI.authorizeTenantMember(values.tenant_id, {
        user_id: detailUser.id,
        role: values.role,
        is_default: values.is_default,
      });
      message.success('用户租户授权已保存');
      setAuthorizeUserModal(false);
      await showDetail(detailUser.id);
    } catch (err: any) {
      if (!err?.errorFields) message.error(safeErrorMessage(err, '用户授权失败'));
    } finally {
      setAuthorizeUserSaving(false);
    }
  };

  const columns = [
    { title: '邮箱', dataIndex: 'email', key: 'email', width: 180 },
    { title: '姓名', dataIndex: 'real_name', key: 'real_name', width: 100, render: (v: string) => v || '-' },
    { title: '手机', dataIndex: 'phone', key: 'phone', width: 120, render: (v: string) => v || '-' },
    { title: '租户数', dataIndex: 'tenant_count', key: 'tc', width: 70 },
    {
      title: '状态', key: 'active', width: 70,
      render: (_: any, r: any) => <Switch checked={r.is_active} onChange={v => handleToggle(r.id, 'is_active', v)} disabled={r.id === currentUserId} size="small" />
    },
    {
      title: '超管', key: 'sa', width: 70,
      render: (_: any, r: any) => <Switch checked={r.is_super_admin} onChange={v => handleToggle(r.id, 'is_super_admin', v)} disabled={r.id === currentUserId} size="small" />
    },
    { title: '注册时间', dataIndex: 'created_at', key: 'cat', width: 150, render: (v: string) => v ? new Date(v).toLocaleString('zh-CN') : '-' },
    { title: '金币余额', dataIndex: 'coin_balance', key: 'coin', width: 90, render: (v: number) => v || 0 },
    {
      title: '操作', key: 'ops', width: 240,
      render: (_: any, r: any) => (
        <Space size="small">
          <Button size="small" icon={<EyeOutlined />} onClick={() => showDetail(r.id)}>详情</Button>
          <Button size="small" icon={<MailOutlined />} onClick={async () => {
            try { openEmailComposer(await adminAPI.getUserDetail(r.id)); }
            catch { message.error('加载用户信息失败'); }
          }}>邮件</Button>
          <Button size="small" onClick={() => setResetModal(r.id)}>重置密码</Button>
          <Button size="small" icon={<DollarOutlined />} onClick={() => setCoinModal(r.id)}>调整金币</Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Search placeholder="搜索邮箱/姓名/手机" onSearch={v => { setSearch(v); fetchUsers(v); }} allowClear style={{ width: 300, maxWidth: '100%' }} />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { createForm.resetFields(); setCreateModal(true); }}>新增用户</Button>
        <Text type="secondary">共 {total} 个用户</Text>
      </Space>
      <Table columns={columns} dataSource={users} rowKey="id" loading={loading}
        pagination={{ pageSize: 20 }} scroll={{ x: 900 }} size="small" />

      <Drawer
        title={detailUser ? `用户: ${detailUser.real_name || detailUser.email}` : '用户详情'}
        open={!!detailUser}
        onClose={() => { setDetailUser(null); setEditingUser(false); }}
        width="min(720px, 100vw)"
        extra={detailUser && (
          <Space wrap>
            <Button icon={<MailOutlined />} onClick={() => openEmailComposer(detailUser)}>发送邮件</Button>
            {!editingUser ? (
              <Button type="primary" icon={<EditOutlined />} onClick={startEditUser}>编辑</Button>
            ) : (
              <>
                <Button type="primary" icon={<SaveOutlined />} onClick={handleUserSave}>保存</Button>
                <Button onClick={() => setEditingUser(false)}>取消</Button>
              </>
            )}
          </Space>
        )}
      >
        {detailUser && !editingUser && (
          <>
            <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
              <Descriptions.Item label="邮箱">{detailUser.email}</Descriptions.Item>
              <Descriptions.Item label="姓名">{detailUser.real_name || '-'}</Descriptions.Item>
              <Descriptions.Item label="用户名">{detailUser.username || '-'}</Descriptions.Item>
              <Descriptions.Item label="手机">{detailUser.phone || '-'}</Descriptions.Item>
              <Descriptions.Item label="账号状态"><Tag color={detailUser.is_active ? 'green' : 'red'}>{detailUser.is_active ? '启用' : '禁用'}</Tag></Descriptions.Item>
              <Descriptions.Item label="邮箱验证"><Tag color={detailUser.is_verified ? 'green' : 'default'}>{detailUser.is_verified ? '已验证' : '未验证'}</Tag></Descriptions.Item>
              <Descriptions.Item label="平台权限">{detailUser.is_super_admin ? <Tag color="gold">超级管理员</Tag> : '普通用户'}</Descriptions.Item>
              <Descriptions.Item label="最后登录">{detailUser.last_login_at ? new Date(detailUser.last_login_at).toLocaleString('zh-CN') : '从未'}</Descriptions.Item>
              <Descriptions.Item label="注册时间">{detailUser.created_at ? new Date(detailUser.created_at).toLocaleString('zh-CN') : '-'}</Descriptions.Item>
            </Descriptions>
            <Divider orientation="left">租户授权</Divider>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => void openAuthorizeUser()}>授权到租户</Button>
            </div>
            {detailUser.tenants?.length ? (
              <Table
                dataSource={detailUser.tenants}
                rowKey="tenant_id"
                pagination={false}
                size="small"
                scroll={{ x: 680 }}
                columns={[
                  { title: '租户', dataIndex: 'tenant_name', key: 'tenant_name' },
                  { title: 'Slug', dataIndex: 'tenant_slug', key: 'tenant_slug', width: 130, render: (value: string) => <Text code>{value}</Text> },
                  {
                    title: '状态', dataIndex: 'tenant_status', key: 'tenant_status', width: 100,
                    render: (status: string) => <Tag color={status === 'active' ? 'green' : status === 'suspended' ? 'orange' : 'default'}>
                      {status === 'active' ? '活跃' : status === 'suspended' ? '暂停/只读' : '已合并'}
                    </Tag>,
                  },
                  { title: '角色', dataIndex: 'role', key: 'role', width: 120, render: (role: string) => <Tag color={role === 'owner' ? 'gold' : role === 'admin' ? 'blue' : 'default'}>{role}</Tag> },
                  {
                    title: '标记', key: 'flags', width: 150,
                    render: (_: unknown, membership: AdminUserTenantMembership) => (
                      <Space size={4} wrap>
                        {membership.is_default && <Tag color="blue">默认</Tag>}
                        {membership.is_primary_owner && <Tag color="gold">主要负责人</Tag>}
                      </Space>
                    ),
                  },
                ]}
              />
            ) : <Empty description="暂无所属租户" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
          </>
        )}
        {detailUser && editingUser && (
          <Form form={userEditForm} layout="vertical">
            <Row gutter={16}>
              <Col xs={24} md={12}><Form.Item name="email" label="邮箱" rules={[{ required: true, type: 'email' }]}><Input /></Form.Item></Col>
              <Col xs={24} md={12}><Form.Item name="phone" label="手机"><Input /></Form.Item></Col>
              <Col xs={24} md={12}><Form.Item name="real_name" label="姓名"><Input /></Form.Item></Col>
              <Col xs={24} md={12}><Form.Item name="username" label="用户名"><Input /></Form.Item></Col>
            </Row>
            <Divider orientation="left">账号状态</Divider>
            <Row gutter={24}>
              <Col xs={24} sm={8}><Form.Item name="is_active" label="启用账号" valuePropName="checked"><Switch disabled={detailUser.id === currentUserId} /></Form.Item></Col>
              <Col xs={24} sm={8}><Form.Item name="is_verified" label="邮箱已验证" valuePropName="checked"><Switch /></Form.Item></Col>
              <Col xs={24} sm={8}><Form.Item name="is_super_admin" label="超级管理员" valuePropName="checked"><Switch disabled={detailUser.id === currentUserId} /></Form.Item></Col>
            </Row>
          </Form>
        )}
      </Drawer>

      <Modal
        title={detailUser ? `授权用户：${detailUser.real_name || detailUser.email}` : '授权用户到租户'}
        open={authorizeUserModal}
        onOk={() => void handleAuthorizeUser()}
        onCancel={() => setAuthorizeUserModal(false)}
        okText="保存授权"
        confirmLoading={authorizeUserSaving}
      >
        <Form form={authorizeUserForm} layout="vertical" initialValues={{ role: 'member', is_default: false }}>
          <Form.Item name="tenant_id" label="活动租户" rules={[{ required: true, message: '请选择租户' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={authorizeTenantOptions.map((tenant) => ({
                value: tenant.id,
                label: `${tenant.name} · ${tenant.slug}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="role" label="角色" rules={[{ required: true }]}>
            <Select options={[
              { value: 'owner', label: 'owner' },
              { value: 'admin', label: 'admin' },
              { value: 'pharmacist', label: 'pharmacist' },
              { value: 'member', label: 'member' },
              { value: 'viewer', label: 'viewer' },
            ]} />
          </Form.Item>
          <Form.Item name="is_default" valuePropName="checked">
            <Checkbox>设为该用户默认租户</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="发送邮件"
        open={!!emailUser}
        onOk={handleSendEmail}
        confirmLoading={emailSending}
        okText="发送"
        onCancel={() => { setEmailUser(null); emailForm.resetFields(); }}
        width={620}
      >
        <Form form={emailForm} layout="vertical">
          <Form.Item label="收件人"><Input value={emailUser?.email} disabled /></Form.Item>
          <Form.Item name="subject" label="主题" rules={[{ required: true, whitespace: true, message: '请输入邮件主题' }]}><Input maxLength={200} showCount /></Form.Item>
          <Form.Item name="content" label="正文" rules={[{ required: true, whitespace: true, message: '请输入邮件正文' }]}>
            <Input.TextArea rows={10} maxLength={20000} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="重置密码" open={!!resetModal} onOk={handleResetPwd} onCancel={() => { setResetModal(null); setNewPwd(''); }}>
        <Input.Password placeholder="输入新密码" value={newPwd} onChange={e => setNewPwd(e.target.value)} />
      </Modal>

      <Modal title="新增用户" open={createModal} onOk={handleCreateUser} onCancel={() => setCreateModal(false)} width={500}>
        <Form form={createForm} layout="vertical">
          <Form.Item name="email" label="邮箱" rules={[{ required: true, type: 'email' }]}><Input /></Form.Item>
          <Form.Item name="password" label="密码" rules={[{ required: true, min: 6 }]}><Input.Password /></Form.Item>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="real_name" label="姓名"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="phone" label="手机"><Input /></Form.Item></Col>
          </Row>
          <Form.Item name="username" label="用户名"><Input /></Form.Item>
          <Form.Item name="is_super_admin" label="超级管理员" valuePropName="checked" initialValue={false}><Switch /></Form.Item>
        </Form>
      </Modal>

      <Modal title="调整金币" open={!!coinModal} onOk={handleAdjustCoin} onCancel={() => { setCoinModal(null); setCoinAmount(0); setCoinReason(''); }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Text>调整数量（正数增加，负数扣减）</Text>
            <InputNumber value={coinAmount} onChange={v => setCoinAmount(v || 0)} style={{ width: '100%' }} />
          </div>
          <div>
            <Text>原因</Text>
            <Input placeholder="请输入调整原因" value={coinReason} onChange={e => setCoinReason(e.target.value)} />
          </div>
        </Space>
      </Modal>
    </>
  );
};

// ================================================================
// Tenants Tab (Enhanced)
// ================================================================
const TenantsTab: React.FC = () => {
  const [tenants, setTenants] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<any>(null);
  const [editing, setEditing] = useState(false);
  const [editForm] = Form.useForm();
  const [relocateMember, setRelocateMember] = useState<any>(null);
  const [relocateForm] = Form.useForm();
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [renameTarget, setRenameTarget] = useState<any>(null);
  const [renameValue, setRenameValue] = useState('');
  const [mergeSource, setMergeSource] = useState<any>(null);
  const [mergeTargetId, setMergeTargetId] = useState<string>();
  const [mergeJob, setMergeJob] = useState<any>(null);
  const [mergePreview, setMergePreview] = useState<any>(null);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergeSourceConfirmation, setMergeSourceConfirmation] = useState('');
  const [mergeTargetConfirmation, setMergeTargetConfirmation] = useState('');
  const [patientResolutions, setPatientResolutions] = useState<Record<string, { action: 'merge' | 'rename'; new_patient_code?: string }>>({});
  const [memberModalOpen, setMemberModalOpen] = useState(false);
  const [memberForm] = Form.useForm();
  const [memberUsers, setMemberUsers] = useState<any[]>([]);
  const [memberSaving, setMemberSaving] = useState(false);
  const tenantOwner = detail?.members?.find((member: any) => member.is_primary_owner)
    || detail?.members?.find((member: any) => member.role === 'owner');
  const normalizeTenantNameForUi = (value: unknown) => String(value || '').trim().replace(/\s+/g, ' ').toLocaleLowerCase();

  const fetchTenants = useCallback(async (search?: string) => {
    setLoading(true);
    try {
      const res = await adminAPI.listTenants(0, 200, search);
      setTenants(res.items);
    } catch { message.error('加载租户失败'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTenants(); }, [fetchTenants]);

  const handleStatusChange = async (tenantId: string, status: string) => {
    try {
      await adminAPI.updateTenant(tenantId, { status });
      setTenants(prev => prev.map(t => t.id === tenantId ? { ...t, status } : t));
      message.success('已更新');
    } catch { message.error('操作失败'); }
  };

  const showDetail = async (tenantId: string) => {
    try { setDetail(await adminAPI.getTenantDetail(tenantId)); setEditing(false); }
    catch { message.error('加载详情失败'); }
  };

  const handleEditSave = async () => {
    if (!detail) return;
    try {
      const values = await editForm.validateFields();
      await adminAPI.updateTenant(detail.id, values);
      message.success('已更新');
      setDetail({
        ...detail,
        ...values,
        settings: {
          ...(detail.settings || {}),
          ...(values.join_approval_policy ? { join_approval_policy: values.join_approval_policy } : {}),
        },
      });
      setEditing(false);
      fetchTenants();
    } catch (err: any) { message.error(err?.response?.data?.detail || '更新失败'); }
  };

  const openRenameTenant = (tenant: any) => {
    setRenameTarget(tenant);
    setRenameValue(tenant.name || '');
  };

  const handleRenameTenant = async () => {
    if (!renameTarget || !renameValue.trim()) return;
    try {
      await adminAPI.updateTenant(renameTarget.id, { name: renameValue.trim() });
      message.success('租户已重命名，Slug、数据和成员权限均保持不变');
      if (detail?.id === renameTarget.id) await showDetail(renameTarget.id);
      setRenameTarget(null);
      await fetchTenants();
    } catch (err: unknown) {
      message.error(safeErrorMessage(err, '重命名失败'));
    }
  };

  const resetMerge = () => {
    setMergeSource(null);
    setMergeTargetId(undefined);
    setMergeJob(null);
    setMergePreview(null);
    setMergeSourceConfirmation('');
    setMergeTargetConfirmation('');
    setPatientResolutions({});
  };

  const openMergeTenant = (source: any) => {
    const sameNameTarget = tenants.find((tenant) => (
      tenant.id !== source.id
      && tenant.status === 'active'
      && normalizeTenantNameForUi(tenant.name) === normalizeTenantNameForUi(source.name)
      && tenant.is_name_group_primary
    ));
    setMergeSource(source);
    setMergeTargetId(sameNameTarget?.id);
    setMergeJob(null);
    setMergePreview(null);
    setMergeSourceConfirmation('');
    setMergeTargetConfirmation('');
    setPatientResolutions({});
  };

  const handleMergePreview = async () => {
    if (!mergeSource || !mergeTargetId) return;
    setMergeLoading(true);
    try {
      const response = await adminAPI.previewTenantMerge(mergeSource.id, mergeTargetId);
      setMergeJob(response.job_id);
      setMergePreview(response.preview);
      const automaticResolutions: Record<string, { action: 'merge' | 'rename' }> = {};
      (response.preview?.patient_conflicts || []).forEach((conflict: any) => {
        if (conflict.auto_merge_safe) automaticResolutions[conflict.source.id] = { action: 'merge' };
      });
      setPatientResolutions(automaticResolutions);
      message.success('合并预检完成，尚未修改任何数据');
    } catch (err: unknown) {
      message.error(safeErrorMessage(err, '合并预检失败'));
    } finally {
      setMergeLoading(false);
    }
  };

  const unresolvedPatientConflict = (mergePreview?.patient_conflicts || []).some((conflict: any) => {
    if (conflict.auto_merge_safe) return false;
    const resolution = patientResolutions[conflict.source.id];
    return !resolution || (resolution.action === 'rename' && !resolution.new_patient_code?.trim());
  });

  const handleExecuteMerge = async () => {
    if (!mergeJob || !mergePreview || unresolvedPatientConflict) return;
    setMergeLoading(true);
    try {
      const patient_resolutions = Object.entries(patientResolutions).map(([source_patient_id, resolution]) => ({
        source_patient_id,
        action: resolution.action,
        ...(resolution.action === 'merge'
          ? { target_patient_id: mergePreview.patient_conflicts.find((item: any) => item.source.id === source_patient_id)?.target.id }
          : { new_patient_code: resolution.new_patient_code?.trim() }),
      }));
      const result = await adminAPI.executeTenantMerge({
        job_id: mergeJob,
        source_confirmation_slug: mergeSourceConfirmation,
        target_confirmation_slug: mergeTargetConfirmation,
        patient_resolutions,
      });
      message.success(`租户合并完成：目标租户现有 ${result.after_target?.cases ?? 0} 个病例、${result.after_target?.calculations ?? 0} 条计算记录`);
      if (detail?.id === mergeSource?.id) setDetail(null);
      resetMerge();
      await fetchTenants();
    } catch (err: unknown) {
      message.error(safeErrorMessage(err, '执行合并失败'));
    } finally {
      setMergeLoading(false);
    }
  };

  const openAuthorizeMember = async () => {
    if (!detail) return;
    setMemberModalOpen(true);
    memberForm.resetFields();
    try {
      const response = await adminAPI.listUsers(0, 200);
      const existingIds = new Set((detail.members || []).map((member: any) => member.user_id));
      setMemberUsers((response.items || []).filter((user: any) => user.is_active && !existingIds.has(user.id)));
    } catch {
      message.error('加载可授权用户失败');
    }
  };

  const handleAuthorizeMember = async () => {
    if (!detail) return;
    try {
      const values = await memberForm.validateFields();
      setMemberSaving(true);
      await adminAPI.authorizeTenantMember(detail.id, values);
      message.success('成员授权已保存');
      setMemberModalOpen(false);
      await showDetail(detail.id);
    } catch (err: any) {
      if (!err?.errorFields) message.error(safeErrorMessage(err, '成员授权失败'));
    } finally {
      setMemberSaving(false);
    }
  };

  const updateMemberAuthorization = async (member: any, changes: { role?: string; is_default?: boolean }) => {
    if (!detail) return;
    try {
      await adminAPI.updateTenantMemberAuthorization(detail.id, member.user_id, changes);
      message.success('成员权限已更新');
      await showDetail(detail.id);
    } catch (err: unknown) {
      message.error(safeErrorMessage(err, '成员权限更新失败'));
    }
  };

  const revokeMemberAuthorization = async (member: any) => {
    if (!detail) return;
    try {
      await adminAPI.revokeTenantMember(detail.id, member.user_id);
      message.success('成员授权已撤销');
      await Promise.all([showDetail(detail.id), fetchTenants()]);
    } catch (err: unknown) {
      message.error(safeErrorMessage(err, '撤销授权失败'));
    }
  };

  const handleOwnerChange = async (userId: string) => {
    if (!detail) return;
    try {
      await adminAPI.updateTenantOwner(detail.id, userId);
      message.success('租户管理员已更新');
      await showDetail(detail.id);
    } catch (err: any) {
      message.error(err?.response?.data?.detail || '管理员调整失败');
    }
  };

  const openRelocateMember = (member: any) => {
    const sameNameTarget = tenants.find((tenant) => (
      tenant.id !== detail?.id
      && tenant.status === 'active'
      && normalizeTenantNameForUi(tenant.name) === normalizeTenantNameForUi(detail?.name)
      && tenant.is_name_group_primary
    ));
    relocateForm.setFieldsValue({
      mode: sameNameTarget ? 'existing' : 'new',
      target_tenant_id: sameNameTarget?.id,
      new_tenant_name: detail?.name,
      confirm_data_stays_with_source: false,
    });
    setRelocateMember(member);
  };

  const handleRelocateMember = async () => {
    if (!detail || !relocateMember) return;
    try {
      const values = await relocateForm.validateFields();
      await adminAPI.relocateTenantMember(detail.id, relocateMember.user_id, {
        target_tenant_id: values.mode === 'existing' ? values.target_tenant_id : undefined,
        new_tenant_name: values.mode === 'new' ? values.new_tenant_name : undefined,
        confirm_data_stays_with_source: values.confirm_data_stays_with_source,
      });
      message.success('成员租户归属已更新');
      setRelocateMember(null);
      relocateForm.resetFields();
      await Promise.all([showDetail(detail.id), fetchTenants()]);
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(safeErrorMessage(err, '调整租户归属失败'));
    }
  };

  const openDeleteTenant = (tenant: any) => {
    setDeleteTarget(tenant);
    setDeleteConfirmation('');
  };

  const handleDeleteTenant = async () => {
    if (!deleteTarget || deleteConfirmation !== deleteTarget.slug) return;
    setDeleting(true);
    try {
      await adminAPI.deleteTenant(deleteTarget.id, deleteConfirmation);
      message.success('租户已删除');
      if (detail?.id === deleteTarget.id) {
        setDetail(null);
        setEditing(false);
      }
      setDeleteTarget(null);
      setDeleteConfirmation('');
      await fetchTenants();
    } catch (err: unknown) {
      message.error(safeErrorMessage(err, '删除租户失败'));
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      title: '名称', dataIndex: 'name', key: 'name', width: 200,
      render: (v: string, r: any) => (
        <Space wrap size={[6, 4]}>
          <a onClick={() => showDetail(r.id)}>{v}</a>
          {r.name_group_count > 1 && <Tag color="orange">同名 {r.name_group_count}</Tag>}
          {r.name_group_count > 1 && r.is_name_group_primary && <Tag color="blue">最早记录</Tag>}
          {r.status === 'merged' && <Tag color="default">已合并</Tag>}
        </Space>
      )
    },
    { title: 'Slug', dataIndex: 'slug', key: 'slug', width: 120, render: (v: string) => <Text code>{v}</Text> },
    {
      title: '套餐', dataIndex: 'plan', key: 'plan', width: 100,
      render: (v: string) => {
        const colors: Record<string, string> = { free: 'default', basic: 'blue', pro: 'purple', enterprise: 'gold' };
        return <Tag color={colors[v] || 'default'}>{v}</Tag>;
      }
    },
    {
      title: '主管理员', key: 'owner', width: 190,
      render: (_: unknown, tenant: any) => tenant.owner_email ? (
        <Space direction="vertical" size={0}>
          <Text>{tenant.owner_name || tenant.owner_email}</Text>
          {tenant.owner_name && <Text type="secondary">{tenant.owner_email}</Text>}
        </Space>
      ) : <Text type="danger">未设置</Text>,
    },
    { title: '成员数', dataIndex: 'member_count', key: 'mc', width: 80, render: (v: number) => <Badge count={v} showZero style={{ backgroundColor: '#0B2545' }} /> },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 80,
      render: (v: string) => {
        const meta: Record<string, { color: string; label: string }> = {
          active: { color: 'green', label: '活跃' },
          suspended: { color: 'orange', label: '暂停/只读' },
          merged: { color: 'default', label: '已合并' },
        };
        return <Tag color={meta[v]?.color || 'default'}>{meta[v]?.label || v}</Tag>;
      }
    },
    { title: '创建时间', dataIndex: 'created_at', key: 'cat', width: 150, render: (v: string) => v ? new Date(v).toLocaleString('zh-CN') : '-' },
    {
      title: '操作', key: 'ops', width: 360,
      render: (_: any, r: any) => (
        <Space size="small" wrap>
          <Button size="small" icon={<EyeOutlined />} onClick={() => showDetail(r.id)}>详情</Button>
          {r.status !== 'merged' && (
            <Button size="small" icon={<EditOutlined />} onClick={() => openRenameTenant(r)}>重命名</Button>
          )}
          {r.status !== 'merged' && r.id !== '00000000-0000-0000-0000-000000000001' && (
            <Button size="small" icon={<SwapOutlined />} onClick={() => openMergeTenant(r)}>合并</Button>
          )}
          {r.status === 'active'
            ? <Popconfirm title="确认暂停此租户?" onConfirm={() => handleStatusChange(r.id, 'suspended')}><Button size="small" danger>暂停</Button></Popconfirm>
            : r.status === 'suspended' ? (
              <>
                <Button size="small" type="primary" onClick={() => handleStatusChange(r.id, 'active')}>恢复</Button>
                {r.id !== '00000000-0000-0000-0000-000000000001' && (
                  <Button size="small" danger icon={<DeleteOutlined />} onClick={() => openDeleteTenant(r)}>删除</Button>
                )}
              </>
            ) : null}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Search placeholder="搜索租户名称" onSearch={v => fetchTenants(v)} allowClear style={{ width: 300, maxWidth: '100%' }} />
        <Button icon={<ReloadOutlined />} onClick={() => fetchTenants()} loading={loading}>刷新</Button>
        <Text type="secondary">共 {tenants.length} 个租户</Text>
      </Space>
      <Table columns={columns} dataSource={tenants} rowKey="id" loading={loading} pagination={{ pageSize: 20 }} scroll={{ x: 1240 }} size="small" />

      <Drawer
        title={detail ? `租户: ${detail.name}` : '租户详情'}
        open={!!detail}
        onClose={() => { setDetail(null); setEditing(false); }}
        width="min(820px, 100vw)"
        extra={!editing ? (
          <Space>
            {detail?.status === 'suspended' && detail?.id !== '00000000-0000-0000-0000-000000000001' && (
              <Button danger icon={<DeleteOutlined />} onClick={() => openDeleteTenant(detail)}>删除</Button>
            )}
            {detail?.status !== 'merged' && (
              <Button icon={<EditOutlined />} onClick={() => openRenameTenant(detail)}>重命名</Button>
            )}
            {detail?.status !== 'merged' && (
              <Button icon={<SwapOutlined />} onClick={() => openMergeTenant(detail)}>合并</Button>
            )}
            {detail?.status !== 'merged' && <Button icon={<SettingOutlined />} onClick={() => {
              editForm.setFieldsValue({
                plan: detail?.plan,
                status: detail?.status,
                join_approval_policy: detail?.settings?.join_approval_policy || 'either',
              });
              setEditing(true);
            }}>套餐/状态</Button>}
          </Space>
        ) : (
          <Space>
            <Button type="primary" onClick={handleEditSave}>保存</Button>
            <Button onClick={() => setEditing(false)}>取消</Button>
          </Space>
        )}
      >
        {detail && !editing && (
          <>
            <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small" title="基本信息" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="名称">{detail.name}</Descriptions.Item>
              <Descriptions.Item label="Slug"><Text code>{detail.slug}</Text></Descriptions.Item>
              <Descriptions.Item label="套餐"><Tag color="blue">{detail.plan}</Tag></Descriptions.Item>
              <Descriptions.Item label="加入审批策略">
                {({
                  tenant_admin_only: '仅租户管理员',
                  system_admin_only: '仅系统管理员',
                  either: '租户管理员或系统管理员',
                } as Record<string, string>)[detail.settings?.join_approval_policy || 'either'] || '租户管理员或系统管理员'}
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={detail.status === 'active' ? 'green' : detail.status === 'suspended' ? 'orange' : 'default'}>
                  {detail.status === 'active' ? '活跃' : detail.status === 'suspended' ? '暂停/只读' : '已合并'}
                </Tag>
              </Descriptions.Item>
              {detail.status === 'merged' && <Descriptions.Item label="合并目标"><Text code>{detail.merged_into_tenant_id}</Text></Descriptions.Item>}
              <Descriptions.Item label="创建时间">{detail.created_at ? new Date(detail.created_at).toLocaleString('zh-CN') : '-'}</Descriptions.Item>
              <Descriptions.Item label="总计算量"><Text strong style={{ color: '#0B2545' }}>{detail.total_calculations}</Text></Descriptions.Item>
              <Descriptions.Item label="租户管理员">
                {tenantOwner ? (
                  <Space wrap>
                    <CrownOutlined style={{ color: '#d48806' }} />
                    <Text strong>{tenantOwner.real_name || tenantOwner.email}</Text>
                    {tenantOwner.real_name && <Text type="secondary">{tenantOwner.email}</Text>}
                  </Space>
                ) : <Text type="danger">未设置</Text>}
              </Descriptions.Item>
            </Descriptions>
            <Divider orientation="left">订阅管理</Divider>
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <Button size="small" icon={<ReloadOutlined />} onClick={() => showDetail(detail.id)}>刷新</Button>
              </div>
              {detail.subscriptions?.length > 0 ? (
                <Table
                  dataSource={detail.subscriptions}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  scroll={{ x: 520 }}
                  columns={[
                    {
                      title: '套餐', dataIndex: 'plan_display_name', key: 'plan', width: 100,
                      render: (v: string, r: any) => (
                        <Space>
                          <Tag color={r.sort_order >= 3 ? 'gold' : r.sort_order >= 2 ? 'blue' : 'default'}>{v}</Tag>
                          {r.is_active && <Tag color="green" style={{ fontSize: 10 }}>生效</Tag>}
                        </Space>
                      ),
                    },
                    {
                      title: '状态', dataIndex: 'status', key: 'status', width: 80,
                      render: (v: string) => <Tag color={v === 'active' ? 'green' : 'red'}>{v === 'active' ? '活跃' : '已取消'}</Tag>,
                    },
                    {
                      title: '到期时间', dataIndex: 'current_period_end', key: 'end', width: 150,
                      render: (v: string) => v ? new Date(v).toLocaleString('zh-CN') : '-',
                    },
                    {
                      title: '操作', key: 'ops', width: 120,
                      render: (_: any, r: any) => (
                        <Space size="small">
                          {r.status === 'cancelled' && (
                            <Popconfirm title="确认重新激活此订阅？" onConfirm={async () => {
                              try {
                                await adminAPI.updateTenantSub(detail.id, r.id, { status: 'active' });
                                message.success('已重新激活');
                                showDetail(detail.id);
                                fetchTenants();
                              } catch { message.error('操作失败'); }
                            }}>
                              <Button size="small" type="primary">重新激活</Button>
                            </Popconfirm>
                          )}
                          {r.status === 'active' && (
                            <Popconfirm title="确认取消此订阅？" onConfirm={async () => {
                              try {
                                await adminAPI.updateTenantSub(detail.id, r.id, { status: 'cancelled' });
                                message.success('已取消');
                                showDetail(detail.id);
                                fetchTenants();
                              } catch { message.error('操作失败'); }
                            }}>
                              <Button size="small" danger>取消</Button>
                            </Popconfirm>
                          )}
                        </Space>
                      ),
                    },
                  ]}
                />
              ) : <Empty description="暂无订阅记录" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
            </div>
            <Divider orientation="left">成员授权</Divider>
            <div>
              {detail.status !== 'merged' && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                  <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => void openAuthorizeMember()}>授权成员</Button>
                </div>
              )}
              {detail.members?.length > 0 ? (
                <Table
                  dataSource={detail.members}
                  columns={[
                    {
                      title: '成员', key: 'member',
                      render: (_: unknown, member: any) => (
                        <div style={{ minWidth: 0 }}>
                          <Text strong ellipsis={{ tooltip: member.real_name || member.username || member.email }} style={{ display: 'block' }}>
                            {member.real_name || member.username || member.email}
                          </Text>
                          <Text type="secondary" ellipsis={{ tooltip: member.email }} style={{ display: 'block' }}>
                            {member.email}
                          </Text>
                        </div>
                      ),
                    },
                    { title: '手机', dataIndex: 'phone', key: 'phone', width: 120, responsive: ['lg'], render: (v: string) => v || '-' },
                    {
                      title: '状态', dataIndex: 'is_active', key: 'active', width: 72,
                      render: (active: boolean) => <Tag color={active ? 'green' : 'red'}>{active ? '启用' : '禁用'}</Tag>,
                    },
                    {
                      title: '角色', dataIndex: 'role', key: 'role', width: 132, responsive: ['sm'],
                      render: (v: string, member: any) => detail.status === 'merged' ? (
                        <Tag color={v === 'owner' ? 'gold' : v === 'admin' ? 'blue' : 'default'}>{v}</Tag>
                      ) : (
                        <Select
                          size="small"
                          value={v}
                          style={{ width: 116 }}
                          onChange={(role) => void updateMemberAuthorization(member, { role })}
                          options={[
                            { value: 'owner', label: 'owner' },
                            { value: 'admin', label: 'admin' },
                            { value: 'pharmacist', label: 'pharmacist' },
                            { value: 'member', label: 'member' },
                            { value: 'viewer', label: 'viewer' },
                          ]}
                        />
                      ),
                    },
                    {
                      title: '加入时间', dataIndex: 'joined_at', key: 'joined_at', width: 160, responsive: ['md'],
                      render: (v: string) => v ? new Date(v).toLocaleString('zh-CN') : '-',
                    },
                    {
                      title: '操作', key: 'actions', width: 250,
                      render: (_: unknown, member: any) => (
                        <Space size={4} wrap>
                          {member.is_primary_owner ? (
                            <Tag icon={<CrownOutlined />} color="gold">主要负责人</Tag>
                          ) : (
                            <Tooltip title={!member.is_active ? '禁用成员不能设为租户管理员' : undefined}>
                              <span>
                                <Popconfirm
                                  title="设为租户管理员"
                                  description={`确认将 ${member.real_name || member.email} 设为主要负责人？其他 owner 仍保留 owner 权限。`}
                                  onConfirm={() => handleOwnerChange(member.user_id)}
                                  disabled={!member.is_active}
                                >
                                  <Button size="small" icon={<CrownOutlined />} disabled={!member.is_active}>设为管理员</Button>
                                </Popconfirm>
                              </span>
                            </Tooltip>
                          )}
                          {!member.is_default && <Button size="small" onClick={() => void updateMemberAuthorization(member, { is_default: true })}>设为默认</Button>}
                          {member.is_default && <Tag color="blue">默认租户</Tag>}
                          <Button size="small" icon={<SwapOutlined />} onClick={() => openRelocateMember(member)}>仅移动成员</Button>
                          <Popconfirm
                            title="撤销租户授权"
                            description="仅移除成员访问权，不删除该租户的病例与历史记录。"
                            onConfirm={() => void revokeMemberAuthorization(member)}
                          >
                            <Button size="small" danger>撤销</Button>
                          </Popconfirm>
                        </Space>
                      ),
                    },
                  ]}
                  rowKey="user_id" pagination={false} tableLayout="fixed" size="small"
                />
              ) : <Empty description="暂无成员" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
            </div>
          </>
        )}
        {detail && editing && (
          <Form form={editForm} layout="vertical">
            <Form.Item name="status" label="状态">
              <Select options={[
                { value: 'active', label: '活跃' },
                { value: 'suspended', label: '暂停' },
              ]}/>
            </Form.Item>
            <Form.Item name="join_approval_policy" label="机构加入审批策略">
              <Select options={[
                { value: 'tenant_admin_only', label: '仅租户管理员审批' },
                { value: 'system_admin_only', label: '仅系统管理员审批' },
                { value: 'either', label: '租户管理员或系统管理员均可' },
              ]} />
            </Form.Item>
          </Form>
        )}
      </Drawer>

      <Modal
        title="重命名租户"
        open={!!renameTarget}
        onOk={() => void handleRenameTenant()}
        onCancel={() => setRenameTarget(null)}
        okText="保存名称"
        okButtonProps={{ disabled: !renameValue.trim() }}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="仅修改显示名称"
          description="租户 ID、唯一 Slug、套餐、成员权限及全部病例和历史记录均保持不变；旧名称会保留为审计别名。"
        />
        <Input value={renameValue} onChange={(event) => setRenameValue(event.target.value)} maxLength={200} showCount />
      </Modal>

      <Modal
        title={mergeSource ? `合并租户：${mergeSource.name}` : '合并租户'}
        open={!!mergeSource}
        onCancel={() => { if (!mergeLoading) resetMerge(); }}
        footer={null}
        width={860}
        closable={!mergeLoading}
        maskClosable={!mergeLoading}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="源租户的全部数据和授权将转入目标租户"
          description="操作在单一数据库事务内执行。成功后源租户标记为“已合并”并保留审计记录，不再允许写入；请勿用“仅移动成员”代替租户合并。"
        />
        <Form layout="vertical">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="源租户">
                <Input value={`${mergeSource?.name || ''} · ${mergeSource?.slug || ''}`} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="目标租户" required>
                <Select
                  showSearch
                  optionFilterProp="label"
                  value={mergeTargetId}
                  onChange={(value) => {
                    setMergeTargetId(value);
                    setMergePreview(null);
                    setMergeJob(null);
                    setMergeSourceConfirmation('');
                    setMergeTargetConfirmation('');
                  }}
                  options={tenants
                    .filter((tenant) => tenant.id !== mergeSource?.id && tenant.status === 'active')
                    .map((tenant) => ({ value: tenant.id, label: `${tenant.name} · ${tenant.slug} · ${tenant.member_count} 人` }))}
                  placeholder="选择保留的目标租户"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        {!mergePreview ? (
          <div style={{ textAlign: 'right' }}>
            <Button onClick={resetMerge} disabled={mergeLoading}>取消</Button>
            <Button type="primary" icon={<FileSearchOutlined />} onClick={() => void handleMergePreview()} loading={mergeLoading} disabled={!mergeTargetId} style={{ marginLeft: 8 }}>
              运行只读预检
            </Button>
          </div>
        ) : (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }} title="数据量核对">
              {Object.keys(mergePreview.source.counts || {}).map((key) => (
                <Descriptions.Item key={key} label={key}>
                  {Number(mergePreview.source.counts[key] || 0)} → {Number(mergePreview.target.counts[key] || 0)}
                </Descriptions.Item>
              ))}
            </Descriptions>

            {(mergePreview.membership_overlaps || []).length > 0 && (
              <Alert
                type="info"
                showIcon
                message={`${mergePreview.membership_overlaps.length} 位成员同时存在于两个租户`}
                description="系统会保留权限更高的角色；用户默认租户仍保持唯一，目标租户会保留一个主要负责人。"
              />
            )}

            {(mergePreview.patient_conflicts || []).length > 0 && (
              <div>
                <Divider orientation="left">患者编号冲突处理</Divider>
                <Space direction="vertical" style={{ width: '100%' }}>
                  {mergePreview.patient_conflicts.map((conflict: any) => {
                    const sourceId = conflict.source.id as string;
                    const resolution = patientResolutions[sourceId];
                    return (
                      <Card key={sourceId} size="small">
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Text>患者编号 <Text code>{conflict.source.patient_id}</Text> 在两个租户中都存在</Text>
                          {conflict.auto_merge_safe ? (
                            <Tag color="green">人口学信息无冲突，将自动合并为同一患者</Tag>
                          ) : (
                            <>
                              <Alert type="error" showIcon message="出生日期或性别存在冲突，必须人工决定" />
                              <Radio.Group
                                value={resolution?.action}
                                onChange={(event) => setPatientResolutions((previous) => ({
                                  ...previous,
                                  [sourceId]: { action: event.target.value },
                                }))}
                              >
                                <Radio value="merge">确认是同一患者，合并到目标记录</Radio>
                                <Radio value="rename">不是同一患者，修改源患者编号</Radio>
                              </Radio.Group>
                              {resolution?.action === 'rename' && (
                                <Input
                                  placeholder="输入在目标租户中唯一的新患者编号"
                                  value={resolution.new_patient_code}
                                  onChange={(event) => setPatientResolutions((previous) => ({
                                    ...previous,
                                    [sourceId]: { action: 'rename', new_patient_code: event.target.value },
                                  }))}
                                />
                              )}
                            </>
                          )}
                        </Space>
                      </Card>
                    );
                  })}
                </Space>
              </div>
            )}

            <Divider orientation="left">双重确认</Divider>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Text>输入源 Slug <Text code>{mergePreview.source.slug}</Text></Text>
                <Input value={mergeSourceConfirmation} onChange={(event) => setMergeSourceConfirmation(event.target.value)} style={{ marginTop: 8 }} />
              </Col>
              <Col xs={24} md={12}>
                <Text>输入目标 Slug <Text code>{mergePreview.target.slug}</Text></Text>
                <Input value={mergeTargetConfirmation} onChange={(event) => setMergeTargetConfirmation(event.target.value)} style={{ marginTop: 8 }} />
              </Col>
            </Row>
            <div style={{ textAlign: 'right' }}>
              <Button onClick={resetMerge} disabled={mergeLoading}>取消</Button>
              <Popconfirm
                title="确认执行不可逆的租户合并？"
                description="系统会移动源租户全部关联数据，并把源租户永久标记为已合并。"
                onConfirm={() => void handleExecuteMerge()}
                disabled={mergeSourceConfirmation !== mergePreview.source.slug
                  || mergeTargetConfirmation !== mergePreview.target.slug
                  || unresolvedPatientConflict}
              >
                <Button
                  danger
                  type="primary"
                  icon={<SwapOutlined />}
                  loading={mergeLoading}
                  disabled={mergeSourceConfirmation !== mergePreview.source.slug
                    || mergeTargetConfirmation !== mergePreview.target.slug
                    || unresolvedPatientConflict}
                  style={{ marginLeft: 8 }}
                >
                  执行合并
                </Button>
              </Popconfirm>
            </div>
          </Space>
        )}
      </Modal>

      <Modal
        title={detail ? `授权成员到：${detail.name}` : '授权成员'}
        open={memberModalOpen}
        onOk={() => void handleAuthorizeMember()}
        onCancel={() => setMemberModalOpen(false)}
        okText="保存授权"
        confirmLoading={memberSaving}
      >
        <Form form={memberForm} layout="vertical" initialValues={{ role: 'member', is_default: false }}>
          <Form.Item name="user_id" label="用户" rules={[{ required: true, message: '请选择用户' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="按邮箱或姓名选择已有用户"
              options={memberUsers.map((user) => ({
                value: user.id,
                label: `${user.real_name || user.email} · ${user.email}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="role" label="租户角色" rules={[{ required: true }]}>
            <Select options={[
              { value: 'owner', label: 'owner — 完整租户管理权限' },
              { value: 'admin', label: 'admin — 管理权限' },
              { value: 'pharmacist', label: 'pharmacist — 药师权限' },
              { value: 'member', label: 'member — 普通成员' },
              { value: 'viewer', label: 'viewer — 只读成员' },
            ]} />
          </Form.Item>
          <Form.Item name="is_default" valuePropName="checked">
            <Checkbox>将该租户设为此用户的默认租户（会自动取消其原默认租户）</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="永久删除租户"
        open={!!deleteTarget}
        onOk={() => void handleDeleteTenant()}
        onCancel={() => {
          if (deleting) return;
          setDeleteTarget(null);
          setDeleteConfirmation('');
        }}
        okText="永久删除"
        cancelText="取消"
        confirmLoading={deleting}
        okButtonProps={{
          danger: true,
          disabled: !deleteTarget || deleteConfirmation !== deleteTarget.slug,
        }}
        closable={!deleting}
        maskClosable={!deleting}
      >
        {deleteTarget && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Alert
              type="error"
              showIcon
              message="此操作不可撤销"
              description="后端仅允许删除已暂停且完全没有成员、订阅、病例、计算、订单及其他关联记录的租户。若仍有关联数据，本次删除会被拒绝。"
            />
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="租户名称">{deleteTarget.name}</Descriptions.Item>
              <Descriptions.Item label="唯一 Slug"><Text code copyable>{deleteTarget.slug}</Text></Descriptions.Item>
            </Descriptions>
            <div>
              <Text>请输入唯一 Slug <Text code>{deleteTarget.slug}</Text> 以确认：</Text>
              <Input
                value={deleteConfirmation}
                onChange={(event) => setDeleteConfirmation(event.target.value)}
                placeholder="输入完整 Slug"
                autoComplete="off"
                style={{ marginTop: 8 }}
              />
            </div>
          </Space>
        )}
      </Modal>

      <Modal
        title={relocateMember ? `调整租户归属: ${relocateMember.real_name || relocateMember.email}` : '调整租户归属'}
        open={!!relocateMember}
        onOk={handleRelocateMember}
        onCancel={() => { setRelocateMember(null); relocateForm.resetFields(); }}
        okText="确认调整"
        width={620}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="仅调整成员归属"
          description="病例、计算、订阅及其他业务数据仍保留在原租户。若原租户只剩该成员，迁出后原租户会自动暂停。"
        />
        <Form form={relocateForm} layout="vertical">
          <Form.Item name="mode" label="调整方式" rules={[{ required: true }]}>
            <Radio.Group>
              <Radio.Button value="existing">移动到已有租户</Radio.Button>
              <Radio.Button value="new">拆分为新租户</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(previous, current) => previous.mode !== current.mode}>
            {({ getFieldValue }) => getFieldValue('mode') === 'existing' ? (
              <Form.Item name="target_tenant_id" label="目标租户" rules={[{ required: true, message: '请选择目标租户' }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={tenants
                    .filter((tenant) => tenant.id !== detail?.id && tenant.status === 'active')
                    .map((tenant) => ({
                      value: tenant.id,
                      label: `${tenant.name} · ${tenant.slug}${tenant.is_name_group_primary ? ' · 最早记录' : ''}`,
                    }))}
                />
              </Form.Item>
            ) : (
              <Form.Item name="new_tenant_name" label="新租户名称" rules={[{ required: true, whitespace: true, message: '请输入新租户名称' }]}>
                <Input maxLength={200} />
              </Form.Item>
            )}
          </Form.Item>
          <Form.Item
            name="confirm_data_stays_with_source"
            valuePropName="checked"
            rules={[{
              validator: (_, checked) => checked ? Promise.resolve() : Promise.reject(new Error('请确认业务数据保留规则')),
            }]}
          >
            <Checkbox>我确认业务数据不会自动迁移，并已核对该成员的数据归属</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// ================================================================
// Drugs Tab (Database-backed Drug Configuration Management)
// ================================================================
const ADMINISTRATION_MODE_OPTIONS = [
  { value: 'intermittent', label: '静脉滴注' },
  { value: 'continuous', label: '连续输注' },
  { value: 'oral', label: '口服' },
  { value: 'bolus', label: '静脉注射' },
] as const;

const ADMINISTRATION_MODE_LABELS: Record<string, string> = {
  intermittent: '静脉滴注',
  continuous: '连续输注',
  oral: '口服',
  bolus: '静脉注射',
};

const getAdministrationModeLabel = (mode: string): string => (
  ADMINISTRATION_MODE_LABELS[mode] || mode
);

const normalizeLoadingProfiles = (config: any, supportedModes: string[]) => {
  const policy = config?.loading_dose_policy;
  if (Array.isArray(policy?.profiles)) return policy.profiles;
  if (policy?.profiles && typeof policy.profiles === 'object') {
    return Object.entries(policy.profiles).map(([mode, value]) => ({
      ...(value as Record<string, any>),
      id: (value as Record<string, any>)?.id || mode,
      administration_mode: (value as Record<string, any>)?.administration_mode || mode,
    }));
  }
  const legacy = config?.loading_dose;
  if (!legacy || legacy.enabled !== true) return supportedModes.map((mode) => ({
    id: mode,
    administration_mode: mode,
    enabled: false,
    basis: 'mg_kg',
    value: null,
    count: 1,
    interval_h: null,
    infusion_duration_h: mode === 'intermittent' ? config?.default_infusion_duration_h ?? 0 : 0,
  }));
  const basis = legacy.dose_mg_kg != null ? 'mg_kg' : legacy.dose_mg_m2 != null ? 'mg_m2' : 'mg';
  const value = legacy.dose_mg_kg ?? legacy.dose_mg_m2 ?? legacy.dose_mg;
  return supportedModes.map((mode) => ({
    id: mode,
    administration_mode: mode,
    enabled: true,
    basis,
    value,
    count: legacy.count ?? 1,
    interval_h: legacy.interval_h ?? null,
    infusion_duration_h: mode === 'intermittent' ? legacy.infusion_duration_h ?? config?.default_infusion_duration_h ?? 0 : 0,
  }));
};

const REGIMEN_DOSE_BASIS_OPTIONS = [
  { value: 'mg_kg', label: '按体重（mg/kg）' },
  { value: 'mg', label: '固定剂量（mg）' },
  { value: 'mg_m2', label: '按体表面积（mg/m²）' },
];

const REGIMEN_CONDITION_OPERATOR_OPTIONS = [
  { value: '<', label: '<' },
  { value: '<=', label: '≤' },
  { value: '==', label: '=' },
  { value: '!=', label: '≠' },
  { value: '>=', label: '≥' },
  { value: '>', label: '>' },
  { value: 'in', label: '属于' },
];

const REGIMEN_SELECTION_MODE_OPTIONS = [
  { value: 'exclusive', label: '自动匹配（优先级）' },
  { value: 'user_select', label: '由用户选择' },
  { value: 'candidate_set', label: '合并候选集' },
];

const REGIMEN_POLICY_OPTIONS = [
  { value: 'hard_block', label: '阻断计算' },
  { value: 'warning', label: '警告后继续' },
  { value: 'ignore', label: '忽略' },
];

const parseRegimenConditionValue = (value: unknown): string | number | boolean => {
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  const text = String(value ?? '').trim();
  if (text === 'true') return true;
  if (text === 'false') return false;
  const numeric = Number(text);
  return text !== '' && Number.isFinite(numeric) ? numeric : text;
};

const normalizeRegimenProfiles = (config: any, supportedModes: string[]) => {
  const rawProfiles = Array.isArray(config?.regimen_profiles) ? config.regimen_profiles : [];
  const legacyBasis = config?.dose_basis || (config?.candidate_doses_mg?.length ? 'mg' : config?.candidate_doses_mg_m2?.length ? 'mg_m2' : 'mg_kg');
  const legacyDoseValues = legacyBasis === 'mg'
    ? config?.candidate_doses_mg || []
    : legacyBasis === 'mg_m2'
      ? config?.candidate_doses_mg_m2 || []
      : config?.candidate_doses_mg_kg || [];
  const profiles = rawProfiles.length ? rawProfiles : [{
    id: 'legacy_default',
    name: '默认给药方案',
    priority: 0,
    enabled: true,
    dose_basis: legacyBasis,
    candidate_doses: legacyDoseValues,
    candidate_intervals_h: config?.candidate_intervals_h || [],
    supported_administration_modes: supportedModes,
    patient_conditions: {},
  }];
  return profiles.map((profile: any, index: number) => {
    const conditions = profile?.patient_conditions?.all
      || (profile?.patient_conditions?.field ? [profile.patient_conditions] : []);
    return {
      id: String(profile?.id || `regimen-${index + 1}`),
      name: String(profile?.name || profile?.label || `方案 ${index + 1}`),
      priority: Number(profile?.priority ?? 0),
      enabled: profile?.enabled !== false,
      fallback: profile?.fallback === true,
      population_tags: Array.isArray(profile?.population_tags)
        ? profile.population_tags.join(', ')
        : String(profile?.population_tags || ''),
      selection_mode: profile?.selection_mode || '',
      dose_basis: profile?.dose_basis || 'mg_kg',
      candidate_doses_text: Array.isArray(profile?.candidate_doses)
        ? profile.candidate_doses.join(', ')
        : '',
      candidate_intervals_text: Array.isArray(profile?.candidate_intervals_h)
        ? profile.candidate_intervals_h.join(', ')
        : '',
      supported_administration_modes: normalizeAdministrationModes(
        profile?.supported_administration_modes,
        supportedModes,
      ),
      conditions: Array.isArray(conditions)
        ? conditions.map((condition: any) => ({
            field: String(condition?.field || ''),
            operator: String(condition?.op || condition?.operator || '=='),
            value: condition?.value ?? '',
          }))
        : [],
    };
  });
};

const buildRegimenPreviewConfig = (values: any): Record<string, any> => ({
  regimen_selection_policy: {
    default_mode: values.regimen_default_mode || 'exclusive',
    missing_covariates: values.regimen_missing_covariates || 'hard_block',
    unmatched_profile: values.regimen_unmatched_profile || 'hard_block',
    overlap_policy: values.regimen_overlap_policy || 'priority_then_safety',
  },
  regimen_profiles: (Array.isArray(values.regimen_profiles) ? values.regimen_profiles : []).map((profile: any, index: number) => ({
    id: String(profile?.id || `regimen-${index + 1}`),
    name: String(profile?.name || `方案 ${index + 1}`),
    enabled: profile?.enabled !== false,
    fallback: profile?.fallback === true,
    population_tags: String(profile?.population_tags || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean),
    priority: Number(profile?.priority ?? 0),
    selection_mode: profile?.selection_mode || undefined,
    dose_basis: profile?.dose_basis || 'mg_kg',
    candidate_doses: String(profile?.candidate_doses_text || '').split(',').map((value: string) => Number(value.trim())).filter((value: number) => Number.isFinite(value) && value > 0),
    candidate_intervals_h: String(profile?.candidate_intervals_text || '').split(',').map((value: string) => Number(value.trim())).filter((value: number) => Number.isFinite(value) && value > 0),
    supported_administration_modes: profile?.supported_administration_modes || [],
    patient_conditions: Array.isArray(profile?.conditions) && profile.conditions.length
      ? { all: profile.conditions.filter((condition: any) => condition?.field && condition?.value !== '').map((condition: any) => ({ field: condition.field, op: condition.operator || '==', value: parseRegimenConditionValue(condition.value) })) }
      : {},
  })),
});

const normalizeRegimenPolicyForm = (config: any) => {
  const policy = config?.regimen_selection_policy || {};
  return {
    regimen_default_mode: policy.default_mode || 'exclusive',
    regimen_missing_covariates: policy.missing_covariates || 'hard_block',
    regimen_unmatched_profile: policy.unmatched_profile || 'hard_block',
    regimen_overlap_policy: policy.overlap_policy || policy.tie_breaker || 'priority_then_safety',
  };
};

const TARGET_DEFAULTS: Record<string, { parameter: string; min: number | null; max: number | null; unit: string; reference: string }> = {
  'Target AUC': { parameter: 'AUC24', min: 400, max: 600, unit: 'mg*h/L', reference: '' },
  'AUC/MIC': { parameter: 'AUC24/MIC', min: 400, max: 600, unit: 'h', reference: '' },
  'Target Concentration': { parameter: 'Cmin', min: 15, max: 20, unit: 'mg/L', reference: '' },
  'Target Cmax': { parameter: 'Cmax', min: 20, max: 40, unit: 'mg/L', reference: '' },
  '%fT>MIC': { parameter: '%fT>MIC', min: 40, max: 100, unit: '%', reference: '' },
  'Cmax/MIC': { parameter: 'Cmax/MIC', min: 8, max: 12, unit: 'ratio', reference: '' },
};

type SafetyRuleCategory = 'dose' | 'exposure' | 'administration';

const SAFETY_RULE_CATEGORY_META: Record<SafetyRuleCategory, { label: string; color: string }> = {
  dose: { label: '剂量边界', color: 'blue' },
  exposure: { label: '暴露边界', color: 'orange' },
  administration: { label: '给药管理', color: 'green' },
};

const getSafetyRuleCategory = (metric: unknown): SafetyRuleCategory => {
  const value = String(metric || '').toLowerCase();
  if (value.startsWith('exposure.')) return 'exposure';
  if (value.startsWith('administration.')) return 'administration';
  return 'dose';
};

const normalizeAdministrationModes = (value: unknown, fallback: string[] = ['intermittent']): string[] => {
  const normalize = (items: unknown[]) => Array.from(new Set(
    items
      .map((item) => String(item ?? '').trim().toLowerCase())
      .filter((item) => item === 'intermittent' || item === 'continuous' || item === 'oral' || item === 'bolus'),
  ));

  const normalized = normalize(Array.isArray(value) ? value : (value ? [value] : []));
  if (normalized.length > 0) return normalized;
  return normalize(fallback);
};

const normalizeRoutes = (value: unknown): string[] => {
  const items = Array.isArray(value) ? value : (value ? [value] : []);
  return Array.from(new Set(
    items
      .map((item) => String(item ?? '').trim())
      .filter(Boolean),
  ));
};

const inferAdministrationModesFromRoute = (routeValue: unknown): string[] => {
  const routes = normalizeRoutes(routeValue);
  const modes = new Set<string>();

  routes.forEach((route) => {
    const normalized = route.toLowerCase();
    const looksOral = route.includes('口服') || normalized.includes('oral') || normalized === 'po';
    const looksContinuous = route.includes('连续') || route.includes('持续') || route.includes('泵入') || normalized.includes('continuous');
    const looksBolus = (
      route.includes('静脉注射')
      || route.includes('静推')
      || normalized.includes('bolus')
      || normalized.includes('iv injection')
    );
    const looksInfusion = (
      route.includes('滴注')
      || route.includes('输注')
      || normalized.includes('infusion')
    );
    const looksParenteral = (
      route.includes('静脉')
      || route.includes('注射')
      || route.includes('滴注')
      || route.includes('输注')
      || route.includes('肌注')
      || route.includes('皮下')
      || normalized.includes('intravenous')
      || normalized.includes('infusion')
      || normalized === 'iv'
      || normalized === 'im'
      || normalized.includes('subcutaneous')
    );

    if (looksOral) modes.add('oral');
    if (looksContinuous) modes.add('continuous');
    if (looksBolus) modes.add('bolus');
    if (looksInfusion || (looksParenteral && !looksBolus) || (!looksOral && !looksContinuous && !looksBolus)) {
      modes.add('intermittent');
    }
  });

  return normalizeAdministrationModes(Array.from(modes), []);
};

const resolveSupportedAdministrationModes = (
  value: unknown,
  routeValue: unknown,
  fallback: string[] = ['intermittent'],
): string[] => {
  const explicit = normalizeAdministrationModes(value, []);
  const inferred = inferAdministrationModesFromRoute(routeValue);

  if (inferred.length === 0) {
    return explicit.length > 0 ? explicit : normalizeAdministrationModes(fallback);
  }

  if (inferred.length === 1 && inferred[0] === 'oral') {
    return ['oral'];
  }

  if (explicit.length === 0) {
    return inferred;
  }

  return normalizeAdministrationModes([...explicit, ...inferred], inferred);
};

const resolveRouteAwareDefaultAdministrationMode = (
  value: unknown,
  supportedModes: unknown,
  routeValue: unknown,
): string => {
  const inferred = inferAdministrationModesFromRoute(routeValue);
  if (inferred.length === 1 && inferred[0] === 'oral') {
    return 'oral';
  }
  return resolveDefaultAdministrationMode(value, supportedModes);
};

const clampAdministrationModesToSupported = (value: unknown, supportedModes: unknown, fallback: string[] = ['intermittent']): string[] => {
  const normalizedSupported = normalizeAdministrationModes(supportedModes, fallback);
  const normalizedValue = normalizeAdministrationModes(value, fallback);
  const clamped = normalizedValue.filter((mode) => normalizedSupported.includes(mode));
  return clamped.length > 0 ? clamped : normalizedSupported;
};

const resolveDefaultAdministrationMode = (value: unknown, supportedModes: unknown): string => {
  const normalizedSupported = normalizeAdministrationModes(supportedModes, ['intermittent']);
  const requested = String(value ?? '').trim().toLowerCase();
  return normalizedSupported.includes(requested) ? requested : (normalizedSupported[0] || 'intermittent');
};

const resolveTargetDefaultModes = (method: string, supportedModes: unknown): string[] => {
  const normalizedSupported = normalizeAdministrationModes(supportedModes, ['intermittent']);
  const intermittentModes = normalizedSupported.filter((mode) => mode !== 'continuous');
  const fallbackByMethod: Record<string, string[]> = {
    'Target AUC': normalizedSupported,
    'AUC/MIC': normalizedSupported,
    '%fT>MIC': normalizedSupported,
    'Target Concentration': intermittentModes,
    'Target Cmax': intermittentModes,
    'Cmax/MIC': intermittentModes,
  };
  const candidateModes = normalizeAdministrationModes(
    fallbackByMethod[method] || ['intermittent'],
    normalizedSupported,
  ).filter((mode) => normalizedSupported.includes(mode));
  return candidateModes.length > 0 ? candidateModes : normalizedSupported;
};

const buildDefaultTargetConfig = (method: string, supportedModes: unknown): Record<string, any> => {
  const fallback = TARGET_DEFAULTS[method] || { parameter: '', min: null, max: null, unit: '', reference: '' };
  const administrationModes = resolveTargetDefaultModes(method, supportedModes);
  const parameter = method === 'Target Concentration'
    && administrationModes.length === 1
    && administrationModes[0] === 'continuous'
    ? 'Css'
    : fallback.parameter;
  return {
    method,
    parameter,
    min: fallback.min,
    max: fallback.max,
    unit: fallback.unit,
    reference: fallback.reference,
    administration_modes: administrationModes,
  };
};

const normalizeTargetConfig = (target: any, supportedModes: unknown): Record<string, any> => {
  const method = typeof target?.method === 'string' && target.method.trim()
    ? target.method.trim()
    : 'Target AUC';
  const defaults = buildDefaultTargetConfig(method, supportedModes);
  const administrationModes = clampAdministrationModesToSupported(
    target?.administration_modes,
    supportedModes,
    defaults.administration_modes,
  );
  const parameter = typeof target?.parameter === 'string' && target.parameter.trim()
    ? target.parameter.trim()
    : (
      method === 'Target Concentration'
      && administrationModes.length === 1
      && administrationModes[0] === 'continuous'
        ? 'Css'
        : defaults.parameter
    );
  return {
    ...defaults,
    ...target,
    method,
    parameter,
    unit: normalizePkpdTargetUnit(method, target?.unit ?? defaults.unit),
    reference: target?.reference ?? defaults.reference,
    administration_modes: administrationModes,
  };
};

const deriveContinuousTargetParameter = (targets: any[], supportedModes: unknown): string => {
  const normalizedSupported = normalizeAdministrationModes(supportedModes, ['intermittent']);
  if (!normalizedSupported.includes('continuous')) return 'Css';

  const normalizedTargets = (targets || []).map((target) => normalizeTargetConfig(target, normalizedSupported));
  const continuousTarget = normalizedTargets.find((target) => (
    target.method === 'Target Concentration'
    && Array.isArray(target.administration_modes)
    && target.administration_modes.includes('continuous')
    && String(target.parameter || '').trim()
  ));
  return continuousTarget?.parameter || 'Css';
};

const renderAdministrationModeTags = (modes: unknown) => {
  const normalized = normalizeAdministrationModes(modes, []);
  if (normalized.length === 0) {
    return <Text type="secondary">-</Text>;
  }
  return (
    <Space size={[4, 4]} wrap>
      {normalized.map((mode) => (
        <Tag key={mode} color={mode === 'continuous' ? 'purple' : (mode === 'oral' ? 'green' : 'blue')}>
          {getAdministrationModeLabel(mode)}
        </Tag>
      ))}
    </Space>
  );
};

const EVALUATION_RULE_FIELD_OPTIONS = [
  { value: 'point_estimate_status', label: '点估计状态' },
  { value: 'pta_status', label: 'PTA 达标状态（按后台 PTA 下限）' },
  { value: 'max_dose_exceeded', label: '超过硬剂量上限' },
  { value: 'dose_mg', label: '单次剂量 (mg)' },
  { value: 'dose_mg_kg', label: '单次剂量 (mg/kg)' },
  { value: 'dose_mg_m2', label: '单次剂量 (mg/m²)' },
  { value: 'daily_dose_mg', label: '日剂量 (mg/day)' },
  { value: 'predicted_auc24', label: '预测 AUC24' },
  { value: 'predicted_auc24_mic', label: '预测 AUC24/MIC' },
  { value: 'predicted_cmin', label: '预测 Cmin' },
  { value: 'predicted_cmax', label: '预测 Cmax' },
  { value: 'toxicity_risk', label: '毒性风险' },
  { value: 'pk_formula_valid', label: 'PK公式法参数有效' },
];

const EVALUATION_RULE_OPERATOR_OPTIONS = [
  { value: '==', label: '=' },
  { value: '!=', label: '!=' },
  { value: '>', label: '>' },
  { value: '>=', label: '>=' },
  { value: '<', label: '<' },
  { value: '<=', label: '<=' },
  { value: 'between', label: '介于' },
  { value: 'in', label: '属于' },
  { value: 'exists', label: '存在' },
  { value: 'not_exists', label: '不存在' },
];

const EVALUATION_OUTPUT_OPTIONS = [
  { value: 'target_met', label: '符合目标', color: 'green' },
  { value: 'clinical_review', label: '需临床复核', color: 'yellow' },
  { value: 'target_not_met', label: '未满足条件', color: 'red' },
  { value: 'recommended', label: '符合方案', color: 'green' },
  { value: 'cautious_recommended', label: '谨慎推荐', color: 'orange' },
  { value: 'cautious', label: '谨慎', color: 'yellow' },
  { value: 'not_recommended', label: '未满足条件', color: 'red' },
  { value: 'not_evaluable', label: '无法评价', color: 'blue' },
];

const EVALUATION_STATUS_PRIORITY: Record<string, number> = {
  target_met: 20,
  clinical_review: 30,
  target_not_met: 40,
  recommended: 40,
  cautious_recommended: 50,
  cautious: 60,
  not_recommended: 70,
  not_evaluable: 10,
};

const LEGACY_EVALUATION_STATUS_MAP: Record<string, string> = {
  target_met: 'target_met',
  clinical_review: 'clinical_review',
  target_not_met: 'target_not_met',
  best_feasible: 'clinical_review',
};

const DEFAULT_EVALUATION_PREVIEW_SAMPLE = JSON.stringify({
  dose_mg: 500,
  dose_mg_kg: 10,
  interval_h: 12,
  pta: 92,
  pta_target: null,
  pta_met: null,
  pta_status: 'not_configured',
  primary_target_met: true,
  predicted_auc24: 510,
  target_met_raw: true,
  max_dose_exceeded: false,
  pkpd_results: {
    'Target AUC': {
      value: 510,
      pta: 92,
      target_min: 400,
      target_max: 600,
      target_met: true,
      target_met_raw: true,
    },
  },
}, null, 2);

const DEFAULT_REMEDIAL_DOSING_CONFIG = {
  enabled: false,
  evidence_reference_set: 'liu2024_ag',
  candidate_dose_mode: 'dosage_form_grid',
  candidate_dose_multipliers: [0, 0.5, 1, 1.5, 2] as number[],
  candidate_doses_mg: [] as number[],
  min_split_dose_mg: 0,
  full_strengths_mg: [] as number[],
  reference_dose_mg: null as number | null,
  allow_split: true,
  decision_point_count: 2,
  candidate_max_dose_ratio: 2,
  max_candidate_count: 500,
  single_dose_limit_mode: 'off' as 'off' | 'warning' | 'hard',
  single_dose_limit_basis: 'current_regimen' as 'current_regimen' | 'strategy_reference',
  single_dose_limit_ratio: null as number | null,
  total_dose_limit_mode: 'off' as 'off' | 'warning' | 'hard',
  total_dose_limit_basis: 'current_regimen' as 'current_regimen' | 'strategy_reference',
  equivalence_tolerance_h: null as number | null,
  use_complexity_weight: false,
  total_dose_min_ratio: null as number | null,
  total_dose_max_ratio: null as number | null,
  therapeutic_window: {
    lower: null as number | null,
    upper: null as number | null,
    metric: 'concentration',
    unit: 'mg/L',
  },
  concentration_unit: 'mg/L',
  model_concentration_unit: 'mg/L',
  monte_carlo_n: 1000,
  monte_carlo_seed: 20240602,
  default_weights: {
    high: 1,
    low: 1,
    simple: 0,
  },
  baseline_rule: {
    take_if_delay_lt_h: null as number | null,
  },
};

const buildUnitFormValues = (source: any = {}) => ({
  dose_mass_unit: source?.units?.dose?.display_mass_unit || source?.dose_mass_unit || 'mg',
  concentration_unit: source?.units?.concentration?.display_unit || source?.concentration_unit || 'mg/L',
  model_concentration_unit: source?.units?.concentration?.model_unit || source?.model_concentration_unit || 'mg/L',
  auc_unit: source?.units?.auc?.display_unit || source?.auc_unit || 'mg*h/L',
  mic_unit: source?.units?.mic?.display_unit || source?.mic_unit || 'mg/L',
});

const parseNumberListText = (value: unknown): number[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => Number(item))
      .filter((item) => Number.isFinite(item) && item > 0);
  }
  return String(value ?? '')
    .split(',')
    .map((item) => Number(item.trim()))
    .filter((item) => Number.isFinite(item) && item > 0);
};

const buildRemedialFormValues = (source: any = {}) => {
  const cfg = source?.remedial_dosing || {};
  const window = cfg.therapeutic_window || {};
  const weights = cfg.default_weights || {};
  const baselineRule = cfg.baseline_rule || {};

  return {
    remedial_enabled: cfg.enabled ?? DEFAULT_REMEDIAL_DOSING_CONFIG.enabled,
    remedial_evidence_reference_set: cfg.evidence_reference_set ?? DEFAULT_REMEDIAL_DOSING_CONFIG.evidence_reference_set,
    remedial_candidate_dose_mode: cfg.candidate_dose_mode ?? DEFAULT_REMEDIAL_DOSING_CONFIG.candidate_dose_mode,
    remedial_candidate_dose_multipliers: Array.isArray(cfg.candidate_dose_multipliers)
      ? cfg.candidate_dose_multipliers.join(', ')
      : DEFAULT_REMEDIAL_DOSING_CONFIG.candidate_dose_multipliers.join(', '),
    remedial_candidate_doses_mg: Array.isArray(cfg.candidate_doses_mg)
      ? cfg.candidate_doses_mg.join(', ')
      : '',
    remedial_min_split_dose_mg: cfg.min_split_dose_mg ?? DEFAULT_REMEDIAL_DOSING_CONFIG.min_split_dose_mg,
    remedial_full_strengths_mg: Array.isArray(cfg.full_strengths_mg)
      ? cfg.full_strengths_mg.join(', ')
      : '',
    remedial_reference_dose_mg: cfg.reference_dose_mg ?? cfg.regular_strength_mg ?? null,
    remedial_allow_split: cfg.allow_split ?? DEFAULT_REMEDIAL_DOSING_CONFIG.allow_split,
    remedial_decision_point_count: cfg.decision_point_count ?? DEFAULT_REMEDIAL_DOSING_CONFIG.decision_point_count,
    remedial_candidate_max_dose_ratio: cfg.candidate_max_dose_ratio ?? DEFAULT_REMEDIAL_DOSING_CONFIG.candidate_max_dose_ratio,
    remedial_max_candidate_count: cfg.max_candidate_count ?? DEFAULT_REMEDIAL_DOSING_CONFIG.max_candidate_count,
    remedial_single_dose_limit_mode: cfg.single_dose_limit_mode ?? DEFAULT_REMEDIAL_DOSING_CONFIG.single_dose_limit_mode,
    remedial_single_dose_limit_basis: cfg.single_dose_limit_basis ?? DEFAULT_REMEDIAL_DOSING_CONFIG.single_dose_limit_basis,
    remedial_single_dose_limit_ratio: cfg.single_dose_limit_ratio ?? DEFAULT_REMEDIAL_DOSING_CONFIG.single_dose_limit_ratio,
    remedial_total_dose_limit_mode: cfg.total_dose_limit_mode ?? DEFAULT_REMEDIAL_DOSING_CONFIG.total_dose_limit_mode,
    remedial_total_dose_limit_basis: cfg.total_dose_limit_basis ?? DEFAULT_REMEDIAL_DOSING_CONFIG.total_dose_limit_basis,
    remedial_equivalence_tolerance_h: cfg.equivalence_tolerance_h ?? cfg.near_best_tolerance_h ?? DEFAULT_REMEDIAL_DOSING_CONFIG.equivalence_tolerance_h,
    remedial_use_complexity_weight: cfg.use_complexity_weight ?? DEFAULT_REMEDIAL_DOSING_CONFIG.use_complexity_weight,
    remedial_total_dose_min_ratio: cfg.total_dose_min_ratio ?? DEFAULT_REMEDIAL_DOSING_CONFIG.total_dose_min_ratio,
    remedial_total_dose_max_ratio: cfg.total_dose_max_ratio ?? DEFAULT_REMEDIAL_DOSING_CONFIG.total_dose_max_ratio,
    remedial_window_mode: window.mode || (window.auto === false ? 'configured' : 'simulation_reference'),
    remedial_window_lower: window.lower ?? DEFAULT_REMEDIAL_DOSING_CONFIG.therapeutic_window.lower,
    remedial_window_upper: window.upper ?? DEFAULT_REMEDIAL_DOSING_CONFIG.therapeutic_window.upper,
    remedial_window_metric: window.metric ?? DEFAULT_REMEDIAL_DOSING_CONFIG.therapeutic_window.metric,
    remedial_window_unit: window.unit ?? DEFAULT_REMEDIAL_DOSING_CONFIG.therapeutic_window.unit,
    remedial_concentration_unit: cfg.concentration_unit ?? window.unit ?? DEFAULT_REMEDIAL_DOSING_CONFIG.concentration_unit,
    remedial_model_concentration_unit: cfg.model_concentration_unit ?? DEFAULT_REMEDIAL_DOSING_CONFIG.model_concentration_unit,
    remedial_monte_carlo_n: cfg.monte_carlo_n ?? DEFAULT_REMEDIAL_DOSING_CONFIG.monte_carlo_n,
    remedial_monte_carlo_seed: cfg.monte_carlo_seed ?? DEFAULT_REMEDIAL_DOSING_CONFIG.monte_carlo_seed,
    remedial_weight_high: weights.high ?? DEFAULT_REMEDIAL_DOSING_CONFIG.default_weights.high,
    remedial_weight_low: weights.low ?? DEFAULT_REMEDIAL_DOSING_CONFIG.default_weights.low,
    remedial_weight_simple: weights.simple ?? DEFAULT_REMEDIAL_DOSING_CONFIG.default_weights.simple,
    remedial_baseline_take_if_delay_lt_h: baselineRule.take_if_delay_lt_h ?? DEFAULT_REMEDIAL_DOSING_CONFIG.baseline_rule.take_if_delay_lt_h,
  };
};

const buildRemedialDosingConfigFromValues = (values: any) => {
  const displayUnit = values.concentration_unit
    || values.remedial_concentration_unit
    || values.remedial_window_unit
    || DEFAULT_REMEDIAL_DOSING_CONFIG.concentration_unit;
  const modelUnit = values.model_concentration_unit
    || values.remedial_model_concentration_unit
    || DEFAULT_REMEDIAL_DOSING_CONFIG.model_concentration_unit;
  return ({
  enabled: !!values.remedial_enabled,
  evidence_reference_set: values.remedial_evidence_reference_set || DEFAULT_REMEDIAL_DOSING_CONFIG.evidence_reference_set,
  candidate_dose_mode: values.remedial_candidate_dose_mode || DEFAULT_REMEDIAL_DOSING_CONFIG.candidate_dose_mode,
  candidate_dose_multipliers: parseNonNegativeNumberListText(values.remedial_candidate_dose_multipliers),
  candidate_doses_mg: parseNonNegativeNumberListText(values.remedial_candidate_doses_mg),
  min_split_dose_mg: values.remedial_min_split_dose_mg ?? DEFAULT_REMEDIAL_DOSING_CONFIG.min_split_dose_mg,
  full_strengths_mg: parseNumberListText(values.remedial_full_strengths_mg),
  reference_dose_mg: values.remedial_reference_dose_mg ?? null,
  allow_split: values.remedial_allow_split !== false,
  decision_point_count: values.remedial_decision_point_count ?? DEFAULT_REMEDIAL_DOSING_CONFIG.decision_point_count,
  candidate_max_dose_ratio: values.remedial_candidate_max_dose_ratio ?? DEFAULT_REMEDIAL_DOSING_CONFIG.candidate_max_dose_ratio,
  max_candidate_count: values.remedial_max_candidate_count ?? DEFAULT_REMEDIAL_DOSING_CONFIG.max_candidate_count,
  single_dose_limit_mode: values.remedial_single_dose_limit_mode ?? DEFAULT_REMEDIAL_DOSING_CONFIG.single_dose_limit_mode,
  single_dose_limit_basis: values.remedial_single_dose_limit_basis ?? DEFAULT_REMEDIAL_DOSING_CONFIG.single_dose_limit_basis,
  single_dose_limit_ratio: values.remedial_single_dose_limit_ratio ?? null,
  total_dose_limit_mode: values.remedial_total_dose_limit_mode ?? DEFAULT_REMEDIAL_DOSING_CONFIG.total_dose_limit_mode,
  total_dose_limit_basis: values.remedial_total_dose_limit_basis ?? DEFAULT_REMEDIAL_DOSING_CONFIG.total_dose_limit_basis,
  equivalence_tolerance_h: values.remedial_equivalence_tolerance_h ?? null,
  use_complexity_weight: !!values.remedial_use_complexity_weight,
  total_dose_min_ratio: values.remedial_total_dose_min_ratio ?? DEFAULT_REMEDIAL_DOSING_CONFIG.total_dose_min_ratio,
  total_dose_max_ratio: values.remedial_total_dose_max_ratio ?? DEFAULT_REMEDIAL_DOSING_CONFIG.total_dose_max_ratio,
  therapeutic_window: {
    mode: values.remedial_window_mode || 'simulation_reference',
    auto: (values.remedial_window_mode || 'simulation_reference') !== 'configured',
    lower: values.remedial_window_lower ?? null,
    upper: values.remedial_window_upper ?? null,
    metric: values.remedial_window_metric || DEFAULT_REMEDIAL_DOSING_CONFIG.therapeutic_window.metric,
    unit: values.remedial_window_unit || displayUnit,
  },
  concentration_unit: displayUnit,
  model_concentration_unit: modelUnit,
  concentration_scale: unitConversionFactor(modelUnit, displayUnit, 'concentration'),
  monte_carlo_n: values.remedial_monte_carlo_n ?? DEFAULT_REMEDIAL_DOSING_CONFIG.monte_carlo_n,
  monte_carlo_seed: values.remedial_monte_carlo_seed ?? DEFAULT_REMEDIAL_DOSING_CONFIG.monte_carlo_seed,
  default_weights: {
    high: values.remedial_weight_high ?? DEFAULT_REMEDIAL_DOSING_CONFIG.default_weights.high,
    low: values.remedial_weight_low ?? DEFAULT_REMEDIAL_DOSING_CONFIG.default_weights.low,
    simple: values.remedial_weight_simple ?? DEFAULT_REMEDIAL_DOSING_CONFIG.default_weights.simple,
  },
  baseline_rule: {
    take_if_delay_lt_h: values.remedial_baseline_take_if_delay_lt_h ?? null,
  },
  });
};

const parseNonNegativeNumberListText = (value: unknown): number[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => Number(item))
      .filter((item) => Number.isFinite(item) && item >= 0);
  }
  return String(value ?? '')
    .split(',')
    .map((item) => Number(item.trim()))
    .filter((item) => Number.isFinite(item) && item >= 0);
};

type EvaluationRuleFormCondition = {
  field?: string;
  operator?: string;
  value?: any;
};

const normalizeEvaluationRuleValue = (condition: EvaluationRuleFormCondition) => {
  const operator = condition.operator;
  const raw = condition.value;
  if (operator === 'exists' || operator === 'not_exists') return undefined;
  if (condition.field === 'max_dose_exceeded') {
    if (typeof raw === 'boolean') return raw;
    return String(raw).toLowerCase() === 'true';
  }
  if (operator === 'between' || operator === 'in') {
    if (Array.isArray(raw)) return raw;
    return String(raw ?? '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => {
        const parsed = Number(item);
        return Number.isFinite(parsed) ? parsed : item;
      });
  }
  const parsed = Number(raw);
  return raw !== '' && raw != null && Number.isFinite(parsed) ? parsed : raw;
};

const buildEvaluationConditionTree = (logic: string, conditions: EvaluationRuleFormCondition[]) => {
  const normalized = conditions
    .filter((condition) => condition?.field && condition?.operator)
    .map((condition) => {
      const next: Record<string, any> = {
        field: condition.field,
        operator: condition.operator,
      };
      const value = normalizeEvaluationRuleValue(condition);
      if (condition.operator !== 'exists' && condition.operator !== 'not_exists') {
        next.value = value;
      }
      return next;
    });

  if (normalized.length === 0) {
    throw new Error('请至少配置一个判断条件');
  }
  return logic === 'OR' ? { any: normalized } : { all: normalized };
};

const unpackEvaluationConditionTree = (tree: any): { logic: 'AND' | 'OR'; conditions: EvaluationRuleFormCondition[] } => {
  if (Array.isArray(tree?.any)) return { logic: 'OR', conditions: tree.any };
  if (Array.isArray(tree?.OR)) return { logic: 'OR', conditions: tree.OR };
  if (Array.isArray(tree?.all)) return { logic: 'AND', conditions: tree.all };
  if (Array.isArray(tree?.AND)) return { logic: 'AND', conditions: tree.AND };
  if (tree?.field && tree?.operator) return { logic: 'AND', conditions: [tree] };
  return { logic: 'AND', conditions: [{ field: 'pta_status', operator: '==', value: 'met' }] };
};

const evaluationRuleUsesLegacyNumericPta = (tree: any): boolean => {
  if (!tree || typeof tree !== 'object') return false;
  if (tree.field === 'pta') return true;
  return ['all', 'AND', 'any', 'OR'].some((key) => (
    Array.isArray(tree[key]) && tree[key].some((child: any) => evaluationRuleUsesLegacyNumericPta(child))
  )) || ['not', 'NOT'].some((key) => evaluationRuleUsesLegacyNumericPta(tree[key]));
};

const buildDefaultEvaluationRuleValues = (drugId: string, methods: string[]) => {
  const defaultScope = drugId ? (methods[0] ? 'drug_method' : 'drug') : 'global';
  return {
    scope: defaultScope,
    drug_name: defaultScope === 'global' ? undefined : drugId,
    method: defaultScope === 'drug_method' ? methods[0] : undefined,
    evaluation_mode: 'population',
    rule_name: drugId ? '药物评价规则草稿' : '全局评价规则草稿',
    description: '',
    priority: EVALUATION_STATUS_PRIORITY.target_met,
    status: 'draft',
    output_status: 'recommended',
    output_label: '符合目标',
    output_color: 'green',
    logic: 'AND',
    conditions: [
      { field: 'point_estimate_status', operator: '==', value: 'in_window' },
      { field: 'pta_status', operator: 'in', value: ['met', 'not_configured', 'not_applicable'] },
      { field: 'max_dose_exceeded', operator: '==', value: false },
    ],
  };
};

const evaluationConditionGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(270px, 1.15fr) minmax(132px, 0.45fr) minmax(420px, 2fr) 40px',
  columnGap: 12,
  alignItems: 'center',
};

const evaluationConditionHeaderStyle: React.CSSProperties = {
  ...evaluationConditionGridStyle,
  color: '#667085',
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '20px',
  padding: '0 0 6px',
};

const evaluationConditionRowStyle: React.CSSProperties = {
  ...evaluationConditionGridStyle,
  minHeight: 32,
  padding: '6px 0',
  borderTop: '1px solid #f1f4f8',
};

const DoseEvaluationRulesEditor: React.FC<{
  drugId: string;
  drugName?: string;
  methods?: string[];
}> = ({ drugId, drugName, methods = [] }) => {
  const [ruleForm] = Form.useForm();
  const [rules, setRules] = useState<any[]>([]);
  const [loadingRules, setLoadingRules] = useState(false);
  const [savingRule, setSavingRule] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<number | null>(null);
  const [previewJson, setPreviewJson] = useState(DEFAULT_EVALUATION_PREVIEW_SAMPLE);
  const [previewRows, setPreviewRows] = useState<any[]>([]);
  const scopeValue = Form.useWatch('scope', ruleForm);
  const outputStatus = Form.useWatch('output_status', ruleForm);
  const conditionValues = Form.useWatch('conditions', ruleForm) || [];
  const methodSignature = methods.join('|');
  const availableMethods = useMemo(() => methods.filter(Boolean), [methodSignature]);
  const methodOptions = useMemo(() => Array.from(new Set([
    ...availableMethods,
    'Target AUC',
    'AUC/MIC',
    '%fT>MIC',
    'Target Concentration',
    'Target Cmax',
    'Cmax/MIC',
  ].filter(Boolean))).map((method) => ({ value: method, label: method })), [availableMethods]);

  const fetchRules = useCallback(async () => {
    setLoadingRules(true);
    try {
      const res = await adminAPI.listEvaluationRules();
      const items = res.items || [];
      const scopeOrder: Record<string, number> = { drug_method: 0, drug: 1, global: 2 };
      setRules(
        items
          .filter((rule: any) => rule.scope === 'global' || (drugId && rule.drug_name === drugId))
          .sort((a: any, b: any) => (
            (scopeOrder[a.scope] ?? 9) - (scopeOrder[b.scope] ?? 9)
            || (a.priority ?? 0) - (b.priority ?? 0)
            || (a.id ?? 0) - (b.id ?? 0)
          )),
      );
    } catch (err) {
      message.error('加载评价规则失败');
    } finally {
      setLoadingRules(false);
    }
  }, [drugId]);

  useEffect(() => {
    ruleForm.setFieldsValue(buildDefaultEvaluationRuleValues(drugId, availableMethods));
    setEditingRuleId(null);
    fetchRules();
  }, [drugId, fetchRules, availableMethods, ruleForm]);

  useEffect(() => {
    if (scopeValue === 'global') {
      ruleForm.setFieldsValue({ drug_name: undefined, method: undefined });
      return;
    }
    if (scopeValue === 'drug') {
      ruleForm.setFieldsValue({ drug_name: drugId || undefined, method: undefined });
      return;
    }
    if (scopeValue === 'drug_method') {
      ruleForm.setFieldsValue({
        drug_name: drugId || undefined,
        method: ruleForm.getFieldValue('method') || availableMethods[0] || methodOptions[0]?.value,
      });
    }
  }, [availableMethods, drugId, methodOptions, ruleForm, scopeValue]);

  useEffect(() => {
    const option = EVALUATION_OUTPUT_OPTIONS.find((item) => item.value === outputStatus);
    if (option) {
      const currentLabel = ruleForm.getFieldValue('output_label');
      const currentColor = ruleForm.getFieldValue('output_color');
      if (!currentLabel || EVALUATION_OUTPUT_OPTIONS.some((item) => item.label === currentLabel)) {
        ruleForm.setFieldValue('output_label', option.label);
      }
      if (!currentColor || EVALUATION_OUTPUT_OPTIONS.some((item) => item.color === currentColor)) {
        ruleForm.setFieldValue('output_color', option.color);
      }
      const currentPriority = ruleForm.getFieldValue('priority');
      const defaultPriorities = Object.values(EVALUATION_STATUS_PRIORITY);
      if (!currentPriority || currentPriority === 100 || defaultPriorities.includes(currentPriority)) {
        ruleForm.setFieldValue('priority', EVALUATION_STATUS_PRIORITY[option.value] || 100);
      }
    }
  }, [outputStatus, ruleForm]);

  const buildRulePayload = async () => {
    const values = await ruleForm.validateFields();
    const effectiveScope = values.scope || (drugId ? 'drug' : 'global');
    if ((effectiveScope === 'drug' || effectiveScope === 'drug_method') && !drugId) {
      throw new Error('请先保存药物基础信息，再配置药物级评价规则');
    }
    const effectiveMethod = effectiveScope === 'drug_method'
      ? (values.method || availableMethods[0] || methodOptions[0]?.value)
      : null;
    if (effectiveScope === 'drug_method' && !effectiveMethod) {
      throw new Error('药物+方法级评价规则必须选择计算方法');
    }
    const conditionTree = buildEvaluationConditionTree(values.logic, values.conditions || []);
    return {
      scope: effectiveScope,
      drug_name: effectiveScope === 'global' ? null : drugId,
      method: effectiveMethod,
      evaluation_mode: values.evaluation_mode || 'population',
      rule_name: values.rule_name,
      description: values.description || null,
      priority: values.priority,
      status: 'draft',
      condition_tree: conditionTree,
      output_status: values.output_status,
      output_label: values.output_label,
      output_color: values.output_color,
    };
  };

  const resetRuleForm = () => {
    setEditingRuleId(null);
    ruleForm.setFieldsValue(buildDefaultEvaluationRuleValues(drugId, availableMethods));
    setPreviewRows([]);
  };

  const handleEditRule = (rule: any) => {
    const unpacked = unpackEvaluationConditionTree(rule.condition_tree);
    const isDraftEdit = rule.status === 'draft';
    const copiedGlobalIntoDrug = !isDraftEdit && drugId && rule.scope === 'global';
    const copiedScope = copiedGlobalIntoDrug
      ? (availableMethods[0] ? 'drug_method' : 'drug')
      : rule.scope;
    const copiedMethod = copiedScope === 'drug_method'
      ? (rule.method || availableMethods[0] || methodOptions[0]?.value)
      : undefined;
    setEditingRuleId(isDraftEdit ? rule.id : null);
    const normalizedOutputStatus = LEGACY_EVALUATION_STATUS_MAP[rule.output_status] || rule.output_status;
    const normalizedOutput = EVALUATION_OUTPUT_OPTIONS.find((item) => item.value === normalizedOutputStatus);
    ruleForm.setFieldsValue({
      scope: copiedScope,
      drug_name: copiedScope === 'global' ? undefined : (rule.drug_name || drugId || undefined),
      method: copiedMethod,
      evaluation_mode: rule.evaluation_mode || 'population',
      rule_name: isDraftEdit
        ? rule.rule_name
        : `${rule.rule_name}${copiedGlobalIntoDrug ? '（药物覆盖草稿）' : '（修订草稿）'}`,
      description: rule.description || '',
      priority: rule.priority,
      status: 'draft',
      output_status: normalizedOutputStatus,
      output_label: normalizedOutput?.label || rule.output_label,
      output_color: normalizedOutput?.color || rule.output_color,
      logic: unpacked.logic,
      conditions: unpacked.conditions,
    });
  };

  const handleSaveRule = async () => {
    setSavingRule(true);
    try {
      const payload = await buildRulePayload();
      if (editingRuleId) {
        await adminAPI.updateEvaluationRule(editingRuleId, payload);
      } else {
        await adminAPI.createEvaluationRule(payload);
      }
      message.success('评价规则草稿已保存');
      resetRuleForm();
      fetchRules();
    } catch (err: any) {
      message.error(safeErrorMessage(err, err?.message || '保存评价规则失败'));
    } finally {
      setSavingRule(false);
    }
  };

  const handlePreview = async () => {
    try {
      const sample = JSON.parse(previewJson);
      const regimens = Array.isArray(sample) ? sample : [sample];
      const payload = await buildRulePayload();
      const res = await adminAPI.previewEvaluationRules({
        drug_name: payload.drug_name || drugId || 'global',
        method: payload.method || ruleForm.getFieldValue('method') || availableMethods[0] || 'Target AUC',
        regimens,
        rule: payload,
      });
      setPreviewRows(res.regimens || []);
    } catch (err: any) {
      message.error(safeErrorMessage(err, '预览失败，请检查规则和候选方案 JSON'));
    }
  };

  const handlePublish = async (id: number) => {
    try {
      await adminAPI.publishEvaluationRule(id);
      message.success('评价规则已发布');
      fetchRules();
    } catch (err: any) {
      message.error(safeErrorMessage(err, '发布失败'));
    }
  };

  const handleArchive = async (id: number) => {
    try {
      await adminAPI.archiveEvaluationRule(id);
      message.success('评价规则已归档');
      fetchRules();
    } catch (err: any) {
      message.error(safeErrorMessage(err, '归档失败'));
    }
  };

  const handleDeleteRule = async (id: number) => {
    try {
      await adminAPI.deleteEvaluationRule(id);
      message.success('评价规则已删除');
      if (editingRuleId === id) resetRuleForm();
      fetchRules();
    } catch (err: any) {
      message.error(safeErrorMessage(err, '删除失败'));
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={12}>
      <Alert
        type="info"
        showIcon
        message="剂量评价由后端规则统一判定"
        description="群体模型规则可按 PTA 数值分层；PK公式法请选择“PK公式法（仅点估计）”，此模式不使用 PTA。"
      />

      {!drugId && (
        <Alert
          type="warning"
          showIcon
          message="新增药物尚未保存"
          description="当前只能配置全局规则；药物级或药物+方法级规则需要先保存药物 ID。"
        />
      )}

      {drugId && scopeValue === 'global' && (
        <Alert
          type="warning"
          showIcon
          message="当前草稿会保存为全局兜底规则"
          description="不同药物的 PK/PD 指标和安全阈值不同，建议在药物页面优先使用“按药物覆盖”或“按药物+方法覆盖”。"
        />
      )}

      <Table
        size="small"
        loading={loadingRules}
        rowKey="id"
        pagination={{ pageSize: 6 }}
        dataSource={rules}
        columns={[
          {
            title: '状态',
            dataIndex: 'status',
            width: 90,
            render: (status: string) => (
              <Tag color={status === 'active' ? 'green' : (status === 'draft' ? 'gold' : 'default')}>
                {status === 'active' ? '生效' : (status === 'draft' ? '草稿' : '归档')}
              </Tag>
            ),
          },
          {
            title: '作用域',
            dataIndex: 'scope',
            width: 130,
            render: (_: string, rule: any) => {
              if (rule.scope === 'global') return <Tag>全局默认</Tag>;
              if (rule.scope === 'drug_method') return <Tag color="blue">{rule.drug_name} / {rule.method}</Tag>;
              return <Tag color="cyan">{rule.drug_name}</Tag>;
            },
          },
          { title: '优先级', dataIndex: 'priority', width: 80 },
          {
            title: '规则名称',
            dataIndex: 'rule_name',
            render: (name: string, rule: any) => (
              <Space size={4} wrap>
                <span>{name}</span>
                {evaluationRuleUsesLegacyNumericPta(rule.condition_tree) && (
                  <Tag color="blue">PTA 数值分层</Tag>
                )}
              </Space>
            ),
          },
          {
            title: '评价模式',
            dataIndex: 'evaluation_mode',
            width: 145,
            render: (mode: string) => (
              <Tag color={mode === 'pk_formula' ? 'orange' : 'blue'}>
                {mode === 'pk_formula' ? 'PK公式法' : '群体模型'}
              </Tag>
            ),
          },
          {
            title: '输出',
            width: 130,
            render: (_: any, rule: any) => <Tag color={rule.output_color}>{rule.output_label}</Tag>,
          },
          {
            title: '操作',
            width: 250,
            render: (_: any, rule: any) => {
              const canDelete = rule.status !== 'active' && !rule.is_system;
              const deleteTitle = rule.status === 'active'
                ? '已发布规则请先归档后再删除'
                : (rule.is_system ? '系统内置规则不允许物理删除' : '确认删除该规则？');
              return (
                <Space size={4}>
                  <Button size="small" icon={<EditOutlined />} onClick={() => handleEditRule(rule)}>
                    {rule.status === 'draft' ? '编辑' : '复制草稿'}
                  </Button>
                  {rule.status !== 'active' && (
                    <Button size="small" type="link" onClick={() => handlePublish(rule.id)}>发布</Button>
                  )}
                  {rule.status !== 'archived' && (
                    <Popconfirm title="确认归档该规则？" onConfirm={() => handleArchive(rule.id)}>
                      <Button size="small" danger type="link">归档</Button>
                    </Popconfirm>
                  )}
                  <Popconfirm
                    title={deleteTitle}
                    disabled={!canDelete}
                    onConfirm={() => handleDeleteRule(rule.id)}
                  >
                    <Button size="small" danger type="link" disabled={!canDelete}>删除</Button>
                  </Popconfirm>
                </Space>
              );
            },
          },
        ]}
      />

      <Card size="small" title={editingRuleId ? '编辑规则草稿' : '新增规则草稿'} extra={<Button size="small" onClick={resetRuleForm}>清空</Button>}>
        <Form form={ruleForm} layout="vertical" size="small" component={false}>
          <Row gutter={12}>
            <Col span={6}>
              <Form.Item name="scope" label="作用域" rules={[{ required: true }]}>
                <Select options={[
                  { value: 'global', label: '全局默认（兜底）' },
                  { value: 'drug', label: '按药物覆盖', disabled: !drugId },
                  { value: 'drug_method', label: '按药物+方法覆盖', disabled: !drugId },
                ]} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="药物">
                <Input
                  disabled
                  value={scopeValue === 'global' ? '全局默认（不限定药物）' : (drugName ? `${drugName} (${drugId})` : drugId)}
                  placeholder="保存药物后自动填充"
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="method" label="计算方法" rules={scopeValue === 'drug_method' ? [{ required: true, message: '请选择计算方法' }] : undefined}>
                <Select disabled={scopeValue !== 'drug_method'} options={methodOptions} placeholder="仅药物+方法覆盖时选择" />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="priority" label="优先级" rules={[{ required: true }]}>
                <InputNumber min={1} max={9999} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={5}>
              <Form.Item name="evaluation_mode" label="评价模式" rules={[{ required: true }]}>
                <Select options={[
                  { value: 'population', label: '群体模型（含 PTA）' },
                  { value: 'pk_formula', label: 'PK公式法（仅点估计）' },
                ]} />
              </Form.Item>
            </Col>
            <Col span={6}><Form.Item name="rule_name" label="规则名称" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={5}><Form.Item name="description" label="说明"><Input /></Form.Item></Col>
            <Col span={3}>
              <Form.Item name="status" label="编辑状态">
                <Select disabled options={[{ value: 'draft', label: '草稿' }]} />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="output_status" label="输出状态" rules={[{ required: true }]}>
                <Select
                  options={EVALUATION_OUTPUT_OPTIONS.map((item) => ({ value: item.value, label: item.label }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}><Form.Item name="output_label" label="显示文案" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={6}><Form.Item name="output_color" label="颜色" rules={[{ required: true }]}><Select options={[
              { value: 'green', label: '绿色' },
              { value: 'yellow', label: '黄色' },
              { value: 'orange', label: '橙色' },
              { value: 'red', label: '红色' },
              { value: 'blue', label: '蓝色' },
            ]} /></Form.Item></Col>
            <Col span={6}><Form.Item name="logic" label="条件关系" rules={[{ required: true }]}><Radio.Group options={[
              { value: 'AND', label: '全部满足' },
              { value: 'OR', label: '任一满足' },
            ]} /></Form.Item></Col>
          </Row>

          <Form.List name="conditions">
            {(fields, { add, remove }) => (
              <div style={{
                width: '100%',
                border: '1px solid #edf1f5',
                borderRadius: 8,
                background: '#fbfcfe',
                padding: '10px 12px 12px',
              }}>
                <div style={evaluationConditionHeaderStyle}>
                  <Text type="secondary">条件</Text>
                  <Text type="secondary">逻辑表达式</Text>
                  <Text type="secondary">值</Text>
                  <span />
                </div>
                {fields.map((field) => {
                  const condition = conditionValues?.[field.name] || {};
                  const operator = condition.operator;
                  const fieldName = condition.field;
                  return (
                    <div key={field.key} style={evaluationConditionRowStyle}>
                      <div>
                        <Form.Item name={[field.name, 'field']} noStyle rules={[{ required: true }]}>
                          <Select
                            showSearch
                            optionFilterProp="label"
                            options={EVALUATION_RULE_FIELD_OPTIONS}
                            placeholder="选择条件"
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </div>
                      <div>
                        <Form.Item name={[field.name, 'operator']} noStyle rules={[{ required: true }]}>
                          <Select
                            options={EVALUATION_RULE_OPERATOR_OPTIONS}
                            placeholder="比较符"
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </div>
                      <div>
                        {operator === 'exists' || operator === 'not_exists' ? (
                          <div style={{
                            height: 24,
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0 11px',
                            color: '#98a2b3',
                            border: '1px solid #edf1f5',
                            borderRadius: 6,
                            background: '#fff',
                          }}>
                            无需填写值
                          </div>
                        ) : (
                          <Form.Item name={[field.name, 'value']} noStyle rules={[{ required: true, message: '请填写比较值' }]}>
                            {fieldName === 'point_estimate_status' ? (
                              <Select options={[
                                { value: 'in_window', label: '在目标窗内' },
                                { value: 'below_window', label: '低于目标窗' },
                                { value: 'above_window', label: '高于目标窗' },
                              ]} style={{ width: '100%' }} />
                            ) : fieldName === 'pta_status' ? (
                              <Select options={[
                                { value: 'met', label: '已达后台 PTA 下限' },
                                { value: 'not_met', label: '未达后台 PTA 下限' },
                                { value: 'not_configured', label: '后台未配置 PTA 下限' },
                                { value: 'unavailable', label: 'PTA 结果不可用' },
                                { value: 'not_applicable', label: 'PTA 不适用' },
                              ]} mode={operator === 'in' ? 'multiple' : undefined} style={{ width: '100%' }} />
                            ) : fieldName === 'max_dose_exceeded' ? (
                              <Select options={[
                                { value: true, label: '是' },
                                { value: false, label: '否' },
                              ]} style={{ width: '100%' }} />
                            ) : (
                              <Input
                                placeholder={operator === 'between' ? '例如 80,90' : (operator === 'in' ? '用逗号分隔多个值' : '比较值')}
                                style={{ width: '100%' }}
                              />
                            )}
                          </Form.Item>
                        )}
                      </div>
                      <Button
                        size="small"
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => remove(field.name)}
                        style={{ justifySelf: 'center' }}
                      />
                    </div>
                  );
                })}
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => add({ field: 'pta_status', operator: '==', value: 'met' })}
                  block
                  style={{ marginTop: 8, borderColor: '#e6edf5', color: '#344054' }}
                >
                  增加判断条件
                </Button>
              </div>
            )}
          </Form.List>
        </Form>

        <Space style={{ marginTop: 12 }}>
          <Button type="primary" icon={<SaveOutlined />} loading={savingRule} onClick={handleSaveRule}>保存草稿</Button>
          <Button icon={<EyeOutlined />} onClick={handlePreview}>预览命中</Button>
        </Space>
      </Card>

      <Collapse
        size="small"
        items={[{
          key: 'advanced-preview-json',
          label: '高级：预览候选方案 JSON（可选）',
          children: (
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              <Text type="secondary">
                日常配置不需要修改这里；只有需要验证特殊字段组合时，才编辑候选方案 JSON 后点击“预览命中”。
              </Text>
              <Input.TextArea rows={8} value={previewJson} onChange={(event) => setPreviewJson(event.target.value)} />
            </Space>
          ),
        }]}
      />

      {previewRows.length > 0 && (
        <Table
          size="small"
          rowKey={(_row: any, index?: number) => `preview-${index ?? 0}`}
          pagination={false}
          dataSource={previewRows}
          columns={[
            { title: '剂量', dataIndex: 'dose_mg', width: 80 },
            { title: 'PTA', dataIndex: 'pta', width: 80 },
            {
              title: '评价',
              render: (_: any, row: any) => (
                row.evaluation
                  ? <Tag color={row.evaluation.color}>{row.evaluation.label}</Tag>
                  : <Text type="secondary">未预览</Text>
              ),
            },
            {
              title: '原因',
              render: (_: any, row: any) => (row.evaluation?.reasons || []).join('；') || '-',
            },
          ]}
        />
      )}
    </Space>
  );
};

const DrugActivationAssignments: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<any>({ drugs: [], tenants: [], users: [] });
  const [scope, setScope] = useState<'tenant' | 'user'>('tenant');
  const [targetId, setTargetId] = useState<string>();
  const [selectedDrugIds, setSelectedDrugIds] = useState<string[]>([]);

  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getDrugActivationAssignments();
      setData(res || { drugs: [], tenants: [], users: [] });
    } catch (error) {
      message.error(safeErrorMessage(error, '加载药物激活分配失败'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void fetchAssignments(); }, [fetchAssignments]);

  const targets = scope === 'tenant' ? (data.tenants || []) : (data.users || []);
  const selectedTarget = targets.find((item: any) => item.id === targetId);
  const drugMap = useMemo(
    () => new Map((data.drugs || []).map((drug: any) => [drug.drug_id, drug])),
    [data.drugs],
  );
  const renderDrugAvailabilityTag = (status: string) => (
    status === 'inactive'
      ? <Tag color="gold">研究开放</Tag>
      : <Tag color="green">全局启用</Tag>
  );
  const drugOptions = useMemo(
    () => (data.drugs || [])
      .map((drug: any) => ({
        value: drug.drug_id,
        searchLabel: `${drug.drug_name || getDrugLabel(drug.drug_id, drug.drug_id)} ${drug.drug_id} ${drug.status === 'inactive' ? '研究开放' : '全局启用'}`,
        label: (
          <Space size={6}>
            <span>{drug.drug_name || getDrugLabel(drug.drug_id, drug.drug_id)}</span>
            <Text type="secondary">({drug.drug_id})</Text>
            {renderDrugAvailabilityTag(drug.status)}
          </Space>
        ),
      })),
    [data.drugs],
  );
  const targetOptions = useMemo(
    () => targets.map((target: any) => ({
      value: target.id,
      label: scope === 'tenant'
        ? `${target.name} (${target.slug || target.id})`
        : `${target.real_name || target.username || target.email} (${target.email})`,
    })),
    [scope, targets],
  );

  useEffect(() => {
    if (!targets.length) {
      setTargetId(undefined);
      return;
    }
    if (!targetId || !targets.some((item: any) => item.id === targetId)) {
      setTargetId(targets[0].id);
    }
  }, [targetId, targets]);

  useEffect(() => {
    setSelectedDrugIds(selectedTarget?.activated_drugs || []);
  }, [selectedTarget]);

  const selectedRows = selectedDrugIds.map((drugId) => {
    const drug = drugMap.get(drugId) as any;
    return drug || { drug_id: drugId, drug_name: getDrugLabel(drugId, drugId), status: 'unknown' };
  });

  const handleSave = async () => {
    if (!selectedTarget) {
      message.warning('请先选择分配对象');
      return;
    }
    setSaving(true);
    try {
      if (scope === 'tenant') {
        await adminAPI.updateTenantDrugActivation(selectedTarget.id, selectedDrugIds);
      } else {
        await adminAPI.updateUserDrugActivation(selectedTarget.id, { drug_ids: selectedDrugIds });
      }
      message.success('药物激活分配已保存');
      await fetchAssignments();
    } catch (error) {
      message.error(safeErrorMessage(error, '保存药物激活分配失败'));
    } finally {
      setSaving(false);
    }
  };

  const handleInheritTenant = async () => {
    if (!selectedTarget) return;
    setSaving(true);
    try {
      await adminAPI.updateUserDrugActivation(selectedTarget.id, { inherit_tenant: true });
      message.success('已恢复为继承机构药物');
      await fetchAssignments();
    } catch (error) {
      message.error(safeErrorMessage(error, '恢复继承失败'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Card bordered={false} style={ADMIN_SURFACE_STYLE}>
        <Space direction="vertical" size={14} style={{ width: '100%' }}>
          <Space wrap>
            <Select
              value={scope}
              style={{ width: 140 }}
              options={[
                { value: 'tenant', label: '按机构' },
                { value: 'user', label: '按用户' },
              ]}
              onChange={(nextScope: 'tenant' | 'user') => {
                setScope(nextScope);
                setTargetId(undefined);
              }}
            />
            <Select
              showSearch
              value={targetId}
              loading={loading}
              style={{ minWidth: 320 }}
              placeholder="选择机构或用户"
              options={targetOptions}
              optionFilterProp="label"
              onChange={setTargetId}
            />
            <Button icon={<ReloadOutlined />} loading={loading} onClick={fetchAssignments}>刷新</Button>
            <Button type="primary" icon={<SaveOutlined />} loading={saving} disabled={!selectedTarget} onClick={handleSave}>保存分配</Button>
            {scope === 'user' && selectedTarget?.user_override && (
              <Button loading={saving} onClick={handleInheritTenant}>继承机构</Button>
            )}
          </Space>

          {scope === 'user' && selectedTarget && (
            <Alert
              type={selectedTarget.user_override ? 'info' : 'warning'}
              showIcon
              message={selectedTarget.user_override
                ? '该用户正在使用用户级药物分配。'
                : '该用户当前继承所在机构药物；保存后会生成用户级覆盖配置。'}
            />
          )}

          <Select
            mode="multiple"
            value={selectedDrugIds}
            style={{ width: '100%' }}
            placeholder="选择可使用的药物"
            options={drugOptions}
            optionFilterProp="searchLabel"
            onChange={setSelectedDrugIds}
            maxTagCount="responsive"
          />
        </Space>
      </Card>

      <Table
        style={{ marginTop: 12 }}
        rowKey="drug_id"
        size="small"
        loading={loading}
        dataSource={selectedRows}
        pagination={false}
        columns={[
          { title: '药物', dataIndex: 'drug_name', render: (value: string, row: any) => value || getDrugLabel(row.drug_id, row.drug_id) },
          { title: '编码', dataIndex: 'drug_id', render: (value: string) => <Text code>{value}</Text> },
          {
            title: '状态',
            dataIndex: 'status',
            width: 100,
            render: (value: string) => (
              value === 'unknown'
                ? <Tag>未知</Tag>
                : renderDrugAvailabilityTag(value)
            ),
          },
        ]}
      />
    </div>
  );
};

const renderModelAssignmentTag = (record: any) => {
  if (record?.model_status === 'active' || record?.assignment_status === 'global') {
    return <Tag color="green">全局开放</Tag>;
  }

  const tenantCount = record?.assigned_tenant_count || 0;
  const userCount = record?.assigned_user_count || 0;
  if (record?.assignment_status === 'scoped' || tenantCount > 0 || userCount > 0) {
    return (
      <Space size={4} wrap>
        <Tag color="blue">定向开放</Tag>
        {tenantCount > 0 && <Tag>{tenantCount} 机构</Tag>}
        {userCount > 0 && <Tag>{userCount} 用户</Tag>}
      </Space>
    );
  }

  return <Tag>未开放</Tag>;
};

const ModelActivationAssignments: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<any>({ models: [], tenants: [], users: [] });
  const [scope, setScope] = useState<'tenant' | 'user'>('tenant');
  const [targetId, setTargetId] = useState<string>();
  const [selectedModelIds, setSelectedModelIds] = useState<string[]>([]);

  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getModelActivationAssignments();
      setData(res || { models: [], tenants: [], users: [] });
    } catch (error) {
      message.error(safeErrorMessage(error, '加载模型开放分配失败'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void fetchAssignments(); }, [fetchAssignments]);

  const targets = scope === 'tenant' ? (data.tenants || []) : (data.users || []);
  const selectedTarget = targets.find((item: any) => item.id === targetId);
  const modelMap = useMemo(
    () => new Map((data.models || []).map((model: any) => [model.model_id, model])),
    [data.models],
  );
  const modelOptions = useMemo(
    () => (data.models || []).map((model: any) => ({
      value: model.model_id,
      searchLabel: `${model.model_id} ${model.name || ''} ${model.drug_label || model.drug_name || ''} ${model.population || ''}`,
      label: (
        <Space size={6}>
          <span>{model.name || model.model_id}</span>
          <Text type="secondary">({model.model_id})</Text>
          {model.drug_label || model.drug_name ? <Tag color="blue">{model.drug_label || getDrugLabel(model.drug_name, model.drug_name)}</Tag> : null}
          {renderModelAssignmentTag(model)}
        </Space>
      ),
    })),
    [data.models],
  );
  const targetOptions = useMemo(
    () => targets.map((target: any) => ({
      value: target.id,
      label: scope === 'tenant'
        ? `${target.name} (${target.slug || target.id})`
        : `${target.real_name || target.username || target.email} (${target.email})`,
    })),
    [scope, targets],
  );

  useEffect(() => {
    if (!targets.length) {
      setTargetId(undefined);
      return;
    }
    if (!targetId || !targets.some((item: any) => item.id === targetId)) {
      setTargetId(targets[0].id);
    }
  }, [targetId, targets]);

  useEffect(() => {
    setSelectedModelIds(selectedTarget?.activated_models || []);
  }, [selectedTarget]);

  const selectedRows = selectedModelIds.map((modelId) => {
    const model = modelMap.get(modelId) as any;
    return model || { model_id: modelId, name: modelId, model_status: 'unknown', assignment_status: 'unknown' };
  });

  const handleSave = async () => {
    if (!selectedTarget) {
      message.warning('请先选择分配对象');
      return;
    }
    setSaving(true);
    try {
      if (scope === 'tenant') {
        await adminAPI.updateTenantModelActivation(selectedTarget.id, selectedModelIds);
      } else {
        await adminAPI.updateUserModelActivation(selectedTarget.id, { model_ids: selectedModelIds });
      }
      message.success('模型开放分配已保存');
      await fetchAssignments();
    } catch (error) {
      message.error(safeErrorMessage(error, '保存模型开放分配失败'));
    } finally {
      setSaving(false);
    }
  };

  const handleInheritTenant = async () => {
    if (!selectedTarget) return;
    setSaving(true);
    try {
      await adminAPI.updateUserModelActivation(selectedTarget.id, { inherit_tenant: true });
      message.success('已恢复为继承机构模型');
      await fetchAssignments();
    } catch (error) {
      message.error(safeErrorMessage(error, '恢复继承失败'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Card bordered={false} style={ADMIN_SURFACE_STYLE}>
        <Space direction="vertical" size={14} style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="全局开放模型无需分配；这里主要用于把全局未开放的研究模型定向开放给指定机构或用户。"
          />
          <Space wrap>
            <Select
              value={scope}
              style={{ width: 140 }}
              options={[
                { value: 'tenant', label: '按机构' },
                { value: 'user', label: '按用户' },
              ]}
              onChange={(nextScope: 'tenant' | 'user') => {
                setScope(nextScope);
                setTargetId(undefined);
              }}
            />
            <Select
              showSearch
              value={targetId}
              loading={loading}
              style={{ minWidth: 320 }}
              placeholder="选择机构或用户"
              options={targetOptions}
              optionFilterProp="label"
              onChange={setTargetId}
            />
            <Button icon={<ReloadOutlined />} loading={loading} onClick={fetchAssignments}>刷新</Button>
            <Button type="primary" icon={<SaveOutlined />} loading={saving} disabled={!selectedTarget} onClick={handleSave}>保存分配</Button>
            {scope === 'user' && selectedTarget?.user_override && (
              <Button loading={saving} onClick={handleInheritTenant}>继承机构</Button>
            )}
          </Space>

          {scope === 'user' && selectedTarget && (
            <Alert
              type={selectedTarget.user_override ? 'info' : 'warning'}
              showIcon
              message={selectedTarget.user_override
                ? '该用户正在使用用户级模型分配。'
                : '该用户当前继承所在机构模型；保存后会生成用户级覆盖配置。'}
            />
          )}

          <Select
            mode="multiple"
            value={selectedModelIds}
            style={{ width: '100%' }}
            placeholder="选择定向开放的模型"
            options={modelOptions}
            optionFilterProp="searchLabel"
            onChange={setSelectedModelIds}
            maxTagCount="responsive"
          />
        </Space>
      </Card>

      <Table
        style={{ marginTop: 12 }}
        rowKey="model_id"
        size="small"
        loading={loading}
        dataSource={selectedRows}
        pagination={false}
        columns={[
          { title: '模型', dataIndex: 'name', render: (value: string, row: any) => value || row.model_id },
          { title: '模型ID', dataIndex: 'model_id', render: (value: string) => <Text code>{value}</Text> },
          { title: '药物', render: (_: any, row: any) => row.drug_label || getDrugLabel(row.drug_name, row.drug_name || '-') },
          {
            title: '全局状态',
            dataIndex: 'model_status',
            width: 100,
            render: (value: string) => (
              value === 'active'
                ? <Tag color="green">全局开放</Tag>
                : value === 'unknown'
                  ? <Tag>未知</Tag>
                  : <Tag>全局关闭</Tag>
            ),
          },
          {
            title: '开放范围',
            key: 'assignment_status',
            width: 180,
            render: (_: any, row: any) => renderModelAssignmentTag(row),
          },
        ]}
      />
    </div>
  );
};


const DrugsTab: React.FC = () => {
  const screens = Grid.useBreakpoint();
  const [drugs, setDrugs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [editingDrug, setEditingDrug] = useState<any>(null);
  const [form] = Form.useForm();
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedDrug, setSelectedDrug] = useState<any>(null);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [regimenPreview, setRegimenPreview] = useState<any>(null);
  const [drugSearch, setDrugSearch] = useState('');
  const [drugStatusFilter, setDrugStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [pkFormulaStrategies, setPkFormulaStrategies] = useState<any[]>([]);
  const drugCovariateEditorRef = useRef<NestedEditorSaveHandle | null>(null);

  const fetchDrugs = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.listDrugParams();
      setDrugs(res.drugs || []);
    } catch { message.error('加载药物参数失败'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchDrugs();
    adminAPI.listPkFormulaStrategies()
      .then((res) => setPkFormulaStrategies(Array.isArray(res?.items) ? res.items : []))
      .catch(() => message.error('加载 PK 公式策略失败'));
  }, []);

  const syncAdministrationModesFromRoute = useCallback((routeValue: unknown) => {
    const nextSupportedModes = resolveSupportedAdministrationModes(
      form.getFieldValue('supported_administration_modes'),
      routeValue,
      ['intermittent'],
    );
    const nextDefaultMode = resolveRouteAwareDefaultAdministrationMode(
      form.getFieldValue('default_administration_mode'),
      nextSupportedModes,
      routeValue,
    );

    form.setFieldsValue({
      supported_administration_modes: nextSupportedModes,
      default_administration_mode: nextDefaultMode,
    });
  }, [form]);

  const getTargetDisplay = (targets: any) => {
    if (!targets) return '-';
    const t = Array.isArray(targets) ? targets[0] : targets.primary;
    if (!t) return '-';
    const modes = normalizeAdministrationModes(t.administration_modes, []);
    const modeSuffix = modes.length > 0
      ? ` [${modes.map((mode) => getAdministrationModeLabel(mode)).join(' / ')}]`
      : '';
    return `${t.parameter} ${t.min}-${t.max} ${t.unit}${modeSuffix}`;
  };

  const filteredDrugs = useMemo(() => {
    const query = drugSearch.trim().toLowerCase();
    return drugs.filter((drug) => {
      if (drugStatusFilter !== 'all' && drug.status !== drugStatusFilter) return false;
      if (!query) return true;
      return [drug.drug_name, drug.drug_name_en, drug.drug_id, drug.category]
        .some((value) => String(value || '').toLowerCase().includes(query));
    });
  }, [drugs, drugSearch, drugStatusFilter]);

  const drugSummary = useMemo(() => ({
    total: drugs.length,
    active: drugs.filter((drug) => drug.status === 'active').length,
    remedial: drugs.filter((drug) => drug.remedial_dosing?.enabled).length,
    incomplete: drugs.filter((drug) => !drug.calculation_methods?.length || !drug.targets?.length).length,
  }), [drugs]);
  const selectedSafetyRules = useMemo(() => normalizeSafetyRules(selectedDrug), [selectedDrug]);

  const safetyRulesForForm = (config: any) => normalizeSafetyRules(config).map((rule: any) => ({
    ...rule,
    patient_conditions_json: rule.patient_conditions
      ? JSON.stringify(rule.patient_conditions)
      : (rule.scope?.patient_conditions ? JSON.stringify(rule.scope.patient_conditions) : ''),
  }));

  const openEdit = (drug: any) => {
    setEditingDrug(drug);
    const route = Array.isArray(drug.route) ? drug.route : (drug.route ? [drug.route] : []);
    const supportedAdministrationModes = resolveSupportedAdministrationModes(
      drug.supported_administration_modes,
      route,
      ['intermittent'],
    );
    form.setFieldsValue({
      drug_id: drug.drug_id,
      drug_name: drug.drug_name,
      drug_name_en: drug.drug_name_en || '',
      category: drug.category || '',
      route,
      status: drug.status || 'active',
      dose_basis: drug.dose_basis || (drug.candidate_doses_mg_m2?.length ? 'mg_m2' : (drug.candidate_doses_mg?.length ? 'mg' : 'mg_kg')),
      ...buildUnitFormValues(drug),
      ai_prompt_config: normalizeAIInterpretationConfig(
        drug.ai_prompt_config,
        [...(drug.calculation_methods || []), ...(drug.remedial_dosing?.enabled ? ['Remedial Dose'] : [])],
      ),
      candidate_doses_mg_kg: drug.candidate_doses_mg_kg?.join(', ') || '',
      candidate_doses_mg: drug.candidate_doses_mg?.join(', ') || '',
      candidate_doses_mg_m2: drug.candidate_doses_mg_m2?.join(', ') || '',
      candidate_intervals_h: drug.candidate_intervals_h?.join(', ') || '',
      ...normalizeRegimenPolicyForm(drug),
      regimen_profiles: normalizeRegimenProfiles(drug, supportedAdministrationModes),
      default_infusion_duration_h: drug.default_infusion_duration_h,
      default_mic: drug.default_mic,
      mic_breakpoints: drug.mic_breakpoints?.join(', ') || '',
      default_fu: drug.default_fu,
      simulation_settings: {
        pk_curve_monte_carlo_n: drug.simulation_settings?.pk_curve_monte_carlo_n
          ?? drug.curve_monte_carlo_n ?? drug.pk_curve?.monte_carlo_n ?? 100,
        pk_curve_monte_carlo_seed: drug.simulation_settings?.pk_curve_monte_carlo_seed
          ?? drug.curve_monte_carlo_seed ?? drug.pk_curve?.monte_carlo_seed ?? 42,
        pta_monte_carlo_n: drug.simulation_settings?.pta_monte_carlo_n
          ?? drug.pta_n_sim ?? 2000,
        pta_monte_carlo_seed: drug.simulation_settings?.pta_monte_carlo_seed
          ?? drug.pta_monte_carlo_seed ?? 42,
      },
      loading_dose_enabled: drug.loading_dose?.enabled || false,
      loading_dose_mg_kg: drug.loading_dose?.dose_mg_kg,
      loading_dose_mg: drug.loading_dose?.dose_mg,
      loading_dose_mg_m2: drug.loading_dose?.dose_mg_m2,
      loading_dose_count: drug.loading_dose?.count ?? 1,
      loading_dose_policy: {
        supported: drug.loading_dose_policy?.supported === true || drug.loading_dose?.enabled === true,
        default_enabled: drug.loading_dose_policy
          ? drug.loading_dose_policy.default_enabled === true
          : drug.loading_dose?.enabled === true,
        allow_user_override: drug.loading_dose_policy?.allow_user_override !== false,
      },
      loading_profiles: normalizeLoadingProfiles(drug, supportedAdministrationModes),
      supported_administration_modes: supportedAdministrationModes,
      default_administration_mode: resolveRouteAwareDefaultAdministrationMode(
        drug.default_administration_mode,
        supportedAdministrationModes,
        route,
      ),
      continuous_requires_loading_dose: !!drug.continuous_infusion?.requires_loading_dose,
      continuous_default_bag_interval_h: drug.continuous_infusion?.default_bag_interval_h ?? 24,
      continuous_max_loading_dose_mg_kg: drug.continuous_infusion?.max_loading_dose_mg_kg,
      continuous_max_loading_dose_mg_m2: drug.continuous_infusion?.max_loading_dose_mg_m2,
      supports_pk_formula: drug.supports_pk_formula === true && !!drug.pk_formula?.strategy_key,
      pk_formula_strategy_key: drug.pk_formula?.strategy_key || '',
      pk_formula_strategy_version: drug.pk_formula?.strategy_version || '',
      pk_formula_threshold_multipliers: drug.pk_formula?.mic_analysis?.threshold_multipliers?.join(', ') || '1, 4',
      pk_formula_candidate_infusion_durations_h: drug.pk_formula?.candidate_infusion_durations_h?.join(', ') || '',
      calculation_methods: drug.calculation_methods || [],
      safety_rules: safetyRulesForForm(drug),
      targets_list: (drug.targets || []).map((target: any) => normalizeTargetConfig(target, supportedAdministrationModes)),
      sampling_strategy: drug.sampling_requirements?.sampling_strategy || '',
      peak_timing: drug.sampling_requirements?.peak_timing || '',
      trough_timing: drug.sampling_requirements?.trough_timing || '',
      minimum_samples: drug.sampling_requirements?.minimum_samples,
      sampling_windows: drug.sampling_requirements?.sampling_windows || [],
      ...buildRemedialFormValues(drug),
    });
    setEditModal(true);
  };

  const handleRegimenPreview = async () => {
    const drugId = String(form.getFieldValue('drug_id') || editingDrug?.drug_id || '').trim();
    if (!drugId) {
      message.warning('请先填写药物ID');
      return;
    }
    try {
      const preview = await adminAPI.previewRegimenConfig(drugId, {
        config: buildRegimenPreviewConfig(form.getFieldsValue(true)),
      });
      setRegimenPreview(preview);
    } catch (error: any) {
      message.error(error?.response?.data?.detail || '方案预览失败');
    }
  };

  const openCreate = () => {
    setEditingDrug(null);
    form.resetFields();
    form.setFieldsValue({
      status: 'active',
      supports_pk_formula: false,
      pk_formula_strategy_key: '',
      pk_formula_strategy_version: '',
      pk_formula_threshold_multipliers: '1, 4',
      pk_formula_candidate_infusion_durations_h: '',
      dose_basis: 'mg_kg',
      ...buildUnitFormValues(),
      calculation_methods: [],
      ai_prompt_config: normalizeAIInterpretationConfig(undefined, []),
      targets_list: [],
      safety_rules: [],
      ...normalizeRegimenPolicyForm({}),
      regimen_profiles: [],
      sampling_windows: [],
      simulation_settings: {
        pk_curve_monte_carlo_n: 100,
        pk_curve_monte_carlo_seed: 42,
        pta_monte_carlo_n: 2000,
        pta_monte_carlo_seed: 42,
      },
      supported_administration_modes: ['intermittent'],
      default_administration_mode: 'intermittent',
      continuous_requires_loading_dose: false,
      continuous_default_bag_interval_h: 24,
      loading_dose_policy: { supported: false, default_enabled: false, allow_user_override: true },
      loading_profiles: [{ id: 'intermittent_loading', administration_mode: 'intermittent', enabled: false, basis: 'mg_kg', value: null, count: 1, interval_h: null, infusion_duration_h: 0 }],
      ...buildRemedialFormValues(),
    });
    setEditModal(true);
  };

  /** 将完整drug config对象填充到表单字段 */
  const populateFormFromConfig = (cfg: any) => {
    const route = Array.isArray(cfg.route) ? cfg.route : (cfg.route ? [cfg.route] : []);
    const supportedAdministrationModes = resolveSupportedAdministrationModes(
      cfg.supported_administration_modes,
      route,
      ['intermittent'],
    );
    form.setFieldsValue({
      drug_id: cfg.drug_id,
      ai_prompt_config: normalizeAIInterpretationConfig(
        cfg.ai_prompt_config,
        [...(cfg.calculation_methods || []), ...(cfg.remedial_dosing?.enabled ? ['Remedial Dose'] : [])],
      ),
      drug_name: cfg.drug_name,
      drug_name_en: cfg.drug_name_en || '',
      category: cfg.category || '',
      route,
      status: cfg.status || 'active',
      dose_basis: cfg.dose_basis || (cfg.candidate_doses_mg_m2?.length ? 'mg_m2' : (cfg.candidate_doses_mg?.length ? 'mg' : 'mg_kg')),
      ...buildUnitFormValues(cfg),
      candidate_doses_mg_kg: cfg.candidate_doses_mg_kg?.join(', ') || '',
      candidate_doses_mg: cfg.candidate_doses_mg?.join(', ') || '',
      candidate_doses_mg_m2: cfg.candidate_doses_mg_m2?.join(', ') || '',
      candidate_intervals_h: cfg.candidate_intervals_h?.join(', ') || '',
      ...normalizeRegimenPolicyForm(cfg),
      regimen_profiles: normalizeRegimenProfiles(cfg, supportedAdministrationModes),
      default_infusion_duration_h: cfg.default_infusion_duration_h,
      default_mic: cfg.default_mic,
      mic_breakpoints: cfg.mic_breakpoints?.join(', ') || '',
      default_fu: cfg.default_fu,
      supports_pk_formula: cfg.supports_pk_formula === true && !!cfg.pk_formula?.strategy_key,
      pk_formula_strategy_key: cfg.pk_formula?.strategy_key || '',
      pk_formula_strategy_version: cfg.pk_formula?.strategy_version || '',
      pk_formula_threshold_multipliers: cfg.pk_formula?.mic_analysis?.threshold_multipliers?.join(', ') || '1, 4',
      pk_formula_candidate_infusion_durations_h: cfg.pk_formula?.candidate_infusion_durations_h?.join(', ') || '',
      simulation_settings: {
        pk_curve_monte_carlo_n: cfg.simulation_settings?.pk_curve_monte_carlo_n
          ?? cfg.curve_monte_carlo_n ?? cfg.pk_curve?.monte_carlo_n ?? 100,
        pk_curve_monte_carlo_seed: cfg.simulation_settings?.pk_curve_monte_carlo_seed
          ?? cfg.curve_monte_carlo_seed ?? cfg.pk_curve?.monte_carlo_seed ?? 42,
        pta_monte_carlo_n: cfg.simulation_settings?.pta_monte_carlo_n
          ?? cfg.pta_n_sim ?? 2000,
        pta_monte_carlo_seed: cfg.simulation_settings?.pta_monte_carlo_seed
          ?? cfg.pta_monte_carlo_seed ?? 42,
      },
      loading_dose_enabled: cfg.loading_dose?.enabled || false,
      loading_dose_mg_kg: cfg.loading_dose?.dose_mg_kg,
      loading_dose_mg: cfg.loading_dose?.dose_mg,
      loading_dose_mg_m2: cfg.loading_dose?.dose_mg_m2,
      loading_dose_count: cfg.loading_dose?.count ?? 1,
      loading_dose_policy: {
        supported: cfg.loading_dose_policy?.supported === true || cfg.loading_dose?.enabled === true,
        default_enabled: cfg.loading_dose_policy
          ? cfg.loading_dose_policy.default_enabled === true
          : cfg.loading_dose?.enabled === true,
        allow_user_override: cfg.loading_dose_policy?.allow_user_override !== false,
      },
      loading_profiles: normalizeLoadingProfiles(cfg, supportedAdministrationModes),
      supported_administration_modes: supportedAdministrationModes,
      default_administration_mode: resolveRouteAwareDefaultAdministrationMode(
        cfg.default_administration_mode,
        supportedAdministrationModes,
        route,
      ),
      continuous_requires_loading_dose: !!cfg.continuous_infusion?.requires_loading_dose,
      continuous_default_bag_interval_h: cfg.continuous_infusion?.default_bag_interval_h ?? 24,
      continuous_max_loading_dose_mg_kg: cfg.continuous_infusion?.max_loading_dose_mg_kg,
      continuous_max_loading_dose_mg_m2: cfg.continuous_infusion?.max_loading_dose_mg_m2,
      calculation_methods: cfg.calculation_methods || [],
      safety_rules: safetyRulesForForm(cfg),
      targets_list: (cfg.targets || []).map((target: any) => normalizeTargetConfig(target, supportedAdministrationModes)),
      sampling_strategy: cfg.sampling_requirements?.sampling_strategy || '',
      peak_timing: cfg.sampling_requirements?.peak_timing || '',
      trough_timing: cfg.sampling_requirements?.trough_timing || '',
      minimum_samples: cfg.sampling_requirements?.minimum_samples,
      sampling_windows: cfg.sampling_requirements?.sampling_windows || [],
      ...buildRemedialFormValues(cfg),
    });
  };

  /** AI自动生成药物配置 */
  const handleAIGenerate = async () => {
    const drugId = form.getFieldValue('drug_id');
    const drugName = form.getFieldValue('drug_name');
    const drugNameEn = form.getFieldValue('drug_name_en');
    const category = form.getFieldValue('category');
    const route = form.getFieldValue('route');
    if (!drugName) {
      message.warning('请先填写药物中文名');
      return;
    }
    setAiGenerating(true);
    try {
      const res = await adminAPI.aiGenerateDrugConfig({
        drug_id: drugId || '',
        drug_name: drugName,
        drug_name_en: drugNameEn || '',
        category: category || '',
        route: route || '静脉滴注',
      });
      const cfg = res.config;
      populateFormFromConfig(cfg);
      message.success('AI已生成药物配置，请根据临床实际情况审核修改');
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : 'AI生成失败，请稍后重试');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleSave = async () => {
    try {
      await form.validateFields();            // validate rendered fields
      const values = form.getFieldsValue(true); // get ALL values including lazy-rendered tabs
      const regimenSelectionPolicy: Record<string, unknown> = {
        default_mode: values.regimen_default_mode || 'exclusive',
        missing_covariates: values.regimen_missing_covariates || 'hard_block',
        unmatched_profile: values.regimen_unmatched_profile || 'hard_block',
        overlap_policy: values.regimen_overlap_policy || 'priority_then_safety',
        tie_breaker: values.regimen_overlap_policy || 'priority_then_safety',
      };
      const regimenProfiles = (Array.isArray(values.regimen_profiles) ? values.regimen_profiles : [])
        .filter((profile: any) => profile && String(profile.id || '').trim())
        .map((profile: any) => {
          const conditions = (Array.isArray(profile.conditions) ? profile.conditions : [])
            .filter((condition: any) => condition && String(condition.field || '').trim())
            .map((condition: any) => ({
              field: String(condition.field).trim(),
              op: condition.operator || '==',
              value: condition.operator === 'in'
                ? String(condition.value || '').split(',').map((item) => parseRegimenConditionValue(item.trim())).filter((item) => item !== '')
                : parseRegimenConditionValue(condition.value),
            }));
          return {
            id: String(profile.id).trim(),
            name: String(profile.name || profile.id).trim(),
            priority: Number(profile.priority || 0),
            enabled: profile.enabled !== false,
            fallback: profile.fallback === true,
            population_tags: String(profile.population_tags || '')
              .split(',')
              .map((item) => item.trim())
              .filter(Boolean),
            selection_mode: profile.selection_mode || undefined,
            dose_basis: profile.dose_basis || 'mg_kg',
            candidate_doses: parseNumberListText(profile.candidate_doses_text),
            candidate_intervals_h: parseNumberListText(profile.candidate_intervals_text),
            supported_administration_modes: Array.isArray(profile.supported_administration_modes)
              ? profile.supported_administration_modes
              : [],
            patient_conditions: conditions.length ? { all: conditions } : {},
          };
        });
      const supportedAdministrationModes = resolveSupportedAdministrationModes(
        values.supported_administration_modes,
        values.route,
        ['intermittent'],
      );
      const defaultAdministrationMode = resolveRouteAwareDefaultAdministrationMode(
        values.default_administration_mode,
        supportedAdministrationModes,
        values.route,
      );
      const selectedMethods = Array.isArray(values.calculation_methods) ? values.calculation_methods : [];
      const targets = (values.targets_list || [])
        .filter((target: any) => target && selectedMethods.includes(target.method))
        .map((target: any) => normalizeTargetConfig({
          ...target,
          method: String(target.method || '').trim(),
          parameter: String(target.parameter || '').trim(),
          unit: String(target.unit || '').trim(),
          reference: String(target.reference || '').trim(),
        }, supportedAdministrationModes));
      const derivedContinuousTargetParameter = deriveContinuousTargetParameter(targets, supportedAdministrationModes);
      const safetyRules = normalizeSafetyRules({
        safety_rules: (values.safety_rules || []).map((rule: any) => {
          const next = { ...rule };
          const conditionText = String(next.patient_conditions_json || '').trim();
          delete next.patient_conditions_json;
          if (conditionText) {
            try {
              const parsed = JSON.parse(conditionText);
              if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('患者条件必须是 JSON 对象');
              next.scope = { ...(next.scope || {}), patient_conditions: parsed };
            } catch {
              throw new Error(`安全规则“${next.name || '未命名'}”的患者条件 JSON 无效`);
            }
          }
          return next;
        }),
        continuous_infusion: {
          max_loading_dose_mg_kg: values.continuous_max_loading_dose_mg_kg,
          max_loading_dose_mg_m2: values.continuous_max_loading_dose_mg_m2,
        },
      });
      const sampling: any = {
        sampling_strategy: values.sampling_strategy || '',
        peak_timing: values.peak_timing || '',
        trough_timing: values.trough_timing || '',
        minimum_samples: values.minimum_samples ?? 1,
        sampling_windows: values.sampling_windows || [],
      };
      const doseBasis = values.dose_basis || 'mg_kg';
      const loadingProfiles = Array.isArray(values.loading_profiles)
        ? values.loading_profiles
            .filter((profile: any) => profile && profile.administration_mode)
            .map((profile: any) => ({
              ...profile,
              id: profile.id || `${profile.administration_mode}_loading`,
              count: profile.count ?? 1,
              infusion_duration_h: profile.administration_mode === 'oral' || profile.administration_mode === 'bolus'
                ? 0
                : profile.infusion_duration_h ?? values.default_infusion_duration_h ?? 0,
            }))
        : [];
      const loadingPolicy = {
        supported: values.loading_dose_policy?.supported === true,
        default_enabled: values.loading_dose_policy?.default_enabled === true,
        allow_user_override: values.loading_dose_policy?.allow_user_override !== false,
        applicable_workflows: ['initial'],
        profiles: loadingProfiles,
      };
      const firstLoadingProfile = loadingProfiles.find((profile: any) => profile.enabled !== false) || loadingProfiles[0];
      const selectedPkFormulaStrategy = pkFormulaStrategies.find(
        (strategy: any) => strategy.strategy_key === values.pk_formula_strategy_key,
      );
      const pkFormulaStrategyKey = String(values.pk_formula_strategy_key || '').trim();
      const pkFormulaStrategyVersion = String(
        values.pk_formula_strategy_version || selectedPkFormulaStrategy?.version || '',
      ).trim();
      const pkFormulaThresholdMultipliers = parseNumberListText(values.pk_formula_threshold_multipliers);
      const pkFormulaCandidateInfusions = parseNumberListText(values.pk_formula_candidate_infusion_durations_h);
      const existingPkFormula = editingDrug?.pk_formula && typeof editingDrug.pk_formula === 'object'
        ? editingDrug.pk_formula
        : {};
      const pkFormula = {
        ...existingPkFormula,
        enabled: values.supports_pk_formula === true,
        ...(pkFormulaStrategyKey ? {
          strategy_key: pkFormulaStrategyKey,
          strategy_version: pkFormulaStrategyVersion || undefined,
        } : {}),
        mic_analysis: {
          ...(existingPkFormula.mic_analysis || {}),
          threshold_multipliers: pkFormulaThresholdMultipliers.length
            ? pkFormulaThresholdMultipliers
            : [1, 4],
          primary_multiplier: 1,
        },
        ...(pkFormulaCandidateInfusions.length
          ? { candidate_infusion_durations_h: pkFormulaCandidateInfusions }
          : { candidate_infusion_durations_h: [] }),
      };
      const drugData: any = {
        drug_id: values.drug_id, drug_name: values.drug_name, drug_name_en: values.drug_name_en || '',
        category: values.category || '', route: values.route || [], status: values.status || 'active',
        dose_basis: doseBasis,
        dose_mass_unit: values.dose_mass_unit || 'mg',
        concentration_unit: values.concentration_unit || 'mg/L',
        model_concentration_unit: values.model_concentration_unit || 'mg/L',
        auc_unit: values.auc_unit || 'mg*h/L',
        mic_unit: values.mic_unit || 'mg/L',
        units: {
          dose: {
            basis: doseBasis,
            display_mass_unit: values.dose_mass_unit || 'mg',
            canonical_mass_unit: 'mg',
          },
          concentration: {
            display_unit: values.concentration_unit || 'mg/L',
            model_unit: values.model_concentration_unit || 'mg/L',
            canonical_unit: 'mg/L',
          },
          auc: {
            display_unit: values.auc_unit || 'mg*h/L',
            model_unit: editingDrug?.units?.auc?.model_unit || 'mg*h/L',
            canonical_unit: 'mg*h/L',
          },
          mic: {
            display_unit: values.mic_unit || 'mg/L',
            canonical_unit: 'mg/L',
          },
        },
        candidate_doses_mg_kg: values.candidate_doses_mg_kg ? values.candidate_doses_mg_kg.split(',').map((s: string) => parseFloat(s.trim())).filter((n: number) => !isNaN(n)) : [],
        candidate_doses_mg: values.candidate_doses_mg ? values.candidate_doses_mg.split(',').map((s: string) => parseFloat(s.trim())).filter((n: number) => !isNaN(n)) : [],
        candidate_doses_mg_m2: values.candidate_doses_mg_m2 ? values.candidate_doses_mg_m2.split(',').map((s: string) => parseFloat(s.trim())).filter((n: number) => !isNaN(n)) : [],
        candidate_intervals_h: values.candidate_intervals_h ? values.candidate_intervals_h.split(',').map((s: string) => parseFloat(s.trim())).filter((n: number) => !isNaN(n)) : [],
        regimen_selection_policy: regimenSelectionPolicy || {},
        regimen_profiles: regimenProfiles || [],
        default_infusion_duration_h: values.default_infusion_duration_h ?? null,
        default_mic: values.default_mic ?? null,
        mic_breakpoints: parseNumberListText(values.mic_breakpoints),
        default_fu: values.default_fu ?? null,
        simulation_settings: {
          pk_curve_monte_carlo_n: values.simulation_settings?.pk_curve_monte_carlo_n ?? 100,
          pk_curve_monte_carlo_seed: values.simulation_settings?.pk_curve_monte_carlo_seed ?? 42,
          pta_monte_carlo_n: values.simulation_settings?.pta_monte_carlo_n ?? 2000,
          pta_monte_carlo_seed: values.simulation_settings?.pta_monte_carlo_seed ?? 42,
        },
        // Compatibility snapshots for older calculation workers and model packages.
        curve_monte_carlo_n: values.simulation_settings?.pk_curve_monte_carlo_n ?? 100,
        curve_monte_carlo_seed: values.simulation_settings?.pk_curve_monte_carlo_seed ?? 42,
        pta_n_sim: values.simulation_settings?.pta_monte_carlo_n ?? 2000,
        supported_administration_modes: supportedAdministrationModes,
        default_administration_mode: defaultAdministrationMode,
        continuous_infusion: {
          requires_loading_dose: !!values.continuous_requires_loading_dose,
          target_parameter: derivedContinuousTargetParameter,
          default_bag_interval_h: values.continuous_default_bag_interval_h ?? 24,
          max_loading_dose_mg_kg: values.continuous_max_loading_dose_mg_kg ?? null,
          max_loading_dose_mg_m2: values.continuous_max_loading_dose_mg_m2 ?? null,
        },
        loading_dose: {
          enabled: Boolean(loadingPolicy.supported && firstLoadingProfile?.enabled !== false),
          dose_mg_kg: firstLoadingProfile?.basis === 'mg_kg' ? firstLoadingProfile.value ?? null : null,
          dose_mg: firstLoadingProfile?.basis === 'mg' ? firstLoadingProfile.value ?? null : null,
          dose_mg_m2: firstLoadingProfile?.basis === 'mg_m2' ? firstLoadingProfile.value ?? null : null,
          count: firstLoadingProfile?.count ?? 1,
        },
        loading_dose_policy: loadingPolicy,
        calculation_methods: values.calculation_methods || [],
        remedial_dosing: buildRemedialDosingConfigFromValues(values),
        supports_pk_formula: values.supports_pk_formula !== false,
        pk_formula: pkFormula,
        // required_covariates / optional_covariates / custom_covariates removed in Phase 3
        targets,
        safety_rules: safetyRules.map(serializeSafetyRule),
        safety_constraints: buildLegacySafetyConstraints(
          safetyRules,
          editingDrug?.safety_constraints,
        ),
        sampling_requirements: sampling,
        ai_prompt_config: normalizeAIInterpretationConfig(
          values.ai_prompt_config,
          [...(values.calculation_methods || []), ...(values.remedial_enabled ? ['Remedial Dose'] : [])],
        ),
      };
      // 保留AI生成的额外字段（population_regimens, tdm_recommendation, population_notes等）
      if (editingDrug) {
        const FORM_KEYS = new Set(Object.keys(drugData));
        Object.keys(editingDrug).forEach(k => { if (!FORM_KEYS.has(k)) drugData[k] = editingDrug[k]; });
      }
        if (editingDrug) await adminAPI.updateDrugParam(editingDrug.drug_id, drugData);
        else await adminAPI.createDrugParam(drugData);

        const hasPendingCovariateChanges = Boolean(drugCovariateEditorRef.current?.hasUnsavedChanges());
        if (editingDrug?.drug_id && hasPendingCovariateChanges) {
          await drugCovariateEditorRef.current?.save({ silent: true });
        }

        message.success('已保存'); setEditModal(false); fetchDrugs();
    } catch (err: any) {
      if (err?.errorFields) return;
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const msg = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map((d: any) => d.msg).join('; ')
          : status ? `请求失败 (${status})`
            : err instanceof Error ? err.message : '保存失败';
      message.error(msg);
    }
  };

  const handleDelete = async (drugId: string) => {
    try { await adminAPI.deleteDrugParam(drugId); message.success('已删除'); fetchDrugs(); }
    catch { message.error('删除失败'); }
  };

  const renderTargets = (targets: any) => {
    if (!targets) return '-';
    const list = Array.isArray(targets) ? targets : [targets.primary, targets.secondary].filter(Boolean);
    return list.map((t: any, i: number) => (
      <div key={i}>
        {t.method || t.parameter}: {t.min}-{t.max} {t.unit}
        {Array.isArray(t.administration_modes) && t.administration_modes.length > 0
          ? ` [${normalizeAdministrationModes(t.administration_modes, []).map((mode) => getAdministrationModeLabel(mode)).join(' / ')}]`
          : ''}
      </div>
    ));
  };

  const columns = [
    {
      title: '药物', key: 'name', width: 250,
      render: (_: any, r: any) => (
        <div className="drug-admin__identity" onClick={() => { setSelectedDrug(r); setDetailOpen(true); }}>
          <Avatar shape="square" size={34} icon={<MedicineBoxOutlined />} className="drug-admin__avatar" />
          <div className="drug-admin__identity-copy">
            <Text strong>{r.drug_name}</Text>
            <Text type="secondary" ellipsis>{r.drug_name_en || r.drug_id}</Text>
          </div>
        </div>
      ),
    },
    {
      title: '分类与状态', key: 'classification', width: 150,
      render: (_: any, r: any) => (
        <Space direction="vertical" size={3}>
          <Text>{r.category || '未分类'}</Text>
          <Tag color={r.status === 'active' ? 'success' : 'default'}>{r.status === 'active' ? '启用' : '停用'}</Tag>
        </Space>
      ),
    },
    {
      title: '给药', key: 'administration', width: 190,
      render: (_: any, r: any) => (
        <Space direction="vertical" size={4}>
          <Text>{normalizeRoutes(r.route).join(' / ') || '-'}</Text>
          {renderAdministrationModeTags(r.supported_administration_modes)}
        </Space>
      ),
    },
    {
      title: '计算与靶值', key: 'calculation', width: 280,
      render: (_: any, r: any) => (
        <Space direction="vertical" size={4}>
          <Space size={[4, 4]} wrap>
            {r.calculation_methods?.slice(0, 3).map((method: string) => <Tag key={method}>{method}</Tag>) || '-'}
            {(r.calculation_methods?.length || 0) > 3 && <Text type="secondary">+{r.calculation_methods.length - 3}</Text>}
          </Space>
          <Text type="secondary" ellipsis>{getTargetDisplay(r.targets)}</Text>
        </Space>
      ),
    },
    {
      title: '补服策略', key: 'remedial', width: 160,
      render: (_: any, r: any) => r.remedial_dosing?.enabled ? (
        <Space direction="vertical" size={3}>
          <Tag color="processing">已启用</Tag>
          <Text type="secondary">{r.remedial_dosing?.allow_split === false ? '仅完整规格' : '允许拆分'}</Text>
        </Space>
      ) : <Text type="secondary">未启用</Text>,
    },
    {
      title: '', key: 'ops', width: 124, fixed: 'right' as const, align: 'right' as const,
      render: (_: any, r: any) => (
        <Space size={4}>
          <Tooltip title="查看详情"><Button type="text" size="small" icon={<EyeOutlined />} onClick={() => { setSelectedDrug(r); setDetailOpen(true); }} /></Tooltip>
          <Tooltip title="编辑配置"><Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} /></Tooltip>
          <Popconfirm title="确认删除?" onConfirm={() => handleDelete(r.drug_id)}>
            <Tooltip title="删除"><Button type="text" size="small" danger icon={<DeleteOutlined />} /></Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Tabs
        defaultActiveKey="config"
        items={[
          {
            key: 'config',
            label: '药物配置',
            children: (
              <div className="drug-admin">
                <div className="drug-admin__summary">
                  {[
                    ['全部药物', drugSummary.total],
                    ['启用', drugSummary.active],
                    ['补服已配置', drugSummary.remedial],
                    ['待完善', drugSummary.incomplete],
                  ].map(([label, value]) => (
                    <div className="drug-admin__metric" key={String(label)}>
                      <span>{label}</span><strong>{value}</strong>
                    </div>
                  ))}
                </div>
                <div className="drug-admin__toolbar">
                  <Space size={8} wrap>
                    <Search
                      allowClear
                      value={drugSearch}
                      onChange={(event) => setDrugSearch(event.target.value)}
                      placeholder="搜索药物名称、ID或分类"
                      style={{ width: 280 }}
                    />
                    <Segmented
                      value={drugStatusFilter}
                      onChange={(value) => setDrugStatusFilter(value as 'all' | 'active' | 'inactive')}
                      options={[
                        { label: '全部', value: 'all' },
                        { label: '启用', value: 'active' },
                        { label: '停用', value: 'inactive' },
                      ]}
                    />
                  </Space>
                  <Space size={6}>
                    <Tooltip title="刷新"><Button icon={<ReloadOutlined />} onClick={fetchDrugs} loading={loading} /></Tooltip>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新增药物</Button>
                  </Space>
                </div>
                <Table
                  className="drug-admin__table"
                  columns={columns}
                  dataSource={filteredDrugs}
                  rowKey="drug_id"
                  loading={loading}
                  pagination={{ pageSize: 20, showSizeChanger: false, showTotal: (total) => `${total} 项` }}
                  size="small"
                  scroll={{ x: 1150 }}
                />
              </div>
            ),
          },
          {
            key: 'activation',
            label: '激活分配',
            children: <DrugActivationAssignments />,
          },
        ]}
      />

      {/* Platform-standard drug detail modal. Admin-only configuration stays available here. */}
      <AppDetailModal
        className="drug-detail-modal"
        title={selectedDrug ? `${selectedDrug.drug_name} · ${selectedDrug.drug_name_en || selectedDrug.drug_id}` : '药物详情'}
        subtitle="后台药物配置详情"
        status={selectedDrug ? (
          <Tag color={selectedDrug.status === 'active' ? 'green' : 'default'}>
            {selectedDrug.status === 'active' ? '启用' : '停用'}
          </Tag>
        ) : undefined}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        width={920}
        footer={selectedDrug ? (
          <>
            <Button onClick={() => setDetailOpen(false)}>关闭</Button>
            <Button type="primary" icon={<EditOutlined />} onClick={() => { setDetailOpen(false); openEdit(selectedDrug); }}>
              编辑配置
            </Button>
          </>
        ) : null}
      >
        {selectedDrug && (
          <>
            <div className="drug-detail-drawer__section">
              <div className="drug-detail-drawer__section-title"><MedicineBoxOutlined /> 基本信息</div>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="药物ID"><Text code>{selectedDrug.drug_id}</Text></Descriptions.Item>
              <Descriptions.Item label="分类">{selectedDrug.category || '-'}</Descriptions.Item>
              <Descriptions.Item label="给药途径">{normalizeRoutes(selectedDrug.route).join(' / ') || '-'}</Descriptions.Item>
              <Descriptions.Item label="状态"><Tag color={selectedDrug.status === 'active' ? 'green' : 'default'}>{selectedDrug.status === 'active' ? '启用' : '停用'}</Tag></Descriptions.Item>
              <Descriptions.Item label="支持给药模式">{renderAdministrationModeTags(selectedDrug.supported_administration_modes)}</Descriptions.Item>
              <Descriptions.Item label="默认给药模式">{getAdministrationModeLabel(resolveRouteAwareDefaultAdministrationMode(selectedDrug.default_administration_mode, selectedDrug.supported_administration_modes, selectedDrug.route))}</Descriptions.Item>
              <Descriptions.Item label="默认输注时间">{selectedDrug.default_infusion_duration_h ?? '-'} h</Descriptions.Item>
              <Descriptions.Item label="默认MIC">{selectedDrug.default_mic ?? '-'} {displayUnitLabel(selectedDrug.units?.mic?.display_unit || selectedDrug.mic_unit || 'mg/L')}</Descriptions.Item>
              <Descriptions.Item label="MIC断点">{selectedDrug.mic_breakpoints?.join(', ') || '-'}</Descriptions.Item>
              <Descriptions.Item label="前台剂量单位">{getDoseDisplayUnit(selectedDrug)}</Descriptions.Item>
              <Descriptions.Item label="浓度单位">{displayUnitLabel(selectedDrug.units?.concentration?.display_unit || selectedDrug.concentration_unit || 'mg/L')}</Descriptions.Item>
              <Descriptions.Item label="模型原生浓度单位">{displayUnitLabel(selectedDrug.units?.concentration?.model_unit || selectedDrug.model_concentration_unit || 'mg/L')}</Descriptions.Item>
              <Descriptions.Item label="游离分数">{selectedDrug.default_fu ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="PK公式策略">
                {selectedDrug.supports_pk_formula && selectedDrug.pk_formula?.strategy_key
                  ? `${selectedDrug.pk_formula.strategy_key} v${selectedDrug.pk_formula.strategy_version || '-'}`
                  : '未启用'}
              </Descriptions.Item>
              <Descriptions.Item label="计算方法">{selectedDrug.calculation_methods?.map((m: string) => <Tag key={m} color="blue">{m}</Tag>) || '-'}</Descriptions.Item>
              <Descriptions.Item label="模拟精度">
                PK 曲线 {selectedDrug.simulation_settings?.pk_curve_monte_carlo_n ?? selectedDrug.curve_monte_carlo_n ?? 100} 次；PTA {selectedDrug.simulation_settings?.pta_monte_carlo_n ?? selectedDrug.pta_n_sim ?? 2000} 次
              </Descriptions.Item>
            </Descriptions></div>
            <div className="drug-detail-drawer__section">
              <div className="drug-detail-drawer__section-title"><ThunderboltOutlined /> 给药与计算</div>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="目标参数">{deriveContinuousTargetParameter(selectedDrug.targets || [], selectedDrug.supported_administration_modes)}</Descriptions.Item>
              <Descriptions.Item label="连续输注间隔">{selectedDrug.continuous_infusion?.default_bag_interval_h ?? '-'} h</Descriptions.Item>
              <Descriptions.Item label="启用负荷剂量">{selectedDrug.continuous_infusion?.requires_loading_dose ? '是' : '否'}</Descriptions.Item>
              <Descriptions.Item label="负荷剂量上限">
                {selectedDrug.dose_basis === 'mg_m2'
                  ? `${selectedDrug.continuous_infusion?.max_loading_dose_mg_m2 ?? '-'} mg/m²`
                  : `${selectedDrug.continuous_infusion?.max_loading_dose_mg_kg ?? '-'} mg/kg`}
              </Descriptions.Item>
            </Descriptions></div>
            <div className="drug-detail-drawer__section">
              <div className="drug-detail-drawer__section-title"><SafetyCertificateOutlined /> 靶标与安全边界</div>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="靶值">{renderTargets(selectedDrug.targets)}</Descriptions.Item>
            </Descriptions>
            {(selectedDrug.candidate_doses_mg_kg?.length > 0 || selectedDrug.candidate_doses_mg?.length > 0 || selectedDrug.candidate_doses_mg_m2?.length > 0) && (
              <Descriptions column={2} size="small" style={{ marginTop: 8 }}>
                <Descriptions.Item label={
                  selectedDrug.dose_basis === 'mg_m2'
                    ? '候选剂量 (mg/m²)'
                    : selectedDrug.dose_basis === 'mg'
                      ? '候选剂量 (mg)'
                      : '候选剂量 (mg/kg)'
                }>
                  {selectedDrug.dose_basis === 'mg_m2'
                    ? (selectedDrug.candidate_doses_mg_m2?.join(', ') || '-')
                    : selectedDrug.dose_basis === 'mg'
                      ? (selectedDrug.candidate_doses_mg?.join(', ') || '-')
                      : (selectedDrug.candidate_doses_mg_kg?.join(', ') || '-')}
                </Descriptions.Item>
                <Descriptions.Item label="候选间隔 (h)">{selectedDrug.candidate_intervals_h?.join(', ') || '-'}</Descriptions.Item>
              </Descriptions>
            )}
            {selectedSafetyRules.length > 0 && (
              <Descriptions column={1} size="small" style={{ marginTop: 8 }}>
                {selectedSafetyRules.map((rule) => (
                  <Descriptions.Item key={rule.id} label={rule.enabled ? '启用规则' : '已停用规则'}>
                    <Space size={6} wrap>
                      <Tag color={rule.severity === 'hard_block' ? 'red' : rule.severity === 'warning' ? 'orange' : 'blue'}>
                        {rule.severity === 'hard_block' ? '硬性阻断' : rule.severity === 'warning' ? '警告' : '提示'}
                      </Tag>
                      <Text>{formatSafetyRuleSummary(rule)}</Text>
                    </Space>
                  </Descriptions.Item>
                ))}
              </Descriptions>
            )}
            </div>
            <div className="drug-detail-drawer__section">
              <div className="drug-detail-drawer__section-title"><ClockCircleOutlined /> 补服配置</div>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="状态">
                <Tag color={selectedDrug.remedial_dosing?.enabled ? 'processing' : 'default'}>
                  {selectedDrug.remedial_dosing?.enabled ? '已启用' : '未启用'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="剂型约束">
                {selectedDrug.remedial_dosing?.allow_split === false ? '仅完整规格' : '允许拆分'}
              </Descriptions.Item>
              <Descriptions.Item label="完整规格">
                {selectedDrug.remedial_dosing?.full_strengths_mg?.join(', ') || '-'} {getDoseDisplayUnit(selectedDrug)}
              </Descriptions.Item>
              <Descriptions.Item label="候选参考剂量">
                {selectedDrug.remedial_dosing?.reference_dose_mg
                  ?? selectedDrug.remedial_dosing?.regular_strength_mg
                  ?? selectedDrug.remedial_dosing?.full_strengths_mg?.[0]
                  ?? '-'} {getDoseDisplayUnit(selectedDrug)}
              </Descriptions.Item>
              <Descriptions.Item label="优化决策时点">
                {selectedDrug.remedial_dosing?.decision_point_count || 2} 个
              </Descriptions.Item>
              <Descriptions.Item label="候选剂量模式">
                {selectedDrug.remedial_dosing?.candidate_dose_mode || 'dosage_form_grid'}
              </Descriptions.Item>
            </Descriptions></div>
          </>
        )}
      </AppDetailModal>

      {/* Edit/Create Modal */}
      <Modal
        className="drug-editor-modal"
        title={editingDrug ? `编辑: ${editingDrug.drug_name}` : '新增药物'}
        open={editModal}
        onOk={handleSave}
        onCancel={() => setEditModal(false)}
        width="min(96vw, 1440px)"
        style={{ top: 12 }}
        styles={{ body: { maxHeight: '84vh', overflow: 'hidden', paddingBottom: 8 } }}
        okText="保存"
        cancelText="取消"
        destroyOnClose
      >
        <div className="drug-editor-modal__toolbar">
          <Tooltip title="填写基本信息(中文名、英文名、分类、给药途径)后，AI自动生成其余配置">
            <Button size="small" type="primary" ghost icon={<RobotOutlined />}
              loading={aiGenerating} onClick={handleAIGenerate}>
              AI智能填充
            </Button>
          </Tooltip>
        </div>
        <Form form={form} layout="vertical" size="small">
            <Tabs
              className="drug-editor-tabs"
              size="small"
              tabPosition={screens.lg ? 'left' : 'top'}
              destroyInactiveTabPane={false}
              items={[
              {
                key: 'basic', label: '基本信息', forceRender: true, children: (
                  <>
                    <Row gutter={16}>
                      <Col span={8}><Form.Item name="drug_id" label="药物ID" rules={[{ required: true }]}><Input disabled={!!editingDrug} /></Form.Item></Col>
                      <Col span={8}><Form.Item name="drug_name" label="中文名" rules={[{ required: true }]}><Input /></Form.Item></Col>
                      <Col span={8}><Form.Item name="drug_name_en" label="英文名"><Input /></Form.Item></Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={8}><Form.Item name="category" label="分类"><Input placeholder="如: 糖肽类抗生素" /></Form.Item></Col>
                      <Col span={8}><Form.Item name="route" label="给药途径">
                        <Select
                          mode="tags"
                          placeholder="选择或输入给药途径"
                          onChange={(nextRoute) => syncAdministrationModesFromRoute(nextRoute)}
                          options={[
                            { value: '静脉滴注', label: '静脉滴注' },
                            { value: '口服', label: '口服' },
                          ]}
                        />
                      </Form.Item></Col>
                      <Col span={4}><Form.Item name="status" label="状态">
                        <Select options={[{ value: 'active', label: '启用' }, { value: 'inactive', label: '停用' }]}
                         />
                      </Form.Item></Col>
                      <Col span={4}><Form.Item name="supports_pk_formula" label="PK公式法" valuePropName="checked" tooltip="是否允许用户跳过模型使用PK公式法(两点法)"><Switch /></Form.Item></Col>
                    </Row>
                  </>
                )
              },
              {
                key: 'dosing', label: '剂量参数', forceRender: true, children: (
                  <>
                    <section className="admin-editor-section">
                      <div className="admin-editor-section__title">临床单位</div>
                      <Row gutter={16}>
                        <Col xs={12} md={5}>
                          <Form.Item name="dose_mass_unit" label="剂量显示单位" rules={[{ required: true }]}>
                            <Select options={DOSE_MASS_UNIT_OPTIONS} />
                          </Form.Item>
                        </Col>
                        <Col xs={12} md={5}>
                          <Form.Item name="concentration_unit" label="浓度显示单位" rules={[{ required: true }]}>
                            <Select options={CONCENTRATION_UNIT_OPTIONS} />
                          </Form.Item>
                        </Col>
                        <Col xs={12} md={5}>
                          <Form.Item
                            name="model_concentration_unit"
                            label="模型原生浓度单位"
                            tooltip="由已发布模型版本声明；药物配置仅保留兼容快照。"
                            rules={[{ required: true }]}
                          >
                            <Select options={CONCENTRATION_UNIT_OPTIONS} disabled />
                          </Form.Item>
                        </Col>
                        <Col xs={12} md={5}>
                          <Form.Item name="auc_unit" label="AUC 显示单位" rules={[{ required: true }]}>
                            <Select options={AUC_UNIT_OPTIONS} />
                          </Form.Item>
                        </Col>
                        <Col xs={12} md={4}>
                          <Form.Item name="mic_unit" label="MIC 显示单位" rules={[{ required: true }]}>
                            <Select options={CONCENTRATION_UNIT_OPTIONS} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Alert
                        type="info"
                        showIcon
                        message="现有 dose_mg* 字段继续以 mg 为内部标准单位；此处控制临床界面、报告和结果快照的显示单位。"
                      />
                    </section>
                    <Divider orientation="left" plain>剂量基准与候选搜索空间</Divider>
                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item name="dose_basis" label="剂量基准" rules={[{ required: true }]}>
                          <Select options={[
                            { value: 'mg_kg', label: '按体重（内部 mg/kg）' },
                            { value: 'mg', label: '固定剂量（内部 mg）' },
                            { value: 'mg_m2', label: '按体表面积（内部 mg/m²）' },
                          ]} />
                        </Form.Item>
                      </Col>
                      <Col span={16}>
                        <Alert
                          type="info"
                          showIcon
                          message="候选剂量、负荷剂量与最大剂量会随剂量基准切换"
                        />
                      </Col>
                    </Row>
                    <Form.Item noStyle shouldUpdate={(prev, next) => prev.dose_basis !== next.dose_basis}>
                      {({ getFieldValue }) => {
                        const doseBasis = getFieldValue('dose_basis') || 'mg_kg';
                        return (
                          <Row gutter={16}>
                            {doseBasis === 'mg_kg' && (
                              <Col span={12}><Form.Item name="candidate_doses_mg_kg" label="候选剂量 - 按体重 (mg/kg, 逗号分隔)"><Input placeholder="10, 12, 15, 18, 20" /></Form.Item></Col>
                            )}
                            {doseBasis === 'mg' && (
                              <Col span={12}><Form.Item name="candidate_doses_mg" label="候选剂量 - 固定 (mg, 逗号分隔)"><Input placeholder="500, 1000, 2000" /></Form.Item></Col>
                            )}
                            {doseBasis === 'mg_m2' && (
                              <Col span={12}><Form.Item name="candidate_doses_mg_m2" label="候选剂量 - 按体表面积 (mg/m², 逗号分隔)"><Input placeholder="75, 100, 120" /></Form.Item></Col>
                            )}
                          </Row>
                        );
                      }}
                    </Form.Item>
                    <Row gutter={16}>
                      <Col span={12}><Form.Item name="candidate_intervals_h" label="候选间隔 (h, 逗号分隔)"><Input placeholder="6, 8, 12, 18, 24" /></Form.Item></Col>
                      <Col span={12}><Form.Item name="default_infusion_duration_h" label="默认输注时间 (h)"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
                    </Row>
                    <Card size="small" title="多给药方案" style={{ marginBottom: 12 }}>
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message="按患者条件选择 mg/kg 或固定 mg 方案"
                        description="方案、剂量基准、候选剂量、给药间隔和患者条件均可在表格中配置；留空时继续使用传统候选剂量。"
                      />
                      <Row gutter={12}>
                        <Col xs={24} md={6}><Form.Item name="regimen_default_mode" label="选择策略"><Select options={REGIMEN_SELECTION_MODE_OPTIONS} /></Form.Item></Col>
                        <Col xs={24} md={6}><Form.Item name="regimen_missing_covariates" label="缺少协变量"><Select options={REGIMEN_POLICY_OPTIONS} /></Form.Item></Col>
                        <Col xs={24} md={6}><Form.Item name="regimen_unmatched_profile" label="无匹配方案"><Select options={REGIMEN_POLICY_OPTIONS} /></Form.Item></Col>
                        <Col xs={24} md={6}><Form.Item name="regimen_overlap_policy" label="方案重叠处理"><Select options={[{ value: 'priority_then_safety', label: '优先级→安全边界' }]} /></Form.Item></Col>
                      </Row>
                      <Form.List name="regimen_profiles">
                        {(fields, { add, remove }) => (
                          <>
                            <Table
                              bordered
                              size="small"
                              pagination={false}
                              scroll={{ x: 1450 }}
                              rowKey={(field: any) => String(field.key)}
                              dataSource={fields}
                              expandable={{
                                expandedRowRender: (field: any) => (
                                  <Form.List name={[field.name, 'conditions']}>
                                    {(conditionFields, { add: addCondition, remove: removeCondition }) => (
                                      <div style={{ padding: '4px 12px 0' }}>
                                        <Space wrap style={{ marginBottom: 8 }}>
                                          <Form.Item {...field} name={[field.name, 'fallback']} valuePropName="checked" noStyle>
                                            <Switch size="small" />
                                          </Form.Item>
                                          <Typography.Text type="secondary">标记为无匹配兜底方案</Typography.Text>
                                          <Form.Item {...field} name={[field.name, 'population_tags']} noStyle>
                                            <Input placeholder="人群标签（可选，逗号分隔）" style={{ width: 240 }} />
                                          </Form.Item>
                                        </Space>
                                        <Typography.Text strong>患者匹配条件（全部满足）</Typography.Text>
                                        {conditionFields.map((conditionField) => (
                                          <Space key={conditionField.key} align="baseline" style={{ display: 'flex', marginTop: 8 }}>
                                            <Form.Item {...conditionField} name={[conditionField.name, 'field']} rules={[{ required: true, message: '请输入协变量' }]}>
                                              <Input placeholder="age_years / weight / crcl" style={{ width: 180 }} />
                                            </Form.Item>
                                            <Form.Item {...conditionField} name={[conditionField.name, 'operator']} initialValue="==">
                                              <Select options={REGIMEN_CONDITION_OPERATOR_OPTIONS} style={{ width: 100 }} />
                                            </Form.Item>
                                            <Form.Item {...conditionField} name={[conditionField.name, 'value']} rules={[{ required: true, message: '请输入值' }]}>
                                              <Input placeholder="12 或 male" style={{ width: 150 }} />
                                            </Form.Item>
                                            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeCondition(conditionField.name)} />
                                          </Space>
                                        ))}
                                        <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => addCondition({ operator: '==' })}>新增条件</Button>
                                      </div>
                                    )}
                                  </Form.List>
                                ),
                                rowExpandable: () => true,
                              }}
                              columns={[
                                { title: '启用', width: 68, render: (_: unknown, field: any) => <Form.Item name={[field.name, 'enabled']} valuePropName="checked" noStyle><Switch size="small" /></Form.Item> },
                                { title: '方案名称', width: 170, render: (_: unknown, field: any) => <Form.Item name={[field.name, 'name']} rules={[{ required: true }]} noStyle><Input placeholder="儿童按体重" /></Form.Item> },
                                { title: '方案 ID', width: 150, render: (_: unknown, field: any) => <Form.Item name={[field.name, 'id']} rules={[{ required: true }]} noStyle><Input placeholder="pediatric" /></Form.Item> },
                                { title: '优先级', width: 88, render: (_: unknown, field: any) => <Form.Item name={[field.name, 'priority']} noStyle><InputNumber min={0} precision={0} style={{ width: '100%' }} /></Form.Item> },
                                { title: '单方案策略', width: 150, render: (_: unknown, field: any) => <Form.Item name={[field.name, 'selection_mode']} noStyle><Select allowClear options={REGIMEN_SELECTION_MODE_OPTIONS} placeholder="跟随全局" /></Form.Item> },
                                { title: '剂量基准', width: 150, render: (_: unknown, field: any) => <Form.Item name={[field.name, 'dose_basis']} rules={[{ required: true }]} noStyle><Select options={REGIMEN_DOSE_BASIS_OPTIONS} /></Form.Item> },
                                { title: '候选剂量', width: 150, render: (_: unknown, field: any) => <Form.Item name={[field.name, 'candidate_doses_text']} rules={[{ required: true }]} noStyle><Input placeholder="10 或 600" /></Form.Item> },
                                { title: '间隔(h)', width: 130, render: (_: unknown, field: any) => <Form.Item name={[field.name, 'candidate_intervals_text']} rules={[{ required: true }]} noStyle><Input placeholder="8, 12" /></Form.Item> },
                                { title: '给药模式', width: 190, render: (_: unknown, field: any) => <Form.Item name={[field.name, 'supported_administration_modes']} noStyle><Select mode="multiple" options={ADMINISTRATION_MODE_OPTIONS as any} placeholder="全部" /></Form.Item> },
                                { title: '操作', width: 64, render: (_: unknown, field: any) => <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} /> },
                              ]}
                            />
                            <Button
                              type="dashed"
                              block
                              icon={<PlusOutlined />}
                              style={{ marginTop: 10 }}
                              onClick={() => add({ id: `regimen_${fields.length + 1}`, name: `方案 ${fields.length + 1}`, priority: 0, enabled: true, selection_mode: '', dose_basis: 'mg_kg', candidate_doses_text: '', candidate_intervals_text: '', supported_administration_modes: [], conditions: [] })}
                            >
                              新增给药方案
                            </Button>
                          </>
                        )}
                      </Form.List>
                    <Space style={{ marginTop: 10 }}>
                      <Button size="small" icon={<EyeOutlined />} onClick={handleRegimenPreview}>预览方案匹配</Button>
                      {regimenPreview ? <Tag color={regimenPreview.valid === false ? 'red' : 'green'}>{regimenPreview.valid === false ? '配置需修正' : `${regimenPreview.profiles?.length || 0} 个方案已解析`}</Tag> : null}
                    </Space>
                    {regimenPreview ? (
                      <Alert
                        style={{ marginTop: 10 }}
                        type={regimenPreview.valid === false ? 'error' : (regimenPreview.overlaps?.length ? 'warning' : 'success')}
                        showIcon
                        message={regimenPreview.valid === false ? regimenPreview.error?.message : (regimenPreview.overlaps?.length ? `检测到 ${regimenPreview.overlaps.length} 组可能重叠的方案，将按优先级处理` : '未发现静态方案重叠')}
                        description={regimenPreview.fallback_profile_id ? `兜底方案：${regimenPreview.fallback_profile_id}` : '如需在无匹配时继续计算，请标记一个兜底方案。'}
                      />
                    ) : null}
                    </Card>
                    <Divider orientation="left" plain style={{ margin: '8px 0' }}>负荷剂量模板（按给药模式）</Divider>
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message="负荷剂量属于药物方案配置，不按计算方法重复配置；每次初始给药时用户仍可确认或调整。"
                    />
                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item name={['loading_dose_policy', 'supported']} label="支持负荷剂量" valuePropName="checked">
                          <Switch />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name={['loading_dose_policy', 'default_enabled']} label="默认启用" valuePropName="checked">
                          <Switch />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name={['loading_dose_policy', 'allow_user_override']} label="允许前台调整" valuePropName="checked">
                          <Switch defaultChecked />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.List name="loading_profiles">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map((field) => (
                            <Card key={field.key} size="small" style={{ marginBottom: 8 }}>
                              <Row gutter={[8, 8]} align="middle" className="drug-loading-profile-row">
                                <Col xs={24} md={4}><Form.Item {...field} name={[field.name, 'id']} label="方案标识" rules={[{ required: true }]}><Input /></Form.Item></Col>
                                <Col xs={24} md={4}><Form.Item {...field} name={[field.name, 'administration_mode']} label="给药模式" rules={[{ required: true }]}><Select options={ADMINISTRATION_MODE_OPTIONS as any} /></Form.Item></Col>
                                <Col xs={12} md={3}><Form.Item {...field} name={[field.name, 'basis']} label="剂量基准"><Select options={[{ value: 'mg_kg', label: 'mg/kg' }, { value: 'mg', label: 'mg' }, { value: 'mg_m2', label: 'mg/m²' }]} /></Form.Item></Col>
                                <Col xs={12} md={3}><Form.Item {...field} name={[field.name, 'value']} label="剂量值"><InputNumber min={0.001} style={{ width: '100%' }} /></Form.Item></Col>
                                <Col xs={12} md={2}><Form.Item {...field} name={[field.name, 'count']} label="次数"><InputNumber min={1} max={20} precision={0} style={{ width: '100%' }} /></Form.Item></Col>
                                <Col xs={12} md={2}><Form.Item {...field} name={[field.name, 'interval_h']} label="间隔(h)"><InputNumber min={0.1} style={{ width: '100%' }} /></Form.Item></Col>
                                <Col xs={12} md={2}><Form.Item {...field} name={[field.name, 'infusion_duration_h']} label="输注(h)"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
                                <Col xs={12} md={3}><Form.Item {...field} name={[field.name, 'enabled']} label="启用" valuePropName="checked"><Switch /></Form.Item></Col>
                                <Col xs={24} md={1}><Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} /></Col>
                              </Row>
                            </Card>
                          ))}
                          <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({ id: `profile_${fields.length + 1}`, administration_mode: 'intermittent', enabled: true, basis: 'mg_kg', count: 1, value: null, infusion_duration_h: 0 })}>
                            新增给药模式负荷方案
                          </Button>
                        </>
                      )}
                    </Form.List>
                    <Form.Item noStyle shouldUpdate={(prev, next) => (
                      prev.supports_pk_formula !== next.supports_pk_formula
                      || prev.pk_formula_strategy_key !== next.pk_formula_strategy_key
                    )}>
                      {({ getFieldValue, setFieldsValue }) => {
                        const formulaEnabled = getFieldValue('supports_pk_formula') === true;
                        const selectedKey = getFieldValue('pk_formula_strategy_key');
                        const selectedStrategy = pkFormulaStrategies.find((item: any) => item.strategy_key === selectedKey);
                        return (
                          <div className="admin-editor-section" style={{ marginTop: 12 }}>
                            <div className="admin-editor-section__title"><ExperimentOutlined /> PK 公式法策略</div>
                            <Alert
                              type={formulaEnabled ? 'info' : 'warning'}
                              showIcon
                              style={{ marginBottom: 12 }}
                              message={formulaEnabled ? '已启用 PK 公式法' : '未启用 PK 公式法'}
                              description={formulaEnabled
                                ? '必须选择后台注册的公式策略；未配置有效策略时，后端会拒绝保存和计算。'
                                : '关闭后前台不会显示 PK 公式法入口；如需启用，请同时选择一个公式策略。'}
                            />
                            <Row gutter={16}>
                              <Col xs={24} md={8}>
                                <Form.Item name="pk_formula_strategy_key" label="公式策略" rules={formulaEnabled ? [{ required: true, message: '请选择公式策略' }] : []}>
                                  <Select
                                    allowClear
                                    placeholder="选择后台注册策略"
                                    options={pkFormulaStrategies.map((strategy: any) => ({
                                      value: strategy.strategy_key,
                                      label: `${strategy.label || strategy.strategy_key} (${strategy.strategy_key})`,
                                    }))}
                                    onChange={(value) => {
                                      const strategy = pkFormulaStrategies.find((item: any) => item.strategy_key === value);
                                      setFieldsValue({ pk_formula_strategy_version: strategy?.version || '' });
                                    }}
                                  />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={4}>
                                <Form.Item name="pk_formula_strategy_version" label="策略版本">
                                  <Input disabled placeholder={selectedStrategy?.version || '自动读取'} />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={6}>
                                <Form.Item name="pk_formula_threshold_multipliers" label="MIC 阈值倍数">
                                  <Input placeholder="1, 4" />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={6}>
                                <Form.Item name="pk_formula_candidate_infusion_durations_h" label="候选输注时长 (h)">
                                  <Input placeholder="留空使用当前输注时长" />
                                </Form.Item>
                              </Col>
                            </Row>
                            {selectedStrategy?.clinical_sampling_order && (
                              <Text type="secondary">临床采样顺序：{selectedStrategy.clinical_sampling_order}</Text>
                            )}
                          </div>
                        );
                      }}
                    </Form.Item>
                    <Divider orientation="left" plain style={{ margin: '8px 0' }}>PK/PD 参数</Divider>
                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item noStyle shouldUpdate={(prev, next) => prev.mic_unit !== next.mic_unit}>
                          {({ getFieldValue }) => (
                            <Form.Item name="default_mic" label={`默认MIC (${displayUnitLabel(getFieldValue('mic_unit') || 'mg/L')})`}>
                              <InputNumber style={{ width: '100%' }} step={0.1} />
                            </Form.Item>
                          )}
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          name="mic_breakpoints"
                          label="MIC断点 (逗号分隔)"
                          tooltip="留空时默认按 0.25、0.5、1、2、4、8、16、32 mg/L 分析；后台配置非空时按配置计算，本次用户输入 MIC 会自动纳入。"
                        >
                          <Input placeholder="0.25, 0.5, 1, 2, 4, 8, 16, 32" />
                        </Form.Item>
                      </Col>
                      <Col span={8}><Form.Item name="default_fu" label="游离分数 fu"><InputNumber style={{ width: '100%' }} step={0.01} min={0} max={1} /></Form.Item></Col>
                    </Row>
                    <Divider orientation="left" plain style={{ margin: '8px 0' }}>计算性能与模拟精度</Divider>
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message="按药物配置蒙特卡洛模拟规模"
                      description="PK 曲线次数影响预测带绘制；PTA 次数影响目标达成概率的稳定性。次数越高结果越稳定，但计算时间和资源消耗也会增加。"
                    />
                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name={['simulation_settings', 'pk_curve_monte_carlo_n']}
                          label="PK 曲线蒙特卡洛次数"
                          tooltip="0 表示关闭预测带；允许范围 0–1000。"
                          rules={[{ required: true, type: 'number', min: 0, max: 1000, message: '请输入 0–1000 的整数' }]}
                        >
                          <InputNumber min={0} max={1000} precision={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name={['simulation_settings', 'pk_curve_monte_carlo_seed']}
                          label="PK 曲线随机种子"
                          rules={[{ required: true, type: 'number', min: 1, max: 2147483647, message: '请输入有效随机种子' }]}
                        >
                          <InputNumber min={1} max={2147483647} precision={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name={['simulation_settings', 'pta_monte_carlo_n']}
                          label="PTA 蒙特卡洛次数"
                          tooltip="用于 PTA 目标达成概率计算；允许范围 100–10000。"
                          rules={[{ required: true, type: 'number', min: 100, max: 10000, message: '请输入 100–10000 的整数' }]}
                        >
                          <InputNumber min={100} max={10000} precision={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name={['simulation_settings', 'pta_monte_carlo_seed']}
                          label="PTA 随机种子"
                          rules={[{ required: true, type: 'number', min: 1, max: 2147483647, message: '请输入有效随机种子' }]}
                        >
                          <InputNumber min={1} max={2147483647} precision={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                )
              },
              {
                key: 'calc', label: '给药与计算', forceRender: true, children: (
                  <>
                    <Form.Item noStyle shouldUpdate={(prev, next) => (
                      prev.supported_administration_modes !== next.supported_administration_modes
                      || prev.calculation_methods !== next.calculation_methods
                      || prev.remedial_enabled !== next.remedial_enabled
                      || prev.remedial_window_mode !== next.remedial_window_mode
                      || prev.remedial_use_complexity_weight !== next.remedial_use_complexity_weight
                    )}>
                      {({ getFieldValue, setFieldsValue }) => {
                        const supportedModes = normalizeAdministrationModes(
                          getFieldValue('supported_administration_modes'),
                          ['intermittent'],
                        );
                        const continuousEnabled = supportedModes.includes('continuous');
                        const remedialEnabled = !!getFieldValue('remedial_enabled');
                        const methods: string[] = getFieldValue('calculation_methods') || [];
                        const curList: any[] = Array.isArray(getFieldValue('targets_list'))
                          ? getFieldValue('targets_list')
                          : [];
                        const normalizedTargets = curList
                          .filter((target: any) => target && methods.includes(target.method))
                          .map((target: any) => normalizeTargetConfig(target, supportedModes));
                        const missingTargets = methods
                          .filter((method) => !normalizedTargets.some((target: any) => target.method === method))
                          .map((method) => buildDefaultTargetConfig(method, supportedModes));
                        const synced = [...normalizedTargets, ...missingTargets];
                        if (JSON.stringify(synced) !== JSON.stringify(curList)) {
                          setTimeout(() => setFieldsValue({ targets_list: synced }), 0);
                        }
                        return (
                          <>
                            <Alert
                              showIcon
                              type={continuousEnabled ? 'info' : 'warning'}
                              style={{ marginBottom: 12 }}
                              message="前台剂量计算页的给药模式切换仅依赖后台数据库配置"
                              description={continuousEnabled
                                ? '当前药物已启用“连续输注”。保存后，前台才会显示对应的给药模式切换、连续输注结果卡片，以及连续输注下的目标靶值。'
                                : '若希望前台出现“连续输注”，必须先在后台药物配置中启用该模式。'}
                            />

                            <div className="drug-calc-section-heading drug-calc-section-heading--first">
                              <MedicineBoxOutlined />
                              <span>给药模式</span>
                            </div>
                            <Row gutter={16}>
                              <Col span={12}>
                                <Form.Item
                                  name="supported_administration_modes"
                                  label="支持给药模式"
                                  rules={[{ required: true, message: '请至少选择一种给药模式' }]}
                                >
                                  <Select
                                    mode="multiple"
                                    options={ADMINISTRATION_MODE_OPTIONS as any}
                                    placeholder="请选择可用的给药模式"
                                  />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item
                                  name="default_administration_mode"
                                  label="默认给药模式"
                                  rules={[{ required: true, message: '请选择默认给药模式' }]}
                                >
                                  <Select
                                    options={ADMINISTRATION_MODE_OPTIONS
                                      .filter((option) => supportedModes.includes(option.value))
                                      .map((option) => ({ value: option.value, label: option.label }))}
                                  />
                                </Form.Item>
                              </Col>
                            </Row>

                            <div className="drug-calc-section-heading">
                              <SwapOutlined />
                              <span>连续输注附加参数</span>
                            </div>
                            <Alert
                              type="info"
                              showIcon
                              style={{ marginBottom: 12 }}
                              message="连续输注的目标参数不再单独维护"
                              description="系统会根据下方“目标靶值”表中适用于连续输注的 Target Concentration 行自动推导，通常为 Css。"
                            />
                            <Form.Item noStyle shouldUpdate={(prev, next) => prev.dose_basis !== next.dose_basis}>
                              {({ getFieldValue }) => {
                                const doseBasis = getFieldValue('dose_basis') || 'mg_kg';
                                return (
                                  <Row gutter={16}>
                                    <Col span={8}>
                                      <Form.Item name="continuous_requires_loading_dose" label="启用负荷剂量" valuePropName="checked">
                                        <Switch disabled={!continuousEnabled} />
                                      </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                      <Form.Item name="continuous_default_bag_interval_h" label="连续输注间隔 (h)">
                                        <InputNumber disabled={!continuousEnabled} min={1} style={{ width: '100%' }} />
                                      </Form.Item>
                                    </Col>
                                    {doseBasis === 'mg_kg' && (
                                      <Col span={8}>
                                        <Form.Item name="continuous_max_loading_dose_mg_kg" label="负荷剂量上限 (mg/kg)">
                                          <InputNumber disabled={!continuousEnabled} min={0} style={{ width: '100%' }} />
                                        </Form.Item>
                                      </Col>
                                    )}
                                    {doseBasis === 'mg_m2' && (
                                      <Col span={8}>
                                        <Form.Item name="continuous_max_loading_dose_mg_m2" label="负荷剂量上限 (mg/m²)">
                                          <InputNumber disabled={!continuousEnabled} min={0} style={{ width: '100%' }} />
                                        </Form.Item>
                                      </Col>
                                    )}
                                  </Row>
                                );
                              }}
                            </Form.Item>

                            <div className="drug-calc-section-heading">
                              <ExperimentOutlined />
                              <span>计算方法</span>
                            </div>
                            <Form.Item name="calculation_methods" label="计算方法（可多选组合）">
                              <Select mode="multiple" placeholder="选择计算方法" options={[
                                { value: 'Target AUC', label: '目标AUC24' },
                                { value: 'AUC/MIC', label: 'AUC24/MIC' },
                                { value: '%fT>MIC', label: '%fT>MIC + PTA' },
                                { value: 'Target Concentration', label: '目标浓度' },
                                { value: 'Target Cmax', label: '目标Cmax（峰浓度）' },
                                { value: 'Cmax/MIC', label: 'Cmax/MIC' },
                              ]} />
                            </Form.Item>

                            <Form.Item label="目标靶值">
                              {methods.length === 0 ? <Text type="secondary">请先选择计算方法</Text> : (
                                <>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                    <Text type="secondary">
                                      同一计算方法可以配置多条目标，用于区分间断输注与连续输注；“适用给药模式”会自动限制在“支持给药模式”的范围内。
                                    </Text>
                                    <Button
                                      size="small"
                                      icon={<PlusOutlined />}
                                      onClick={() => {
                                        const nextMethod = methods[0];
                                        if (!nextMethod) return;
                                        setTimeout(() => {
                                          setFieldsValue({
                                            targets_list: [
                                              ...synced,
                                              buildDefaultTargetConfig(nextMethod, supportedModes),
                                            ],
                                          });
                                        }, 0);
                                      }}
                                    >
                                      新增一行
                                    </Button>
                                  </div>
                                  <Table
                                    size="small"
                                    pagination={false}
                                    rowKey={(_record: any, idx?: number) => `target-${idx ?? 0}`}
                                    dataSource={synced}
                                    columns={[
                                      {
                                        title: '方法',
                                        width: 150,
                                        render: (_: any, _r: any, idx: number) => (
                                          <Form.Item name={['targets_list', idx, 'method']} noStyle>
                                            <Select size="small" options={methods.map((method) => ({ value: method, label: method }))} />
                                          </Form.Item>
                                        )
                                      },
                                      {
                                        title: '参数',
                                        width: 110,
                                        render: (_: any, _r: any, idx: number) => (
                                          <Form.Item name={['targets_list', idx, 'parameter']} noStyle><Input size="small" /></Form.Item>
                                        )
                                      },
                                      {
                                        title: '最小值',
                                        width: 80,
                                        render: (_: any, _r: any, idx: number) => (
                                          <Form.Item name={['targets_list', idx, 'min']} noStyle><InputNumber size="small" style={{ width: '100%' }} /></Form.Item>
                                        )
                                      },
                                      {
                                        title: '最大值',
                                        width: 80,
                                        render: (_: any, _r: any, idx: number) => (
                                          <Form.Item name={['targets_list', idx, 'max']} noStyle><InputNumber size="small" style={{ width: '100%' }} /></Form.Item>
                                        )
                                      },
                                      {
                                        title: '单位',
                                        width: 150,
                                        render: (_: any, record: any, idx: number) => {
                                          const fixedUnit = getFixedPkpdTargetUnit(record.method);
                                          const unitOptions = getPkpdTargetUnitOptions(record.method);
                                          return (
                                            <Tooltip title={getPkpdTargetUnitHelp(record.method)}>
                                              <Form.Item name={['targets_list', idx, 'unit']} noStyle>
                                                {fixedUnit ? (
                                                  <Input size="small" readOnly />
                                                ) : (
                                                  <Select
                                                    size="small"
                                                    showSearch
                                                    optionFilterProp="label"
                                                    options={unitOptions}
                                                  />
                                                )}
                                              </Form.Item>
                                            </Tooltip>
                                          );
                                        }
                                      },
                                      {
                                        title: '适用给药模式',
                                        width: 190,
                                        render: (_: any, _r: any, idx: number) => (
                                          <Form.Item name={['targets_list', idx, 'administration_modes']} noStyle>
                                            <Select
                                              size="small"
                                              mode="multiple"
                                              options={ADMINISTRATION_MODE_OPTIONS
                                                .filter((option) => supportedModes.includes(option.value))
                                                .map((option) => ({ value: option.value, label: option.label }))}
                                            />
                                          </Form.Item>
                                        )
                                      },
                                      {
                                        title: '参考文献',
                                        render: (_: any, _r: any, idx: number) => (
                                          <Form.Item name={['targets_list', idx, 'reference']} noStyle><Input size="small" /></Form.Item>
                                        )
                                      },
                                      {
                                        title: '',
                                        width: 36,
                                        render: (_: any, record: any, idx: number) => {
                                          const sameMethodCount = synced.filter((item: any) => item.method === record.method).length;
                                          if (sameMethodCount <= 1) return null;
                                          return (
                                            <MinusCircleOutlined
                                              style={{ color: '#ff4d4f' }}
                                              onClick={() => {
                                                const nextTargets = synced.filter((_: any, targetIdx: number) => targetIdx !== idx);
                                                setTimeout(() => setFieldsValue({ targets_list: nextTargets }), 0);
                                              }}
                                            />
                                          );
                                        }
                                      },
                                    ]}
                                  />
                                </>
                              )}
                            </Form.Item>

                            <div className="drug-calc-section-heading">
                              <SafetyCertificateOutlined />
                              <span>补服配置</span>
                            </div>
                            <div className="drug-remedial">
                              <div className="drug-remedial__status">
                                <div>
                                  <Text strong>补服决策支持</Text>
                                  <div><Text type="secondary">延迟服药与完全漏服统一采用多决策时点离散剂量组合搜索。</Text></div>
                                </div>
                                <Space>
                                  <Tag color={remedialEnabled ? 'success' : 'default'}>{remedialEnabled ? '已启用' : '未启用'}</Tag>
                                  <Form.Item name="remedial_enabled" valuePropName="checked" noStyle><Switch /></Form.Item>
                                </Space>
                              </div>

                              <section className="drug-remedial__section">
                                <div className="drug-remedial__section-title">候选剂量策略与剂型</div>
                                <Row gutter={12}>
                                  <Col xs={24} md={6}>
                                    <Form.Item name="remedial_candidate_dose_mode" label="候选剂量生成方式">
                                      <Select disabled={!remedialEnabled} options={[
                                        { value: 'dosage_form_grid', label: '剂型与拆分网格（推荐）' },
                                        { value: 'multiplier_list', label: '参考剂量倍数' },
                                        { value: 'explicit_list', label: '明确剂量列表' },
                                      ]} />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <Form.Item name="remedial_evidence_reference_set" label="文献证据参照">
                                      <Select disabled={!remedialEnabled} options={[
                                        { value: 'liu2024_ag', label: 'Liu 2024 策略 A-G' },
                                        { value: 'none', label: '不关联文献策略' },
                                      ]} />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <Form.Item name="remedial_full_strengths_mg" label="可用完整规格 (mg)" tooltip="用于约束候选剂量可由哪些完整规格组合得到。">
                                      <Input disabled={!remedialEnabled} placeholder="如 75, 110, 150" />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <Form.Item
                                      name="remedial_reference_dose_mg"
                                      label="候选参考剂量"
                                      tooltip="用于候选倍数、搜索上限和文献策略匹配；留空且仅配置一个完整规格时自动采用。"
                                    >
                                      <InputNumber disabled={!remedialEnabled} min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={12} md={3}>
                                    <Form.Item name="remedial_allow_split" label="允许拆分" valuePropName="checked">
                                      <Switch disabled={!remedialEnabled} />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={12} md={3}>
                                    <Form.Item name="remedial_min_split_dose_mg" label="拆分最小剂量">
                                      <InputNumber disabled={!remedialEnabled} min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                  </Col>
                                </Row>
                                <Row gutter={12}>
                                  <Col xs={24} md={8}>
                                    <Form.Item name="remedial_candidate_dose_multipliers" label="候选剂量倍数" tooltip="仅在参考剂量倍数模式使用，0 表示该时点不服药。">
                                      <Input disabled={!remedialEnabled || getFieldValue('remedial_candidate_dose_mode') !== 'multiplier_list'} placeholder="0, 0.5, 1, 1.5, 2" />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={8}>
                                    <Form.Item name="remedial_candidate_doses_mg" label="明确候选剂量" tooltip="仅在明确剂量列表模式使用，0 表示该时点不服药。">
                                      <Input disabled={!remedialEnabled || getFieldValue('remedial_candidate_dose_mode') !== 'explicit_list'} placeholder="0, 5, 10, 15, 20" />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={12} md={4}>
                                    <Form.Item name="remedial_candidate_max_dose_ratio" label="搜索剂量上限倍数">
                                      <InputNumber disabled={!remedialEnabled} min={0.1} step={0.1} style={{ width: '100%' }} />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={12} md={4}>
                                    <Form.Item name="remedial_max_candidate_count" label="最大候选组合数">
                                      <InputNumber disabled={!remedialEnabled} min={1} max={10000} step={50} style={{ width: '100%' }} />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </section>

                              <section className="drug-remedial__section">
                                <div className="drug-remedial__section-title">剂量约束</div>
                                <Row gutter={12}>
                                  <Col xs={24} md={6}>
                                    <Form.Item name="remedial_single_dose_limit_mode" label="单次剂量规则">
                                      <Select disabled={!remedialEnabled} options={[
                                        { value: 'off', label: '关闭' },
                                        { value: 'warning', label: '仅警告，继续模拟' },
                                        { value: 'hard', label: '超过上限时阻止计算' },
                                      ]} />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <Form.Item name="remedial_single_dose_limit_basis" label="单次剂量计算基准">
                                      <Select
                                        disabled={!remedialEnabled || getFieldValue('remedial_single_dose_limit_mode') === 'off'}
                                        options={[
                                          { value: 'current_regimen', label: '患者当前单次剂量（推荐）' },
                                          { value: 'strategy_reference', label: '候选参考剂量' },
                                        ]}
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <Form.Item name="remedial_single_dose_limit_ratio" label="单次剂量上限倍数">
                                      <InputNumber disabled={!remedialEnabled || getFieldValue('remedial_single_dose_limit_mode') === 'off'} min={0.1} step={0.1} style={{ width: '100%' }} />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <Form.Item name="remedial_total_dose_limit_mode" label="累计剂量规则">
                                      <Select disabled={!remedialEnabled} options={[
                                        { value: 'off', label: '关闭' },
                                        { value: 'warning', label: '仅警告，继续模拟' },
                                        { value: 'hard', label: '超出范围时阻止计算' },
                                      ]} />
                                    </Form.Item>
                                  </Col>
                                </Row>
                                <Row gutter={12}>
                                  <Col xs={24} md={8}>
                                    <Form.Item name="remedial_total_dose_limit_basis" label="累计剂量计算基准">
                                      <Select disabled={!remedialEnabled || getFieldValue('remedial_total_dose_limit_mode') === 'off'} options={[
                                        { value: 'current_regimen', label: '患者当前方案（推荐）' },
                                        { value: 'strategy_reference', label: '候选参考剂量' },
                                      ]} />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={8}>
                                    <Form.Item name="remedial_total_dose_min_ratio" label="累计剂量下限比例">
                                      <InputNumber disabled={!remedialEnabled || getFieldValue('remedial_total_dose_limit_mode') === 'off'} min={0} step={0.05} style={{ width: '100%' }} />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={8}>
                                    <Form.Item name="remedial_total_dose_max_ratio" label="累计剂量上限比例">
                                      <InputNumber disabled={!remedialEnabled || getFieldValue('remedial_total_dose_limit_mode') === 'off'} min={0.05} step={0.05} style={{ width: '100%' }} />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </section>

                              <section className="drug-remedial__section">
                                <div className="drug-remedial__section-title">模拟与治疗窗</div>
                                <Row gutter={12}>
                                  <Col xs={24} md={8}>
                                    <Form.Item
                                      name="remedial_window_mode"
                                      label="治疗窗来源"
                                      tooltip="稳态模式按第 5 百分位谷浓度至第 95 百分位峰浓度生成 OTR；人工模式直接采用所填上下限。两种模式均以候选曲线 p05/p95 包络的超窗时长进行排序。"
                                    >
                                      <Segmented disabled={!remedialEnabled} block options={[
                                        { label: '稳态 5%–95%', value: 'simulation_reference' },
                                        { label: '人工指定范围', value: 'configured' },
                                      ]} />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={12} md={4}>
                                    <Form.Item name="remedial_window_lower" label="下限">
                                      <InputNumber disabled={!remedialEnabled || getFieldValue('remedial_window_mode') !== 'configured'} step={0.1} style={{ width: '100%' }} />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={12} md={4}>
                                    <Form.Item name="remedial_window_upper" label="上限">
                                      <InputNumber disabled={!remedialEnabled || getFieldValue('remedial_window_mode') !== 'configured'} step={0.1} style={{ width: '100%' }} />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={12} md={4}>
                                    <Form.Item name="remedial_window_unit" label="单位">
                                      <Select disabled={!remedialEnabled} options={CONCENTRATION_UNIT_OPTIONS} />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={12} md={4}>
                                    <Form.Item name="remedial_monte_carlo_n" label="模拟人数">
                                      <InputNumber disabled={!remedialEnabled} min={100} max={20000} step={100} style={{ width: '100%' }} />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </section>

                              <section className="drug-remedial__section">
                                <div className="drug-remedial__section-title">排序与等效判定</div>
                                <Row gutter={12}>
                                  <Col xs={12} md={5}>
                                    <Form.Item name="remedial_weight_high" label="高于治疗窗权重">
                                      <InputNumber disabled={!remedialEnabled} min={0} step={0.1} style={{ width: '100%' }} />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={12} md={5}>
                                    <Form.Item name="remedial_weight_low" label="低于治疗窗权重">
                                      <InputNumber disabled={!remedialEnabled} min={0} step={0.1} style={{ width: '100%' }} />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={12} md={5}>
                                    <Form.Item name="remedial_equivalence_tolerance_h" label="等效容差 (h)" tooltip="留空时 q12h 自动使用 0.1 h，q24h 使用 0.2 h。">
                                      <InputNumber disabled={!remedialEnabled} min={0} step={0.05} placeholder="自动" style={{ width: '100%' }} />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={12} md={5}>
                                    <Form.Item name="remedial_weight_simple" label="操作复杂度权重">
                                      <InputNumber disabled={!remedialEnabled || !getFieldValue('remedial_use_complexity_weight')} min={0} step={0.1} style={{ width: '100%' }} />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={4}>
                                    <Form.Item name="remedial_use_complexity_weight" label="纳入复杂度" valuePropName="checked">
                                      <Switch disabled={!remedialEnabled} />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </section>

                              <Collapse ghost size="small" items={[{
                                key: 'advanced-remedial',
                                label: '高级参数',
                                children: (
                                  <Row gutter={12}>
                                    <Col xs={12} md={4}><Form.Item name="remedial_decision_point_count" label="优化决策时点数" tooltip="默认 2；配置 3 个及以上时属于扩展模拟。"><InputNumber disabled={!remedialEnabled} min={2} max={5} style={{ width: '100%' }} /></Form.Item></Col>
                                    <Col xs={12} md={4}><Form.Item name="remedial_baseline_take_if_delay_lt_h" label="基线阈值 (h)"><InputNumber disabled={!remedialEnabled} min={0} step={0.5} style={{ width: '100%' }} /></Form.Item></Col>
                                    <Col xs={12} md={4}><Form.Item name="remedial_monte_carlo_seed" label="随机种子"><InputNumber disabled={!remedialEnabled} min={1} style={{ width: '100%' }} /></Form.Item></Col>
                                    <Col xs={12} md={6}><Form.Item name="remedial_window_metric" label="治疗窗指标"><Input disabled={!remedialEnabled} /></Form.Item></Col>
                                    <Col xs={12} md={6}>
                                      <Form.Item label="模型→显示换算">
                                        <Form.Item noStyle shouldUpdate={(prev, next) => (
                                          prev.model_concentration_unit !== next.model_concentration_unit
                                          || prev.concentration_unit !== next.concentration_unit
                                        )}>
                                          {({ getFieldValue: readUnit }) => {
                                            const modelUnit = readUnit('model_concentration_unit') || 'mg/L';
                                            const displayUnit = readUnit('concentration_unit') || 'mg/L';
                                            const factor = unitConversionFactor(modelUnit, displayUnit, 'concentration');
                                            return <Text code>{`1 ${displayUnitLabel(modelUnit)} = ${factor} ${displayUnitLabel(displayUnit)}`}</Text>;
                                          }}
                                        </Form.Item>
                                      </Form.Item>
                                    </Col>
                                  </Row>
                                ),
                              }]} />
                            </div>
                          </>
                        );
                      }}
                    </Form.Item>
                  </>
                )
              },
              {
                key: 'safety', label: '安全规则', forceRender: true, children: (
                  <div className="drug-safety-rules">
                    <div className="drug-safety-rules__intro">
                      <div>
                        <Typography.Text strong>安全边界</Typography.Text>
                        <Typography.Text type="secondary">按剂量、暴露和给药方式配置可审计的限制规则</Typography.Text>
                      </div>
                      <Tag bordered={false}>药物级配置</Tag>
                    </div>
                    <Form.List name="safety_rules">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map((field) => (
                          <div key={field.key} className="drug-safety-rule">
                            <Form.Item noStyle shouldUpdate={(prev, next) => (
                              prev.safety_rules?.[field.name]?.metric !== next.safety_rules?.[field.name]?.metric
                            )}>
                              {({ getFieldValue }) => {
                                const metric = (getFieldValue(['safety_rules', field.name, 'metric']) || 'dose.single') as SafetyMetric;
                                const category = getSafetyRuleCategory(metric);
                                const categoryMeta = SAFETY_RULE_CATEGORY_META[category];
                                const metricLabel = SAFETY_METRIC_OPTIONS.find((option) => option.value === metric)?.label || metric;
                                return (
                                  <div className={`drug-safety-rule__header drug-safety-rule__header--${category}`}>
                                    <Space size={8}>
                                      <Tag color={categoryMeta.color}>{categoryMeta.label}</Tag>
                                      <Typography.Text type="secondary">{metricLabel}</Typography.Text>
                                    </Space>
                                  </div>
                                );
                              }}
                            </Form.Item>
                            <Row gutter={12} align="bottom">
                              <Col xs={24} md={5}>
                                <Form.Item {...field} name={[field.name, 'name']} label="规则名称" rules={[{ required: true, message: '请输入规则名称' }]}>
                                  <Input placeholder="例如：AUC24 上限" />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={5}>
                                <Form.Item {...field} name={[field.name, 'metric']} label="指标" rules={[{ required: true, message: '请选择指标' }]}>
                                  <Select
                                    options={SAFETY_METRIC_OPTIONS}
                                    onChange={(metric: SafetyMetric) => {
                                      const defaults = createSafetyRule(metric);
                                      form.setFieldValue(['safety_rules', field.name, 'unit'], defaults.unit);
                                      form.setFieldValue(['safety_rules', field.name, 'operator'], defaults.operator);
                                      form.setFieldValue(['safety_rules', field.name, 'severity'], defaults.severity);
                                    }}
                                  />
                                </Form.Item>
                              </Col>
                              <Col xs={12} md={4}>
                                <Form.Item {...field} name={[field.name, 'operator']} label="条件" rules={[{ required: true }]}>
                                  <Select options={SAFETY_OPERATOR_OPTIONS} />
                                </Form.Item>
                              </Col>
                              <Col xs={12} md={3}>
                                <Form.Item {...field} name={[field.name, 'value']} label="阈值" rules={[{ required: true, type: 'number', message: '请输入阈值' }]}>
                                  <InputNumber min={0} style={{ width: '100%' }} />
                                </Form.Item>
                              </Col>
                              <Form.Item noStyle shouldUpdate={(prev, next) => (
                                prev.safety_rules?.[field.name]?.operator !== next.safety_rules?.[field.name]?.operator
                              )}>
                                {({ getFieldValue }) => getFieldValue(['safety_rules', field.name, 'operator']) === 'between' ? (
                                  <Col xs={12} md={3}>
                                    <Form.Item {...field} name={[field.name, 'upper_value']} label="上限" rules={[{ required: true, type: 'number', message: '请输入上限' }]}>
                                      <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                  </Col>
                                ) : null}
                              </Form.Item>
                              <Form.Item noStyle shouldUpdate={(prev, next) => (
                                prev.safety_rules?.[field.name]?.metric !== next.safety_rules?.[field.name]?.metric
                              )}>
                                {({ getFieldValue }) => {
                                  const metric = (getFieldValue(['safety_rules', field.name, 'metric']) || 'dose.single') as SafetyMetric;
                                  return (
                                    <Col xs={12} md={4}>
                                      <Form.Item {...field} name={[field.name, 'unit']} label="单位" rules={[{ required: true }]}>
                                        <Select options={getSafetyRuleUnitOptions(metric)} />
                                      </Form.Item>
                                    </Col>
                                  );
                                }}
                              </Form.Item>
                              <Col xs={12} md={4}>
                                <Form.Item {...field} name={[field.name, 'severity']} label="严重程度" rules={[{ required: true }]}>
                                  <Select options={SAFETY_SEVERITY_OPTIONS} />
                                </Form.Item>
                              </Col>
                              <Col xs={12} md={2}>
                                <Form.Item {...field} name={[field.name, 'enabled']} label="启用" valuePropName="checked">
                                  <Switch />
                                </Form.Item>
                              </Col>
                              <Col xs={12} md={2} style={{ paddingBottom: 24 }}>
                                <Tooltip title="删除规则">
                                  <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} aria-label="删除规则" />
                                </Tooltip>
                              </Col>
                            </Row>
                            <Row gutter={12}>
                              <Col xs={24} md={7}>
                                <Form.Item {...field} name={[field.name, 'methods']} label="适用计算方法">
                                  <Select mode="multiple" allowClear options={Object.keys(TARGET_DEFAULTS).map((method) => ({ value: method, label: method }))} />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={5}>
                                <Form.Item {...field} name={[field.name, 'administration_modes']} label="适用给药方式">
                                  <Select mode="multiple" allowClear options={ADMINISTRATION_MODE_OPTIONS as unknown as Array<{ value: string; label: string }>} />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={6}>
                                <Form.Item {...field} name={[field.name, 'administration_routes']} label="适用给药途径">
                                  <Select mode="multiple" allowClear options={SAFETY_ROUTE_OPTIONS} />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={6}>
                                <Form.Item {...field} name={[field.name, 'description']} label="说明">
                                  <Input />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={6}>
                                <Form.Item {...field} name={[field.name, 'patient_conditions_json']} label="患者条件(JSON)">
                                  <Input placeholder='{"all":[{"field":"age_years","op":"<","value":12}]}' />
                                </Form.Item>
                              </Col>
                            </Row>
                          </div>
                        ))}
                        <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add(createSafetyRule())}>
                          添加安全规则
                        </Button>
                      </>
                    )}
                    </Form.List>
                  </div>
                ),
              },
              {
                key: 'sampling', label: '采样要求', forceRender: true, children: (
                  <>
                    <Row gutter={16}>
                      <Col span={8}><Form.Item name="sampling_strategy" label="采样策略"><Input placeholder="贝叶斯法" /></Form.Item></Col>
                      <Col span={8}><Form.Item name="peak_timing" label="峰浓度采样时间"><Input placeholder="输注结束后1-2小时" /></Form.Item></Col>
                      <Col span={8}><Form.Item name="trough_timing" label="谷浓度采样时间"><Input placeholder="下次给药前30分钟内" /></Form.Item></Col>
                    </Row>
                    <Form.Item name="minimum_samples" label="最少采样点数"><InputNumber min={0} style={{ width: 120 }} /></Form.Item>
                    <Form.Item label="采样窗口">
                      <Form.List name="sampling_windows">
                        {(fields, { add, remove }) => (
                          <>
                            <Table size="small" pagination={false} dataSource={fields.map(f => ({ ...f, key: f.key }))}
                              columns={[
                                {
                                  title: '名称', width: 100, render: (_: any, _r: any, idx: number) => (
                                    <Form.Item name={[idx, 'name']} noStyle><Input size="small" placeholder="谷浓度" /></Form.Item>
                                  )
                                },
                                {
                                  title: '时间范围', width: 150, render: (_: any, _r: any, idx: number) => (
                                    <Form.Item name={[idx, 'time_range']} noStyle><Input size="small" placeholder="下次给药前0" /></Form.Item>
                                  )
                                },
                                {
                                  title: '描述', render: (_: any, _r: any, idx: number) => (
                                    <Form.Item name={[idx, 'description']} noStyle><Input size="small" /></Form.Item>
                                  )
                                },
                                {
                                  title: '', width: 32, render: (_: any, _r: any, idx: number) => (
                                    <MinusCircleOutlined onClick={() => remove(idx)} style={{ color: '#ff4d4f' }} />
                                  )
                                },
                              ]}
                            />
                            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} size="small" style={{ marginTop: 8 }}>添加采样窗口</Button>
                          </>
                        )}
                      </Form.List>
                    </Form.Item>
                  </>
                )
              },
              {
                key: 'ai_prompt', label: 'AI解读配置', forceRender: true, children: (
                  <Form.Item
                    noStyle
                    shouldUpdate={(prev, next) => prev.ai_prompt_config !== next.ai_prompt_config || prev.calculation_methods !== next.calculation_methods || prev.remedial_enabled !== next.remedial_enabled}
                  >
                    {({ getFieldValue }) => (
                      <AIInterpretationProfileEditor
                        value={getFieldValue('ai_prompt_config')}
                        methods={[
                          ...(getFieldValue('calculation_methods') || []),
                          ...(getFieldValue('remedial_enabled') ? ['Remedial Dose'] : []),
                        ]}
                        onChange={(next) => form.setFieldValue('ai_prompt_config', next)}
                      />
                    )}
                  </Form.Item>
                )
              },
              {
                key: 'evaluation_rules', label: '评价规则', children: (
                  <Form.Item noStyle shouldUpdate={(prev, next) => (
                    prev.drug_id !== next.drug_id
                    || prev.drug_name !== next.drug_name
                    || prev.calculation_methods !== next.calculation_methods
                  )}>
                    {({ getFieldValue }) => (
                      <DoseEvaluationRulesEditor
                        key={editingDrug?.drug_id || getFieldValue('drug_id') || 'new-evaluation-rules'}
                        drugId={editingDrug?.drug_id || getFieldValue('drug_id') || ''}
                        drugName={editingDrug?.drug_name || getFieldValue('drug_name') || ''}
                        methods={getFieldValue('calculation_methods') || []}
                      />
                    )}
                  </Form.Item>
                )
              },
              {
                key: 'drug_covariates', label: '协变量作用范围矩阵', children: (
                  <DrugCovariateEditor
                    key={editingDrug?.drug_id || 'new'}
                    ref={drugCovariateEditorRef}
                    drugId={editingDrug?.drug_id || ''}
                  />
                )
              },
              ]}
            />
          </Form>
      </Modal>
    </>
  );
};

// ================================================================
// Drug Covariate Editor (inline sub-component)
// ================================================================
type NestedEditorSaveHandle = {
  save: (options?: { silent?: boolean }) => Promise<void>;
  hasUnsavedChanges: () => boolean;
};

﻿
type ScopeState = 'off' | 'optional' | 'required';

type DrugCovariateEditorProps = {
  drugId: string;
};

const SCOPE_STATE_OPTIONS: Array<{ label: string; value: ScopeState }> = [
  { value: 'off', label: '\u4e0d\u6536\u96c6' },
  { value: 'optional', label: '\u53ef\u9009' },
  { value: 'required', label: '\u5fc5\u586b' },
];

const POPULATION_SCOPE_VALUES: PopulationGroup[] = ['neonate', 'pediatric', 'adult'];
const SCOPE_GROUP_VALUES = ['general', ...POPULATION_SCOPE_VALUES] as CovariateScopeGroup[];
const MATRIX_SORT_STEP = 10;

const SCOPE_SHORT_HINT: Record<CovariateScopeGroup, string> = {
  general: '\u8986\u76d6\u5168\u90e8\u4eba\u7fa4',
  neonate: '\u4ec5\u65b0\u751f\u513f',
  pediatric: '\u4ec5\u513f\u7ae5',
  adult: '\u4ec5\u6210\u4eba',
};

const normalizeMatrixRow = (row: any) => {
  const scopeSettings: Record<CovariateScopeGroup, any> = {
    general: { ...(row.scope_settings?.general || {}), state: row.scope_settings?.general?.state || 'off' },
    neonate: { ...(row.scope_settings?.neonate || {}), state: row.scope_settings?.neonate?.state || 'off' },
    pediatric: { ...(row.scope_settings?.pediatric || {}), state: row.scope_settings?.pediatric?.state || 'off' },
    adult: { ...(row.scope_settings?.adult || {}), state: row.scope_settings?.adult?.state || 'off' },
  };

  const generalOn = scopeSettings.general.state !== 'off';
  const specificOn = POPULATION_SCOPE_VALUES.some((scope) => scopeSettings[scope].state !== 'off');

  if (generalOn) {
    POPULATION_SCOPE_VALUES.forEach((scope) => {
      scopeSettings[scope] = { ...scopeSettings[scope], state: 'off', disabled: true };
    });
    scopeSettings.general = { ...scopeSettings.general, disabled: false };
  } else if (specificOn) {
    scopeSettings.general = { ...scopeSettings.general, state: 'off', disabled: true };
    POPULATION_SCOPE_VALUES.forEach((scope) => {
      scopeSettings[scope] = { ...scopeSettings[scope], disabled: false };
    });
  } else {
    SCOPE_GROUP_VALUES.forEach((scope) => {
      scopeSettings[scope] = { ...scopeSettings[scope], disabled: false };
    });
  }

  const rowSortOrder = Number(row.sort_order);
  const fallbackSortOrder = SCOPE_GROUP_VALUES
    .map((scope) => Number(scopeSettings[scope]?.display_order))
    .find((value) => Number.isFinite(value));

  return {
    ...row,
    sort_order: Number.isFinite(rowSortOrder) ? rowSortOrder : (fallbackSortOrder ?? 2000),
    scope_settings: scopeSettings,
    has_any_scope_enabled: SCOPE_GROUP_VALUES.some((scope) => scopeSettings[scope].state !== 'off'),
  };
};

const sortMatrixRows = (input: any[]) => (
  [...input]
    .map((row) => normalizeMatrixRow(row))
    .sort((left, right) => {
      const sortDelta = (left.sort_order || 2000) - (right.sort_order || 2000);
      if (sortDelta !== 0) return sortDelta;
      return String(left.covariate_key || '').localeCompare(String(right.covariate_key || ''));
    })
);

const resequenceMatrixRows = (input: any[]) => (
  input.map((row, index) => {
    const nextOrder = (index + 1) * MATRIX_SORT_STEP;
    return normalizeMatrixRow({
      ...row,
      sort_order: nextOrder,
      scope_settings: Object.fromEntries(
        SCOPE_GROUP_VALUES.map((scope) => [scope, { ...(row.scope_settings?.[scope] || {}), display_order: nextOrder }]),
      ),
    });
  })
);

const SCOPE_STATE_META: Record<ScopeState, { label: string; tone: string }> = {
  off: { label: '\u4e0d\u6536\u96c6', tone: '#98A2B3' },
  optional: { label: '\u53ef\u9009', tone: '#1677FF' },
  required: { label: '\u5fc5\u586b', tone: '#D92D20' },
};

const SCOPE_GROUP_STYLE_META: Record<CovariateScopeGroup, { accent: string; tint: string }> = {
  general: { accent: '#1677FF', tint: '#EFF6FF' },
  neonate: { accent: '#F08C2E', tint: '#FFF7ED' },
  pediatric: { accent: '#14B8A6', tint: '#F0FDFA' },
  adult: { accent: '#7C3AED', tint: '#F5F3FF' },
};

const getScopeSummaryTagStyle = (scope: CovariateScopeGroup, state: ScopeState): React.CSSProperties => ({
  marginInlineEnd: 0,
  borderRadius: 999,
  paddingInline: 8,
  border: `1px solid ${SCOPE_GROUP_STYLE_META[scope].accent}22`,
  background: state === 'required' ? `${SCOPE_GROUP_STYLE_META[scope].accent}18` : `${SCOPE_GROUP_STYLE_META[scope].accent}10`,
  color: SCOPE_GROUP_STYLE_META[scope].accent,
  fontSize: 11,
  lineHeight: '18px',
  height: 20,
});

const getEnabledScopeEntries = (row: any): Array<{ scope: CovariateScopeGroup; state: ScopeState }> => (
  SCOPE_GROUP_VALUES
    .map((scope) => ({ scope, state: (row.scope_settings?.[scope]?.state || 'off') as ScopeState }))
    .filter((item) => item.state !== 'off')
);

const getScopeHint = (row: any, scope: CovariateScopeGroup): string => {
  const cell = row.scope_settings?.[scope] || { state: 'off', disabled: false };
  if (scope === 'general') {
    return cell.disabled ? '\u5148\u5173\u95ed\u7279\u5b9a\u4eba\u7fa4\u914d\u7f6e' : '\u542f\u7528\u540e\u9501\u5b9a\u5176\u4ed6\u4eba\u7fa4';
  }
  return cell.disabled ? '\u5173\u95ed\u901a\u7528\u540e\u53ef\u8bbe\u7f6e' : '\u4ec5\u4f5c\u7528\u4e8e\u8be5\u4eba\u7fa4';
};

const renderScopeSummary = (row: any) => {
  const enabledScopes = getEnabledScopeEntries(row);
  if (!enabledScopes.length) {
    return <Tag style={{ borderRadius: 999, marginInlineEnd: 0, fontSize: 11, color: '#98A2B3' }}>{'\u672a\u91c7\u96c6'}</Tag>;
  }
  return (
    <Space size={[4, 4]} wrap>
      {enabledScopes.map(({ scope, state }) => (
        <Tag key={`${row.covariate_key}-${scope}`} style={getScopeSummaryTagStyle(scope, state)}>
          {getCovariateScopeLabel(scope)} {'\u00b7'} {SCOPE_STATE_META[state].label}
        </Tag>
      ))}
    </Space>
  );
};

const DrugCovariateEditor = React.forwardRef(function DrugCovariateEditor(
  { drugId }: DrugCovariateEditorProps,
  ref: React.ForwardedRef<NestedEditorSaveHandle>,
) {
  const [rows, setRows] = useState<DrugCovariateScopeMatrixRow[]>([]);
  const [availableCandidates, setAvailableCandidates] = useState<DrugCovariateScopeCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [removingKey, setRemovingKey] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [configuredOnly, setConfiguredOnly] = useState(false);
  const [selectedCovariateKey, setSelectedCovariateKey] = useState<string | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [pendingAddKeys, setPendingAddKeys] = useState<string[]>([]);
  const [addKeyword, setAddKeyword] = useState('');

  const applyMatrixResponse = useCallback((response?: DrugCovariateScopeMatrixResponse, options?: { markClean?: boolean }) => {
    const nextRows = sortMatrixRows(response?.rows || []) as DrugCovariateScopeMatrixRow[];
    setRows(nextRows);
    setAvailableCandidates(response?.available_candidates || []);
    if (options?.markClean !== false) setDirty(false);
    setSelectedCovariateKey((prev) => prev && nextRows.some((row) => row.covariate_key === prev) ? prev : (nextRows[0]?.covariate_key || null));
  }, []);

  const loadMatrix = useCallback(async () => {
    if (!drugId) {
      setRows([]);
      setAvailableCandidates([]);
      setDirty(false);
      return;
    }
    setLoading(true);
    try {
      applyMatrixResponse(await covariateAPI.scopeMatrix(drugId));
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '\u52a0\u8f7d\u534f\u53d8\u91cf\u4f5c\u7528\u8303\u56f4\u77e9\u9635\u5931\u8d25');
    } finally {
      setLoading(false);
    }
  }, [applyMatrixResponse, drugId]);

  useEffect(() => { void loadMatrix(); }, [loadMatrix]);

  const persistRows = useCallback(async (options?: { silent?: boolean }) => {
    if (!drugId) return;
    const payload = rows.map((row) => ({
      covariate_key: row.covariate_key,
      sort_order: row.sort_order,
      scope_settings: Object.fromEntries(SCOPE_GROUP_VALUES.map((scope) => [scope, { state: row.scope_settings?.[scope]?.state || 'off' }])),
    }));
    applyMatrixResponse(await covariateAPI.updateScopeMatrix(drugId, payload));
    if (!options?.silent) message.success('\u77e9\u9635\u914d\u7f6e\u5df2\u4fdd\u5b58');
  }, [applyMatrixResponse, drugId, rows]);

  const flushDraftIfNeeded = useCallback(async () => {
    if (dirty) await persistRows({ silent: true });
  }, [dirty, persistRows]);

  useImperativeHandle(ref, () => ({
    save: async (options) => {
      setSaving(true);
      try {
        await persistRows(options);
      } finally {
        setSaving(false);
      }
    },
    hasUnsavedChanges: () => dirty,
  }), [dirty, persistRows]);

  const updateCell = useCallback((covariateKey: string, scope: CovariateScopeGroup, nextState: ScopeState) => {
    setRows((prev) => prev.map((row) => row.covariate_key !== covariateKey ? row : normalizeMatrixRow({
      ...row,
      scope_settings: { ...row.scope_settings, [scope]: { ...row.scope_settings?.[scope], state: nextState } },
    })) as DrugCovariateScopeMatrixRow[]);
    setDirty(true);
  }, []);

  const reorderDisabled = configuredOnly || Boolean(keyword.trim());

  const moveRow = useCallback((covariateKey: string, direction: -1 | 1) => {
    if (reorderDisabled) {
      message.info('\u8bf7\u5148\u6e05\u7a7a\u7b5b\u9009\u6761\u4ef6\u540e\u518d\u8c03\u6574\u987a\u5e8f');
      return;
    }
    setRows((prev) => {
      const currentIndex = prev.findIndex((row) => row.covariate_key === covariateKey);
      const nextIndex = currentIndex + direction;
      if (currentIndex < 0 || nextIndex < 0 || nextIndex >= prev.length) return prev;
      const nextRows = [...prev];
      const [target] = nextRows.splice(currentIndex, 1);
      nextRows.splice(nextIndex, 0, target);
      return resequenceMatrixRows(nextRows) as DrugCovariateScopeMatrixRow[];
    });
    setSelectedCovariateKey(covariateKey);
    setDirty(true);
  }, [reorderDisabled]);

  const handleSync = async () => {
    if (!drugId) return;
    setSyncing(true);
    try {
      await flushDraftIfNeeded();
      applyMatrixResponse(await covariateAPI.syncDrugBindings(drugId));
      message.success('\u6765\u6e90\u5df2\u540c\u6b65');
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '\u540c\u6b65\u6765\u6e90\u5931\u8d25');
    } finally {
      setSyncing(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await persistRows();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || '\u4fdd\u5b58\u5931\u8d25');
    } finally {
      setSaving(false);
    }
  };

  const handleAddCandidates = useCallback(async () => {
    if (!drugId || pendingAddKeys.length === 0) return;
    setAdding(true);
    try {
      await flushDraftIfNeeded();
      applyMatrixResponse(await covariateAPI.addScopeMatrixItems(drugId, pendingAddKeys));
      setPendingAddKeys([]);
      setAddModalOpen(false);
      message.success(`\u5df2\u6dfb\u52a0 ${pendingAddKeys.length} \u9879\u534f\u53d8\u91cf`);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '\u6dfb\u52a0\u534f\u53d8\u91cf\u5931\u8d25');
    } finally {
      setAdding(false);
    }
  }, [applyMatrixResponse, drugId, flushDraftIfNeeded, pendingAddKeys]);

  const handleRemoveCovariate = useCallback(async (covariateKey: string) => {
    if (!drugId) return;
    setRemovingKey(covariateKey);
    try {
      await flushDraftIfNeeded();
      applyMatrixResponse(await covariateAPI.removeScopeMatrixItem(drugId, covariateKey));
      message.success('\u534f\u53d8\u91cf\u5df2\u79fb\u9664');
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '\u79fb\u9664\u534f\u53d8\u91cf\u5931\u8d25');
    } finally {
      setRemovingKey(null);
    }
  }, [applyMatrixResponse, drugId, flushDraftIfNeeded]);

  const summary = useMemo(() => {
    const init = Object.fromEntries((COVARIATE_SCOPE_OPTIONS.map((item) => item.value) as CovariateScopeGroup[]).map((scope) => [scope, { required: 0, optional: 0 }])) as Record<CovariateScopeGroup, { required: number; optional: number }>;
    rows.forEach((row) => {
      (COVARIATE_SCOPE_OPTIONS.map((item) => item.value) as CovariateScopeGroup[]).forEach((scope) => {
        const state = row.scope_settings?.[scope]?.state || 'off';
        if (state === 'required') init[scope].required += 1;
        if (state === 'optional') init[scope].optional += 1;
      });
    });
    return init;
  }, [rows]);

  const configuredRowCount = useMemo(() => rows.filter((row) => row.has_any_scope_enabled).length, [rows]);

  const filteredRows = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    return rows.filter((row) => (!configuredOnly || row.has_any_scope_enabled) && (!q || [row.label, row.label_en, row.covariate_key, row.group_name, row.unit].filter(Boolean).some((value) => String(value).toLowerCase().includes(q))));
  }, [configuredOnly, keyword, rows]);

  useEffect(() => {
    setSelectedCovariateKey((prev) => prev && filteredRows.some((row) => row.covariate_key === prev) ? prev : (filteredRows[0]?.covariate_key || null));
  }, [filteredRows]);

  const selectedRow = useMemo(() => filteredRows.find((row) => row.covariate_key === selectedCovariateKey) || filteredRows[0] || null, [filteredRows, selectedCovariateKey]);

  const filteredAddCandidates = useMemo(() => {
    const q = addKeyword.trim().toLowerCase();
    if (!q) return availableCandidates;
    return availableCandidates.filter((candidate) =>
      [candidate.label, candidate.label_en, candidate.key, candidate.group_name, candidate.unit]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [addKeyword, availableCandidates]);

  const selectedAddLabels = useMemo(() => pendingAddKeys.map((key) => {
    const candidate = availableCandidates.find((item) => item.key === key);
    return `${candidate?.label || key}${candidate?.unit ? ` (${candidate.unit})` : ''}`;
  }), [availableCandidates, pendingAddKeys]);

  const togglePendingCandidate = useCallback((candidateKey: string) => {
    setPendingAddKeys((prev) => prev.includes(candidateKey) ? prev.filter((key) => key !== candidateKey) : [...prev, candidateKey]);
  }, []);

  return (
    <>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div className="admin-drug-covariate-editor__topbar">
          <div>
            <Text strong className="admin-drug-covariate-editor__title">{'\u534f\u53d8\u91cf\u77e9\u9635'}</Text>
            <div className="admin-drug-covariate-editor__meta">
              <span>{'\u5df2\u7eb3\u5165'} {rows.length}</span>
              <span>{'\u5df2\u91c7\u96c6'} {configuredRowCount}</span>
              <span>{'\u5f85\u6dfb\u52a0'} {availableCandidates.length}</span>
            </div>
          </div>
          <Space size={8} wrap>
            <Search allowClear placeholder={'\u641c\u7d22\u534f\u53d8\u91cf'} style={{ width: 220 }} value={keyword} onChange={(e) => setKeyword(e.target.value)} />
            <Checkbox checked={configuredOnly} onChange={(event) => setConfiguredOnly(event.target.checked)}>{'\u4ec5\u770b\u5df2\u91c7\u96c6'}</Checkbox>
            <Button icon={<ReloadOutlined />} onClick={handleSync} loading={syncing}>{'\u540c\u6b65\u6765\u6e90'}</Button>
            <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving} disabled={!dirty}>{'\u4fdd\u5b58'}</Button>
          </Space>
        </div>

        <div className="admin-drug-covariate-editor__summary-strip">
          {SCOPE_GROUP_VALUES.map((scope) => (
            <div key={scope} className="admin-drug-covariate-editor__summary-pill" style={{ ['--scope-accent' as any]: SCOPE_GROUP_STYLE_META[scope].accent, ['--scope-bg' as any]: SCOPE_GROUP_STYLE_META[scope].tint }}>
              <span>{getCovariateScopeLabel(scope)}</span>
              <em>{summary[scope].required} / {summary[scope].optional}</em>
            </div>
          ))}
        </div>

        <Row gutter={[12, 12]} align="stretch" className="admin-drug-covariate-editor__layout">
          <Col xs={24} lg={11} xl={10}>
            <Card
              size="small"
              title={'\u534f\u53d8\u91cf\u5217\u8868'}
              extra={<Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setAddModalOpen(true)} disabled={availableCandidates.length === 0}>{'\u6dfb\u52a0\u534f\u53d8\u91cf'}</Button>}
              className="admin-drug-covariate-editor__list-card"
              bodyStyle={{ padding: 8 }}
            >
              <Text type="secondary" className="admin-drug-covariate-editor__caption">
                {reorderDisabled ? '\u7b5b\u9009\u4e2d\uff0c\u6682\u4e0d\u652f\u6301\u6392\u5e8f\u3002' : '\u4f7f\u7528\u4e0a\u4e0b\u952e\u8c03\u6574\u987a\u5e8f\u3002'}
              </Text>
              <div className="admin-drug-covariate-editor__list-scroll">
                {filteredRows.length > 0 ? filteredRows.map((row) => {
                  const isSelected = row.covariate_key === selectedRow?.covariate_key;
                  const rowIndex = rows.findIndex((item) => item.covariate_key === row.covariate_key);
                  const secondaryMeta = [row.label_en, row.covariate_key !== row.label_en ? row.covariate_key : null].filter(Boolean).join(' · ');
                  return (
                    <button type="button" key={row.covariate_key} className={`admin-covariate-item${isSelected ? ' admin-covariate-item--selected' : ''}`} onClick={() => setSelectedCovariateKey(row.covariate_key)}>
                      <span className="admin-covariate-item__index">#{rowIndex + 1}</span>
                      <span className="admin-covariate-item__identity">
                        <span className="admin-covariate-item__label">{row.label || row.covariate_key}</span>
                        <span className="admin-covariate-item__meta">{secondaryMeta || row.covariate_key}</span>
                        {row.unit ? <span className="admin-covariate-item__unit">{row.unit}</span> : null}
                      </span>
                      <span className="admin-covariate-item__side">
                        <span className="admin-covariate-item__summary">{renderScopeSummary(row)}</span>
                        <span className="admin-covariate-item__actions" onClick={(event) => event.stopPropagation()}>
                          <Tooltip title={reorderDisabled ? '\u8bf7\u5148\u6e05\u7a7a\u7b5b\u9009\u6761\u4ef6' : '\u4e0a\u79fb'}><Button type="text" size="small" icon={<UpOutlined />} disabled={reorderDisabled || rowIndex <= 0} onClick={() => moveRow(row.covariate_key, -1)} /></Tooltip>
                          <Tooltip title={reorderDisabled ? '\u8bf7\u5148\u6e05\u7a7a\u7b5b\u9009\u6761\u4ef6' : '\u4e0b\u79fb'}><Button type="text" size="small" icon={<DownOutlined />} disabled={reorderDisabled || rowIndex < 0 || rowIndex >= rows.length - 1} onClick={() => moveRow(row.covariate_key, 1)} /></Tooltip>
                          <Popconfirm title={'\u79fb\u9664\u8be5\u534f\u53d8\u91cf\uff1f'} description={'\u79fb\u9664\u540e\u5c06\u540c\u6b65\u6e05\u7a7a\u8be5\u836f\u7269\u4e0b\u8be5\u534f\u53d8\u91cf\u7684\u5168\u90e8\u8303\u56f4\u914d\u7f6e\u3002'} okText={'\u786e\u8ba4'} cancelText={'\u53d6\u6d88'} onConfirm={() => void handleRemoveCovariate(row.covariate_key)}>
                            <Button type="text" danger size="small" loading={removingKey === row.covariate_key} icon={<DeleteOutlined />} />
                          </Popconfirm>
                        </span>
                      </span>
                    </button>
                  );
                }) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={'\u6ca1\u6709\u7b26\u5408\u7b5b\u9009\u6761\u4ef6\u7684\u534f\u53d8\u91cf'} />}
              </div>
            </Card>
          </Col>

          <Col xs={24} lg={13} xl={14}>
            <Card size="small" title={'\u4f5c\u7528\u8303\u56f4\u8be6\u60c5'} className="admin-drug-covariate-editor__detail-card" bodyStyle={{ padding: 12 }}>
              {selectedRow ? (
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <div className="admin-drug-covariate-editor__detail-header">
                    <div>
                      <Text strong className="admin-drug-covariate-editor__detail-title">{selectedRow.label || selectedRow.covariate_key}</Text>
                      <div className="admin-drug-covariate-editor__detail-tags">
                        <Tag>{selectedRow.covariate_key}</Tag>
                        {selectedRow.unit ? <Tag color="blue">{selectedRow.unit}</Tag> : null}
                        <Tag>{GROUP_LABELS[selectedRow.group_name || 'demographics'] || selectedRow.group_name || '人口统计'}</Tag>
                        <Tag color="default">{'\u4e92\u65a5'}</Tag>
                      </div>
                    </div>
                    <Text type="secondary">{'\u5df2\u542f\u7528'} {getEnabledScopeEntries(selectedRow).length} {'\u4e2a\u8303\u56f4'}</Text>
                  </div>

                  <div className="admin-drug-covariate-editor__scope-grid">
                    {SCOPE_GROUP_VALUES.map((scope) => {
                      const cell = (selectedRow.scope_settings?.[scope] || { state: 'off', disabled: false, source_tags: [] }) as { state: ScopeState; disabled?: boolean; source_tags?: string[] };
                      const sourceTags = Array.isArray(cell.source_tags) ? cell.source_tags : [];
                      return (
                        <Card
                          key={`${selectedRow.covariate_key}-${scope}`}
                          size="small"
                          className={`admin-drug-covariate-editor__scope-card${cell.disabled ? ' admin-drug-covariate-editor__scope-card--locked' : ''}`}
                          title={
                            <div className="admin-drug-covariate-editor__scope-head">
                              <span className="admin-drug-covariate-editor__scope-name" style={{ color: SCOPE_GROUP_STYLE_META[scope].accent }}>{getCovariateScopeLabel(scope)}</span>
                              <span className="admin-drug-covariate-editor__scope-note">{SCOPE_SHORT_HINT[scope]}</span>
                            </div>
                          }
                          headStyle={{ borderBottom: 'none', paddingBottom: 0 }}
                          bodyStyle={{ paddingTop: 10 }}
                        >
                          <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <Radio.Group className="admin-drug-covariate-editor__scope-radio" size="small" optionType="button" buttonStyle="solid" value={cell.state} disabled={cell.disabled || loading || saving || syncing || adding || removingKey === selectedRow.covariate_key} onChange={(event) => updateCell(selectedRow.covariate_key, scope, event.target.value as ScopeState)}>
                              {SCOPE_STATE_OPTIONS.map((option) => <Radio.Button key={`${scope}-${option.value}`} value={option.value}>{option.label}</Radio.Button>)}
                            </Radio.Group>
                            <div className="admin-drug-covariate-editor__scope-foot">
                              <Text type="secondary">{getScopeHint(selectedRow, scope)}</Text>
                              <Tag color="default" style={{ marginInlineEnd: 0 }}>{cell.disabled ? '\u5df2\u9501\u5b9a' : SCOPE_STATE_META[cell.state].label}</Tag>
                            </div>
                            {sourceTags.length > 0 ? <div className="admin-drug-covariate-editor__source-inline"><Text type="secondary">{'\u6765\u6e90'}</Text><Space size={[4, 4]} wrap>{sourceTags.map((tag: string) => <Tag key={`${selectedRow.covariate_key}-${scope}-${tag}`}>{tag}</Tag>)}</Space></div> : null}
                          </Space>
                        </Card>
                      );
                    })}
                  </div>
                </Space>
              ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={'\u8bf7\u5148\u9009\u62e9\u4e00\u4e2a\u534f\u53d8\u91cf'} />}
            </Card>
          </Col>
        </Row>
      </Space>

      <Modal
        title={'\u6dfb\u52a0\u534f\u53d8\u91cf'}
        open={addModalOpen}
        onOk={() => void handleAddCandidates()}
        onCancel={() => { setAddModalOpen(false); setPendingAddKeys([]); }}
        okText={'\u52a0\u5165\u5217\u8868'}
        cancelText={'\u53d6\u6d88'}
        confirmLoading={adding}
        okButtonProps={{ disabled: pendingAddKeys.length === 0 }}
        width={720}
        bodyStyle={{ minHeight: 300 }}
      >
        <Space direction="vertical" size={14} style={{ width: '100%' }}>
          <div className="admin-drug-covariate-editor__add-intro">
            <Text strong>{'\u4ece\u5168\u5c40\u534f\u53d8\u91cf\u5e93\u4e2d\u9009\u62e9\u7eb3\u5165\u9879'}</Text>
            <Text type="secondary">{'\u52a0\u5165\u540e\u9ed8\u8ba4\u5168\u90e8\u4e3a\u201c\u4e0d\u6536\u96c6\u201d\uff0c\u53ef\u5728\u53f3\u4fa7\u7ee7\u7eed\u8bbe\u7f6e\u8303\u56f4\u4e0e\u5f3a\u5ea6\u3002'}</Text>
          </div>
          <Search
            allowClear
            value={addKeyword}
            onChange={(e) => setAddKeyword(e.target.value)}
            placeholder={'\u641c\u7d22\u534f\u53d8\u91cf\u540d\u79f0 / key / \u5206\u7ec4'}
            className="admin-drug-covariate-editor__add-search"
          />
          <div className="admin-drug-covariate-editor__add-panel">
            <div className="admin-drug-covariate-editor__add-list">
              {filteredAddCandidates.length > 0 ? filteredAddCandidates.map((candidate) => {
                const selected = pendingAddKeys.includes(candidate.key);
                return (
                  <button
                    key={candidate.key}
                    type="button"
                    className={`admin-drug-covariate-editor__add-item${selected ? ' admin-drug-covariate-editor__add-item--selected' : ''}`}
                    onClick={() => togglePendingCandidate(candidate.key)}
                  >
                    <div className="admin-drug-covariate-editor__candidate-option">
                      <div className="admin-drug-covariate-editor__candidate-main">
                        <span className="admin-drug-covariate-editor__candidate-label">{candidate.label || candidate.key}</span>
                        <span className="admin-drug-covariate-editor__candidate-meta">
                          {[candidate.label_en, candidate.key, (candidate.group_name ? (GROUP_LABELS[candidate.group_name] || candidate.group_name) : null)].filter(Boolean).join(' · ')}
                        </span>
                      </div>
                      <Space size={8}>
                        {candidate.unit ? <span className="admin-drug-covariate-editor__candidate-unit">{candidate.unit}</span> : null}
                        <Tag color={selected ? 'blue' : 'default'} style={{ marginInlineEnd: 0 }}>
                          {selected ? '\u5df2\u9009\u4e2d' : '\u70b9\u51fb\u6dfb\u52a0'}
                        </Tag>
                      </Space>
                    </div>
                  </button>
                );
              }) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={'\u6ca1\u6709\u53ef\u6dfb\u52a0\u7684\u534f\u53d8\u91cf'} style={{ margin: '32px 0' }} />}
            </div>
            <div className="admin-drug-covariate-editor__add-count">
              <Text type="secondary">{'\u5019\u9009'} {filteredAddCandidates.length} {'\u9879'} · {'\u5df2\u9009'} {pendingAddKeys.length} {'\u9879'}</Text>
            </div>
          </div>
          <div className="admin-drug-covariate-editor__add-footer">
            <Text type="secondary">{'\u5f53\u524d\u53ef\u6dfb\u52a0'} {availableCandidates.length} {'\u9879'}</Text>
            {selectedAddLabels.length > 0 ? <div className="admin-drug-covariate-editor__add-selected">{selectedAddLabels.map((label) => <Tag key={label}>{label}</Tag>)}</div> : null}
          </div>
        </Space>
      </Modal>
    </>
  );
});
DrugCovariateEditor.displayName = 'DrugCovariateEditor';


// ================================================================
// Models Tab (Full Management with inline script editing)
// ================================================================
// ================================================================
// Shared label maps for Chinese localization
// ================================================================
const GROUP_LABELS: Record<string, string> = {
  demographics: '人口统计',
  labs: '实验室指标',
  conditions: '临床状态',
  genetics: '遗传基因',
};
const TRANSFORM_LABELS: Record<string, string> = {
  one_hot: '独热映射',
  value_map: '值映射',
  formula: '公式转换',
};
const POPULATION_LABELS: Record<string, string> = {
  neonate: '新生儿',
  pediatric: '儿童',
  adult: '成人',
};

interface ModelsTabProps {
  onOpenClinicalValidation?: (modelId: string) => void;
}

const ModelsTab: React.FC<ModelsTabProps> = ({ onOpenClinicalValidation }) => {
  const navigate = useNavigate();
  const [models, setModels] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filterDrug, setFilterDrug] = useState<string | undefined>();
  const [filterStatus, setFilterStatus] = useState<string | undefined>();
  const [filterValidation, setFilterValidation] = useState<string | undefined>();
  const [modelPage, setModelPage] = useState(1);

  // Detail editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingModelId, setEditingModelId] = useState<string | null>(null);
  const [editorData, setEditorData] = useState<any>(null);
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorSaving, setEditorSaving] = useState(false);
  const [editorJsonText, setEditorJsonText] = useState('');
  const [editorJsonError, setEditorJsonError] = useState('');
  const [editorForm] = Form.useForm();
  const modelCovariateEditorRef = useRef<NestedEditorSaveHandle | null>(null);

  // Population tags (for Select options, loaded from backend)
  const [allPopulationTags, setAllPopulationTags] = useState<string[]>([]);

  const [scriptsData, setScriptsData] = useState<any>(null);

  // Validation management state
  const [validationDrawerOpen, setValidationDrawerOpen] = useState(false);
  const [validationModelId, setValidationModelId] = useState<string | null>(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [validationSaving, setValidationSaving] = useState(false);
  const [validationForm] = Form.useForm();
  // Image URLs for validation editor
  const [dvPredImages, setDvPredImages] = useState<string[]>([]);
  const [dvIpredImages, setDvIpredImages] = useState<string[]>([]);
  const [cwresImages, setCwresImages] = useState<string[]>([]);
  const [vpcImages, setVpcImages] = useState<string[]>([]);

  // Model Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editWizardModelId, setEditWizardModelId] = useState<string | undefined>();
  const [scriptUpgradeModel, setScriptUpgradeModel] = useState<AdminModelMeta>();
  const [statusChangingModelId, setStatusChangingModelId] = useState<string | null>(null);
  const [reviewingModelId, setReviewingModelId] = useState<string | null>(null);
  const [candidateRefreshToken, setCandidateRefreshToken] = useState(0);
  const [activeModelTab, setActiveModelTab] = useState('models');
  const [exportingModelId, setExportingModelId] = useState<string | null>(null);
  const [newModelChoiceOpen, setNewModelChoiceOpen] = useState(false);
  const [modelPackageFile, setModelPackageFile] = useState<File | null>(null);
  const [modelPackageUploading, setModelPackageUploading] = useState(false);
  const MODEL_PAGE_SIZE = 8;

  const fetchModels = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.listModelsMeta(filterDrug);
      setModels(res.models || []);
      setScriptsData({
        summary: res.script_summary || null,
        orphan_scripts: res.orphan_scripts || { cpp: [], r: [] },
      });
    }
    catch { message.error('加载模型失败'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchModels(); }, [filterDrug]);

  const applyModelStatusChange = async (
    modelId: string,
    nextStatus: string,
    currentStatus: string,
  ) => {
    if (nextStatus === currentStatus) return;
    if (nextStatus !== 'active') {
      await adminAPI.toggleModelStatus(modelId, 'inactive');
      return;
    }
    const preflight = await adminAPI.preflightModelActivation(modelId);
    if (!preflight.decision.allowed) {
      throw new Error(preflight.decision.message);
    }
    await adminAPI.activateModel(modelId, {
      ...(preflight.qualification_run_id
        ? { qualification_run_id: preflight.qualification_run_id }
        : {}),
    });
  };

  const handleExportModelPackage = async (modelId: string) => {
    setExportingModelId(modelId);
    try {
      const response = await adminAPI.exportModelPackage(modelId);
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: 'application/zip' });
      const disposition = String(response.headers?.['content-disposition'] || '');
      const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1]
        || `${modelId}-model-package.zip`;
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.click();
      window.URL.revokeObjectURL(url);
      message.success('模型包导出成功');
    } catch (error) {
      message.error(formatErrorMessage(error, '模型包导出失败'));
    } finally {
      setExportingModelId(null);
    }
  };

  const openNewModel = () => {
    setModelPackageFile(null);
    setNewModelChoiceOpen(true);
  };

  const createModelFromPackage = async () => {
    if (!modelPackageFile) {
      message.warning('请先选择模型包 ZIP');
      return;
    }
    setModelPackageUploading(true);
    try {
      const response = await adminAPI.uploadModelCandidatePackage(modelPackageFile);
      const manifestModelId = typeof response.manifest?.model_id === 'string'
        ? response.manifest.model_id
        : '';
      if (!manifestModelId) {
        message.error('模型包已上传，但未能识别 model_id');
        return;
      }
      let modelId = response.model_id || manifestModelId;
      if (!response.model_id) {
        const linked = await adminAPI.createModelDraftFromCandidatePackage(response.id);
        modelId = linked.model_id || modelId;
      }
      message.success('模型包已导入，登记资料已预填，请继续补齐并提交');
      setNewModelChoiceOpen(false);
      setEditWizardModelId(modelId);
      setWizardOpen(true);
      await fetchModels();
      setCandidateRefreshToken((value) => value + 1);
    } catch (error) {
      message.error(formatErrorMessage(error, '导入模型包失败'));
    } finally {
      setModelPackageUploading(false);
    }
  };

  const handleModelStatusChange = async (model: any, nextStatus: 'active' | 'inactive') => {
    const currentStatus = model.model_status || 'inactive';
    if (currentStatus === nextStatus) return;
    setStatusChangingModelId(model.model_id);
    try {
      await applyModelStatusChange(model.model_id, nextStatus, currentStatus);
      message.success(nextStatus === 'active'
        ? `模型 ${model.model_id} 已批准全局测试`
        : `模型 ${model.model_id} 已退出全局测试`);
      await fetchModels();
    } catch (error: any) {
      message.error(formatErrorMessage(
        error,
        nextStatus === 'active'
          ? '模型未满足全局测试条件，请先完成技术资格、版本审核与发布'
          : '退出全局测试失败',
      ));
    } finally {
      setStatusChangingModelId(null);
    }
  };

  const handleApproveRegisteredModel = async (model: any) => {
    setReviewingModelId(model.model_id);
    try {
      await adminAPI.reviewModel(model.model_id, {
        review_status: 'approved',
        review_note: model.qualification_status === 'legacy_accepted'
          ? '管理员在模型管理页审核通过存量模型'
          : '管理员在模型管理页审核通过已完成资格验证的模型',
        qualification_run_id: model.workflow?.qualification_run?.id,
      });
      message.success(`模型 ${model.model_id} 已审核通过，可继续激活`);
      await fetchModels();
    } catch (error: any) {
      message.error(formatErrorMessage(error, '模型审核失败'));
    } finally {
      setReviewingModelId(null);
    }
  };

  const fetchPopulationTags = async () => {
    try {
      const res = await adminAPI.getPopulationTags();
      setAllPopulationTags(res.tags || []);
    } catch { /* ignore */ }
  };

  const handleEdit = async (modelId: string) => {
    setEditingModelId(modelId); setEditorOpen(true); setEditorLoading(true);
    fetchPopulationTags();
    try {
      const data = await adminAPI.getModelDetail(modelId);
      setEditorData(data);
      setEditorJsonText(JSON.stringify(data, null, 2));
      populateEditorForm(data);
    }
    catch { message.error('加载模型详情失败'); }
    finally { setEditorLoading(false); }
  };

  const handleSaveJson = async () => {
    if (!editingModelId) return;
    try {
      const parsed = JSON.parse(editorJsonText);
      const nextStatus = parsed?.model_metadata?.model_status;
      const currentStatus = editorData?.model_metadata?.model_status;
      const hasPendingBindingChanges = Boolean(modelCovariateEditorRef.current?.hasUnsavedChanges());
      setEditorJsonError('');
      setEditorSaving(true);
      if (hasPendingBindingChanges) {
        await modelCovariateEditorRef.current?.save({ silent: true });
      }
      await adminAPI.updateModelDetail(editingModelId, parsed);
      if (nextStatus && nextStatus !== currentStatus) {
        await applyModelStatusChange(editingModelId, nextStatus, currentStatus);
      }
      message.success(hasPendingBindingChanges ? '模型与协变量绑定已保存' : '模型元数据已保存');
      setEditorOpen(false);
      fetchModels();
    } catch (err: any) {
      if (err instanceof SyntaxError) setEditorJsonError('JSON格式错误: ' + err.message);
      else message.error(formatErrorMessage(err, '保存失败'));
    } finally { setEditorSaving(false); }
  };

  const handleSaveForm = async () => {
    if (!editingModelId || !editorData) return;
    setEditorSaving(true);
    try {
      await editorForm.validateFields();
      const values = editorForm.getFieldsValue(true);
      const currentStatus = editorData?.model_metadata?.model_status || 'inactive';
      const nextStatus = values.model_status || 'inactive';
      const hasPendingBindingChanges = Boolean(modelCovariateEditorRef.current?.hasUnsavedChanges());
      if (hasPendingBindingChanges) {
        await modelCovariateEditorRef.current?.save({ silent: true });
      }
      const activeTags: string[] = values.population_tags || [];
      const payload = {
        model_metadata: {
          model_id: values.model_id,
          first_author: values.first_author || '',
          publication_year: values.publication_year ?? null,
          pmid: values.pmid || '',
          doi: values.doi || '',
          journal: values.journal || '',
          model_status: values.model_status || 'inactive',
          validation_status: values.validation_status || 'pending',
        },
        drug_info: {
          name: values.drug_name || '',
          population: values.population || '',
          indication: values.indication || '',
          population_tags: activeTags,
        },
        reliability_metrics: {
          sample_size: { total: values.sample_total ?? null },
          external_validation: { performed: values.external_validation_performed ?? false },
        },
      };
      await adminAPI.updateModelDetail(editingModelId, payload);
      if (nextStatus !== currentStatus) {
        await applyModelStatusChange(editingModelId, nextStatus, currentStatus);
      }
      message.success(hasPendingBindingChanges ? '模型与协变量绑定已保存' : '模型元数据已保存');
      setEditorOpen(false);
      fetchModels();
    } catch (err: any) {
      message.error(formatErrorMessage(err, '保存失败'));
    } finally { setEditorSaving(false); }
  };

  const populateEditorForm = (data: any) => {
    const meta = data?.model_metadata || {};
    const drug = data?.drug_info || {};
    const bc = data?.boundary_conditions || {};
    const rel = data?.reliability_metrics || {};
    editorForm.setFieldsValue({
      model_id: meta.model_id || '',
      first_author: meta.first_author || '',
      publication_year: meta.publication_year ?? null,
      pmid: meta.pmid || '',
      doi: meta.doi || '',
      journal: meta.journal || '',
      model_status: meta.model_status || 'inactive',
      validation_status: meta.validation_status || 'pending',
      drug_name: drug.name || '',
      population: drug.population || '',
      indication: drug.indication || '',
      population_tags: drug.population_tags || [],
      age_min: bc.age?.min ?? null,
      age_max: bc.age?.max ?? null,
      age_unit: bc.age?.unit || '岁',
      weight_min: bc.weight?.min ?? null,
      weight_max: bc.weight?.max ?? null,
      weight_unit: bc.weight?.unit || 'kg',
      scr_min: bc.renal_function?.min ?? null,
      scr_max: bc.renal_function?.max ?? null,
      scr_unit: bc.renal_function?.unit || 'μmol/L',
      crcl_min: bc.crcl?.min ?? null,
      crcl_max: bc.crcl?.max ?? null,
      is_tumor: bc.is_tumor ?? false,
      is_dialysis: bc.is_dialysis ?? false,
      is_ventilation: bc.is_ventilation ?? false,
      exclusion_criteria: Array.isArray(bc.hard_exclusion_criteria)
        ? bc.hard_exclusion_criteria.join('\n')
        : '',
      sample_total: rel.sample_size?.total ?? null,
      external_validation_performed: rel.external_validation?.performed ?? false,
    });
  };

  const handleDelete = async (modelId: string) => {
    try { await adminAPI.deleteModel(modelId); message.success('模型已删除'); fetchModels(); }
    catch (err: any) { message.error(err?.response?.data?.detail || '删除失败'); }
  };

  const handleEditValidation = async (modelId: string) => {
    setValidationModelId(modelId);
    setValidationDrawerOpen(true);
    setValidationLoading(true);
    try {
      const data = await adminAPI.getModelDetail(modelId);
      const vr = data?.reliability_metrics?.validation_report || {};
      const pe = vr.parameter_evaluation || {};
      // Load image URLs
      setDvPredImages(vr.gof?.dv_vs_pred_images || []);
      setDvIpredImages(vr.gof?.dv_vs_ipred_images || []);
      setCwresImages(vr.gof?.cwres_images || []);
      setVpcImages(vr.predictive_ability?.vpc_images || []);
      validationForm.setFieldsValue({
        gof_dv_vs_pred: vr.gof?.dv_vs_pred_description || '',
        gof_dv_vs_ipred: vr.gof?.dv_vs_ipred_description || '',
        gof_cwres: vr.gof?.cwres_description || '',
        gof_conclusion: vr.gof?.conclusion || '',
        bootstrap_n: vr.stability?.bootstrap_n ?? null,
        bootstrap_success_rate: vr.stability?.bootstrap_success_rate ?? null,
        parameter_bias_percent: vr.stability?.parameter_bias_percent ?? null,
        stability_conclusion: vr.stability?.conclusion || '',
        vpc_description: vr.predictive_ability?.vpc_description || '',
        npde_mean: vr.predictive_ability?.npde_mean ?? null,
        npde_variance: vr.predictive_ability?.npde_variance ?? null,
        npde_normality_p: vr.predictive_ability?.npde_normality_p ?? null,
        pred_conclusion: vr.predictive_ability?.conclusion || '',
        mpe: vr.error_metrics?.mpe ?? null,
        mpe_percent: vr.error_metrics?.mpe_percent ?? null,
        mae: vr.error_metrics?.mae ?? null,
        mape: vr.error_metrics?.mape ?? null,
        rmse: vr.error_metrics?.rmse ?? null,
        error_conclusion: vr.error_metrics?.conclusion || '',
        parameters: pe.parameters?.length > 0 ? pe.parameters : [],
        overall_rse_acceptable: pe.overall_rse_acceptable ?? null,
        overall_shrinkage_acceptable: pe.overall_shrinkage_acceptable ?? null,
        param_conclusion: pe.conclusion || '',
        overall_conclusion: vr.overall_conclusion || '',
        validation_date: vr.validation_date ? dayjs(vr.validation_date) : null,
        validated_by: vr.validated_by || '',
        validation_status: data?.model_metadata?.validation_status || 'pending',
        validation_center_summary: vr.validation_center || null,
      });
    } catch { message.error('加载模型验证数据失败'); }
    finally { setValidationLoading(false); }
  };

  const handleSaveValidation = async () => {
    if (!validationModelId) return;
    setValidationSaving(true);
    try {
      const v = validationForm.getFieldsValue(true);
      const payload = {
        reliability_metrics: {
          validation_report: {
            gof: {
              dv_vs_pred_description: v.gof_dv_vs_pred || '',
              dv_vs_pred_images: dvPredImages,
              dv_vs_ipred_description: v.gof_dv_vs_ipred || '',
              dv_vs_ipred_images: dvIpredImages,
              cwres_description: v.gof_cwres || '',
              cwres_images: cwresImages,
              conclusion: v.gof_conclusion || '',
            },
            stability: {
              bootstrap_n: v.bootstrap_n ?? null,
              bootstrap_success_rate: v.bootstrap_success_rate ?? null,
              parameter_bias_percent: v.parameter_bias_percent ?? null,
              conclusion: v.stability_conclusion || '',
            },
            predictive_ability: {
              vpc_description: v.vpc_description || '',
              vpc_images: vpcImages,
              npde_mean: v.npde_mean ?? null,
              npde_variance: v.npde_variance ?? null,
              npde_normality_p: v.npde_normality_p ?? null,
              conclusion: v.pred_conclusion || '',
            },
            error_metrics: {
              mpe: v.mpe ?? null,
              mpe_percent: v.mpe_percent ?? null,
              mae: v.mae ?? null,
              mape: v.mape ?? null,
              rmse: v.rmse ?? null,
              conclusion: v.error_conclusion || '',
            },
            parameter_evaluation: {
              parameters: (v.parameters || []).filter((p: any) => p?.name),
              overall_rse_acceptable: v.overall_rse_acceptable ?? null,
              overall_shrinkage_acceptable: v.overall_shrinkage_acceptable ?? null,
              conclusion: v.param_conclusion || '',
            },
            overall_conclusion: v.overall_conclusion || '',
            validation_date: v.validation_date ? v.validation_date.format('YYYY-MM-DD') : null,
            validated_by: v.validated_by || '',
          },
        },
        model_metadata: {
          validation_status: v.validation_status || 'pending',
        },
      };
      await adminAPI.updateModelDetail(validationModelId, payload);
      message.success('验证数据已保存');
      setValidationDrawerOpen(false);
      fetchModels();
    } catch (err: any) {
      message.error(formatErrorMessage(err, '保存失败'));
    } finally { setValidationSaving(false); }
  };

  const formatRange = (range: any) => {
    if (!range) return '-';
    const min = range.min ?? range.minimum; const max = range.max ?? range.maximum; const unit = range.unit || '';
    if (min != null && max != null) return `${min} - ${max} ${unit}`.trim();
    if (min != null) return `≥ ${min} ${unit}`.trim();
    if (max != null) return `≤ ${max} ${unit}`.trim();
    return '-';
  };

  const renderPopulationTags = (tags?: string[]) => {
    if (!tags?.length) return <Text type="secondary">-</Text>;
    return (
      <Space wrap size={[4, 4]}>
        {tags.map((tag) => (
          <Tag key={tag} className="clinical-entity-tag" color={getPopulationTagColor(tag)} style={{ marginInlineEnd: 0 }}>
            {tag}
          </Tag>
        ))}
      </Space>
    );
  };

  const renderRCovariates = (covariates?: string[]) => {
    const items = (covariates || []).filter(Boolean);
    if (!items.length) return <Text type="secondary">-</Text>;

    const preview = items.slice(0, 2);
    const remaining = items.length - preview.length;
    const content = (
      <Space wrap size={[4, 4]}>
        {preview.map((item) => (
          <Tag key={item} className="clinical-entity-tag" style={getCovariateTagStyle(item)}>
            {item}
          </Tag>
        ))}
        {remaining > 0 && <Tag style={{ marginInlineEnd: 0 }}>+{remaining}</Tag>}
      </Space>
    );

    return remaining > 0 ? <Tooltip title={items.join(', ')}>{content}</Tooltip> : content;
  };

  const candidatePackageState = (record: any) => {
    const status = String(record.candidate_package_status || '');
    const packageVersion = record.candidate_package_version
      ? ` · v${record.candidate_package_version}`
      : '';
    const states: Record<string, { label: string; tone: 'success' | 'warning' | 'danger' | 'info' }> = {
      not_started: { label: '待资格验证', tone: 'warning' },
      qualification_running: { label: '资格验证中', tone: 'info' },
      platform_qualified: { label: '技术资格通过', tone: 'success' },
      clinical_validated: { label: '临床验证通过', tone: 'success' },
      script_failed: { label: '资格验证失败', tone: 'danger' },
      package_invalid: { label: '模型包无效', tone: 'danger' },
      source_rejected: { label: '来源未通过', tone: 'danger' },
    };
    if (status && states[status]) {
      return { ...states[status], hint: `当前模型包${packageVersion || ''}`.trim() };
    }
    return record.draft_mode
      ? { label: '草稿待上传', tone: 'neutral' as const, hint: '发布可执行版本前需关联模型包 ZIP' }
      : { label: '未附模型包', tone: 'warning' as const, hint: '可在“更多操作”中补充模型包并完成技术资格检查' };
  };

  const renderModelLifecycle = (record: any) => {
    if (record.workflow) {
      const stageLabels: Record<string, string> = {
        registration: '待完善资料',
        package: '待模型包',
        qualification: '待技术资格',
        review: '待版本审核',
        publication: '待发布入库',
        activation: '待全局启用',
        complete: '当前运行',
      };
      const tone: CompactStatusTone = record.workflow.stage === 'complete'
        ? 'success'
        : record.workflow.stage === 'qualification' ? 'warning' : 'info';
      return (
        <div className="model-lifecycle" aria-label="模型生命周期状态">
          <CompactStatusGlyph
            label="当前阶段"
            value={stageLabels[record.workflow.stage] || record.workflow.stage}
            tone={tone}
            hint={record.workflow.blockers?.[0]?.message}
            icon={<CloudUploadOutlined />}
          />
        </div>
      );
    }
    const candidate = candidatePackageState(record);
    const qualificationStates: Record<string, { label: string; tone: 'success' | 'warning' | 'danger' | 'info' | 'neutral' }> = {
      legacy_accepted: { label: '存量认可', tone: 'info' },
      platform_qualified: { label: '平台通过', tone: 'success' },
      clinical_validated: { label: '临床通过', tone: 'success' },
      not_started: { label: '未开始', tone: 'neutral' },
      qualification_running: { label: '运行中', tone: 'info' },
      script_failed: { label: '失败', tone: 'danger' },
    };
    const qualification = qualificationStates[record.qualification_status]
      || { label: record.qualification_status || '未开始', tone: 'neutral' as const };
    const reviewStates: Record<string, { label: string; tone: 'success' | 'warning' | 'danger' }> = {
      approved: { label: '已通过', tone: 'success' },
      pending: { label: '待审核', tone: 'warning' },
      rejected: { label: '已驳回', tone: 'danger' },
    };
    const review = reviewStates[record.review_status] || { label: '待审核', tone: 'warning' as const };
    const qualificationSummary = record.candidate_package_id
      ? (['qualification_running', 'script_failed', 'package_invalid', 'source_rejected'].includes(record.candidate_package_status)
        ? candidate
        : qualification)
      : record.qualification_status === 'legacy_accepted'
        ? { label: '存量认可', tone: 'info' as const, hint: '当前模型未附模型包，继续按存量认可规则运行' }
        : candidate;
    const releaseSummary = record.review_status === 'rejected'
      ? { label: '已驳回', tone: 'danger' as const }
      : record.published_qualification
        ? { label: '已发布', tone: 'success' as const }
        : record.review_status === 'pending'
          ? { label: '待审核', tone: 'warning' as const }
          : record.qualification_status === 'legacy_accepted'
            ? { label: '存量模型', tone: 'info' as const }
            : review.label === '已通过'
              ? { label: '待发布', tone: 'neutral' as const }
              : { label: '未发布', tone: 'neutral' as const };
    const qualificationHint = (qualificationSummary as { hint?: string }).hint;

    return (
      <div className="model-lifecycle" aria-label="模型生命周期状态">
        <CompactStatusGlyph label="建模" value={record.draft_mode ? '草稿' : '已提交'} tone={record.draft_mode ? 'warning' : 'info'} icon={<EditOutlined />} />
        <CompactStatusGlyph label="资格" value={qualificationSummary.label} tone={qualificationSummary.tone as CompactStatusTone} hint={qualificationHint} icon={<ExperimentOutlined />} />
        <CompactStatusGlyph label="发布" value={releaseSummary.label} tone={releaseSummary.tone as CompactStatusTone} icon={<CloudUploadOutlined />} />
      </div>
    );
  };

  const renderModelOperations = (record: any) => {
    const validationStates: Record<string, { label: string; tone: 'success' | 'warning' | 'neutral' }> = {
      validated: { label: '已验证', tone: 'success' },
      external_validated: { label: '外部验证', tone: 'success' },
      pending: { label: '未开展', tone: 'warning' },
      not_validated: { label: '未形成证据', tone: 'neutral' },
    };
    const scriptStates: Record<string, { label: string; tone: 'success' | 'warning' | 'danger' }> = {
      complete: { label: '脚本完整', tone: 'success' },
      partial: { label: '脚本待补', tone: 'warning' },
      missing: { label: '脚本缺失', tone: 'danger' },
    };
    const validation = validationStates[record.validation_status]
      || { label: record.validation_status || '未验证', tone: 'neutral' as const };
    const script = scriptStates[record.script_registration_status]
      || { label: record.script_registration_status || '脚本未知', tone: 'neutral' as const };
    const tenantCount = record.assigned_tenant_count || 0;
    const userCount = record.assigned_user_count || 0;
    const availability = record.publication_state === 'unpublished'
      ? { label: '已下架', tone: 'danger' as const, hint: '模型已强制下架，定向分配也不能绕过' }
      : record.model_status !== 'active'
      ? { label: '已停用', tone: 'neutral' as const, hint: '当前模型未参与全局匹配' }
      : record.assignment_status === 'global'
        ? { label: '全局开放', tone: 'success' as const, hint: '所有允许使用该药物的用户均可匹配' }
        : record.assignment_status === 'scoped' || tenantCount > 0 || userCount > 0
          ? { label: '定向开放', tone: 'info' as const, hint: `${tenantCount} 个机构 · ${userCount} 个用户` }
          : { label: '未开放', tone: 'warning' as const, hint: '模型已激活，但尚未配置开放范围' };

    return (
      <div className="model-operations" aria-label="模型运行状态">
        <CompactStatusGlyph label="可用" value={availability.label} tone={availability.tone} hint={availability.hint} icon={<CheckOutlined />} />
        <CompactStatusGlyph
          label="临床证据"
          value={validation.label}
          tone={validation.tone as CompactStatusTone}
          hint="独立状态，可随时在临床证据中心发起验证；不阻断登记、技术资格、发布或启用"
          icon={<FileSearchOutlined />}
        />
        <CompactStatusGlyph label="脚本" value={script.label} tone={script.tone as CompactStatusTone} icon={<CodeOutlined />} />
      </div>
    );
  };

  const filteredModels = useMemo(() => {
    let result = models;
    if (searchText.trim()) {
      const kw = searchText.trim().toLowerCase();
      result = result.filter((m) => {
        const populationTags = (m.population_tags || []).join(' ').toLowerCase();
        return (
          m.name?.toLowerCase().includes(kw)
          || m.model_id?.toLowerCase().includes(kw)
          || m.population?.toLowerCase().includes(kw)
          || populationTags.includes(kw)
        );
      });
    }
    if (filterStatus) result = result.filter(m => m.model_status === filterStatus);
    if (filterValidation) result = result.filter(m => m.validation_status === filterValidation);
    return result;
  }, [models, searchText, filterStatus, filterValidation]);

  const drugFilterOptions = useMemo(() => {
    const drugs = Array.from(new Set(models.map(m => m.drug).filter(Boolean)));
    return drugs.map(d => ({ label: d, value: d }));
  }, [models]);

  const modelColumns = [
    {
      title: '模型信息',
      key: 'identity',
      width: 230,
      fixed: 'left' as const,
      render: (_: any, r: any) => (
        <div className="model-admin__identity">
          <Avatar className="model-admin__avatar" shape="square" size={34} icon={<ExperimentOutlined />} />
          <div className="model-admin__identity-copy">
            <Tooltip title={r.name || r.model_id}>
              <span className="model-admin__identity-title">{r.name || r.model_id}</span>
            </Tooltip>
            <span className="model-admin__identity-meta">{r.model_id}</span>
            {(r.first_author || r.publication_year) && (
              <span className="model-admin__identity-meta">
                {[r.first_author, r.publication_year].filter(Boolean).join(' · ')}
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      title: '药物与人群', key: 'drug_population', width: 170,
      render: (_: any, r: any) => (
        <div className="model-admin__drug-population">
          {r.drug ? <Tag className="clinical-entity-tag" style={getDrugTagStyle(r.drug)}>{r.drug}</Tag> : <Text type="secondary">未配置药物</Text>}
          {renderPopulationTags(r.population_tags)}
        </div>
      ),
    },
    {
      title: '状态与可用性', key: 'status', width: 270,
      render: (_: any, r: any) => (
        <div className="model-admin__status-stack">
          {renderModelLifecycle(r)}
          {renderModelOperations(r)}
        </div>
      ),
    },
    {
      title: '脚本与适用范围', key: 'script', width: 250,
      render: (_: any, r: any) => (
        <div className="model-script-summary">
          <Text code className="model-script-summary__name">{r.script_file_base || '-'}</Text>
          <div className="model-script-summary__meta">
            <Text type="secondary">{(r.r_covariates_raw || []).length} 个协变量</Text>
            {renderRCovariates(r.r_covariates_raw)}
          </div>
          {r.covariate_contract_ready ? (
            <Tag color="green" style={{ marginInlineEnd: 0 }}>
              {r.covariate_contract_summary?.confirmed_count || 0}/{r.covariate_contract_summary?.raw_count || 0} 已关联
            </Tag>
          ) : (
            <Tag color="red" style={{ marginInlineEnd: 0 }}>
              {r.covariate_contract_summary?.blocking_count || 0} 个关联问题
            </Tag>
          )}
          <span className="model-admin__scope-line">
            年龄 {formatRange(r.age_range)} · 体重 {formatRange(r.weight_range)}
          </span>
        </div>
      ),
    },
    {
      title: '问题', dataIndex: 'script_issues', key: 'script_issues', width: 160, ellipsis: true,
      render: (issues: string[]) => issues?.length ? <Text type="danger">{issues.join('；')}</Text> : <Text type="secondary">-</Text>,
    },
    {
      title: '', key: 'ops', width: 118, fixed: 'right' as const, align: 'right' as const,
      render: (_: any, r: any) => {
        const qualificationAccepted = ['legacy_accepted', 'platform_qualified', 'clinical_validated']
          .includes(r.qualification_status);
        const canReview = r.workflow?.next_action === 'review';
        const needsBaselinePackage = !r.draft_mode && !r.candidate_package_id;
        const canQuickUpgrade = !r.draft_mode && Boolean(r.candidate_package_id);
        const actionLoading = statusChangingModelId === r.model_id
          || reviewingModelId === r.model_id
          || exportingModelId === r.model_id;
        const actionItems = [
          ...(needsBaselinePackage ? [{
            key: 'baseline-package',
            icon: <CloudUploadOutlined />,
            label: '补充模型包',
          }] : []),
          ...(canQuickUpgrade ? [{
            key: 'script-upgrade',
            icon: <CodeOutlined />,
            label: '脚本快速升级',
          }] : []),
          {
            key: 'export-package',
            icon: <DownloadOutlined />,
            label: '导出模型包',
          },
          {
            key: 'clinical-validation',
            icon: <ExperimentOutlined />,
            label: '发起临床验证',
          },
          ...(canReview ? [{
            key: 'review',
            icon: <CheckOutlined />,
            label: '审核通过',
          }] : []),
          r.model_status === 'active'
            ? {
              key: 'deactivate',
              icon: <CloseOutlined />,
              label: '回收全局状态',
              danger: true,
            }
            : {
              key: 'activate',
              icon: <CheckOutlined />,
              label: r.workflow?.next_action === 'activate' ? '批准全局测试' : '全局测试（需先完成流程）',
              disabled: r.workflow?.next_action !== 'activate',
            },
          ...(r.publication_state === 'unpublished'
            ? [{ key: 'republish', icon: <CloudUploadOutlined />, label: '恢复发布' }]
            : [{ key: 'unpublish', icon: <CloseOutlined />, label: '模型下架', danger: true }]),
          ...(r.model_status !== 'active' ? [
            { type: 'divider' as const },
            {
              key: 'delete',
              icon: <DeleteOutlined />,
              label: '删除模型',
              danger: true,
            },
          ] : []),
        ];

        const handleAction = ({ key }: { key: string }) => {
          if (key === 'baseline-package') {
            Modal.confirm({
              title: '为存量模型补充模型包',
              content: (
                <Space direction="vertical" size={8}>
                  <Text>这是一次性迁移入口，用于尚无历史模型包的模型。</Text>
                  <Text>
                    ZIP 内 <Text code>manifest.model_id</Text> 必须与现有模型 ID{' '}
                    <Text code>{r.model_id}</Text> 完全一致。
                  </Text>
                  <Text type="secondary">
                    首次模型包建立后，后续版本请直接使用“脚本快速升级”，无需再次提交完整模型资料。
                  </Text>
                </Space>
              ),
              okText: '进入基线导入',
              cancelText: '取消',
              centered: true,
              onOk: () => {
                setEditWizardModelId(r.model_id);
                setWizardOpen(true);
              },
            });
          }
          if (key === 'script-upgrade') {
            setScriptUpgradeModel(r);
          }
          if (key === 'export-package') {
            void handleExportModelPackage(r.model_id);
          }
          if (key === 'clinical-validation') {
            onOpenClinicalValidation?.(r.model_id);
          }
          if (key === 'review') {
            Modal.confirm({
              title: '确认审核通过此模型？',
              content: r.qualification_status === 'legacy_accepted'
                ? '该模型属于迁移前已投入使用的存量模型。审核通过后，可继续执行激活预检。'
                : '该模型已完成资格验证。审核通过后，可继续执行激活预检。',
              okText: '审核通过',
              cancelText: '取消',
              centered: true,
              onOk: () => handleApproveRegisteredModel(r),
            });
          }
          if (key === 'activate') {
            Modal.confirm({
              title: '批准模型进入全局测试？',
              content: '批准后模型将参与全局匹配；临床证据状态继续独立展示，不作为本次启用门槛。',
              okText: '批准全局测试',
              cancelText: '取消',
              centered: true,
              onOk: () => handleModelStatusChange(r, 'active'),
            });
          }
          if (key === 'deactivate') {
            Modal.confirm({
              title: '确认退出全局测试？',
              content: '回收后模型将退出全局匹配；已有租户或用户定向分配不会自动移除。',
              okText: '确认回收',
              cancelText: '取消',
              okButtonProps: { danger: true },
              centered: true,
              onOk: () => handleModelStatusChange(r, 'inactive'),
            });
          }
          if (key === 'unpublish' || key === 'republish') {
            const nextState = key === 'unpublish' ? 'unpublished' : 'published';
            Modal.confirm({
              title: nextState === 'unpublished' ? '确认下架模型？' : '确认恢复发布模型？',
              content: nextState === 'unpublished'
                ? '下架后模型不会出现在前台，也不能用于新的计算；历史病例和报告仍保留。'
                : '恢复发布后，模型仍需满足全局激活和资格条件才会参与匹配。',
              okText: nextState === 'unpublished' ? '确认下架' : '恢复发布',
              cancelText: '取消',
              okButtonProps: { danger: nextState === 'unpublished' },
              onOk: async () => {
                await adminAPI.updateModelPublication(r.model_id, nextState, nextState === 'unpublished' ? '管理员手动下架' : undefined);
                await fetchModels();
              },
            });
          }
          if (key === 'delete') {
            Modal.confirm({
              title: '确认删除此模型？',
              content: `模型 ${r.model_id} 删除后无法恢复。`,
              okText: '确认删除',
              cancelText: '取消',
              okButtonProps: { danger: true },
              centered: true,
              onOk: () => handleDelete(r.model_id),
            });
          }
        };

        return (
          <Space.Compact>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => { setEditWizardModelId(r.model_id); setWizardOpen(true); }}
            >
              编辑
            </Button>
            <Tooltip title={actionLoading ? '正在处理模型状态' : '更多操作'}>
              <Dropdown
                menu={{ items: actionItems, onClick: handleAction }}
                placement="bottomRight"
                trigger={['click']}
                disabled={actionLoading}
              >
                <Button
                  size="small"
                  icon={<DownOutlined />}
                  loading={actionLoading}
                  aria-label={`打开 ${r.model_id} 操作菜单`}
                />
              </Dropdown>
            </Tooltip>
          </Space.Compact>
        );
      },
    },
  ];

  return (
    <>
      <Tabs
        activeKey={activeModelTab}
        onChange={setActiveModelTab}
        items={[
          {
            key: 'models', label: '模型管理', children: (
              <div className="drug-admin model-admin">
                <div className="drug-admin__summary">
                  {[
                    { title: '模型总数', value: models.length },
                    { title: '全局激活', value: models.filter((item) => item.model_status === 'active').length },
                    { title: '待资格 / 审核', value: models.filter((item) => !item.draft_mode && (!['platform_qualified', 'clinical_validated', 'legacy_accepted'].includes(item.qualification_status) || item.review_status !== 'approved')).length },
                    { title: '资产待完善', value: models.filter((item) => item.draft_mode || (!item.draft_mode && !item.candidate_package_id) || item.script_registration_status !== 'complete').length },
                  ].map((s) => (
                    <div className="drug-admin__metric" key={s.title}>
                      <span>{s.title}</span><strong>{s.value}</strong>
                    </div>
                  ))}
                </div>

                <div className="drug-admin__toolbar">
                  <Space wrap size={[8, 8]}>
                    <Search placeholder="搜索模型名称 / ID / 人群" allowClear style={{ width: 280 }}
                      value={searchText} onChange={e => setSearchText(e.target.value)} />
                    <Select placeholder="药物" allowClear style={{ width: 140 }} value={filterDrug} onChange={v => setFilterDrug(v)} options={drugFilterOptions} />
                    <Select placeholder="全局状态" allowClear style={{ width: 120 }} value={filterStatus} onChange={setFilterStatus}
                    options={[{ value: 'active', label: '激活' }, { value: 'inactive', label: '停用' }]} />
                    <Select placeholder="临床证据" allowClear style={{ width: 130 }} value={filterValidation} onChange={setFilterValidation}
                    options={[{ value: 'validated', label: '已验证' }, { value: 'pending', label: '未开展' }, { value: 'not_validated', label: '未形成证据' }]} />
                  </Space>
                  <Space size={6}>
                    <Tooltip title={`刷新 · 当前显示 ${filteredModels.length} / ${models.length}`}>
                      <Button icon={<ReloadOutlined />} onClick={fetchModels} loading={loading} />
                    </Tooltip>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openNewModel}>新建模型</Button>
                  </Space>
                </div>

                <Table
                  className="drug-admin__table model-management__table"
                  columns={modelColumns}
                  dataSource={filteredModels}
                  rowKey="model_id"
                  loading={loading}
                  pagination={{
                    pageSize: MODEL_PAGE_SIZE,
                    current: modelPage,
                    onChange: (page) => setModelPage(page),
                    showTotal: () => <CompactStatusLegend />,
                  }}
                  size="small"
                  scroll={{ x: 1200 }}
                />

                {scriptsData?.orphan_scripts && (scriptsData.orphan_scripts.cpp?.length > 0 || scriptsData.orphan_scripts.r?.length > 0) && (
                  <Collapse
                    size="small"
                    className="model-management__orphan-collapse"
                    items={[{
                      key: 'orphan-scripts',
                      label: `孤立脚本 · ${(scriptsData.orphan_scripts.cpp?.length || 0) + (scriptsData.orphan_scripts.r?.length || 0)} 个`,
                      children: (
                        <Space wrap size={[4, 4]}>
                          {scriptsData.orphan_scripts.cpp?.map((f: string) => <Tag key={f}>{f}</Tag>)}
                          {scriptsData.orphan_scripts.r?.map((f: string) => <Tag key={f}>{f}</Tag>)}
                        </Space>
                      ),
                    }]}
                  />
                )}
              </div>
            )
          },
          {
            key: 'qualification', label: '模型包与技术资格', children: (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <CandidatePackagesTab
                  refreshToken={candidateRefreshToken}
                  onEditModel={(modelId) => {
                    setEditWizardModelId(modelId);
                    setWizardOpen(true);
                  }}
                />
              </Space>
            ),
          },
          {
            key: 'script-health', label: '脚本健康', children: (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <ModelScriptHealthPanel
                  models={models}
                  modelsLoading={loading}
                  refreshToken={candidateRefreshToken}
                  onModelsChanged={fetchModels}
                />
              </Space>
            ),
          },
          {
            key: 'activation', label: '开放分配', children: <ModelActivationAssignments />,
          },
          {
            key: 'validation', label: '临床证据记录', children: (
              <>
                <Space style={{ marginBottom: 16 }} wrap>
                  <Search placeholder="搜索模型名称/ID" allowClear style={{ width: 280 }}
                    value={searchText} onChange={e => setSearchText(e.target.value)} />
                  <Select placeholder="药物" allowClear style={{ width: 140 }} value={filterDrug} onChange={v => setFilterDrug(v)} options={drugFilterOptions} />
                  <Button icon={<ReloadOutlined />} onClick={fetchModels} loading={loading}>刷新</Button>
                  <Text type="secondary">共 {filteredModels.length} 个模型</Text>
                </Space>
                <Table
                  columns={[
                     { title: '模型ID', dataIndex: 'model_id', key: 'mid', width: 180, ellipsis: true },
                     { title: '名称', dataIndex: 'name', key: 'name', width: 160, ellipsis: true },
                     { title: '药物', dataIndex: 'drug', key: 'drug', width: 80, render: (v: string) => v ? <Tag color="blue">{v}</Tag> : '-' },
                     {
                       title: '人群标签', dataIndex: 'population_tags', key: 'population_tags', width: 180,
                       render: (tags: string[]) => renderPopulationTags(tags),
                     },
                     {
                       title: '临床证据', dataIndex: 'validation_status', key: 'vs', width: 90,
                       render: (v: string) => {
                        const c: Record<string, string> = { validated: 'green', pending: 'orange', not_validated: 'default', external_validated: 'cyan' };
                        const l: Record<string, string> = { validated: '已验证', pending: '未开展', not_validated: '未形成证据', external_validated: '外部验证' };
                        return <Tag color={c[v] || 'default'}>{l[v] || v}</Tag>;
                      }
                    },
                    {
                      title: 'n', key: 'n', width: 60,
                      render: (_: any, r: any) => r.validation_summary?.sample_size || '-'
                    },
                    {
                      title: 'MPE', key: 'mpe', width: 70,
                      render: (_: any, r: any) => r.validation_summary?.mpe != null ? r.validation_summary.mpe : '-'
                    },
                    {
                      title: 'RMSE', key: 'rmse', width: 70,
                      render: (_: any, r: any) => r.validation_summary?.rmse != null ? r.validation_summary.rmse : '-'
                    },
                    {
                      title: '外部验证', key: 'ext', width: 70, align: 'center' as const,
                      render: (_: any, r: any) => r.validation_summary?.has_external_validation
                        ? <CheckOutlined style={{ color: '#52c41a' }} />
                        : <CloseOutlined style={{ color: '#d9d9d9' }} />
                    },
                    {
                      title: '操作', key: 'ops', width: 100,
                      render: (_: any, r: any) => (
                        <Button size="small" icon={<EditOutlined />} onClick={() => handleEditValidation(r.model_id)}>
                          编辑证据
                        </Button>
                      ),
                    },
                  ]}
                  dataSource={filteredModels}
                  rowKey="model_id"
                  loading={loading}
                  pagination={{ pageSize: 20 }}
                  size="small"
                  scroll={{ x: 1000 }}
                />
              </>
            )
          },
        ]}
      />

      {/* Model metadata editor */}
      <Modal
        title={editingModelId ? `编辑模型: ${editingModelId}` : '模型详情'}
        width="min(1120px, 96vw)"
        open={editorOpen}
        footer={null}
        centered
        onCancel={() => { setEditorOpen(false); setEditingModelId(null); setEditorData(null); editorForm.resetFields(); }}
        destroyOnClose
        styles={{ body: { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto', paddingTop: 8 } }}
      >
        {editorLoading ? (
          <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /><div style={{ marginTop: 16 }}>加载中...</div></div>
        ) : editorData ? (
          <Tabs items={[
            {
              key: 'form', label: '快捷标注', children: (
                <Form form={editorForm} layout="vertical" size="small">
                  <Tabs size="small" items={[
                    {
                      key: 'basic', label: '基本信息', children: (
                        <>
                          <Form.Item name="model_id" label="模型ID">
                            <Input readOnly disabled />
                          </Form.Item>
                          <Row gutter={16}>
                            <Col span={12}><Form.Item name="first_author" label="第一作者"><Input /></Form.Item></Col>
                            <Col span={12}><Form.Item name="publication_year" label="发表年份"><InputNumber style={{ width: '100%' }} min={1950} max={2030} /></Form.Item></Col>
                          </Row>
                          <Row gutter={16}>
                            <Col span={12}><Form.Item name="pmid" label="PMID"><Input /></Form.Item></Col>
                            <Col span={12}><Form.Item name="doi" label="DOI"><Input /></Form.Item></Col>
                          </Row>
                          <Form.Item name="journal" label="期刊"><Input /></Form.Item>
                          <Row gutter={16}>
                            <Col span={12}>
                              <Form.Item name="model_status" label="模型状态">
                                <Select options={[{ value: 'active', label: '激活' }, { value: 'inactive', label: '停用' }]}
                                 />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item name="validation_status" label="临床证据状态">
                                <Select options={[
                                  { value: 'validated', label: '已验证' },
                                  { value: 'pending', label: '未开展' },
                                  { value: 'not_validated', label: '未形成证据' },
                                ]}/>
                              </Form.Item>
                            </Col>
                          </Row>
                        </>
                      )
                    },
                    {
                      key: 'drug', label: '药物与人群', children: (
                        <>
                          <Form.Item name="drug_name" label="药物名称"><Input /></Form.Item>
                          <Form.Item name="population" label="目标人群"><Input /></Form.Item>
                          <Form.Item name="indication" label="适应症"><Input.TextArea rows={3} /></Form.Item>
                          <Form.Item name="population_tags" label="适用人群标签"
                            tooltip="选择预定义标签或输入自定义标签后回车。定义标签后可在「边界条件」中为每个人群配置专属边界。"
                          >
                            <Select mode="tags" placeholder="选择或输入人群标签" tokenSeparators={[',']}
                              options={allPopulationTags.map(t => ({ label: t, value: t }))}
                                                         />
                          </Form.Item>
                        </>
                      )
                    },
                    {
                      key: 'reliability', label: '可靠性', children: (
                        <>
                          <Form.Item name="sample_total" label="总样本量"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
                          <Form.Item name="external_validation_performed" label="已外部验证" valuePropName="checked"><Switch /></Form.Item>
                        </>
                      )
                    },
                    {
                      key: 'covariates', label: '协变量绑定', forceRender: true, children: (
                        <ModelCovariateBindingEditor ref={modelCovariateEditorRef} modelId={editingModelId || ''} />
                      )
                    },
                    {
                      key: 'boundary', label: '边界条件', children: (
                        <BoundaryConditionManager modelId={editingModelId || ''} />
                      )
                    },
                  ]} />
                  <Divider style={{ margin: '12px 0' }} />
                  <Space>
                    <Button type="primary" onClick={handleSaveForm} loading={editorSaving}>保存</Button>
                  </Space>
                </Form>
              )
            },
            {
              key: 'json', label: <><CodeOutlined /> 完整元数据</>, children: (
                <div>
                  <Alert message="此处仅显示结构化草稿生成的只读快照；请在注册向导和表格式编辑区维护模型信息。" type="info" showIcon style={{ marginBottom: 12 }} />
                  <Input.TextArea value={editorJsonText}
                    readOnly
                    rows={30} style={{ fontFamily: 'Consolas, Monaco, "Courier New", monospace', fontSize: 12 }}
                  />
                  <Space style={{ marginTop: 12, display: 'none' }}>
                    <Button type="primary" onClick={handleSaveJson} loading={editorSaving}>保存</Button>
                    <Button onClick={() => { try { setEditorJsonText(JSON.stringify(JSON.parse(editorJsonText), null, 2)); setEditorJsonError(''); } catch (e: any) { setEditorJsonError('JSON格式错误: ' + e.message); } }}>格式化</Button>
                    <Button onClick={() => { if (editorData) { setEditorJsonText(JSON.stringify(editorData, null, 2)); setEditorJsonError(''); } }}>还原</Button>
                  </Space>
                </div>
              )
            },
          ]} />
        ) : null}
      </Modal>

      {/* Validation editor */}
      <Modal
        title={validationModelId ? `编辑临床证据: ${validationModelId}` : '临床证据管理'}
        width="min(1180px, 96vw)"
        open={validationDrawerOpen}
        centered
        onCancel={() => { setValidationDrawerOpen(false); setValidationModelId(null); validationForm.resetFields(); setDvPredImages([]); setDvIpredImages([]); setCwresImages([]); setVpcImages([]); }}
        destroyOnClose
        okText="保存证据"
        cancelText="取消"
        onOk={handleSaveValidation}
        confirmLoading={validationSaving}
        styles={{ body: { maxHeight: 'calc(100vh - 190px)', overflowY: 'auto', paddingTop: 8 } }}
      >
        {validationLoading ? (
          <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /><div style={{ marginTop: 16 }}>加载中...</div></div>
        ) : (
          <Form form={validationForm} layout="vertical" size="small">
            <Form.Item shouldUpdate noStyle>
              {({ getFieldValue }) => {
                const vc = getFieldValue('validation_center_summary');
                return vc ? (
                  <Alert
                    type="success"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="已同步临床证据中心结果"
                    description={
                      <Space direction="vertical" size={4}>
                        <Text>
                          状态：{vc.overall_status || '-'} · 样本点：{vc.n_predictions ?? '-'} · 受试者：{vc.n_subjects ?? '-'}
                        </Text>
                        <Text>
                          RMSE：{vc.rmse ?? '-'} · MAPE：{vc.mape ?? '-'}% · F30：{vc.f30 ?? '-'}%
                        </Text>
                        <Text type="secondary">{vc.conclusion || ''}</Text>
                      </Space>
                    }
                  />
                ) : null;
              }}
            </Form.Item>
            <Collapse defaultActiveKey={['gof', 'stability', 'pred', 'error', 'param']} size="small"
              items={[
                {
                  key: 'gof', label: '拟合优度 (GOF)', children: (
                    <>
                      <Form.Item name="gof_dv_vs_pred" label="DV vs PRED 描述">
                        <Input.TextArea rows={2} placeholder="描述实测值与群体预测值的关系..." />
                      </Form.Item>
                      <Form.Item label="DV vs PRED 图片">
                        <FileUploader category="image" value={dvPredImages} onChange={setDvPredImages} maxCount={3} />
                      </Form.Item>
                      <Form.Item name="gof_dv_vs_ipred" label="DV vs IPRED 描述">
                        <Input.TextArea rows={2} placeholder="描述实测值与个体预测值的关系..." />
                      </Form.Item>
                      <Form.Item label="DV vs IPRED 图片">
                        <FileUploader category="image" value={dvIpredImages} onChange={setDvIpredImages} maxCount={3} />
                      </Form.Item>
                      <Form.Item name="gof_cwres" label="CWRES 描述">
                        <Input.TextArea rows={2} placeholder="描述条件加权残差分布..." />
                      </Form.Item>
                      <Form.Item label="CWRES 图片">
                        <FileUploader category="image" value={cwresImages} onChange={setCwresImages} maxCount={3} />
                      </Form.Item>
                      <Form.Item name="gof_conclusion" label="结论">
                        <Input.TextArea rows={2} />
                      </Form.Item>
                    </>
                  )
                },
                {
                  key: 'stability', label: '稳定性评价', children: (
                    <>
                      <Row gutter={16}>
                        <Col span={8}><Form.Item name="bootstrap_n" label="Bootstrap次数"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
                        <Col span={8}><Form.Item name="bootstrap_success_rate" label="成功率 (%)"><InputNumber style={{ width: '100%' }} min={0} max={100} step={0.1} /></Form.Item></Col>
                        <Col span={8}><Form.Item name="parameter_bias_percent" label="参数偏差 (%)"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
                      </Row>
                      <Form.Item name="stability_conclusion" label="结论"><Input.TextArea rows={2} /></Form.Item>
                    </>
                  )
                },
                {
                  key: 'pred', label: '预测能力 (VPC / NPDE)', children: (
                    <>
                      <Form.Item name="vpc_description" label="VPC 描述">
                        <Input.TextArea rows={2} placeholder="描述视觉预测检查结果..." />
                      </Form.Item>
                      <Form.Item label="VPC 图片">
                        <FileUploader category="image" value={vpcImages} onChange={setVpcImages} maxCount={3} />
                      </Form.Item>
                      <Row gutter={16}>
                        <Col span={8}><Form.Item name="npde_mean" label="NPDE 均值"><InputNumber style={{ width: '100%' }} step={0.01} /></Form.Item></Col>
                        <Col span={8}><Form.Item name="npde_variance" label="NPDE 方差"><InputNumber style={{ width: '100%' }} step={0.01} /></Form.Item></Col>
                        <Col span={8}><Form.Item name="npde_normality_p" label="正态性 p 值"><InputNumber style={{ width: '100%' }} step={0.001} /></Form.Item></Col>
                      </Row>
                      <Form.Item name="pred_conclusion" label="结论"><Input.TextArea rows={2} /></Form.Item>
                    </>
                  )
                },
                {
                  key: 'error', label: '误差评价', children: (
                    <>
                      <Row gutter={16}>
                        <Col span={8}><Form.Item name="mpe" label="MPE"><InputNumber style={{ width: '100%' }} step={0.01} /></Form.Item></Col>
                        <Col span={8}><Form.Item name="mpe_percent" label="MPE%"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
                        <Col span={8}><Form.Item name="mae" label="MAE"><InputNumber style={{ width: '100%' }} step={0.01} /></Form.Item></Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={8}><Form.Item name="mape" label="MAPE (%)"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
                        <Col span={8}><Form.Item name="rmse" label="RMSE"><InputNumber style={{ width: '100%' }} step={0.01} /></Form.Item></Col>
                      </Row>
                      <Form.Item name="error_conclusion" label="结论"><Input.TextArea rows={2} /></Form.Item>
                    </>
                  )
                },
                {
                  key: 'param', label: '参数评价 (RSE / Shrinkage)', children: (
                    <>
                      <Form.List name="parameters">
                        {(fields, { add, remove }) => (
                          <>
                            {fields.map(({ key, name, ...restField }) => (
                              <Row key={key} gutter={8} align="middle" style={{ marginBottom: 4 }}>
                                <Col span={5}><Form.Item {...restField} name={[name, 'name']} noStyle><Input placeholder="参数名" /></Form.Item></Col>
                                <Col span={5}><Form.Item {...restField} name={[name, 'estimate']} noStyle><InputNumber placeholder="估计值" style={{ width: '100%' }} step={0.001} /></Form.Item></Col>
                                <Col span={5}><Form.Item {...restField} name={[name, 'rse_percent']} noStyle><InputNumber placeholder="RSE%" style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
                                <Col span={5}><Form.Item {...restField} name={[name, 'shrinkage_percent']} noStyle><InputNumber placeholder="Shrinkage%" style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
                                <Col span={4}><Button type="link" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} /></Col>
                              </Row>
                            ))}
                            <Button type="dashed" onClick={() => add({ name: '', estimate: null, rse_percent: null, shrinkage_percent: null })} icon={<PlusOutlined />} block style={{ marginTop: 4 }}>
                              添加参数
                            </Button>
                          </>
                        )}
                      </Form.List>
                      <Row gutter={16} style={{ marginTop: 12 }}>
                        <Col span={12}><Form.Item name="overall_rse_acceptable" label="RSE整体可接受" valuePropName="checked"><Switch /></Form.Item></Col>
                        <Col span={12}><Form.Item name="overall_shrinkage_acceptable" label="Shrinkage整体可接受" valuePropName="checked"><Switch /></Form.Item></Col>
                      </Row>
                      <Form.Item name="param_conclusion" label="结论"><Input.TextArea rows={2} /></Form.Item>
                    </>
                  )
                },
              ]}
            />
            <Divider style={{ margin: '12px 0' }} />
            <Form.Item name="overall_conclusion" label="总体结论">
              <Input.TextArea rows={3} placeholder="模型整体验证结论..." />
            </Form.Item>
            <Row gutter={16}>
              <Col span={8}><Form.Item name="validation_date" label="验证日期"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
              <Col span={8}><Form.Item name="validated_by" label="验证人"><Input placeholder="验证人姓名" /></Form.Item></Col>
              <Col span={8}>
                <Form.Item name="validation_status" label="临床证据状态">
                  <Select options={[
                    { value: 'pending', label: '未开展' },
                    { value: 'validated', label: '已验证' },
                    { value: 'not_validated', label: '未形成证据' },
                    { value: 'external_validated', label: '外部验证' },
                  ]}/>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        )}
      </Modal>

      {/* Model Wizard Drawer */}
      <ModelScriptUpgradeModal
        open={Boolean(scriptUpgradeModel)}
        modelId={scriptUpgradeModel?.model_id}
        currentVersion={scriptUpgradeModel?.candidate_package_version}
        onCancel={() => setScriptUpgradeModel(undefined)}
        onCompleted={fetchModels}
      />

      <ModelWizard
        visible={wizardOpen}
        onClose={() => {
          setWizardOpen(false);
          setEditWizardModelId(undefined);
          setCandidateRefreshToken((value) => value + 1);
          fetchModels();
        }}
        editModelId={editWizardModelId}
        onSubmitted={() => {
          setActiveModelTab('qualification');
          message.success('资料登记完成，模型已入库并保持停用');
        }}
      />

      <Modal
        title="新建模型"
        open={newModelChoiceOpen}
        onCancel={() => setNewModelChoiceOpen(false)}
        footer={null}
        destroyOnHidden
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="优先导入模型包"
            description="自动创建草稿并预填登记资料。"
          />
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <input
              type="file"
              accept=".zip,application/zip"
              onChange={(event) => setModelPackageFile(event.target.files?.[0] || null)}
            />
            {modelPackageFile ? <Text type="secondary">已选择：{modelPackageFile.name}</Text> : null}
            <Button
              type="primary"
              icon={<CloudUploadOutlined />}
              loading={modelPackageUploading}
              onClick={() => void createModelFromPackage()}
              block
            >
              导入模型包并开始登记
            </Button>
          </Space>
          <Divider style={{ margin: '0' }}>或</Divider>
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              setNewModelChoiceOpen(false);
              setEditWizardModelId(undefined);
              setWizardOpen(true);
            }}
            block
          >
            手工新建模型
          </Button>
        </Space>
      </Modal>
    </>
  );
};

// ================================================================
// Workload Tab
// ================================================================
const COIN_TYPE_LABELS: Record<string, string> = {
  register_bonus: '注册赠送',
  invite_reward: '邀请奖励',
  daily_login: '每日登录',
  exchange: '用户兑换',
  admin_adjust: '管理员调整',
  task_reward: '任务奖励',
};

const formatAdminDateTime = (value?: string) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-');

const saveBlobResponse = (res: any, filename: string) => {
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

const rangeParams = (range: any) => {
  const params: Record<string, any> = {};
  if (range?.[0]) params.start_date = range[0].format('YYYY-MM-DD');
  if (range?.[1]) params.end_date = range[1].format('YYYY-MM-DD');
  return params;
};

const WorkloadDashboardTab: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('drug');

  const buildParams = useCallback(() => rangeParams(dateRange), [dateRange]);

  const fetchWorkload = useCallback(async () => {
    setLoading(true);
    try {
      setData(await adminAPI.workload(buildParams()));
    } catch (error) {
      message.error(safeErrorMessage(error, '加载排行榜失败'));
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { void fetchWorkload(); }, [fetchWorkload]);

  const handleExport = async () => {
    try {
      const res = await adminAPI.exportWorkload(buildParams());
      saveBlobResponse(res, `workload_summary_${dayjs().format('YYYY-MM-DD')}.xlsx`);
      message.success('排行榜汇总表已导出');
    } catch (error) {
      message.error(safeErrorMessage(error, '导出失败'));
    }
  };

  const drugRows = Object.entries(data?.by_drug || {})
    .map(([drug, count]) => ({ drug, drugLabel: getDrugLabel(drug), count }))
    .sort((a: any, b: any) => Number(b.count) - Number(a.count));
  const userRows = data?.by_user || [];
  const dateRows = data?.by_date || [];
  const coinRows = Object.entries(data?.coin_stats?.by_type || {}).map(([type, amount]) => ({ type, amount }));

  return (
    <div className="admin-data-query__shell">
      <Card bordered={false} style={ADMIN_SURFACE_STYLE}>
        <div className="admin-data-query__topbar-row">
          <div className="admin-data-query__title-block">
            <div className="admin-data-query__section-title">运营排行榜</div>
            <Text type="secondary">按药物、用户、日期聚合计算活跃度，辅助观察科研使用趋势与运营质量。</Text>
          </div>
          <Space wrap>
            <RangePicker value={dateRange} onChange={setDateRange} />
            <Button type="primary" icon={<SearchOutlined />} loading={loading} onClick={fetchWorkload}>查询</Button>
            <Button icon={<ExportOutlined />} onClick={handleExport}>导出汇总表</Button>
          </Space>
        </div>
        <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
          <Col xs={12} md={6}><Card size="small"><Statistic title="总计算量" value={data?.total_calculations || 0} /></Card></Col>
          <Col xs={12} md={6}><Card size="small"><Statistic title="初始方案" value={data?.by_type?.initial || 0} /></Card></Col>
          <Col xs={12} md={6}><Card size="small"><Statistic title="优化方案" value={data?.by_type?.optimize || 0} /></Card></Col>
          <Col xs={12} md={6}><Card size="small"><Statistic title="PK 公式" value={data?.by_type?.pk_formula || 0} /></Card></Col>
        </Row>
      </Card>

      <Card bordered={false} style={ADMIN_SURFACE_STYLE} title="邀请码转化漏斗">
        <Row gutter={[12, 12]}>
          {[
            ['registered', '注册'],
            ['email_verified', '邮箱验证'],
            ['tenant_joined', '加入机构'],
            ['first_case_created', '首个病例'],
            ['first_calculation_completed', '首次计算'],
          ].map(([key, label]) => (
            <Col xs={12} md={Math.floor(24 / 5)} key={key}>
              <Statistic title={label} value={data?.referral_funnel?.[key] || 0} />
            </Col>
          ))}
        </Row>
      </Card>

      <Card bordered={false} style={ADMIN_SURFACE_STYLE}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'drug',
              label: '按药物',
              children: (
                <Table
                  rowKey="drug"
                  loading={loading}
                  dataSource={drugRows}
                  pagination={false}
                  size="small"
                  columns={[
                    { title: '排名', width: 72, render: (_: any, __: any, index: number) => index + 1 },
                    { title: '药物', dataIndex: 'drugLabel' },
                    { title: '编码', dataIndex: 'drug', render: (value: string) => <Text code>{value}</Text> },
                    { title: '计算次数', dataIndex: 'count', sorter: (a: any, b: any) => Number(a.count) - Number(b.count) },
                  ]}
                />
              ),
            },
            {
              key: 'user',
              label: '按用户',
              children: (
                <Table
                  rowKey="user_id"
                  loading={loading}
                  dataSource={userRows}
                  pagination={false}
                  size="small"
                  columns={[
                    { title: '排名', width: 72, render: (_: any, __: any, index: number) => index + 1 },
                    { title: '用户', dataIndex: 'name', ellipsis: true },
                    { title: '用户ID', dataIndex: 'user_id', ellipsis: true, render: (value: string) => <Text code>{value}</Text> },
                    { title: '计算次数', dataIndex: 'count', sorter: (a: any, b: any) => Number(a.count) - Number(b.count) },
                  ]}
                />
              ),
            },
            {
              key: 'date',
              label: '按日期',
              children: (
                <Table
                  rowKey="date"
                  loading={loading}
                  dataSource={dateRows}
                  pagination={{ pageSize: 14 }}
                  size="small"
                  columns={[
                    { title: '日期', dataIndex: 'date' },
                    { title: '计算次数', dataIndex: 'count', sorter: (a: any, b: any) => Number(a.count) - Number(b.count) },
                  ]}
                />
              ),
            },
          ]}
        />
      </Card>

      <Row gutter={[14, 14]}>
        <Col xs={24} lg={12}>
          <Card bordered={false} style={ADMIN_SURFACE_STYLE} title="金币流通">
            <Row gutter={12} style={{ marginBottom: 12 }}>
              <Col span={12}><Statistic title="总发放" value={data?.coin_stats?.total_granted || 0} valueStyle={{ color: '#16A34A' }} /></Col>
              <Col span={12}><Statistic title="总消耗" value={data?.coin_stats?.total_consumed || 0} valueStyle={{ color: '#DC2626' }} /></Col>
            </Row>
            <Table
              rowKey="type"
              dataSource={coinRows}
              pagination={false}
              size="small"
              columns={[
                { title: '类型', dataIndex: 'type', render: (value: string) => COIN_TYPE_LABELS[value] || value },
                { title: '金币', dataIndex: 'amount', render: (value: number) => <Text type={value < 0 ? 'danger' : 'success'}>{value > 0 ? `+${value}` : value}</Text> },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card bordered={false} style={ADMIN_SURFACE_STYLE} title={`邀请排行（累计 ${data?.invite_stats?.total_invites || 0} 人）`}>
            <Table
              rowKey="user_id"
              dataSource={data?.invite_stats?.top_inviters || []}
              pagination={false}
              size="small"
              columns={[
                { title: '排名', width: 72, render: (_: any, __: any, index: number) => index + 1 },
                { title: '用户', dataIndex: 'name', ellipsis: true },
                { title: '邀请数', dataIndex: 'invite_count', width: 90 },
                { title: '获得金币', dataIndex: 'reward_total', width: 110, render: (value: number) => <Text strong>{value}</Text> },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

const CoinManagementTab: React.FC = () => {
  const [data, setData] = useState<any>({ items: [], total: 0, summary: {} });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<Record<string, any>>({ skip: 0, limit: 50 });
  const [draft, setDraft] = useState<Record<string, any>>({});
  const [users, setUsers] = useState<any[]>([]);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustLoading, setAdjustLoading] = useState(false);
  const [adjustForm] = Form.useForm();

  const fetchData = useCallback(async (nextFilters = filters) => {
    setLoading(true);
    try {
      setData(await adminAPI.listCoinTransactions(nextFilters));
    } catch (error) {
      message.error(safeErrorMessage(error, '加载金币流水失败'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { void fetchData(); }, [fetchData]);
  useEffect(() => {
    adminAPI.listUsers(0, 200, '').then((res: any) => setUsers(res.items || [])).catch(() => setUsers([]));
  }, []);

  const applyFilters = () => {
    const next: Record<string, any> = { ...draft, ...rangeParams(draft.dateRange), skip: 0, limit: filters.limit || 50 };
    delete next.dateRange;
    Object.keys(next).forEach((key) => (next[key] == null || next[key] === '') && delete next[key]);
    setFilters(next);
    void fetchData(next);
  };

  const resetFilters = () => {
    const next = { skip: 0, limit: filters.limit || 50 };
    setDraft({});
    setFilters(next);
    void fetchData(next);
  };

  const handleExport = async () => {
    try {
      const res = await adminAPI.exportCoinTransactions(filters);
      saveBlobResponse(res, `coin_transactions_${dayjs().format('YYYY-MM-DD')}.xlsx`);
      message.success('金币流水已导出');
    } catch (error) {
      message.error(safeErrorMessage(error, '导出失败'));
    }
  };

  const handleAdjust = async () => {
    try {
      const values = await adjustForm.validateFields();
      setAdjustLoading(true);
      await adminAPI.adjustCoin(values.user_id, Number(values.amount), values.reason);
      message.success(Number(values.amount) >= 0 ? '金币已发放' : '金币已扣减');
      setAdjustOpen(false);
      adjustForm.resetFields();
      void fetchData(filters);
    } catch (error: any) {
      if (!error?.errorFields) message.error(safeErrorMessage(error, '调整失败'));
    } finally {
      setAdjustLoading(false);
    }
  };

  const userOptions = users.map((user) => ({
    value: user.id,
    label: `${user.real_name || user.username || user.email || user.id}${user.email ? ` · ${user.email}` : ''}`,
  }));

  return (
    <div className="admin-data-query__shell">
      <Card bordered={false} style={ADMIN_SURFACE_STYLE}>
        <div className="admin-data-query__topbar-row">
          <div>
            <div className="admin-data-query__section-title">金币管理</div>
            <Text type="secondary">统一查看管理员发放、任务奖励、邀请奖励与用户兑换消耗，支持按用户和收支方向导出。</Text>
          </div>
          <Space wrap>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setAdjustOpen(true)}>发放/扣减金币</Button>
            <Button icon={<ExportOutlined />} onClick={handleExport}>导出表格</Button>
          </Space>
        </div>
        <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
          <Col xs={8}><Card size="small"><Statistic title="流水数" value={data?.summary?.total || 0} /></Card></Col>
          <Col xs={8}><Card size="small"><Statistic title="收入" value={data?.summary?.income || 0} valueStyle={{ color: '#16A34A' }} /></Card></Col>
          <Col xs={8}><Card size="small"><Statistic title="支出" value={data?.summary?.expense || 0} valueStyle={{ color: '#DC2626' }} /></Card></Col>
        </Row>
      </Card>

      <Card bordered={false} style={ADMIN_SURFACE_STYLE} title="查询条件">
        <Space wrap>
          <Select
            showSearch
            allowClear
            placeholder="按用户查询"
            style={{ width: 260 }}
            optionFilterProp="label"
            value={draft.user_id}
            options={userOptions}
            onChange={(value) => setDraft((prev) => ({ ...prev, user_id: value }))}
          />
          <Select
            allowClear
            placeholder="收支"
            style={{ width: 130 }}
            value={draft.direction}
            onChange={(value) => setDraft((prev) => ({ ...prev, direction: value }))}
            options={[
              { value: 'income', label: '收入' },
              { value: 'expense', label: '支出' },
            ]}
          />
          <Select
            allowClear
            placeholder="类型"
            style={{ width: 160 }}
            value={draft.type}
            onChange={(value) => setDraft((prev) => ({ ...prev, type: value }))}
            options={Object.entries(COIN_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
          />
          <RangePicker value={draft.dateRange} onChange={(value) => setDraft((prev) => ({ ...prev, dateRange: value }))} />
          <Input
            allowClear
            placeholder="说明 / 用户 / 类型"
            style={{ width: 220 }}
            value={draft.keyword}
            onChange={(event) => setDraft((prev) => ({ ...prev, keyword: event.target.value }))}
            onPressEnter={applyFilters}
          />
          <Button type="primary" icon={<SearchOutlined />} loading={loading} onClick={applyFilters}>查询</Button>
          <Button onClick={resetFilters}>重置</Button>
        </Space>
      </Card>

      <Card bordered={false} style={ADMIN_SURFACE_STYLE} title="金币流水">
        <Table
          rowKey="id"
          loading={loading}
          dataSource={data?.items || []}
          size="small"
          scroll={{ x: 980 }}
          pagination={{
            total: data?.total || 0,
            pageSize: filters.limit || 50,
            current: Math.floor((filters.skip || 0) / (filters.limit || 50)) + 1,
            onChange: (page, pageSize) => {
              const next = { ...filters, skip: (page - 1) * pageSize, limit: pageSize };
              setFilters(next);
              void fetchData(next);
            },
          }}
          columns={[
            { title: '用户', dataIndex: 'user_name', width: 180, ellipsis: true },
            { title: '邮箱', dataIndex: 'user_email', width: 220, ellipsis: true },
            { title: '收支', dataIndex: 'direction', width: 90, render: (value: string) => <Tag color={value === 'income' ? 'green' : value === 'expense' ? 'red' : 'default'}>{value === 'income' ? '收入' : value === 'expense' ? '支出' : '中性'}</Tag> },
            { title: '类型', dataIndex: 'type', width: 140, render: (value: string) => COIN_TYPE_LABELS[value] || value },
            { title: '金币', dataIndex: 'amount', width: 100, render: (value: number) => <Text type={value < 0 ? 'danger' : 'success'}>{value > 0 ? `+${value}` : value}</Text> },
            { title: '变动后余额', dataIndex: 'balance_after', width: 110 },
            { title: '说明', dataIndex: 'description', ellipsis: true },
            { title: '时间', dataIndex: 'created_at', width: 160, render: formatAdminDateTime },
          ]}
        />
      </Card>

      <Modal
        title="发放 / 扣减金币"
        open={adjustOpen}
        onCancel={() => setAdjustOpen(false)}
        onOk={handleAdjust}
        confirmLoading={adjustLoading}
        okText="确认调整"
      >
        <Form form={adjustForm} layout="vertical">
          <Form.Item name="user_id" label="用户" rules={[{ required: true, message: '请选择用户' }]}>
            <Select showSearch optionFilterProp="label" options={userOptions} placeholder="选择用户" />
          </Form.Item>
          <Form.Item name="amount" label="金币变动" extra="正数为发放，负数为扣减。" rules={[{ required: true, message: '请输入金币变动数量' }]}>
            <InputNumber style={{ width: '100%' }} min={-100000} max={100000} />
          </Form.Item>
          <Form.Item name="reason" label="原因" rules={[{ required: true, message: '请输入调整原因' }]}>
            <Input.TextArea rows={3} placeholder="例如：活动奖励 / 人工补偿 / 兑换扣减" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

const WorkloadTab: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<any>(null);

  const fetchWorkload = async () => {
    setLoading(true);
    const params: any = {};
    if (dateRange?.[0]) params.start_date = dateRange[0].format('YYYY-MM-DD');
    if (dateRange?.[1]) params.end_date = dateRange[1].format('YYYY-MM-DD');
    try { setData(await adminAPI.workload(params)); }
    catch { message.error('加载工作量失败'); }
    finally { setLoading(false); }
  };

  const handleExportWorkload = async () => {
    try {
      const params: any = {};
      if (dateRange?.[0]) params.start_date = dateRange[0].format('YYYY-MM-DD');
      if (dateRange?.[1]) params.end_date = dateRange[1].format('YYYY-MM-DD');
      const res = await adminAPI.exportWorkload(params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `业务分类汇总表_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click(); window.URL.revokeObjectURL(url); message.success('导出成功');
    } catch { message.error('导出失败'); }
  };

  useEffect(() => { fetchWorkload(); }, []);

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <RangePicker onChange={v => setDateRange(v)} />
        <Button type="primary" onClick={fetchWorkload} loading={loading} icon={<SearchOutlined />}>查询</Button>
        <Button icon={<ExportOutlined />} onClick={handleExportWorkload}>导出汇总表</Button>
      </Space>
      {data && (
        <>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}><Card><Statistic title="总计算量" value={data.total_calculations} /></Card></Col>
            {(['initial', 'optimize', 'pk_formula'] as const)
              .filter(k => (data.by_type || {})[k] !== undefined)
              .map(k => (
                <Col span={6} key={k}><Card><Statistic title={{ initial: '初始给药', optimize: '剂量优化', pk_formula: 'PK公式' }[k]} value={(data.by_type || {})[k] as number} /></Card></Col>
              ))}
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Card title="按药物" size="small">
                <Table dataSource={Object.entries(data.by_drug || {}).map(([k, v]) => ({ drug: k, count: v }))}
                  columns={[{ title: '药物', dataIndex: 'drug' }, { title: '次数', dataIndex: 'count' }]}
                  rowKey="drug" pagination={false} size="small" />
              </Card>
            </Col>
            <Col span={12}>
              <Card title="按用户 (Top 20)" size="small">
                <Table dataSource={data.by_user || []}
                  columns={[{ title: '用户', dataIndex: 'name' }, { title: '次数', dataIndex: 'count' }]}
                  rowKey="user_id" pagination={false} size="small" />
              </Card>
            </Col>
          </Row>
          {data.by_date?.length > 0 && (
            <Card title="按日期" size="small" style={{ marginTop: 16 }}>
              <Table dataSource={data.by_date}
                columns={[{ title: '日期', dataIndex: 'date' }, { title: '次数', dataIndex: 'count' }]}
                rowKey="date" pagination={false} size="small" />
            </Card>
          )}

          {/* 金币流通统计 */}
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={12}>
              <Card title="金币流通" size="small">
                <Row gutter={16} style={{ marginBottom: 12 }}>
                  <Col span={12}><Statistic title="总发放" value={data.coin_stats?.total_granted || 0} valueStyle={{ color: '#52c41a' }} /></Col>
                  <Col span={12}><Statistic title="总消耗" value={data.coin_stats?.total_consumed || 0} valueStyle={{ color: '#ff4d4f' }} /></Col>
                </Row>
                <Table dataSource={Object.entries(data.coin_stats?.by_type || {}).map(([k, v]) => ({ type: k, amount: v }))}
                  columns={[
                    { title: '类型', dataIndex: 'type', render: (v: string) => ({ register_bonus: '注册赠送', invite_reward: '邀请奖励', exchange: '兑换消耗', admin_adjust: '管理员调整', task_reward: '任务奖励' }[v] || v) },
                    { title: '金额', dataIndex: 'amount', render: (v: number) => <span style={{ color: v >= 0 ? '#52c41a' : '#ff4d4f' }}>{v > 0 ? '+' : ''}{v}</span> },
                  ]}
                  rowKey="type" pagination={false} size="small" />
              </Card>
            </Col>
            <Col span={12}>
              <Card title={`邀请排行 (共${data.invite_stats?.total_invites || 0}人被邀请)`} size="small">
                <Table dataSource={data.invite_stats?.top_inviters || []}
                  columns={[
                    { title: '排名', key: 'rank', width: 50, render: (_: any, __: any, i: number) => i + 1 },
                    { title: '用户', dataIndex: 'name', ellipsis: true },
                    { title: '邀请数', dataIndex: 'invite_count', width: 70 },
                    { title: '获得金币', dataIndex: 'reward_total', width: 80, render: (v: number) => <span style={{ color: '#C5A55A' }}>{v}</span> },
                  ]}
                  rowKey="user_id" pagination={false} size="small" />
              </Card>
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

// ================================================================
// Data Query Tab
// ================================================================
const DataQueryTab: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useState<any>({ page: 1, page_size: 20 });

  const fetchData = async (p?: any) => {
    const q = p || params; setLoading(true);
    try { setData(await adminAPI.dataQuery(q)); }
    catch { message.error('查询失败'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleExport = async () => {
    try {
      const res = await adminAPI.exportExcel(params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `precidose_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click(); window.URL.revokeObjectURL(url); message.success('导出成功');
    } catch (err: any) {
      let detail: any = '导出失败';
      try {
        if (err?.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          if (json.detail) detail = json.detail;
        } else if (err?.response?.data?.detail) {
          detail = err.response.data.detail;
        }
      } catch { /* ignore parse error */ }
      message.error(safeErrorMessage(detail, '导出失败'));
    }
  };

  const handleExportNonmem = async () => {
    try {
      const res = await adminAPI.exportNonmem(params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `nonmem_research_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click(); window.URL.revokeObjectURL(url); message.success('导出成功');
    } catch (err: any) {
      let detail: any = '导出失败';
      try {
        if (err?.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          if (json.detail) detail = json.detail;
        } else if (err?.response?.data?.detail) {
          detail = err.response.data.detail;
        }
      } catch { /* ignore parse error */ }
      message.error(safeErrorMessage(detail, '导出失败'));
    }
  };

  const drugLabels = DRUG_NAME_MAP;
  const typeLabels = _CALC_TYPE_LABELS;
  const numRender = (v: any) => (v !== '' && v != null ? v : '-');

  const columns = [
    // -- 患者标识 --
    { title: '住院号', dataIndex: 'patient_code', key: 'pc', width: 120, fixed: 'left' as const },
    { title: '性别', dataIndex: 'gender', key: 'sex', width: 60 },
    { title: '诊断', dataIndex: 'diagnosis', key: 'diag', width: 150, ellipsis: true },
    // -- 药物与模型 --
    { title: '药物', dataIndex: 'drug_name', key: 'dn', width: 100, render: (v: string) => drugLabels[v] || v },
    { title: '计算类型', dataIndex: 'calculation_type', key: 'ct', width: 90, render: (v: string) => typeLabels[v] || v },
    { title: '模型', dataIndex: 'model_id', key: 'mid', width: 140, ellipsis: true },
    // -- 协变量 --
    { title: '体重(kg)', dataIndex: 'weight', key: 'wt', width: 85, render: numRender },
    { title: '年龄', dataIndex: 'age', key: 'age', width: 70, render: numRender },
    { title: '身高(cm)', dataIndex: 'height', key: 'ht', width: 85, render: numRender },
    { title: '肌酐', dataIndex: 'scr', key: 'scr', width: 70, render: numRender },
    { title: '白蛋白', dataIndex: 'alb', key: 'alb', width: 75, render: numRender },
    { title: '肌酐清除率', dataIndex: 'clcr', key: 'clcr', width: 100, render: numRender },
    { title: 'PMA(周)', dataIndex: 'pma', key: 'pma', width: 80, render: numRender },
    { title: '胎龄(周)', dataIndex: 'gestational_age', key: 'ga', width: 80, render: numRender },
    // -- 给药参数 --
    { title: '剂量(mg)', dataIndex: 'dose_mg', key: 'dose', width: 85, render: numRender },
    { title: '间隔(h)', dataIndex: 'interval_h', key: 'intv', width: 80, render: numRender },
    { title: '输注(h)', dataIndex: 'infusion_h', key: 'inf', width: 80, render: numRender },
    // -- TDM --
    { title: 'TDM次数', dataIndex: 'tdm_count', key: 'tdmc', width: 80 },
    { title: 'TDM摘要', dataIndex: 'tdm_summary', key: 'tdms', width: 180, ellipsis: true },
    // -- 计算结果 --
    { title: '推荐剂量', dataIndex: 'recommended_dose', key: 'rd', width: 90, render: numRender },
    { title: '推荐间隔', dataIndex: 'recommended_interval', key: 'ri', width: 90, render: numRender },
    { title: 'AUC24', dataIndex: 'auc24', key: 'auc', width: 80, render: numRender },
    { title: '预测Cmin', dataIndex: 'predicted_cmin', key: 'cmin', width: 90, render: numRender },
    { title: '预测Cmax', dataIndex: 'predicted_cmax', key: 'cmax', width: 90, render: numRender },
    // -- PK参数 --
    { title: 'CL', dataIndex: 'cl', key: 'cl', width: 70, render: numRender },
    { title: 'Vd', dataIndex: 'vd', key: 'vd', width: 70, render: numRender },
    { title: 'Ke', dataIndex: 'ke', key: 'ke', width: 70, render: numRender },
    // -- 安全 --
    { title: '安全警告', dataIndex: 'safety_alerts', key: 'sa', width: 150, ellipsis: true },
    // -- 操作者 --
    { title: '操作者', dataIndex: 'created_by_name', key: 'cbn', width: 100 },
    { title: '时间', dataIndex: 'created_at', key: 'cat', width: 150, render: (v: string) => v ? new Date(v).toLocaleString('zh-CN') : '-' },
  ];

  return (
    <>
      <Space style={{ marginBottom: 16 }} wrap>
        <Input placeholder="住院号" onChange={e => setParams((p: any) => ({ ...p, patient_code: e.target.value }))} style={{ width: 150 }} />
        <Select placeholder="药物" allowClear onChange={v => setParams((p: any) => ({ ...p, drug_name: v }))} style={{ width: 120 }}
          options={Object.entries(DRUG_NAME_MAP).map(([value, label]) => ({ value, label }))} />
        <Select placeholder="类型" allowClear onChange={v => setParams((p: any) => ({ ...p, calculation_type: v }))} style={{ width: 120 }}
          options={Object.entries(_CALC_TYPE_LABELS).map(([value, label]) => ({ value, label }))} />
        <DatePicker.RangePicker onChange={(_, ds) => setParams((p: any) => ({ ...p, start_date: ds[0] || undefined, end_date: ds[1] || undefined }))} />
        <Button type="primary" onClick={() => fetchData()} loading={loading} icon={<SearchOutlined />}>查询</Button>
        <Dropdown menu={{
          items: [
            { key: 'excel', label: '计算数据表', onClick: handleExport },
            { key: 'nonmem', label: 'NONMEM/nlmixr2 科研格式', onClick: handleExportNonmem },
          ]
        }}>
          <Button icon={<ExportOutlined />}>导出 <DownloadOutlined /></Button>
        </Dropdown>
      </Space>
      <Table columns={columns} dataSource={data?.items || []} rowKey="id" loading={loading}
        scroll={{ x: 3200 }}
        pagination={{
          current: data?.page, pageSize: data?.page_size, total: data?.total,
          onChange: (p, ps) => { const np = { ...params, page: p, page_size: ps }; setParams(np); fetchData(np); }
        }}
        size="small" />
    </>
  );
};


const DataQueryV2Tab: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deletingRowId, setDeletingRowId] = useState<string | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [detailRecord, setDetailRecord] = useState<any>(null);
  const [params, setParams] = useState<any>({ page: 1, page_size: 20 });
  const [draftFilters, setDraftFilters] = useState<any>({
    patient_code: '',
    drug_name: undefined,
    calculation_type: undefined,
    model_id: undefined,
    created_by: undefined,
  });
  const [dateRange, setDateRange] = useState<any>(null);
  const [viewportHeight, setViewportHeight] = useState<number>(typeof window !== 'undefined' ? window.innerHeight : 960);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchData = useCallback(async (requestParams: any) => {
    setLoading(true);
    try {
      setData(await adminAPI.dataQuery(requestParams));
      setSelectedRowKeys([]);
    } catch (error: any) {
      message.error(safeErrorMessage(error, '加载科研数据失败'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData({ page: 1, page_size: 20 });
  }, [fetchData]);

  const buildQueryParams = useCallback((overrides: any = {}) => {
    const next = { ...params, ...overrides };
    const patientCode = String(draftFilters.patient_code || '').trim();

    if (patientCode) next.patient_code = patientCode;
    else delete next.patient_code;

    if (draftFilters.drug_name) next.drug_name = draftFilters.drug_name;
    else delete next.drug_name;

    if (draftFilters.calculation_type) next.calculation_type = draftFilters.calculation_type;
    else delete next.calculation_type;

    if (draftFilters.model_id) next.model_id = draftFilters.model_id;
    else delete next.model_id;

    if (draftFilters.created_by) next.created_by = draftFilters.created_by;
    else delete next.created_by;

    if (dateRange?.[0]) next.start_date = dayjs(dateRange[0]).format('YYYY-MM-DD');
    else delete next.start_date;

    if (dateRange?.[1]) next.end_date = dayjs(dateRange[1]).format('YYYY-MM-DD');
    else delete next.end_date;

    return next;
  }, [dateRange, draftFilters, params]);

  const handleSearch = useCallback(() => {
    const next = buildQueryParams({ page: 1, page_size: params.page_size || 20 });
    setParams(next);
    void fetchData(next);
  }, [buildQueryParams, fetchData, params.page_size]);

  const handleReset = useCallback(() => {
    const next = { page: 1, page_size: params.page_size || 20 };
    setDraftFilters({
      patient_code: '',
      drug_name: undefined,
      calculation_type: undefined,
      model_id: undefined,
      created_by: undefined,
    });
    setDateRange(null);
    setParams(next);
    void fetchData(next);
  }, [fetchData, params.page_size]);

  const extractBlobDetail = async (error: any, fallback: string) => {
    try {
      if (error?.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        const json = JSON.parse(text);
        return json.detail || fallback;
      }
      return error?.response?.data?.detail || fallback;
    } catch {
      return fallback;
    }
  };

  const handleExport = async () => {
    try {
      const res = await adminAPI.exportExcel(params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `precidose_structured_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      message.success('结构化数据导出成功');
    } catch (error: any) {
      const detail = await extractBlobDetail(error, '结构化数据导出失败');
      message.error(safeErrorMessage(detail, '结构化数据导出失败'));
    }
  };

  const handleExportNonmem = async () => {
    try {
      const res = await adminAPI.exportNonmem(params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `precidose_nonmem_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      message.success('NONMEM 数据导出成功');
    } catch (error: any) {
      const detail = await extractBlobDetail(error, 'NONMEM 数据导出失败');
      message.error(safeErrorMessage(detail, 'NONMEM 数据导出失败'));
    }
  };

  const handleExportSelected = async (format: 'structured' | 'nonmem') => {
    const ids = selectedRowKeys.map(String);
    if (!ids.length) {
      message.warning('请先勾选需要导出的记录');
      return;
    }

    try {
      const res = format === 'structured'
        ? await adminAPI.exportSelectedExcel(ids)
        : await adminAPI.exportSelectedNonmem(ids);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = format === 'structured'
        ? `precidose_structured_selected_${new Date().toISOString().slice(0, 10)}.xlsx`
        : `precidose_nonmem_selected_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      message.success(format === 'structured' ? '已导出勾选的结构化结果' : '已导出勾选的 NONMEM 数据');
    } catch (error: any) {
      const fallback = format === 'structured' ? '导出勾选的结构化结果失败' : '导出勾选的 NONMEM 数据失败';
      const detail = await extractBlobDetail(error, fallback);
      message.error(safeErrorMessage(detail, fallback));
    }
  };

  const rows = data?.items || [];
  const filterOptions = data?.filter_options || {};
  const currentSelectionCount = selectedRowKeys.length;

  const drugOptions = useMemo<DataQueryDrugOption[]>(
    () => (filterOptions.drugs || []).map((item: any) => ({
      value: String(item.value),
      label: item.is_active === false ? `${String(item.label)}（已停用）` : String(item.label),
      rawLabel: String(item.label),
    })),
    [filterOptions.drugs],
  );

  const modelOptions = useMemo(
    () => (filterOptions.models || []).map((item: any) => ({
      value: item.value,
      label: `${item.label}${item.count ? ` (${item.count})` : ''}`,
      rawLabel: item.label,
    })),
    [filterOptions.models],
  );

  const operatorOptions = useMemo(
    () => (filterOptions.operators || []).map((item: any) => ({
      value: item.value,
      label: `${item.label}${item.count ? ` (${item.count})` : ''}`,
      rawLabel: item.label,
    })),
    [filterOptions.operators],
  );

  const modelLabelMap = useMemo(
    () => new Map(modelOptions.map((item: any) => [item.value, item.rawLabel])),
    [modelOptions],
  );
  const drugLabelMap = useMemo<Map<string, string>>(
    () => new Map(drugOptions.map((item) => [item.value, item.rawLabel])),
    [drugOptions],
  );
  const operatorLabelMap = useMemo(
    () => new Map(operatorOptions.map((item: any) => [item.value, item.rawLabel])),
    [operatorOptions],
  );

  const hasValue = useCallback((value: any) => value !== '' && value != null, []);

  const toReplayNumber = useCallback((value: any): number | undefined => {
    if (!hasValue(value)) return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }, [hasValue]);

  const formatNumberCell = useCallback((value: any, decimals = 2) => {
    if (!hasValue(value)) return '-';
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return Math.abs(numeric % 1) > 0.0001 ? numeric.toFixed(decimals) : numeric.toString();
    }
    return String(value);
  }, [hasValue]);

  const formatTextCell = useCallback((value: any, fallback = '-') => {
    if (!hasValue(value)) return fallback;
    if (Array.isArray(value)) {
      const joined = value.filter(Boolean).join('；');
      return joined || fallback;
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  }, [hasValue]);

  const formatTdmSampleTime = useCallback((row: any) => {
    const value = [
      row?.sample_time,
      row?.sampling_time,
      row?.draw_time,
      row?.measured_at,
      row?.sample_datetime,
      row?.sampling_datetime,
      row?.timestamp,
    ].find(hasValue);
    return formatTextCell(value);
  }, [formatTextCell, hasValue]);

  const buildOptimizationReplayDraft = useCallback((row: any) => {
    const sections = row?.structured_sections || {};
    const meta = sections.record_meta || {};
    const caseIntake = sections.case_intake || {};
    const patientSnapshot = sections.dose_patient_snapshot || {};
    const modelInputPatientData = patientSnapshot.model_input_patient_data || {};
    const optimizeInput = modelInputPatientData.optimize_input || {};
    const pkFormulaInput = modelInputPatientData.pk_formula_input || {};
    const modelInfo = sections.model_info || {};
    const targetAnalysis = sections.pkpd_target_analysis || {};
    const currentRegimen = sections.current_regimen || {};
    const regimenRows = Array.isArray(currentRegimen.regimen_history) ? currentRegimen.regimen_history : [];
    const tdmRows = Array.isArray(sections.tdm_results) ? sections.tdm_results : [];
    const hasOptimizeRegimens = Array.isArray(optimizeInput.dosing_regimens) && optimizeInput.dosing_regimens.length > 0;
    const hasOptimizeTdmSamples = Array.isArray(optimizeInput.tdm_data) && optimizeInput.tdm_data.length > 0;
    const replayRegimens = hasOptimizeRegimens
      ? optimizeInput.dosing_regimens
      : regimenRows;
    const replayTdmSamples = hasOptimizeTdmSamples
      ? optimizeInput.tdm_data
      : tdmRows;
    const primaryMethod = optimizeInput.target_type || targetAnalysis.primary_method || row?.primary_method || row?.target_type;
    const targetValues: Record<string, number | undefined> = {};
    const targetAuc = toReplayNumber(optimizeInput.target_auc);
    const targetValue = toReplayNumber(optimizeInput.target_value);
    const mic = toReplayNumber(optimizeInput.mic);
    if (targetAuc !== undefined) targetValues.target_auc = targetAuc;
    if (mic !== undefined) targetValues.mic = mic;
    if (targetValue !== undefined) {
      const targetFieldByMethod: Record<string, string> = {
        'Target Concentration': 'target_cmin',
        'Target Cmax': 'target_cmax',
        'Cmax/MIC': 'target_cmax_mic',
        'AUC/MIC': 'target_auc_mic',
        '%fT>MIC': 'target_ft',
      };
      const targetField = targetFieldByMethod[primaryMethod];
      if (targetField) targetValues[targetField] = targetValue;
    }

    return {
      source: 'admin-data-query',
      calculationType: row?.calculation_type,
      sourceLabel: '后台结构化详情',
      calculationId: row?.id || meta.record_id,
      caseId: optimizeInput.case_id || meta.case_id,
      patientCode: row?.patient_code || meta.patient_code,
      diagnosis: row?.diagnosis || caseIntake.diagnosis,
      gender: row?.gender,
      drugName: row?.drug_name || meta.drug_name,
      covariates: caseIntake.confirmed_covariates || {},
      derivedMetrics: caseIntake.derived_metrics || {},
      modelId: optimizeInput.model_id || modelInfo.selected_model_id || row?.model_id || meta.model_id,
      modelDisplayName: modelInfo.display_name || row?.model_display_name || row?.model_id,
      selectedMethod: primaryMethod,
      pkFormulaInput,
      administrationMode: optimizeInput.administration_mode || row?.administration_mode,
      futureInfusionDurationH: toReplayNumber(
        optimizeInput.future_infusion_duration_h ?? row?.recommended_infusion_h,
      ),
      dosingRegimens: replayRegimens.map((regimen: any) => ({
        dosing_start_time: regimen.dosing_start_time || regimen.start_time,
        dose: toReplayNumber(regimen.dose ?? regimen.dose_mg),
        dosing_interval: toReplayNumber(regimen.dosing_interval ?? regimen.interval_h),
        infusion_duration: toReplayNumber(regimen.infusion_duration ?? regimen.infusion_h),
        n_doses: toReplayNumber(regimen.n_doses ?? regimen.dose_count),
        n_doses_source: regimen.n_doses_source === 'manual' || regimen.dose_count_source === 'manual'
          ? 'manual'
          : 'auto',
      })),
      tdmSamples: replayTdmSamples.map((sample: any, index: number) => ({
        ...sample,
        sampling_time: sample.sampling_time
          || sample.sample_time
          || sample.draw_time
          || sample.measured_at
          || tdmRows[index]?.sample_time,
        concentration: toReplayNumber(sample.concentration ?? sample.concentration_mg_l),
      })),
      targetValues,
      tdmValuesAreDisplayUnits: hasOptimizeTdmSamples,
    };
  }, [toReplayNumber]);

  const handleReplayOptimization = useCallback((row: any) => {
    const draft = buildOptimizationReplayDraft(row);
    if (!draft.caseId || !draft.drugName) {
      message.warning('当前记录缺少病例或药物信息，无法复现优化');
      return;
    }
    try {
      const replayKey = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(`${ADMIN_REPLAY_DRAFT_STORAGE_PREFIX}${replayKey}`, JSON.stringify(draft));
      const replayUrl = new URL('/new-case', window.location.origin);
      replayUrl.searchParams.set('admin_replay_key', replayKey);
      const opened = window.open(replayUrl.toString(), '_blank');
      if (opened) {
        opened.opener = null;
      } else {
        localStorage.removeItem(`${ADMIN_REPLAY_DRAFT_STORAGE_PREFIX}${replayKey}`);
        message.warning('浏览器阻止了新页面，请允许弹窗后重试');
      }
    } catch (error: any) {
      message.error(safeErrorMessage(error, '复现数据准备失败'));
    }
  }, [buildOptimizationReplayDraft]);

  const summary = useMemo(() => {
    const querySummary = data?.summary || {};
    return {
      total: Number(querySummary.total_records ?? data?.total ?? 0),
      distinctPatients: Number(querySummary.distinct_patients ?? 0),
      distinctDrugs: Number(querySummary.distinct_drugs ?? 0),
      initialCount: Number(querySummary.initial_count ?? 0),
      optimizeCount: Number(querySummary.optimize_count ?? 0),
      pkFormulaCount: Number(querySummary.pk_formula_count ?? 0),
      currentPageCount: rows.length,
    };
  }, [data?.summary, data?.total, rows.length]);

  const activeFilterTags = useMemo(() => {
    const tags: string[] = [];
    if (params.patient_code) tags.push(`病例号：${params.patient_code}`);
    if (params.drug_name) tags.push(`药物：${drugLabelMap.get(params.drug_name) || getDrugLabel(params.drug_name)}`);
    if (params.calculation_type) tags.push(`计算类型：${_CALC_TYPE_LABELS[params.calculation_type] || params.calculation_type}`);
    if (params.model_id) tags.push(`模型：${modelLabelMap.get(params.model_id) || params.model_id}`);
    if (params.created_by) tags.push(`操作人：${operatorLabelMap.get(params.created_by) || params.created_by}`);
    if (params.start_date || params.end_date) {
      const start = params.start_date ? dayjs(params.start_date).format('YYYY-MM-DD') : '开始';
      const end = params.end_date ? dayjs(params.end_date).format('YYYY-MM-DD') : '结束';
      tags.push(`时间：${start} - ${end}`);
    }
    return tags;
  }, [drugLabelMap, modelLabelMap, operatorLabelMap, params]);

  const tableLocale = {
    emptyText: <Empty description="暂无符合条件的结构化结果" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
  };

  const handleDeleteRecords = useCallback((ids: string[], options?: { singleRowId?: string }) => {
    if (!ids.length) return;

    Modal.confirm({
      title: ids.length === 1 ? '确认删除当前结果？' : `确认删除选中的 ${ids.length} 条结果？`,
      content: ids.length === 1
        ? '删除后将无法恢复，相关结构化记录会从查询与导出中移除。'
        : '删除后将无法恢复，选中的结构化记录会从查询与导出中移除。',
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        setDeleteSubmitting(true);
        setDeletingRowId(options?.singleRowId || null);
        try {
          const response = await adminAPI.deleteDataQueryRecords(ids);
          message.success(response.message || '删除成功');
          const currentPage = Number(params.page) || 1;
          const currentPageSize = Number(params.page_size) || 20;
          const shouldStepBack = currentPage > 1 && rows.length > 0 && ids.length >= rows.length;
          const next = {
            ...params,
            page: shouldStepBack ? currentPage - 1 : currentPage,
            page_size: currentPageSize,
          };
          setParams(next);
          await fetchData(next);
        } catch (error: any) {
          message.error(safeErrorMessage(error, '删除失败'));
        } finally {
          setDeleteSubmitting(false);
          setDeletingRowId(null);
          setSelectedRowKeys([]);
        }
      },
    });
  }, [fetchData, params, rows.length]);

  const tableScrollY = Math.max(280, viewportHeight - 430);

  const renderCaseCell = (row: any) => (
    <div className="admin-data-query__cell-stack">
      <div className="admin-data-query__cell-title">{row.patient_code || '-'}</div>
      <div className="admin-data-query__cell-subtitle">{row.diagnosis || '未填写诊断信息'}</div>
      <div className="admin-data-query__cell-inline">
        <Tag color="default">{row.gender || '未标注性别'}</Tag>
      </div>
    </div>
  );

  const renderTherapyCell = (row: any) => (
    <div className="admin-data-query__cell-stack">
      <div className="admin-data-query__cell-inline">
        <Tag color="blue">{drugLabelMap.get(row.drug_name) || getDrugLabel(row.drug_name)}</Tag>
        <Tag color="processing">{_CALC_TYPE_LABELS[row.calculation_type] || row.calculation_type || '-'}</Tag>
      </div>
      <div className="admin-data-query__cell-subtitle">{row.model_display_name || row.model_id || '未记录模型'}</div>
    </div>
  );

  const renderRegimenCell = (row: any) => (
    <div className="admin-data-query__metric-list">
      <span>剂量 {formatNumberCell(row.dose_mg)} mg</span>
      <span>q{formatNumberCell(row.interval_h, 0)}h · 输注 {formatNumberCell(row.infusion_h, 1)} h</span>
      <span>TDM {formatNumberCell(row.tdm_count, 0)} 次</span>
    </div>
  );

  const renderExposureCell = (row: any) => (
    <div className="admin-data-query__metric-list">
      <span>AUC24 {formatNumberCell(row.auc24)}</span>
      <span>Cmin {formatNumberCell(row.predicted_cmin)}</span>
      <span>Cmax {formatNumberCell(row.predicted_cmax)}</span>
    </div>
  );

  const renderPkpdCell = (row: any) => {
    const pkpdResults = row?.structured_sections?.pkpd_target_analysis?.pkpd_results || {};
    const entries = Object.entries(pkpdResults).slice(0, 2);

    return (
      <div className="admin-data-query__metric-list">
        {entries.length > 0 ? entries.map(([method, result]: [string, any]) => {
          const displayValue = typeof result === 'object' && result !== null
            ? `${formatNumberCell(result.value)}${result.unit ? ` ${result.unit}` : ''}`
            : formatNumberCell(result);
          return <span key={method}>{method}: {displayValue}</span>;
        }) : <span>暂无 PK/PD 靶值结果</span>}
        <span className="admin-data-query__cell-subtitle">{formatTextCell(row.safety_alerts, '无安全提示')}</span>
      </div>
    );
  };

  const columns = [
    {
      title: '病例',
      key: 'case',
      width: 220,
      fixed: 'left' as const,
      render: (_: any, row: any) => renderCaseCell(row),
    },
    {
      title: '药物 / 模型',
      key: 'therapy',
      width: 240,
      render: (_: any, row: any) => renderTherapyCell(row),
    },
    {
      title: '当前给药方案',
      key: 'regimen',
      width: 200,
      render: (_: any, row: any) => renderRegimenCell(row),
    },
    {
      title: '暴露预测',
      key: 'exposure',
      width: 190,
      render: (_: any, row: any) => renderExposureCell(row),
    },
    {
      title: 'PK/PD 靶值分析',
      key: 'pkpd',
      width: 280,
      render: (_: any, row: any) => renderPkpdCell(row),
    },
    {
      title: '操作人',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      width: 120,
      render: (value: string) => value || '-',
    },
    {
      title: '时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 168,
      render: (value: string) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 220,
      fixed: 'right' as const,
      render: (_: any, row: any) => (
        <Space size={4}>
          <Button size="small" icon={<EyeOutlined />} onClick={() => setDetailRecord(row)}>
            详情
          </Button>
          <Button size="small" icon={<ThunderboltOutlined />} onClick={() => handleReplayOptimization(row)}>
            复现
          </Button>
          <Button
            type="link"
            danger
            size="small"
            icon={<DeleteOutlined />}
            loading={deletingRowId === row.id}
            onClick={() => handleDeleteRecords([row.id], { singleRowId: row.id })}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  const detailSections = detailRecord?.structured_sections || {};
  const regimenRows = Array.isArray(detailSections.current_regimen?.regimen_history)
    ? detailSections.current_regimen.regimen_history
    : [];
  const tdmRows = Array.isArray(detailSections.tdm_results) ? detailSections.tdm_results : [];
  const pkParameterRows = Object.entries(detailSections.individual_pk_parameters?.pk_parameters || {});
  const pkpdRows = Object.entries(detailSections.pkpd_target_analysis?.pkpd_results || {});

  const rawStructuredItems = [
    { key: 'case_intake', label: '新建病例输入与衍生信息', value: detailSections.case_intake },
    { key: 'dose_snapshot', label: '剂量计算患者快照', value: detailSections.dose_patient_snapshot },
    { key: 'model_info', label: '模型信息', value: detailSections.model_info },
    { key: 'recommended', label: '给药方案推荐', value: detailSections.recommended_regimen },
    { key: 'exposure', label: '暴露预测', value: detailSections.exposure_prediction },
    { key: 'pkpd', label: 'PK/PD 靶值分析', value: detailSections.pkpd_target_analysis },
  ].map((section) => ({
    key: section.key,
    label: section.label,
    children: (
      <pre className="admin-data-query__json-block">
        {JSON.stringify(section.value || {}, null, 2)}
      </pre>
    ),
  }));

  return (
    <div className="admin-data-query__shell">
      <Card bordered={false} style={ADMIN_SURFACE_STYLE} className="admin-data-query__topbar">
        <div className="admin-data-query__topbar-row">
          <div className="admin-data-query__title-block">
            <div className="admin-data-query__section-title">科研数据查询与导出</div>
            <Text type="secondary">结构化导出与 NONMEM 导出共享同一套筛选条件，结果区按页展示，详情通过抽屉查看。</Text>
          </div>
          <div className="admin-data-query__actions">
            <Button icon={<ReloadOutlined />} onClick={() => fetchData(params)} loading={loading}>
              刷新
            </Button>
            <Dropdown
              menu={{
                items: [
                  { key: 'excel', label: '导出结构化结果', onClick: handleExport },
                  { key: 'nonmem', label: '导出 NONMEM/nlmixr2', onClick: handleExportNonmem },
                ],
              }}
            >
              <Button type="primary" icon={<ExportOutlined />}>
                导出 <DownloadOutlined />
              </Button>
            </Dropdown>
          </div>
        </div>

        <div className="admin-data-query__summary-strip">
          {[
            { label: '记录', value: summary.total.toLocaleString('zh-CN') },
            { label: '患者', value: summary.distinctPatients.toLocaleString('zh-CN') },
            { label: '药物', value: summary.distinctDrugs.toLocaleString('zh-CN') },
            { label: '初始方案', value: summary.initialCount.toLocaleString('zh-CN') },
            { label: '优化方案', value: summary.optimizeCount.toLocaleString('zh-CN') },
          ].map((item) => (
            <div key={item.label} className="admin-data-query__summary-pill">
              <span className="admin-data-query__summary-pill-label">{item.label}</span>
              <span className="admin-data-query__summary-pill-value">{item.value}</span>
            </div>
          ))}
        </div>
      </Card>

      <div className="admin-data-query__workspace">
        <Card
          bordered={false}
          size="small"
          style={ADMIN_SURFACE_STYLE}
          className="admin-data-query__filters-card"
          title={<Text strong>筛选条件</Text>}
        >
          <div className="admin-data-query__filters">
            <div className="admin-data-query__filter-layout">
              <div className="admin-data-query__filter-main">
                <div className="admin-data-query__filter-grid">
                  <div className="admin-data-query__filter-field">
                    <span className="admin-data-query__filter-label">病例号 / 住院号</span>
                    <Input
                      placeholder="输入病例号或住院号"
                      value={draftFilters.patient_code}
                      onChange={(e) => setDraftFilters((prev: any) => ({ ...prev, patient_code: e.target.value }))}
                      onPressEnter={handleSearch}
                    />
                  </div>

                  <div className="admin-data-query__filter-field">
                    <span className="admin-data-query__filter-label">药物</span>
                    <Select
                      placeholder="全部药物"
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      value={draftFilters.drug_name}
                      onChange={(value) => setDraftFilters((prev: any) => ({ ...prev, drug_name: value }))}
                      options={drugOptions}
                    />
                  </div>

                  <div className="admin-data-query__filter-field">
                    <span className="admin-data-query__filter-label">计算类型</span>
                    <Select
                      placeholder="全部类型"
                      allowClear
                      value={draftFilters.calculation_type}
                      onChange={(value) => setDraftFilters((prev: any) => ({ ...prev, calculation_type: value }))}
                      options={Object.entries(_CALC_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
                    />
                  </div>

                  <div className="admin-data-query__filter-field">
                    <span className="admin-data-query__filter-label">模型</span>
                    <Select
                      placeholder="全部模型"
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      value={draftFilters.model_id}
                      onChange={(value) => setDraftFilters((prev: any) => ({ ...prev, model_id: value }))}
                      options={modelOptions}
                    />
                  </div>

                  <div className="admin-data-query__filter-field">
                    <span className="admin-data-query__filter-label">操作人</span>
                    <Select
                      placeholder="全部操作人"
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      value={draftFilters.created_by}
                      onChange={(value) => setDraftFilters((prev: any) => ({ ...prev, created_by: value }))}
                      options={operatorOptions}
                    />
                  </div>
                </div>
              </div>

              <div className="admin-data-query__filter-toolbar">
                <div className="admin-data-query__filter-field">
                  <span className="admin-data-query__filter-label">时间范围</span>
                  <RangePicker
                    className="admin-data-query__date-range"
                    value={dateRange}
                    onChange={(value) => setDateRange(value)}
                  />
                </div>

                <div className="admin-data-query__filter-rail">
                  <div className="admin-data-query__filter-actions">
                    <div className="admin-data-query__filter-buttons">
                      <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} loading={loading}>
                        查询
                      </Button>
                      <Button onClick={handleReset}>重置</Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {activeFilterTags.length > 0 ? (
              <div className="admin-data-query__filter-hint">
                <div className="admin-data-query__active-tags">
                  {activeFilterTags.map((label) => <Tag key={label}>{label}</Tag>)}
                </div>
              </div>
            ) : null}
          </div>
        </Card>

        <Card
          bordered={false}
          size="small"
          style={ADMIN_SURFACE_STYLE}
          className="admin-data-query__results-card admin-data-query__table-card"
          title={<Text strong>结构化结果</Text>}
          extra={(
            <Space size={12}>
              <Text type="secondary">
                当前显示 {summary.currentPageCount} 条
                {currentSelectionCount > 0 ? `，已选 ${currentSelectionCount} 条` : ''}
              </Text>
              <Dropdown
                menu={{
                  items: [
                    { key: 'selected-structured', label: '导出选中结构化结果', onClick: () => void handleExportSelected('structured') },
                    { key: 'selected-nonmem', label: '导出选中 NONMEM', onClick: () => void handleExportSelected('nonmem') },
                  ],
                }}
                disabled={currentSelectionCount === 0}
              >
                <Button icon={<DownloadOutlined />} disabled={currentSelectionCount === 0}>
                  导出选中 <DownloadOutlined />
                </Button>
              </Dropdown>
              <Button
                danger
                icon={<DeleteOutlined />}
                disabled={currentSelectionCount === 0}
                loading={deleteSubmitting}
                onClick={() => handleDeleteRecords(selectedRowKeys.map(String))}
              >
                删除选中
              </Button>
            </Space>
          )}
        >
          <div className="admin-data-query__table-summary">
            <Text type="secondary">列表聚焦病例、模型、当前方案、暴露预测与 PK/PD 关键结果，完整结构化内容在详情中查看。</Text>
            {summary.pkFormulaCount > 0 ? <Tag color="default">含 PK 公式记录 {summary.pkFormulaCount} 条</Tag> : null}
          </div>

          <Table
            className="admin-data-query__table"
            columns={columns}
            dataSource={rows}
            rowKey="id"
            rowSelection={{
              selectedRowKeys,
              onChange: (keys) => setSelectedRowKeys(keys),
              preserveSelectedRowKeys: false,
            }}
            loading={loading}
            locale={tableLocale}
            scroll={{ x: 1500, y: tableScrollY }}
            pagination={{
              current: data?.page,
              pageSize: data?.page_size,
              total: data?.total,
              showSizeChanger: true,
              pageSizeOptions: ['20', '50', '100'],
              showTotal: (total) => `共 ${total} 条`,
              onChange: (page, pageSize) => {
                const next = { ...params, page, page_size: pageSize };
                setParams(next);
                void fetchData(next);
              },
            }}
            size="small"
          />
        </Card>
      </div>

      <Drawer
        open={!!detailRecord}
        width={920}
        onClose={() => setDetailRecord(null)}
        className="admin-data-query__drawer"
        extra={detailRecord ? (
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            onClick={() => handleReplayOptimization(detailRecord)}
          >
            复现优化
          </Button>
        ) : null}
        title={detailRecord ? (
          <div className="admin-data-query__drawer-title">
            <div className="admin-data-query__section-title">结构化详情</div>
            <Text type="secondary">
              {detailRecord.patient_code || '-'} · {getDrugLabel(detailRecord.drug_name)} · {detailRecord.model_display_name || detailRecord.model_id || '未记录模型'}
            </Text>
          </div>
        ) : null}
      >
        {detailRecord ? (
          <div className="admin-data-query__drawer-content">
            <div className="admin-data-query__drawer-grid">
              <div className="admin-data-query__info-card">
                <Text strong>病例概览</Text>
                <Descriptions column={1} size="small" style={{ marginTop: 12 }}>
                  <Descriptions.Item label="病例号">{detailRecord.patient_code || '-'}</Descriptions.Item>
                  <Descriptions.Item label="诊断">{detailRecord.diagnosis || '-'}</Descriptions.Item>
                  <Descriptions.Item label="药物">{getDrugLabel(detailRecord.drug_name)}</Descriptions.Item>
                  <Descriptions.Item label="计算类型">{_CALC_TYPE_LABELS[detailRecord.calculation_type] || detailRecord.calculation_type || '-'}</Descriptions.Item>
                  <Descriptions.Item label="模型">{detailRecord.model_display_name || detailRecord.model_id || '-'}</Descriptions.Item>
                  <Descriptions.Item label="操作人">{detailRecord.created_by_name || '-'}</Descriptions.Item>
                </Descriptions>
              </div>

              <div className="admin-data-query__info-card">
                <Text strong>核心结果</Text>
                <Descriptions column={1} size="small" style={{ marginTop: 12 }}>
                  <Descriptions.Item label="推荐剂量">{formatNumberCell(detailRecord.recommended_dose)} mg</Descriptions.Item>
                  <Descriptions.Item label="推荐间隔">q{formatNumberCell(detailRecord.recommended_interval, 0)}h</Descriptions.Item>
                  <Descriptions.Item label="输注时长">{formatNumberCell(detailRecord.recommended_infusion_h, 1)} h</Descriptions.Item>
                  <Descriptions.Item label="AUC24">{formatNumberCell(detailRecord.auc24)}</Descriptions.Item>
                  <Descriptions.Item label="预测 Cmin">{formatNumberCell(detailRecord.predicted_cmin)}</Descriptions.Item>
                  <Descriptions.Item label="预测 Cmax">{formatNumberCell(detailRecord.predicted_cmax)}</Descriptions.Item>
                </Descriptions>
              </div>
            </div>

            <div className="admin-data-query__info-card">
              <Text strong>当前给药方案</Text>
              <Table
                size="small"
                pagination={false}
                dataSource={regimenRows}
                rowKey={(_row: any, index?: number) => `${index || 0}`}
                locale={tableLocale}
                columns={[
                  { title: '起始时间', dataIndex: 'start_time', key: 'start_time', render: (value: string) => value || '-' },
                  { title: '剂量 (mg)', dataIndex: 'dose_mg', key: 'dose_mg', render: (value: any) => formatNumberCell(value) },
                  { title: '给药间隔 (h)', dataIndex: 'interval_h', key: 'interval_h', render: (value: any) => formatNumberCell(value, 0) },
                  { title: '输注时间 (h)', dataIndex: 'infusion_h', key: 'infusion_h', render: (value: any) => formatNumberCell(value, 1) },
                  { title: '给药次数', dataIndex: 'dose_count', key: 'dose_count', render: (value: any) => formatNumberCell(value, 0) },
                ]}
                style={{ marginTop: 12 }}
              />
            </div>

            <div className="admin-data-query__info-card">
              <Text strong>TDM 监测结果</Text>
              <Table
                size="small"
                pagination={false}
                dataSource={tdmRows}
                rowKey={(_row: any, index?: number) => `${index || 0}`}
                locale={tableLocale}
                columns={[
                  { title: '采样时间', dataIndex: 'sample_time', key: 'sample_time', render: (_value: string, row: any) => formatTdmSampleTime(row) },
                  { title: '距上次给药后 (h)', dataIndex: 'time_after_dose_h', key: 'time_after_dose_h', render: (value: any) => formatNumberCell(value, 2) },
                  { title: '测定结果 (mg/L)', dataIndex: 'concentration_mg_l', key: 'concentration_mg_l', render: (value: any) => formatNumberCell(value, 2) },
                  { title: '第几次给药后', dataIndex: 'dose_count', key: 'dose_count', render: (value: any) => formatNumberCell(value, 0) },
                ]}
                style={{ marginTop: 12 }}
              />
            </div>

            <div className="admin-data-query__drawer-grid">
              <div className="admin-data-query__info-card">
                <Text strong>个体化 PK 参数</Text>
                <Table
                  size="small"
                  pagination={false}
                  dataSource={pkParameterRows.map(([key, value]) => ({ key, value }))}
                  rowKey="key"
                  locale={tableLocale}
                  columns={[
                    { title: '参数', dataIndex: 'key', key: 'key' },
                    { title: '数值', dataIndex: 'value', key: 'value', render: (value: any) => formatNumberCell(value, 4) },
                  ]}
                  style={{ marginTop: 12 }}
                />
              </div>

              <div className="admin-data-query__info-card">
                <Text strong>PK/PD 靶值分析</Text>
                <Table
                  size="small"
                  pagination={false}
                  dataSource={pkpdRows.map(([method, result]) => ({ method, ...(typeof result === 'object' && result !== null ? result as any : { value: result }) }))}
                  rowKey="method"
                  locale={tableLocale}
                  columns={[
                    { title: '指标', dataIndex: 'method', key: 'method' },
                    { title: '结果', dataIndex: 'value', key: 'value', render: (value: any) => formatNumberCell(value, 2) },
                    { title: '单位', dataIndex: 'unit', key: 'unit', render: (value: string) => value || '-' },
                    {
                      title: '是否达标',
                      dataIndex: 'target_met',
                      key: 'target_met',
                      render: (value: boolean | null) => (
                        value == null ? '-' : <Tag color={value ? 'success' : 'error'}>{value ? '达标' : '未达标'}</Tag>
                      ),
                    },
                  ]}
                  style={{ marginTop: 12 }}
                />
              </div>
            </div>

            <div className="admin-data-query__info-card">
              <Text strong>AI 辅助解读</Text>
              <Paragraph style={{ marginTop: 12, marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                {detailSections.ai_assisted_interpretation?.clinical_interpretation || '暂无 AI 辅助解读'}
              </Paragraph>
            </div>

            <div className="admin-data-query__info-card">
              <Text strong>完整结构化 JSON</Text>
              <Collapse ghost items={rawStructuredItems} style={{ marginTop: 12 }} />
            </div>
          </div>
        ) : null}
      </Drawer>
    </div>
  );
};

// ================================================================
// Audit Logs Tab
// ================================================================
const AuditSearchTab: React.FC = () => {
  const [logs, setLogs] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<Record<string, any>>({ skip: 0, limit: 50 });
  const [draft, setDraft] = useState<Record<string, any>>({});

  const fetchLogs = useCallback(async (nextFilters = filters) => {
    setLoading(true);
    try {
      const res = await adminAPI.listAuditLogs(nextFilters);
      setLogs(res.items || []);
      setTotal(res.total || 0);
    } catch (error) {
      message.error(safeErrorMessage(error, '加载审计日志失败'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { void fetchLogs(); }, [fetchLogs]);

  const applyFilters = () => {
    const next: Record<string, any> = { ...draft, ...rangeParams(draft.dateRange), skip: 0, limit: filters.limit || 50 };
    delete next.dateRange;
    Object.keys(next).forEach((key) => (next[key] == null || next[key] === '') && delete next[key]);
    setFilters(next);
    void fetchLogs(next);
  };

  const resetFilters = () => {
    const next = { skip: 0, limit: filters.limit || 50 };
    setDraft({});
    setFilters(next);
    void fetchLogs(next);
  };

  return (
    <div className="admin-data-query__shell">
      <Card bordered={false} style={ADMIN_SURFACE_STYLE}>
        <div className="admin-data-query__topbar-row">
          <div>
            <div className="admin-data-query__section-title">审计查询</div>
            <Text type="secondary">追踪后台与关键 API 操作，支持按操作、资源、用户、IP、关键词和时间范围检索。</Text>
          </div>
          <Button icon={<ReloadOutlined />} loading={loading} onClick={() => fetchLogs(filters)}>刷新</Button>
        </div>
      </Card>

      <Card bordered={false} style={ADMIN_SURFACE_STYLE} title="查询条件">
        <Space wrap>
          <Input
            allowClear
            placeholder="关键词：操作 / 详情 / 用户"
            style={{ width: 240 }}
            value={draft.keyword}
            onChange={(event) => setDraft((prev) => ({ ...prev, keyword: event.target.value }))}
            onPressEnter={applyFilters}
          />
          <Input
            allowClear
            placeholder="操作"
            style={{ width: 160 }}
            value={draft.action}
            onChange={(event) => setDraft((prev) => ({ ...prev, action: event.target.value }))}
            onPressEnter={applyFilters}
          />
          <Input
            allowClear
            placeholder="资源类型"
            style={{ width: 160 }}
            value={draft.resource_type}
            onChange={(event) => setDraft((prev) => ({ ...prev, resource_type: event.target.value }))}
            onPressEnter={applyFilters}
          />
          <Input
            allowClear
            placeholder="IP"
            style={{ width: 150 }}
            value={draft.ip_address}
            onChange={(event) => setDraft((prev) => ({ ...prev, ip_address: event.target.value }))}
            onPressEnter={applyFilters}
          />
          <RangePicker value={draft.dateRange} onChange={(value) => setDraft((prev) => ({ ...prev, dateRange: value }))} />
          <Button type="primary" icon={<SearchOutlined />} loading={loading} onClick={applyFilters}>查询</Button>
          <Button onClick={resetFilters}>重置</Button>
        </Space>
      </Card>

      <Card bordered={false} style={ADMIN_SURFACE_STYLE} title="审计日志">
        <Table
          rowKey="id"
          columns={[
            { title: '操作', dataIndex: 'action', width: 220, ellipsis: true },
            { title: '资源类型', dataIndex: 'resource_type', width: 120 },
            { title: '用户', dataIndex: 'username', width: 160, ellipsis: true },
            { title: 'IP', dataIndex: 'ip_address', width: 130 },
            { title: '详情', dataIndex: 'details', ellipsis: true, render: (value: any) => value ? <Text code>{JSON.stringify(value).slice(0, 120)}</Text> : '-' },
            { title: '时间', dataIndex: 'created_at', width: 170, render: formatAdminDateTime },
          ]}
          dataSource={logs}
          loading={loading}
          size="small"
          scroll={{ x: 980 }}
          pagination={{
            total,
            pageSize: filters.limit || 50,
            current: Math.floor((filters.skip || 0) / (filters.limit || 50)) + 1,
            onChange: (page, pageSize) => {
              const next = { ...filters, skip: (page - 1) * pageSize, limit: pageSize };
              setFilters(next);
              void fetchLogs(next);
            },
          }}
        />
      </Card>
    </div>
  );
};

const AuditTab: React.FC = () => {
  const [logs, setLogs] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchLogs = async (skip = 0) => {
    setLoading(true);
    try { const res = await adminAPI.listAuditLogs({ skip, limit: 50 }); setLogs(res.items); setTotal(res.total); }
    catch { message.error('加载审计日志失败'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchLogs(); }, []);

  const columns = [
    { title: '操作', dataIndex: 'action', key: 'action', width: 250, ellipsis: true },
    { title: '资源类型', dataIndex: 'resource_type', key: 'rt', width: 100 },
    { title: '用户', dataIndex: 'username', key: 'uid', width: 120, ellipsis: true },
    { title: 'IP', dataIndex: 'ip_address', key: 'ip', width: 120 },
    { title: '时间', dataIndex: 'created_at', key: 'cat', width: 160, render: (v: string) => v ? new Date(v).toLocaleString('zh-CN') : '-' },
  ];

  return (
    <Table columns={columns} dataSource={logs} rowKey="id" loading={loading}
      pagination={{ total, pageSize: 50, onChange: (p) => fetchLogs((p - 1) * 50) }} size="small" />
  );
};

// ================================================================
// Announcements Tab
// ================================================================
// ================================================================
// Landing Page Tab
// ================================================================
const LandingPageTab: React.FC = () => {
  const [activeSection, setActiveSection] = useState('hero_slides');
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState<any>({});

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    setLoading(true);
    try {
      const data = await adminAPI.getConfig();
      console.log('Admin config data:', data);

      // data 是对象 {key: value}，不是数组
      const landingKeys = new Set([
        'navbar_links',
        'landing_hero_slides',
        'landing_banners',
        'landing_hero',
        'landing_trust_bar',
        'landing_features',
        'landing_workflow',
        'landing_testimonials',
        'landing_resources',
        'footer_products',
      ]);

      const mapped: any = {};
      Object.keys(data).forEach((key) => {
        if (landingKeys.has(key)) {
          mapped[key] = data[key];
        }
      });

      console.log('Landing content:', mapped);
      setContent(mapped);
    } catch (error: any) {
      console.error('加载失败:', error);
      message.error(`加载失败: ${error?.response?.data?.detail || error.message || '未知错误'}`);
    } finally {
      setLoading(false);
    }
  };

  const saveSection = async (key: string, value: any) => {
    console.log('[AdminPanel] Saving section:', key, 'with value:', value);
    console.log('[AdminPanel] Value type:', typeof value, 'isArray:', Array.isArray(value));
    try {
      const result = await adminAPI.updateConfig(key, value);
      console.log('[AdminPanel] Save result:', result);
      message.success('保存成功');
      await loadContent();
      console.log('[AdminPanel] Content reloaded successfully');
    } catch (error: any) {
      console.error('保存失败:', error);
      console.error('Error details:', error?.response?.data);
      message.error(`保存失败: ${error?.response?.data?.detail || error.message || '未知错误'}`);
      throw error;
    }
  };

  return (
    <div>
      <Menu
        mode="horizontal"
        selectedKeys={[activeSection]}
        onSelect={(e) => setActiveSection(e.key)}
        style={{ marginBottom: 16 }}
      >
        <Menu.Item key="navbar">NavBar 导航</Menu.Item>
        <Menu.Item key="hero_slides">Hero 轮播</Menu.Item>
        <Menu.Item key="trust_bar">信任指标</Menu.Item>
        <Menu.Item key="features">功能特性</Menu.Item>
        <Menu.Item key="workflow">工作流程</Menu.Item>
        <Menu.Item key="testimonials">用户评价</Menu.Item>
        <Menu.Item key="resources">资源链接</Menu.Item>
        <Menu.Item key="footer_products">Footer 产品</Menu.Item>
      </Menu>

      <Spin spinning={loading}>
        {activeSection === 'navbar' && (
          <NavBarEditor
            data={content.navbar_links || []}
            onSave={(v) => saveSection('navbar_links', v)}
          />
        )}
        {activeSection === 'hero_slides' && (
          <HeroSlidesEditor
            data={content.landing_hero_slides || content.landing_banners || []}
            onSave={(v) => saveSection('landing_hero_slides', v)}
          />
        )}
        {activeSection === 'trust_bar' && (
          <TrustBarEditor
            data={content.landing_trust_bar || []}
            onSave={(v) => saveSection('landing_trust_bar', v)}
          />
        )}
        {activeSection === 'features' && (
          <FeaturesEditor
            data={content.landing_features || []}
            onSave={(v) => saveSection('landing_features', v)}
          />
        )}
        {activeSection === 'workflow' && (
          <WorkflowEditor
            data={content.landing_workflow || []}
            onSave={(v) => saveSection('landing_workflow', v)}
          />
        )}
        {activeSection === 'testimonials' && (
          <TestimonialsEditor
            data={content.landing_testimonials || []}
            onSave={(v) => saveSection('landing_testimonials', v)}
          />
        )}
        {activeSection === 'resources' && (
          <ResourcesEditor
            data={content.landing_resources || []}
            onSave={(v) => saveSection('landing_resources', v)}
          />
        )}
        {activeSection === 'footer_products' && (
          <FooterProductsEditor
            data={content.footer_products || []}
            onSave={(v) => saveSection('footer_products', v)}
          />
        )}
      </Spin>
    </div>
  );
};

// ================================================================
// Announcements Tab
// ================================================================
const AnnouncementsTab: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form] = Form.useForm();

  const fetchAnn = async () => {
    setLoading(true);
    try { const res = await adminAPI.listAnnouncements(); setItems(res.items); }
    catch { message.error('加载公告失败'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAnn(); }, []);

  const openCreate = () => { setEditing(null); form.resetFields(); setModal(true); };
  const openEdit = (record: any) => {
    setEditing(record);
    form.setFieldsValue({ title: record.title, content: record.content, type: record.type });
    setModal(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editing) {
        await adminAPI.updateAnnouncement(editing.id, values);
        message.success('已更新');
      } else {
        await adminAPI.createAnnouncement(values);
        message.success('已创建');
      }
      setModal(false); form.resetFields(); setEditing(null); fetchAnn();
    } catch { message.error(editing ? '更新失败' : '创建失败'); }
  };

  const columns = [
    { title: '标题', dataIndex: 'title', key: 'title', width: 200 },
    { title: '内容', dataIndex: 'content', key: 'content', ellipsis: true },
    {
      title: '类型', dataIndex: 'type', key: 'type', width: 80,
      render: (v: string) => <Tag color={v === 'warning' ? 'orange' : v === 'error' ? 'red' : 'blue'}>{v}</Tag>
    },
    {
      title: '状态', dataIndex: 'is_active', key: 'active', width: 80,
      render: (v: boolean) => <Badge status={v ? 'success' : 'default'} text={v ? '启用' : '禁用'} />
    },
    { title: '创建时间', dataIndex: 'created_at', key: 'cat', width: 150, render: (v: string) => v ? new Date(v).toLocaleString('zh-CN') : '-' },
    {
      title: '操作', key: 'ops', width: 180,
      render: (_: any, r: any) => (
        <Space size="small">
          <Button size="small" onClick={() => openEdit(r)}>编辑</Button>
          <Button size="small" onClick={async () => { await adminAPI.toggleAnnouncement(r.id); fetchAnn(); }}>{r.is_active ? '禁用' : '启用'}</Button>
          <Popconfirm title="确认删除?" onConfirm={async () => { await adminAPI.deleteAnnouncement(r.id); fetchAnn(); }}>
            <Button size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} style={{ marginBottom: 16 }}>发布公告</Button>
      <Table columns={columns} dataSource={items} rowKey="id" loading={loading} pagination={false} size="small" />
      <Modal title={editing ? '编辑公告' : '发布公告'} open={modal} onOk={handleSubmit} onCancel={() => { setModal(false); setEditing(null); }}>
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="标题" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="content" label="内容" rules={[{ required: true }]}><Input.TextArea rows={4} /></Form.Item>
          <Form.Item name="type" label="类型" initialValue="info">
            <Select options={[{ value: 'info', label: '通知' }, { value: 'warning', label: '警告' }, { value: 'success', label: '成功' }, { value: 'error', label: '错误' }]}
             />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// ================================================================
// Plans Tab
// ================================================================
const PlansTab: React.FC = () => {
  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<any>(null);
  const [form] = Form.useForm();

  const fetchPlans = async () => {
    setLoading(true);
    try { const res = await adminAPI.listPlans(); setPlans(res.items); }
    catch { message.error('加载套餐失败'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPlans(); }, []);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      values.price_monthly = Number(values.price_monthly) || 0;
      values.price_yearly = Number(values.price_yearly) || 0;
      values.sort_order = Number(values.sort_order) || 0;
      if (editingPlan) await adminAPI.updatePlan(editingPlan.id, values);
      else await adminAPI.createPlan(values);
      message.success('已保存'); setModal(false); fetchPlans();
    } catch (err: any) { message.error(err?.response?.data?.detail || '保存失败'); }
  };

  const handleDelete = async (planId: string) => {
    try { await adminAPI.deletePlan(planId); message.success('已删除'); fetchPlans(); }
    catch (err: any) { message.error(err?.response?.data?.detail || '删除失败'); }
  };

  const openEdit = (plan: any) => {
    setEditingPlan(plan);
    form.setFieldsValue({ ...plan, features: plan.features || {}, limits: plan.limits || {} });
    setModal(true);
  };

  const openCreate = () => { setEditingPlan(null); form.resetFields(); setModal(true); };

  const columns = [
    { title: '名称', dataIndex: 'name', key: 'name', width: 100 },
    { title: '显示名', dataIndex: 'display_name', key: 'dn', width: 100 },
    { title: '月价', dataIndex: 'price_monthly', key: 'pm', width: 80, render: (v: number) => `¥${v}` },
    { title: '年价', dataIndex: 'price_yearly', key: 'py', width: 80, render: (v: number) => `¥${v}` },
    {
      title: '状态', dataIndex: 'is_active', key: 'active', width: 80,
      render: (v: boolean) => <Tag color={v ? 'green' : 'default'}>{v ? '启用' : '禁用'}</Tag>
    },
    { title: '排序', dataIndex: 'sort_order', key: 'so', width: 60 },
    {
      title: '功能', dataIndex: 'features', key: 'features', width: 200,
      render: (v: any) => v ? Object.entries(v).filter(([, val]) => val).map(([k]) => <Tag key={k} color="blue">{k}</Tag>) : '-'
    },
    {
      title: '操作', key: 'ops', width: 140,
      render: (_: any, r: any) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>编辑</Button>
          <Popconfirm title="确认删除?" onConfirm={() => handleDelete(r.id)}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} style={{ marginBottom: 16 }}>新增套餐</Button>
      <Table columns={columns} dataSource={plans} rowKey="id" loading={loading} pagination={false} size="small" />
      <Modal title={editingPlan ? '编辑套餐' : '新增套餐'} open={modal} onOk={handleSave} onCancel={() => setModal(false)} width={600}>
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}><Form.Item name="name" label="名称(唯一标识)" rules={[{ required: true }]}><Input disabled={!!editingPlan} /></Form.Item></Col>
            <Col span={12}><Form.Item name="display_name" label="显示名" rules={[{ required: true }]}><Input /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}><Form.Item name="price_monthly" label="月价(¥)" initialValue={0}><Input type="number" /></Form.Item></Col>
            <Col span={8}><Form.Item name="price_yearly" label="年价(¥)" initialValue={0}><Input type="number" /></Form.Item></Col>
            <Col span={8}><Form.Item name="sort_order" label="排序" initialValue={0}><Input type="number" /></Form.Item></Col>
          </Row>
          <Form.Item name="is_active" label="启用" valuePropName="checked" initialValue={true}><Switch /></Form.Item>
          <Form.Item label="功能">
            <Table size="small" pagination={false} dataSource={[
              { key: 'ai_assistant', label: 'AI助手' },
              { key: 'report_export', label: '报告导出' },
              { key: 'tdm_optimize', label: 'TDM优化' },
              { key: 'multi_drug', label: '多药物支持' },
              { key: 'custom_models', label: '自定义模型' },
              { key: 'api_access', label: 'API访问' },
            ]} columns={[
              { title: '功能', dataIndex: 'label', width: 150 },
              {
                title: '启用', dataIndex: 'key', width: 80, render: (k: string) => (
                  <Form.Item name={['features', k]} valuePropName="checked" noStyle initialValue={false}><Switch size="small" /></Form.Item>
                )
              },
            ]} />
          </Form.Item>
          <Form.Item label="限制" dependencies={[['features', 'multi_drug']]}>
            {() => {
              const multiDrugOn = form.getFieldValue(['features', 'multi_drug']);
              return (
                <Table size="small" pagination={false} dataSource={[
                  { key: 'max_patients_month', label: '每月最大患者数' },
                  { key: 'max_calculations_month', label: '每月最大计算次数' },
                  { key: 'max_members', label: '最大成员数' },
                  { key: 'max_models', label: '最大模型数' },
                  { key: 'max_drugs', label: '可激活药物数' },
                ]} columns={[
                  {
                    title: '限制项', dataIndex: 'label', width: 180,
                    render: (label: string, row: any) => (
                      <span>
                        {label}
                        {row.key === 'max_drugs' && !multiDrugOn && (
                          <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>(需开启多药物支持)</Text>
                        )}
                      </span>
                    ),
                  },
                  {
                    title: '数值 (-1=无限)', dataIndex: 'key', render: (k: string) => (
                      <Form.Item name={['limits', k]} noStyle initialValue={0}>
                        <InputNumber size="small" style={{ width: 120 }} min={-1} disabled={k === 'max_drugs' && !multiDrugOn} />
                      </Form.Item>
                    )
                  },
                ]} />
              );
            }}
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// ================================================================
// Activation Codes Tab
// ================================================================
const ACTIVATION_STATUS_META: Record<string, { color: string; label: string }> = {
  unused: { color: 'green', label: '未使用' },
  used: { color: 'blue', label: '已兑换' },
  expired: { color: 'default', label: '已过期' },
  revoked: { color: 'red', label: '已注销' },
};

const ActivationCodesUnifiedTab: React.FC = () => {
  const [codes, setCodes] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<Record<string, any>>({ skip: 0, limit: 50 });
  const [draft, setDraft] = useState<Record<string, any>>({});
  const [plans, setPlans] = useState<any[]>([]);
  const [genModal, setGenModal] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [genResult, setGenResult] = useState<any>(null);
  const [editingCode, setEditingCode] = useState<any>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  const fetchData = useCallback(async (nextFilters = filters) => {
    setLoading(true);
    try {
      const [codesRes, statsRes] = await Promise.all([
        adminAPI.listActivationCodes(nextFilters),
        adminAPI.activationCodeStats(),
      ]);
      setCodes(codesRes.items || []);
      setTotal(codesRes.total || 0);
      setStats(statsRes);
    } catch (error) {
      message.error(safeErrorMessage(error, '加载激活码失败'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { void fetchData(); }, [fetchData]);
  useEffect(() => {
    adminAPI.listPlans().then((res: any) => setPlans(res.items || res || [])).catch(() => setPlans([]));
  }, []);

  const applyFilters = () => {
    const next: Record<string, any> = { ...draft, ...rangeParams(draft.dateRange), skip: 0, limit: filters.limit || 50 };
    delete next.dateRange;
    Object.keys(next).forEach((key) => (next[key] == null || next[key] === '') && delete next[key]);
    setFilters(next);
    void fetchData(next);
  };

  const resetFilters = () => {
    const next = { skip: 0, limit: filters.limit || 50 };
    setDraft({});
    setFilters(next);
    void fetchData(next);
  };

  const handleGenerate = async () => {
    try {
      const values = await form.validateFields();
      setGenLoading(true);
      const payload = {
        ...values,
        expires_at: values.expires_at ? values.expires_at.format('YYYY-MM-DD') : undefined,
      };
      const res = await adminAPI.generateActivationCodes(payload);
      setGenResult(res);
      message.success(`已生成 ${res.count} 个激活码`);
      void fetchData(filters);
    } catch (error: any) {
      if (!error?.errorFields) message.error(safeErrorMessage(error, '生成失败'));
    } finally {
      setGenLoading(false);
    }
  };

  const openEdit = (record: any) => {
    setEditingCode(record);
    editForm.setFieldsValue({
      duration_days: record.duration_days,
      expires_at: record.expires_at ? dayjs(record.expires_at) : null,
      source: record.source,
      note: record.note,
    });
  };

  const submitCodeUpdate = async (action: 'update' | 'revoke' | 'restore', record = editingCode) => {
    if (!record) return;
    try {
      setEditLoading(true);
      const values = action === 'update' ? await editForm.validateFields() : {};
      await adminAPI.updateActivationCode(record.id, {
        action,
        ...values,
        expires_at: values.expires_at ? values.expires_at.format('YYYY-MM-DD') : values.expires_at,
      });
      message.success(action === 'revoke' ? '激活码已注销' : action === 'restore' ? '激活码已重新激活' : '激活码已更新');
      setEditingCode(null);
      void fetchData(filters);
    } catch (error: any) {
      if (!error?.errorFields) message.error(safeErrorMessage(error, '操作失败'));
    } finally {
      setEditLoading(false);
    }
  };

  const planOptions = plans.map((plan: any) => ({ value: plan.name, label: plan.display_name || plan.name }));

  return (
    <div className="admin-data-query__shell">
      <Card bordered={false} style={ADMIN_SURFACE_STYLE}>
        <div className="admin-data-query__topbar-row">
          <div>
            <div className="admin-data-query__section-title">激活码统一管理</div>
            <Text type="secondary">收口后台生成发放、第三方渠道、金币兑换与前台用户兑换的激活码生命周期。</Text>
          </div>
          <Space wrap>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { setGenResult(null); form.resetFields(); setGenModal(true); }}>批量生成</Button>
            <Button icon={<ReloadOutlined />} loading={loading} onClick={() => fetchData(filters)}>刷新</Button>
          </Space>
        </div>
        {stats && (
          <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
            <Col xs={12} md={5}><Card size="small"><Statistic title="总数" value={stats.total || 0} /></Card></Col>
            <Col xs={12} md={5}><Card size="small"><Statistic title="未使用" value={stats.unused || 0} valueStyle={{ color: '#16A34A' }} /></Card></Col>
            <Col xs={12} md={5}><Card size="small"><Statistic title="已兑换" value={stats.used || 0} valueStyle={{ color: '#1677FF' }} /></Card></Col>
            <Col xs={12} md={5}><Card size="small"><Statistic title="已过期" value={stats.expired || 0} /></Card></Col>
            <Col xs={12} md={4}><Card size="small"><Statistic title="已注销" value={stats.revoked || 0} valueStyle={{ color: '#DC2626' }} /></Card></Col>
          </Row>
        )}
      </Card>

      <Card bordered={false} style={ADMIN_SURFACE_STYLE} title="查询条件">
        <Space wrap>
          <Input allowClear placeholder="激活码 / 批次 / 备注" style={{ width: 220 }} value={draft.keyword} onChange={(e) => setDraft((prev) => ({ ...prev, keyword: e.target.value }))} onPressEnter={applyFilters} />
          <Select allowClear placeholder="状态" style={{ width: 130 }} value={draft.status} onChange={(value) => setDraft((prev) => ({ ...prev, status: value }))} options={Object.entries(ACTIVATION_STATUS_META).map(([value, meta]) => ({ value, label: meta.label }))} />
          <Select allowClear placeholder="套餐" style={{ width: 150 }} value={draft.plan_name} onChange={(value) => setDraft((prev) => ({ ...prev, plan_name: value }))} options={planOptions} />
          <Input allowClear placeholder="渠道" style={{ width: 140 }} value={draft.source} onChange={(e) => setDraft((prev) => ({ ...prev, source: e.target.value }))} onPressEnter={applyFilters} />
          <RangePicker value={draft.dateRange} onChange={(value) => setDraft((prev) => ({ ...prev, dateRange: value }))} />
          <Button type="primary" icon={<SearchOutlined />} loading={loading} onClick={applyFilters}>查询</Button>
          <Button onClick={resetFilters}>重置</Button>
        </Space>
      </Card>

      <Card bordered={false} style={ADMIN_SURFACE_STYLE} title="激活码列表">
        <Table
          rowKey="id"
          loading={loading}
          dataSource={codes}
          size="small"
          scroll={{ x: 1260 }}
          pagination={{
            total,
            pageSize: filters.limit || 50,
            current: Math.floor((filters.skip || 0) / (filters.limit || 50)) + 1,
            showTotal: (count) => `共 ${count} 条`,
            onChange: (page, pageSize) => {
              const next = { ...filters, skip: (page - 1) * pageSize, limit: pageSize };
              setFilters(next);
              void fetchData(next);
            },
          }}
          columns={[
            { title: '激活码', dataIndex: 'code', width: 190, fixed: 'left' as const, render: (value: string) => <Text copyable code>{value}</Text> },
            { title: '状态', dataIndex: 'status', width: 100, render: (value: string) => { const meta = ACTIVATION_STATUS_META[value] || { color: 'default', label: value }; return <Tag color={meta.color}>{meta.label}</Tag>; } },
            { title: '套餐', dataIndex: 'plan_name', width: 120 },
            { title: '有效天数', dataIndex: 'duration_days', width: 90 },
            { title: '渠道', dataIndex: 'source', width: 120, render: (value: string) => value || '-' },
            { title: '批次', dataIndex: 'batch_id', width: 190, ellipsis: true },
            { title: '生成者', dataIndex: 'created_by_name', width: 150, ellipsis: true, render: (value: string) => value || '-' },
            { title: '兑换用户', dataIndex: 'used_by_name', width: 170, ellipsis: true, render: (_: any, record: any) => record.used_by_name || record.used_by_email || '-' },
            { title: '过期时间', dataIndex: 'expires_at', width: 150, render: (value: string) => value ? dayjs(value).format('YYYY-MM-DD') : '长期' },
            { title: '兑换时间', dataIndex: 'used_at', width: 160, render: formatAdminDateTime },
            {
              title: '操作',
              key: 'ops',
              width: 180,
              fixed: 'right' as const,
              render: (_: any, record: any) => (
                <Space size={6}>
                  <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>管理</Button>
                  {record.status === 'unused' ? (
                    <Popconfirm title="确认注销该未使用激活码？" onConfirm={() => submitCodeUpdate('revoke', record)}>
                      <Button size="small" danger>注销</Button>
                    </Popconfirm>
                  ) : null}
                  {(record.status === 'revoked' || record.status === 'expired') && !record.used_at ? (
                    <Button size="small" onClick={() => submitCodeUpdate('restore', record)}>激活</Button>
                  ) : null}
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title="批量生成激活码"
        open={genModal}
        onCancel={() => setGenModal(false)}
        footer={genResult ? <Button onClick={() => setGenModal(false)}>关闭</Button> : <Button type="primary" loading={genLoading} onClick={handleGenerate}>生成</Button>}
        width={620}
      >
        {genResult ? (
          <div>
            <Alert type="success" message={`已生成 ${genResult.count} 个激活码（批次：${genResult.batch_id}）`} style={{ marginBottom: 12 }} />
            <Input.TextArea value={genResult.codes?.join('\n')} rows={Math.min(genResult.codes?.length || 5, 15)} readOnly style={{ fontFamily: 'monospace' }} />
            <Button size="small" style={{ marginTop: 8 }} onClick={() => { navigator.clipboard.writeText(genResult.codes?.join('\n') || ''); message.success('已复制'); }}>复制全部</Button>
          </div>
        ) : (
          <Form form={form} layout="vertical" initialValues={{ count: 10, duration_days: 365 }}>
            <Form.Item name="plan_name" label="套餐" rules={[{ required: true, message: '请选择套餐' }]}><Select options={planOptions} placeholder="选择套餐" /></Form.Item>
            <Row gutter={16}>
              <Col span={12}><Form.Item name="count" label="数量" rules={[{ required: true }]}><InputNumber min={1} max={500} style={{ width: '100%' }} /></Form.Item></Col>
              <Col span={12}><Form.Item name="duration_days" label="有效天数" rules={[{ required: true }]}><InputNumber min={1} max={3650} style={{ width: '100%' }} /></Form.Item></Col>
            </Row>
            <Form.Item name="expires_at" label="兑换截止日期"><DatePicker style={{ width: '100%' }} /></Form.Item>
            <Form.Item name="source" label="渠道标记"><Input placeholder="如 admin / coin_exchange / conference" /></Form.Item>
            <Form.Item name="note" label="备注"><Input.TextArea rows={2} /></Form.Item>
          </Form>
        )}
      </Modal>

      <Modal
        title={editingCode ? `管理激活码 ${editingCode.code}` : '管理激活码'}
        open={!!editingCode}
        onCancel={() => setEditingCode(null)}
        onOk={() => submitCodeUpdate('update')}
        confirmLoading={editLoading}
        okText="保存"
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="duration_days" label="有效天数" rules={[{ required: true, message: '请输入有效天数' }]}><InputNumber min={1} max={3650} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="expires_at" label="兑换截止日期"><DatePicker style={{ width: '100%' }} allowClear /></Form.Item>
          <Form.Item name="source" label="渠道"><Input /></Form.Item>
          <Form.Item name="note" label="备注"><Input.TextArea rows={3} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

const STATUS_MAP: Record<string, { color: string; label: string }> = {
  unused: { color: 'green', label: '未使用' },
  used: { color: 'blue', label: '已使用' },
  expired: { color: 'default', label: '已过期' },
  revoked: { color: 'red', label: '已作废' },
};

const ActivationCodesTab: React.FC = () => {
  const [codes, setCodes] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [genModal, setGenModal] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [genResult, setGenResult] = useState<any>(null);
  const [plans, setPlans] = useState<any[]>([]);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [codesRes, statsRes] = await Promise.all([
        adminAPI.listActivationCodes(filters),
        adminAPI.activationCodeStats(),
      ]);
      setCodes(codesRes.items || []);
      setStats(statsRes);
    } catch { message.error('加载激活码失败'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [filters]);
  useEffect(() => {
    adminAPI.listPlans().then((r: any) => setPlans(r.items || r || [])).catch(() => { });
  }, []);

  const handleGenerate = async () => {
    try {
      const values = await form.validateFields();
      setGenLoading(true);
      const res = await adminAPI.generateActivationCodes(values);
      setGenResult(res);
      message.success(`已生成 ${res.count} 个激活码`);
      fetchData();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || '生成失败');
    } finally { setGenLoading(false); }
  };

  const columns = [
    {
      title: '激活码', dataIndex: 'code', key: 'code', width: 180,
      render: (v: string) => <Text copyable style={{ fontFamily: 'monospace' }}>{v}</Text>
    },
    { title: '套餐', dataIndex: 'plan_name', key: 'plan', width: 80 },
    { title: '天数', dataIndex: 'duration_days', key: 'days', width: 60 },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 80,
      render: (v: string) => {
        const s = STATUS_MAP[v] || { color: 'default', label: v };
        return <Tag color={s.color}>{s.label}</Tag>;
      },
    },
    { title: '批次', dataIndex: 'batch_id', key: 'batch', width: 180, ellipsis: true },
    { title: '渠道', dataIndex: 'source', key: 'source', width: 80 },
    {
      title: '使用时间', dataIndex: 'used_at', key: 'used_at', width: 120,
      render: (v: string) => v ? formatCN(v, 'YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '操作', key: 'ops', width: 80,
      render: (_: any, r: any) => r.status === 'unused' ? (
        <Popconfirm title="确认作废?" onConfirm={async () => {
          await adminAPI.revokeActivationCode(r.id);
          message.success('已作废');
          fetchData();
        }}>
          <Button size="small" danger>作废</Button>
        </Popconfirm>
      ) : null,
    },
  ];

  return (
    <>
      {stats && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={5}><Card size="small"><Statistic title="总数" value={stats.total} /></Card></Col>
          <Col span={5}><Card size="small"><Statistic title="未使用" value={stats.unused} valueStyle={{ color: '#52c41a' }} /></Card></Col>
          <Col span={5}><Card size="small"><Statistic title="已使用" value={stats.used} valueStyle={{ color: '#0B2545' }} /></Card></Col>
          <Col span={5}><Card size="small"><Statistic title="已过期" value={stats.expired} /></Card></Col>
          <Col span={4}><Card size="small"><Statistic title="已作废" value={stats.revoked} valueStyle={{ color: '#ff4d4f' }} /></Card></Col>
        </Row>
      )}

      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setGenResult(null); form.resetFields(); setGenModal(true); }}>
          批量生成
        </Button>
        <Select placeholder="状态筛选" allowClear style={{ width: 120 }}
          onChange={v => setFilters(f => ({ ...f, status: v }))}
          options={Object.entries(STATUS_MAP).map(([k, v]) => ({ value: k, label: v.label }))}
        />
        <Select placeholder="套餐筛选" allowClear style={{ width: 120 }}
          onChange={v => setFilters(f => ({ ...f, plan_name: v }))}
          options={plans.map((p: any) => ({ value: p.name, label: p.display_name || p.name }))}
        />
      </Space>

      <Table columns={columns} dataSource={codes} rowKey="id" loading={loading} size="small"
        pagination={{ pageSize: 20, showTotal: t => `共 ${t} 条` }}
      />

      <Modal
        title="批量生成激活码"
        open={genModal}
        onCancel={() => setGenModal(false)}
        footer={genResult ? (
          <Button onClick={() => setGenModal(false)}>关闭</Button>
        ) : (
          <Button type="primary" loading={genLoading} onClick={handleGenerate}>生成</Button>
        )}
        width={560}
      >
        {genResult ? (
          <div>
            <Alert type="success" message={`已生成 ${genResult.count} 个激活码 (批次: ${genResult.batch_id})`} style={{ marginBottom: 12 }} />
            <Input.TextArea
              value={genResult.codes?.join('\n')}
              rows={Math.min(genResult.codes?.length || 5, 15)}
              readOnly
              style={{ fontFamily: 'monospace' }}
            />
            <Button size="small" style={{ marginTop: 8 }}
              onClick={() => { navigator.clipboard.writeText(genResult.codes?.join('\n') || ''); message.success('已复制'); }}>
              全部复制
            </Button>
          </div>
        ) : (
          <Form form={form} layout="vertical" initialValues={{ count: 10, duration_days: 365 }}>
            <Form.Item name="plan_name" label="套餐" rules={[{ required: true, message: '请选择套餐' }]}>
              <Select placeholder="选择套餐"
                options={plans.map((p: any) => ({ value: p.name, label: p.display_name || p.name }))}
                             />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="count" label="数量" rules={[{ required: true }]}>
                  <InputNumber min={1} max={500} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="duration_days" label="有效天数">
                  <InputNumber min={1} max={3650} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="source" label="渠道标记">
              <Input placeholder="如: taobao / weidian / conference" />
            </Form.Item>
            <Form.Item name="note" label="备注">
              <Input.TextArea rows={2} />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </>
  );
};

// ================================================================
// Promo Codes Tab
// ================================================================
const PromoTab: React.FC = () => {
  const [codes, setCodes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form] = Form.useForm();

  const fetchCodes = async () => {
    setLoading(true);
    try { const res = await adminAPI.listPromoCodes(); setCodes(res.items); }
    catch { message.error('加载优惠码失败'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCodes(); }, []);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      await adminAPI.createPromoCode(values);
      message.success('已创建'); setModal(false); form.resetFields(); fetchCodes();
    } catch (err: any) { message.error(err?.response?.data?.detail || '创建失败'); }
  };

  const columns = [
    { title: '优惠码', dataIndex: 'code', key: 'code', width: 120 },
    { title: '类型', dataIndex: 'discount_type', key: 'dt', width: 80, render: (v: string) => v === 'percent' ? '百分比' : '固定金额' },
    { title: '折扣值', dataIndex: 'discount_value', key: 'dv', width: 80 },
    { title: '使用/上限', key: 'usage', width: 100, render: (_: any, r: any) => `${r.used_count}/${r.max_uses || '∞'}` },
    {
      title: '状态', dataIndex: 'is_active', key: 'active', width: 80,
      render: (v: boolean) => <Tag color={v ? 'green' : 'default'}>{v ? '有效' : '无效'}</Tag>
    },
    {
      title: '操作', key: 'ops', width: 80,
      render: (_: any, r: any) => (
        <Popconfirm title="确认删除?" onConfirm={async () => { await adminAPI.deletePromoCode(r.id); fetchCodes(); }}>
          <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <>
      <Button type="primary" icon={<PlusOutlined />} onClick={() => setModal(true)} style={{ marginBottom: 16 }}>新增优惠码</Button>
      <Table columns={columns} dataSource={codes} rowKey="id" loading={loading} pagination={false} size="small" />
      <Modal title="新增优惠码" open={modal} onOk={handleCreate} onCancel={() => setModal(false)}>
        <Form form={form} layout="vertical">
          <Form.Item name="code" label="优惠码" rules={[{ required: true }]}><Input /></Form.Item>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="discount_type" label="类型" rules={[{ required: true }]}>
              <Select options={[{ value: 'percent', label: '百分比' }, { value: 'fixed', label: '固定金额' }]}
               />
            </Form.Item></Col>
            <Col span={12}><Form.Item name="discount_value" label="折扣值" rules={[{ required: true }]}><Input type="number" /></Form.Item></Col>
          </Row>
          <Form.Item name="max_uses" label="最大使用次数 (0=无限)" initialValue={0}><Input type="number" /></Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// ================================================================
// AI Usage Tab
// ================================================================
const AIUsageTab: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const loadData = (d: number) => {
    setLoading(true);
    adminAPI.aiUsage(d).then(setData).catch(() => message.error('加载AI用量失败')).finally(() => setLoading(false));
  };

  useEffect(() => { loadData(days); }, [days]);

  if (loading || !data) return <Card loading />;

  const costWarning = data.total_cost_usd > 10;

  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 500, fontSize: 15 }}>AI 调用统计</span>
        <Select value={days} onChange={setDays} style={{ width: 120 }} size="small">
          <Select.Option value={7}>近7天</Select.Option>
          <Select.Option value={30}>近30天</Select.Option>
          <Select.Option value={90}>近90天</Select.Option>
          <Select.Option value={365}>近一年</Select.Option>
        </Select>
      </div>
      {costWarning && <Alert message="费用提醒" description={`过去${days}天 AI 调用费用已达 $${data.total_cost_usd?.toFixed(4)}，请关注消耗趋势。`} type="warning" showIcon style={{ marginBottom: 16 }} />}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}><Card style={{ borderTop: '3px solid #0B2545' }}><Statistic title="总调用次数" value={data.total_calls} /></Card></Col>
        <Col span={6}><Card style={{ borderTop: '3px solid #52c41a' }}><Statistic title="总Token数" value={data.total_tokens} suffix={<span style={{ fontSize: 12, color: '#999' }}>平均 {data.avg_tokens_per_call}/次</span>} /></Card></Col>
        <Col span={6}><Card style={{ borderTop: '3px solid #722ed1' }}>
          <Statistic title="Prompt / Completion" value={data.total_prompt_tokens || 0} suffix={<span style={{ fontSize: 12, color: '#999' }}>/ {data.total_completion_tokens || 0}</span>} />
        </Card></Col>
        <Col span={6}><Card style={{ borderTop: '3px solid #C5A55A' }}><Statistic title="总费用 (USD)" value={data.total_cost_usd} precision={4} prefix="$" /></Card></Col>
      </Row>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card title="按接口" size="small">
            <Table dataSource={data.by_endpoint || []}
              columns={[
                { title: '接口', dataIndex: 'endpoint', ellipsis: true },
                { title: '调用', dataIndex: 'calls', width: 60 },
                { title: 'Tokens', dataIndex: 'tokens', width: 80 },
                { title: '费用($)', dataIndex: 'cost', width: 80, render: (v: number) => `$${(v || 0).toFixed(4)}` },
              ]}
              rowKey="endpoint" pagination={false} size="small" />
          </Card>
        </Col>
        <Col span={8}>
          <Card title="按模型" size="small">
            <Table dataSource={data.by_model || []}
              columns={[
                { title: '模型', dataIndex: 'model', ellipsis: true },
                { title: '调用', dataIndex: 'calls', width: 60 },
                { title: 'Tokens', dataIndex: 'tokens', width: 80 },
                { title: '费用($)', dataIndex: 'cost', width: 80, render: (v: number) => `$${(v || 0).toFixed(4)}` },
              ]}
              rowKey="model" pagination={false} size="small" />
          </Card>
        </Col>
        <Col span={8}>
          <Card title="每日趋势" size="small">
            <Table dataSource={data.daily || []}
              columns={[
                { title: '日期', dataIndex: 'date' },
                { title: '调用', dataIndex: 'calls', width: 50 },
                { title: 'Tokens', dataIndex: 'tokens', width: 70 },
                { title: '费用($)', dataIndex: 'cost', width: 70, render: (v: number) => `$${(v || 0).toFixed(4)}` },
              ]}
              rowKey="date" pagination={{ pageSize: 10, size: 'small' }} size="small" />
          </Card>
        </Col>
      </Row>
    </>
  );
};

const AIUsageV2Tab: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const loadData = useCallback((windowDays: number) => {
    setLoading(true);
    adminAPI.aiUsage(windowDays)
      .then(setData)
      .catch(() => message.error('加载 AI 统计失败'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadData(days);
  }, [days, loadData]);

  const formatInt = useCallback((value: number | string | null | undefined) => (
    Number(value || 0).toLocaleString('zh-CN')
  ), []);

  const formatUsd = useCallback((value: number | string | null | undefined) => (
    `$${Number(value || 0).toFixed(4)}`
  ), []);

  const endpointRows = useMemo(
    () => [...(data?.by_endpoint || [])].sort((a, b) => Number(b.cost || 0) - Number(a.cost || 0)),
    [data],
  );
  const modelRows = useMemo(
    () => [...(data?.by_model || [])].sort((a, b) => Number(b.cost || 0) - Number(a.cost || 0)),
    [data],
  );
  const dailyRows = useMemo(
    () => [...(data?.daily || [])].sort((a, b) => String(b.date).localeCompare(String(a.date))),
    [data],
  );

  if (loading && !data) {
    return <Card loading bordered={false} style={ADMIN_SURFACE_STYLE} />;
  }

  if (!data) {
    return <Empty description="暂无 AI 统计数据" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  const costWarning = Number(data.total_cost_usd || 0) > 10;
  const topEndpoint = endpointRows[0];
  const topModel = modelRows[0];
  const latestDaily = dailyRows[0];
  const metricCards = [
    {
      key: 'calls',
      label: '调用次数',
      value: formatInt(data.total_calls),
      accent: '#1677FF',
    },
    {
      key: 'tokens',
      label: '总 Tokens',
      value: formatInt(data.total_tokens),
      accent: '#15A37A',
    },
    {
      key: 'split',
      label: '输入 / 输出',
      value: `${formatInt(data.total_prompt_tokens)} / ${formatInt(data.total_completion_tokens)}`,
      accent: '#7A5AF8',
    },
    {
      key: 'cost',
      label: '估算费用',
      value: formatUsd(data.total_cost_usd),
      accent: '#F59E0B',
    },
  ];
  const tableLocale = {
    emptyText: <Empty description="该时间范围暂无数据" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
  };

  return (
    <div>
      <Card bordered={false} style={{ ...ADMIN_SURFACE_STYLE, marginBottom: 16, overflow: 'hidden' }}>
        <div className="admin-ai-usage__hero">
          <div className="admin-ai-usage__compact-title">AI 使用</div>
          <div className="admin-ai-usage__controls">
            <Select value={days} onChange={setDays} style={{ width: 136 }}>
              <Select.Option value={7}>近 7 天</Select.Option>
              <Select.Option value={30}>近 30 天</Select.Option>
              <Select.Option value={90}>近 90 天</Select.Option>
              <Select.Option value={365}>近 1 年</Select.Option>
            </Select>
            <Button icon={<ReloadOutlined />} onClick={() => loadData(days)} loading={loading}>
              刷新
            </Button>
          </div>
        </div>
        <div className="admin-ai-usage__metrics">
          {metricCards.map((item) => (
            <div key={item.key} className="admin-ai-usage__metric">
              <div className="admin-ai-usage__metric-label">
                <span>{item.label}</span>
                <span className="admin-ai-usage__metric-chip" style={{ background: item.accent }} />
              </div>
              <div className="admin-ai-usage__metric-value">{item.value}</div>
            </div>
          ))}
        </div>
      </Card>

      {costWarning && (
        <Alert
          type="warning"
          showIcon
          message="费用提醒"
          description={`最近 ${days} 天 AI 估算费用已达到 ${formatUsd(data.total_cost_usd)}，建议结合模型分布与调用趋势继续排查高消耗来源。`}
          style={{ marginBottom: 16, borderRadius: 8 }}
        />
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} xl={16}>
          <Card
            title={<Text strong style={{ color: '#102A43' }}>每日趋势</Text>}
            extra={<Text type="secondary">最近活跃日：{latestDaily?.date || '--'}</Text>}
            className="admin-ai-usage__section-card"
          >
            <Table
              locale={tableLocale}
              dataSource={dailyRows}
              rowKey="date"
              size="small"
              pagination={{ pageSize: 8, size: 'small' }}
              columns={[
                { title: '日期', dataIndex: 'date', width: 120 },
                { title: '调用', dataIndex: 'calls', width: 88, render: formatInt },
                { title: 'Tokens', dataIndex: 'tokens', width: 120, render: formatInt },
                { title: '费用', dataIndex: 'cost', width: 110, render: formatUsd },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} xl={8}>
          <Card
            title={<Text strong style={{ color: '#102A43' }}>经营提示</Text>}
            className="admin-ai-usage__section-card"
          >
            <div className="admin-ai-usage__signal-list">
              <div className="admin-ai-usage__signal">
                <div className="admin-ai-usage__signal-label">最高消耗接口</div>
                <div className="admin-ai-usage__signal-value">{topEndpoint?.endpoint || '--'}</div>
                <div className="admin-ai-usage__signal-helper">
                  {topEndpoint ? `${formatInt(topEndpoint.calls)} 次调用 · ${formatUsd(topEndpoint.cost)}` : '当前时间范围暂无接口消耗数据'}
                </div>
              </div>
              <div className="admin-ai-usage__signal">
                <div className="admin-ai-usage__signal-label">主要模型</div>
                <div className="admin-ai-usage__signal-value">{topModel?.model || '--'}</div>
                <div className="admin-ai-usage__signal-helper">
                  {topModel ? `${formatInt(topModel.calls)} 次调用 · ${formatInt(topModel.tokens)} Tokens` : '当前时间范围暂无模型分布数据'}
                </div>
              </div>
              <div className="admin-ai-usage__signal">
                <div className="admin-ai-usage__signal-label">最近活跃日摘要</div>
                <div className="admin-ai-usage__signal-value">
                  {latestDaily ? `${formatInt(latestDaily.calls)} 次 / ${formatUsd(latestDaily.cost)}` : '--'}
                </div>
                <div className="admin-ai-usage__signal-helper">
                  {latestDaily ? `${latestDaily.date} · ${formatInt(latestDaily.tokens)} Tokens` : '等待新的 AI 使用记录'}
                </div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={12}>
          <Card
            title={<Text strong style={{ color: '#102A43' }}>接口分布</Text>}
            className="admin-ai-usage__section-card"
          >
            <Table
              locale={tableLocale}
              dataSource={endpointRows}
              rowKey="endpoint"
              size="small"
              pagination={false}
              columns={[
                { title: '接口', dataIndex: 'endpoint', ellipsis: true },
                { title: '调用', dataIndex: 'calls', width: 90, render: formatInt },
                { title: 'Tokens', dataIndex: 'tokens', width: 120, render: formatInt },
                { title: '费用', dataIndex: 'cost', width: 110, render: formatUsd },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} xl={12}>
          <Card
            title={<Text strong style={{ color: '#102A43' }}>模型分布</Text>}
            className="admin-ai-usage__section-card"
          >
            <Table
              locale={tableLocale}
              dataSource={modelRows}
              rowKey="model"
              size="small"
              pagination={false}
              columns={[
                { title: '模型', dataIndex: 'model', ellipsis: true },
                { title: '调用', dataIndex: 'calls', width: 90, render: formatInt },
                { title: 'Tokens', dataIndex: 'tokens', width: 120, render: formatInt },
                { title: '费用', dataIndex: 'cost', width: 110, render: formatUsd },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

// ================================================================
// Invoices Tab (Admin Invoice Management)
// ================================================================
const INVOICE_STATUS: Record<string, { label: string; color: string }> = {
  pending: { label: '待开具', color: 'orange' },
  issued: { label: '已开具', color: 'green' },
  cancelled: { label: '已作废', color: 'default' },
};

// ================================================================
// Orders Tab (订单管理)
// ================================================================
const OrdersTab: React.FC = () => {
  const [orders, setOrders] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<any>({});
  const [importModal, setImportModal] = useState(false);
  const [importRows, setImportRows] = useState<any[]>([{ order_no: '', amount: 0, plan_name: '', duration_days: 365, buyer_note: '' }]);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);

  const fetchOrders = async (p = page, f = filters) => {
    setLoading(true);
    try {
      const res = await adminAPI.listOrders({ page: p, page_size: 20, ...f });
      setOrders(res.items); setTotal(res.total);
    } catch { message.error('加载订单失败'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchOrders(); }, []);

  const handleStatusChange = async (id: string, status: string) => {
    try {
      await adminAPI.updateOrder(id, { status });
      message.success('更新成功'); fetchOrders();
    } catch { message.error('更新失败'); }
  };

  const handleImport = async () => {
    const validRows = importRows.filter(r => r.amount > 0 || r.plan_name);
    if (!validRows.length) { message.warning('请填写至少一条订单'); return; }
    setImportLoading(true);
    try {
      const res = await adminAPI.importOrders({ orders: validRows.map(r => ({ ...r, status: 'paid', payment_method: 'third_party' })) });
      setImportResult(res);
      message.success(`成功导入 ${res.imported} 条订单`);
      fetchOrders();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || '导入失败');
    } finally { setImportLoading(false); }
  };

  const statusMap: Record<string, { color: string; text: string }> = {
    pending: { color: 'orange', text: '待支付' },
    paid: { color: 'green', text: '已支付' },
    failed: { color: 'red', text: '失败' },
    refunded: { color: 'purple', text: '已退款' },
  };

  const columns = [
    { title: '订单号', dataIndex: 'order_no', key: 'no', width: 200, ellipsis: true },
    { title: '租户', dataIndex: 'tenant_name', key: 'tn', width: 120, ellipsis: true },
    {
      title: '支付方式', dataIndex: 'payment_method', key: 'pm', width: 100,
      render: (v: string) => ({ wechat: '微信', alipay: '支付宝', third_party: '第三方' }[v] || v)
    },
    {
      title: '金额', dataIndex: 'amount', key: 'amt', width: 100,
      render: (v: number) => <span style={{ fontWeight: 500 }}>¥{v?.toFixed(2)}</span>
    },
    {
      title: '状态', dataIndex: 'status', key: 'st', width: 90,
      render: (v: string) => <Tag color={statusMap[v]?.color}>{statusMap[v]?.text || v}</Tag>
    },
    {
      title: '支付时间', dataIndex: 'paid_at', key: 'pa', width: 160,
      render: (v: string) => v ? new Date(v).toLocaleString('zh-CN') : '-'
    },
    {
      title: '创建时间', dataIndex: 'created_at', key: 'ca', width: 160,
      render: (v: string) => v ? new Date(v).toLocaleString('zh-CN') : '-'
    },
    {
      title: '操作', key: 'ops', width: 160, render: (_: any, r: any) => (
        <Space size="small">
          {r.status === 'pending' && (
            <Popconfirm title="确认标记为已支付?" onConfirm={() => handleStatusChange(r.id, 'paid')}>
              <Button size="small" type="link">标记已付</Button>
            </Popconfirm>
          )}
          {r.status === 'paid' && (
            <Popconfirm title="确认退款?" onConfirm={() => handleStatusChange(r.id, 'refunded')}>
              <Button size="small" type="link" danger>退款</Button>
            </Popconfirm>
          )}
        </Space>
      )
    },
  ];

  return (
    <>
      <Space style={{ marginBottom: 16 }} wrap>
        <Input placeholder="订单号" allowClear onChange={e => setFilters((f: any) => ({ ...f, order_no: e.target.value || undefined }))} style={{ width: 180 }} />
        <Select placeholder="状态" allowClear onChange={v => setFilters((f: any) => ({ ...f, status: v }))} style={{ width: 100 }}
          options={[{ value: 'pending', label: '待支付' }, { value: 'paid', label: '已支付' }, { value: 'failed', label: '失败' }, { value: 'refunded', label: '已退款' }]} />
        <Select placeholder="支付方式" allowClear onChange={v => setFilters((f: any) => ({ ...f, payment_method: v }))} style={{ width: 110 }}
          options={[{ value: 'wechat', label: '微信' }, { value: 'alipay', label: '支付宝' }, { value: 'third_party', label: '第三方' }]} />
        <Button type="primary" icon={<SearchOutlined />} onClick={() => { setPage(1); fetchOrders(1, filters); }}>查询</Button>
        <Button icon={<PlusOutlined />} onClick={() => { setImportResult(null); setImportRows([{ order_no: '', amount: 0, plan_name: '', duration_days: 365, buyer_note: '' }]); setImportModal(true); }}>
          导入第三方订单
        </Button>
      </Space>
      <Table columns={columns} dataSource={orders} rowKey="id" loading={loading} size="small"
        pagination={{ current: page, pageSize: 20, total, onChange: p => { setPage(p); fetchOrders(p, filters); } }} />

      <Modal title="导入第三方订单" open={importModal} onCancel={() => setImportModal(false)}
        width={800} footer={null}>
        <Alert message="填写第三方平台订单信息，指定套餐名称可自动生成激活码" type="info" showIcon style={{ marginBottom: 16 }} />
        <Table dataSource={importRows.map((r, i) => ({ ...r, _idx: i }))} rowKey="_idx" size="small" pagination={false}
          columns={[
            {
              title: '订单号(选填)', dataIndex: 'order_no', render: (_: any, rec: any) =>
                <Input size="small" value={rec.order_no} placeholder="自动生成"
                  onChange={e => { const rows = [...importRows]; rows[rec._idx].order_no = e.target.value; setImportRows(rows); }} />
            },
            {
              title: '金额', dataIndex: 'amount', width: 100, render: (_: any, rec: any) =>
                <InputNumber size="small" min={0} value={rec.amount} style={{ width: '100%' }}
                  onChange={v => { const rows = [...importRows]; rows[rec._idx].amount = v || 0; setImportRows(rows); }} />
            },
            {
              title: '套餐(生成激活码)', dataIndex: 'plan_name', width: 130, render: (_: any, rec: any) =>
                <Input size="small" value={rec.plan_name} placeholder="如 basic"
                  onChange={e => { const rows = [...importRows]; rows[rec._idx].plan_name = e.target.value; setImportRows(rows); }} />
            },
            {
              title: '天数', dataIndex: 'duration_days', width: 80, render: (_: any, rec: any) =>
                <InputNumber size="small" min={1} value={rec.duration_days} style={{ width: '100%' }}
                  onChange={v => { const rows = [...importRows]; rows[rec._idx].duration_days = v || 365; setImportRows(rows); }} />
            },
            {
              title: '备注', dataIndex: 'buyer_note', render: (_: any, rec: any) =>
                <Input size="small" value={rec.buyer_note}
                  onChange={e => { const rows = [...importRows]; rows[rec._idx].buyer_note = e.target.value; setImportRows(rows); }} />
            },
            {
              title: '', width: 40, render: (_: any, rec: any) =>
                importRows.length > 1 ? <Button size="small" type="text" danger icon={<DeleteOutlined />}
                  onClick={() => setImportRows(importRows.filter((_, i) => i !== rec._idx))} /> : null
            },
          ]} />
        <Space style={{ marginTop: 12 }}>
          <Button size="small" icon={<PlusOutlined />}
            onClick={() => setImportRows([...importRows, { order_no: '', amount: 0, plan_name: '', duration_days: 365, buyer_note: '' }])}>
            添加行
          </Button>
          <Button type="primary" loading={importLoading} onClick={handleImport}>确认导入</Button>
        </Space>
        {importResult && (
          <Card size="small" style={{ marginTop: 16 }} title={`导入结果: ${importResult.imported} 条成功`}>
            {importResult.results?.map((r: any, i: number) => (
              <div key={i} style={{ marginBottom: 4 }}>
                <Tag color={r.status === 'imported' ? 'green' : r.status === 'skipped' ? 'orange' : 'red'}>{r.status}</Tag>
                {r.order_no} {r.activation_code && <Tag color="blue">激活码: {r.activation_code}</Tag>}
                {r.reason && <span style={{ color: '#999' }}> ({r.reason})</span>}
              </div>
            ))}
          </Card>
        )}
      </Modal>
    </>
  );
};

const InvoicesTab: React.FC = () => {
  const [invoices, setInvoices] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string | undefined>();
  const [page, setPage] = useState(1);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.listInvoices({ status: filterStatus, page, page_size: 20 });
      setInvoices(res.items || []);
      setTotal(res.total || 0);
    } catch { message.error('加载发票列表失败'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchInvoices(); }, [filterStatus, page]);

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      await adminAPI.updateInvoiceStatus(id, newStatus);
      message.success('状态已更新');
      fetchInvoices();
    } catch (err: any) { message.error(err?.response?.data?.detail || '操作失败'); }
  };

  const columns = [
    { title: '发票号', dataIndex: 'invoice_no', key: 'no', width: 180, ellipsis: true },
    { title: '租户', dataIndex: 'tenant_name', key: 'tenant', width: 120, ellipsis: true },
    { title: '抬头', dataIndex: 'title', key: 'title', width: 160, ellipsis: true, render: (v: string) => v || '-' },
    { title: '金额', dataIndex: 'amount', key: 'amount', width: 90, render: (v: number) => `¥${v?.toFixed(2)}` },
    {
      title: '类型', dataIndex: 'type', key: 'type', width: 70,
      render: (v: string) => v === 'special' ? '专票' : '普票'
    },
    { title: '税号', dataIndex: 'tax_id', key: 'tax_id', width: 160, ellipsis: true, render: (v: string) => v || '-' },
    { title: '邮箱', dataIndex: 'email', key: 'email', width: 160, ellipsis: true, render: (v: string) => v || '-' },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 90,
      render: (v: string) => {
        const s = INVOICE_STATUS[v] || { label: v, color: 'default' };
        return <Tag color={s.color}>{s.label}</Tag>;
      }
    },
    {
      title: '申请时间', dataIndex: 'created_at', key: 'cat', width: 150,
      render: (v: string) => v ? formatCN(v, 'YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '操作', key: 'ops', width: 160,
      render: (_: any, r: any) => (
        <Space size={4}>
          {r.status === 'pending' && (
            <>
              <Popconfirm title="确认标记为已开具?" onConfirm={() => handleStatusChange(r.id, 'issued')}>
                <Button size="small" type="primary">开具</Button>
              </Popconfirm>
              <Popconfirm title="确认作废此发票?" onConfirm={() => handleStatusChange(r.id, 'cancelled')}>
                <Button size="small" danger>作废</Button>
              </Popconfirm>
            </>
          )}
          {r.status === 'issued' && (
            <Popconfirm title="确认作废此发票?" onConfirm={() => handleStatusChange(r.id, 'cancelled')}>
              <Button size="small" danger>作废</Button>
            </Popconfirm>
          )}
        </Space>
      )
    },
  ];

  return (
    <>
      <Space style={{ marginBottom: 16 }} wrap>
        <Select placeholder="状态筛选" allowClear style={{ width: 140 }} value={filterStatus}
          onChange={v => { setFilterStatus(v); setPage(1); }}
          options={Object.entries(INVOICE_STATUS).map(([k, v]) => ({ value: k, label: v.label }))} />
        <Button icon={<ReloadOutlined />} onClick={fetchInvoices} loading={loading}>刷新</Button>
        <Text type="secondary">共 {total} 条</Text>
      </Space>
      <Table columns={columns} dataSource={invoices} rowKey="id" loading={loading}
        pagination={{ current: page, total, pageSize: 20, onChange: setPage, showSizeChanger: false }}
        size="small" scroll={{ x: 1400 }} />
    </>
  );
};

// ================================================================
// Revenue Tab (Fixed with error handling)
// ================================================================
const RevenueTab: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    adminAPI.revenue(6)
      .then(setData)
      .catch((err: any) => {
        setError(formatErrorMessage(err, '加载收支统计失败，可能相关数据表尚无数据'));
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Card loading />;
  if (error) return (
    <Alert message="收支统计" description={error} type="warning" showIcon
      action={<Button size="small" onClick={() => { setError(''); setLoading(true); adminAPI.revenue(6).then(setData).catch((e: any) => setError(formatErrorMessage(e, '加载失败'))).finally(() => setLoading(false)); }}>重试</Button>}
    />
  );
  if (!data) return <Empty description="暂无收支数据" />;

  return (
    <>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}><Card style={{ borderTop: '3px solid #52c41a' }}><Statistic title="总收入" value={data.total_revenue} prefix="¥" precision={2} /></Card></Col>
        <Col span={8}><Card style={{ borderTop: '3px solid #0B2545' }}><Statistic title="活跃订阅" value={data.active_subscriptions} /></Card></Col>
      </Row>
      {data.monthly?.length > 0 ? (
        <Card title="月度收入" size="small">
          <Table dataSource={data.monthly}
            columns={[
              { title: '月份', dataIndex: 'month' },
              { title: '收入', dataIndex: 'revenue', render: (v: number) => `¥${v?.toFixed(2)}` },
              { title: '订单数', dataIndex: 'orders' },
            ]}
            rowKey="month" pagination={false} size="small" />
        </Card>
      ) : (
        <Card size="small"><Empty description="暂无月度收入数据" image={Empty.PRESENTED_IMAGE_SIMPLE} /></Card>
      )}
    </>
  );
};

// ================================================================
// Config Tab (Structured Form UI)
// ================================================================
interface ConfigField {
  key: string;
  label: string;
  type: 'text' | 'switch' | 'number' | 'password' | 'select' | 'textarea' | 'kv_table' | 'model_mapping' | 'logo_library';
  options?: { label: string; value: string }[];
  rows?: number;
  columns?: { key: string; label: string }[];
  placeholder?: string;
  help?: string;
}
interface ConfigEntry { label: string; group: string; description?: string; hidden?: boolean; fields: ConfigField[] }

const CONFIG_GROUPS: { key: string; label: string }[] = [
  { key: 'site', label: '站点基础' },
  { key: 'model', label: '模型服务' },
  { key: 'ai', label: 'AI配置' },
  { key: 'payment', label: '支付配置' },
  { key: 'oauth', label: '社交账号登录' },
  { key: 'integration', label: '第三方集成' },
];

const CONFIG_SCHEMA: Record<string, ConfigEntry> = {
  site_name: { label: '站点名称', group: 'site', fields: [{ key: '_value', label: '站点名称', type: 'text' }] },
  brand_logo_assets: {
    label: '品牌 Logo 资源库',
    group: 'site',
    description: '统一维护各页面使用的 Logo，同一个 Logo 可通过勾选复用到多个页面位置。',
    fields: [{
      key: '_value',
      label: 'Logo 方案',
      type: 'logo_library',
      help: '支持 /images/...、/uploads/... 或 https://...；如果当前 Logo 勾选了已被其他 Logo 占用的位置，系统会自动切换到当前 Logo。',
    }],
  },
  site_logo: {
    hidden: true,
    label: '前台通用Logo',
    group: 'site',
    description: '前台、登录页、页脚默认使用，建议填写不带文字的方形图标 Logo',
    fields: [{
      key: '_value',
      label: 'Logo URL',
      type: 'text',
      placeholder: '/images/logo-icon.png',
      help: '支持填写 /images/... 或 https://...；建议 SVG/PNG 透明背景，1:1 比例。',
    }],
  },
  admin_logo_full: {
    hidden: true,
    label: '后台Logo（带文字）',
    group: 'site',
    description: '后台展开侧栏、管理员登录页使用，建议横向完整品牌 Logo',
    fields: [{
      key: '_value',
      label: '完整Logo URL',
      type: 'text',
      placeholder: '/images/admin-logo-full.svg',
      help: '建议填写带品牌文字的横向 SVG/PNG，推荐 3:1 ~ 5:1 比例；留空则回退到前台 Logo。',
    }],
  },
  admin_logo_icon: {
    hidden: true,
    label: '后台Logo（图标）',
    group: 'site',
    description: '后台折叠侧栏使用，建议填写不带文字的图标 Logo',
    fields: [{
      key: '_value',
      label: '图标Logo URL',
      type: 'text',
      placeholder: '/images/admin-logo-icon.png',
      help: '建议填写 1:1 比例的 SVG/PNG 图标，推荐 64x64 或 128x128；留空则回退到前台 Logo。',
    }],
  },
  login_slogan: { label: '登录页Slogan', group: 'site', description: '一句话描述', fields: [{ key: '_value', label: 'Slogan文字', type: 'text' }] },
  icp_number: { label: 'ICP备案号', group: 'site', fields: [{ key: '_value', label: 'ICP备案号', type: 'text' }] },
  copyright: { label: '版权信息', group: 'site', fields: [{ key: '_value', label: '版权信息', type: 'text' }] },
  contact_email: { label: '联系邮箱', group: 'site', fields: [{ key: '_value', label: '联系邮箱', type: 'text' }] },
  ui_theme: { label: 'UI主题', group: 'site', description: '前端界面主题风格', fields: [{ key: '_value', label: '主题模式', type: 'select', options: [{ label: '浅色科技风', value: 'light' }, { label: '深色科技风', value: 'dark' }] }] },
  coin_config: {
    label: '金币系统配置', group: 'site', description: '注册赠送、邀请奖励、每日登录奖励金币数（留空或0则不启用每日登录奖励）', fields: [
      { key: 'register_bonus', label: '注册赠送金币数', type: 'number' },
      { key: 'invite_reward', label: '邀请奖励金币数', type: 'number' },
      { key: 'daily_login_reward', label: '每日登录奖励金币数', type: 'number' },
    ]
  },
  feature_modules: {
    hidden: true,
    label: '首页功能模块', group: 'site', description: '工作台展示的核心功能卡片（标题+描述）', fields: [
      { key: '_value', label: '功能模块列表', type: 'kv_table', columns: [{ key: 'title', label: '标题' }, { key: 'content', label: '描述' }] },
    ]
  },
  ai_drug_config_prompt: {
    label: 'AI药物配置生成提示词', group: 'ai', description: '新增药物时"AI智能填充"使用的提示词模板', fields: [
      { key: 'system_prompt', label: '系统提示词 (System Prompt)', type: 'textarea', rows: 4 },
      { key: 'user_prompt_template', label: '用户提示词模板 (支持变量: {drug_name}, {drug_name_en}, {category}, {route})', type: 'textarea', rows: 12 },
    ]
  },
  agent_intake_templates: {
    label: 'Agent 快捷录入模板',
    group: 'ai',
    description: '配置 Agent 顾问“清空”右侧的快捷填入按钮。按列表顺序最多展示 3 个；按钮只填入文本，不会自动发起提取。',
    fields: [{
      key: '_value',
      label: '快捷模板（最多 3 个）',
      type: 'kv_table',
      columns: [
        { key: 'title', label: '按钮名称' },
        { key: 'content', label: '模板内容' },
      ],
    }],
  },
  llm_provider_settings: {
    label: 'LLM Provider Routing', group: 'ai', description: 'Toggle providers here. API key, base URL and model stay in backend .env.', fields: [
      {
        key: 'default_provider',
        label: 'Default Provider',
        type: 'select',
        options: [
          { label: 'DeepSeek', value: 'deepseek' },
          { label: 'Kimi', value: 'kimi' },
          { label: 'MiniMax', value: 'minimax' },
          { label: 'GLM', value: 'glm' },
          { label: 'Qwen', value: 'qwen' },
          { label: 'Claude', value: 'claude' },
          { label: 'ChatGPT', value: 'chatgpt' },
          { label: 'Gemini', value: 'gemini' },
        ],
      },
      { key: 'deepseek_enabled', label: 'Enable DeepSeek', type: 'switch' },
      { key: 'kimi_enabled', label: 'Enable Kimi', type: 'switch' },
      { key: 'minimax_enabled', label: 'Enable MiniMax', type: 'switch' },
      { key: 'glm_enabled', label: 'Enable GLM', type: 'switch' },
      { key: 'qwen_enabled', label: 'Enable Qwen', type: 'switch' },
      { key: 'claude_enabled', label: 'Enable Claude', type: 'switch' },
      { key: 'chatgpt_enabled', label: 'Enable ChatGPT', type: 'switch' },
      { key: 'gemini_enabled', label: 'Enable Gemini', type: 'switch' },
    ]
  },
  smtp: {
    label: 'SMTP邮件配置', group: 'integration', description: '配置系统发送邮件的SMTP服务器', fields: [
      { key: 'enabled', label: '启用', type: 'switch' },
      { key: 'host', label: 'SMTP服务器', type: 'text' },
      { key: 'port', label: '端口', type: 'number' },
      { key: 'username', label: '用户名', type: 'text' },
      { key: 'password', label: '密码', type: 'password' },
      { key: 'from_name', label: '发件人名称', type: 'text' },
      { key: 'use_ssl', label: '使用SSL', type: 'switch' },
      { key: 'fallback_host', label: '备用SMTP服务器', type: 'text' },
      { key: 'fallback_port', label: '备用端口', type: 'number' },
      { key: 'fallback_username', label: '备用用户名', type: 'text' },
      { key: 'fallback_password', label: '备用密码', type: 'password' },
      { key: 'fallback_from_name', label: '备用发件人名称', type: 'text' },
      { key: 'fallback_use_ssl', label: '备用使用SSL', type: 'switch' },
    ]
  },
  payment_wechat: {
    label: '微信支付', group: 'payment', description: '微信支付商户配置', fields: [
      { key: 'enabled', label: '启用', type: 'switch' },
      { key: 'mch_id', label: '商户号', type: 'text' },
      { key: 'api_key', label: 'API密钥', type: 'password' },
    ]
  },
  payment_alipay: {
    label: '支付宝', group: 'payment', description: '支付宝支付配置', fields: [
      { key: 'enabled', label: '启用', type: 'switch' },
      { key: 'app_id', label: 'App ID', type: 'text' },
      { key: 'private_key', label: '私钥', type: 'password' },
    ]
  },
  redcap: {
    label: 'REDCap集成', group: 'integration', description: 'REDCap数据同步配置', fields: [
      { key: 'enabled', label: '启用', type: 'switch' },
      { key: 'api_url', label: 'API地址', type: 'text' },
      { key: 'api_token', label: 'API Token', type: 'password' },
      { key: 'project_id', label: '项目ID', type: 'text' },
      { key: 'sync_enabled', label: '定时同步', type: 'switch' },
      { key: 'sync_frequency', label: '同步频率', type: 'text' },
      { key: 'sync_time', label: '同步时间', type: 'text' },
    ]
  },
  oauth_wechat: {
    label: '微信登录', group: 'oauth', description: '微信开放平台OAuth配置', fields: [
      { key: 'enabled', label: '启用', type: 'switch' },
      { key: 'app_id', label: 'App ID', type: 'text' },
      { key: 'app_secret', label: 'App Secret', type: 'password' },
    ]
  },
  oauth_github: {
    label: 'GitHub登录', group: 'oauth', description: 'GitHub OAuth App配置', fields: [
      { key: 'enabled', label: '启用', type: 'switch' },
      { key: 'client_id', label: 'Client ID', type: 'text' },
      { key: 'client_secret', label: 'Client Secret', type: 'password' },
    ]
  },
  oauth_google: {
    label: 'Google登录', group: 'oauth', description: 'Google OAuth配置', fields: [
      { key: 'enabled', label: '启用', type: 'switch' },
      { key: 'client_id', label: 'Client ID', type: 'text' },
      { key: 'client_secret', label: 'Client Secret', type: 'password' },
    ]
  },
  oauth_redirect_base: {
    label: 'OAuth回调基址', group: 'oauth', description: '社交登录回调URL前缀', fields: [
      { key: '_value', label: '回调基址', type: 'text' },
    ]
  },
};

// ─── 协变量管理 Tab ──────────────────────────────────────────────────────────

const CovariatesTab: React.FC = () => {
  const screens = Grid.useBreakpoint();
  const [definitions, setDefinitions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingDef, setEditingDef] = useState<any>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerTab, setDrawerTab] = useState('basic');
  const [form] = Form.useForm();
  // Unit variants state
  const [unitVariants, setUnitVariants] = useState<any[]>([]);
  const [rScriptAliases, setRScriptAliases] = useState<string[]>([]);
  const [uvLoading, setUvLoading] = useState(false);
  // Model mappings for expanded rows + drawer tab
  const [modelMappings, setModelMappings] = useState<Record<string, any[]>>({});
  // Filters
  const [filterGroup, setFilterGroup] = useState<string | undefined>();
  const [searchText, setSearchText] = useState('');
  const [derivationRules, setDerivationRules] = useState<EditableDerivationRule[]>([]);
  const [derivationRulesLoading, setDerivationRulesLoading] = useState(false);
  const [derivationTestInput, setDerivationTestInput] = useState('{\n  "height": 100,\n  "scr": 0.5,\n  "scr_unit": "mg/dL",\n  "age_years": 5\n}');
  const [derivationTestResult, setDerivationTestResult] = useState<any>(null);
  const [selectedDerivationRuleId, setSelectedDerivationRuleId] = useState<string | null>(null);

  const loadDefs = async () => {
    setLoading(true);
    try {
      const data = await covariateAPI.definitions();
      setDefinitions(data?.definitions || []);
    } catch { message.error('加载协变量定义失败'); }
    setLoading(false);
  };

  useEffect(() => { loadDefs(); }, []);

  const resetDerivationEditor = () => {
    setDerivationRules([]);
    setDerivationTestResult(null);
    setSelectedDerivationRuleId(null);
  };

  const loadDerivationRules = async (key: string) => {
    setDerivationRulesLoading(true);
    setDerivationTestResult(null);
    try {
      const data = await covariateAPI.getDerivationRules(key);
      const nextRules = (data?.rules || []).map((rule: any, index: number) => ruleToEditorRule(rule, index));
      setDerivationRules(nextRules);
      setSelectedDerivationRuleId(nextRules[0]?.ui_id || null);
    } catch {
      setDerivationRules([]);
      setSelectedDerivationRuleId(null);
      message.error('加载派生规则失败');
    } finally {
      setDerivationRulesLoading(false);
    }
  };

  const parseDerivationRules = () => {
    const targetKey = editingDef?.key || form.getFieldValue('key');
    if (!targetKey && derivationRules.length > 0) {
      throw new Error('请先填写协变量标识');
    }
    return editorRulesToApiRules(derivationRules, targetKey);
  };

  const handleTestDerivationRules = async () => {
    const targetKey = editingDef?.key || form.getFieldValue('key');
    if (!targetKey) {
      message.warning('请先填写协变量标识');
      return;
    }
    try {
      const rules = parseDerivationRules();
      const covariates = JSON.parse(derivationTestInput || '{}');
      const result = await covariateAPI.testDerivationRules(targetKey, { rules, covariates });
      setDerivationTestResult(result);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || '派生规则测试失败');
    }
  };

  const updateDerivationRule = (ruleIndex: number, patch: Partial<EditableDerivationRule>) => {
    setDerivationRules(prev => prev.map((rule, index) => (
      index === ruleIndex ? { ...rule, ...patch } : rule
    )));
  };

  const updateDerivationCondition = (
    ruleIndex: number,
    conditionIndex: number,
    patch: Partial<EditableConditionRow>,
  ) => {
    setDerivationRules(prev => prev.map((rule, index) => {
      if (index !== ruleIndex) return rule;
      return {
        ...rule,
        conditions: rule.conditions.map((condition, itemIndex) => (
          itemIndex === conditionIndex ? { ...condition, ...patch } : condition
        )),
      };
    }));
  };

  const addDerivationCondition = (ruleIndex: number, field?: string) => {
    setDerivationRules(prev => prev.map((rule, index) => (
      index === ruleIndex
        ? { ...rule, conditions: [...rule.conditions, newConditionRow(field)] }
        : rule
    )));
  };

  const updateDerivationParam = (
    ruleIndex: number,
    paramIndex: number,
    patch: Partial<EditableParamRow>,
  ) => {
    setDerivationRules(prev => prev.map((rule, index) => {
      if (index !== ruleIndex) return rule;
      return {
        ...rule,
        params_entries: rule.params_entries.map((param, itemIndex) => (
          itemIndex === paramIndex ? { ...param, ...patch } : param
        )),
      };
    }));
  };

  const handleSave = async () => {
    try {
      if (derivationRulesLoading) {
        message.warning('派生规则仍在加载，请稍后再保存');
        return;
      }
      const values = await form.validateFields();
      const derivationRulesPayload = parseDerivationRules();
      // Include unit_variants in save payload
      let optionAliases: Record<string, string[]> = {};
      if (values.agent_option_aliases) {
        try {
          const parsed = JSON.parse(values.agent_option_aliases);
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) optionAliases = parsed;
        } catch {
          throw new Error('Agent 选项值别名必须是合法 JSON');
        }
      }
      const normalizedOptions = Array.isArray(values.options)
        ? values.options.map((option: any) => ({
            ...option,
            agent_aliases: optionAliases[String(option?.value || '')] || option?.agent_aliases || [],
          }))
        : values.options;
      const payload = {
        ...values,
        options: normalizedOptions,
        unit_variants: unitVariants,
        r_script_aliases: rScriptAliases,
      };
      delete (payload as any).agent_option_aliases;
      delete (payload as any).derived_from;
      delete (payload as any).derived_formula;
      if (editingDef) {
        await covariateAPI.updateDefinition(editingDef.key, payload);
        await covariateAPI.updateDerivationRules(editingDef.key, derivationRulesPayload);
        if (modelMappings[editingDef.key]) {
          const mappingResponse = await covariateAPI.modelsByCovariate(editingDef.key);
          setModelMappings(prev => ({ ...prev, [editingDef.key]: mappingResponse?.models || [] }));
        }
        message.success('更新成功');
      } else {
        await covariateAPI.createDefinition(payload);
        if (derivationRulesPayload.length > 0) {
          await covariateAPI.updateDerivationRules(values.key, derivationRulesPayload);
        }
        message.success('创建成功');
      }
      setDrawerVisible(false);
      form.resetFields();
      setUnitVariants([]);
      setRScriptAliases([]);
      resetDerivationEditor();
      loadDefs();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '保存失败');
    }
  };

  const handleDelete = async (key: string) => {
    try {
      await covariateAPI.deleteDefinition(key);
      message.success('已删除');
      loadDefs();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '删除失败');
    }
  };

  const groupColors: Record<string, string> = {
    demographics: 'blue', labs: 'green', conditions: 'orange', genetics: 'purple',
  };

  const filteredDefs = useMemo(() => {
    let list = definitions;
    if (filterGroup) list = list.filter(d => d.group_name === filterGroup);
    if (searchText) {
      const s = searchText.toLowerCase();
      list = list.filter(d => d.key?.toLowerCase().includes(s) || d.label?.toLowerCase().includes(s) || d.label_en?.toLowerCase().includes(s));
    }
    return list;
  }, [definitions, filterGroup, searchText]);

  // Group stats
  const groupStats = useMemo(() => {
    const stats: Record<string, number> = {};
    definitions.forEach(d => { stats[d.group_name] = (stats[d.group_name] || 0) + 1; });
    return stats;
  }, [definitions]);

  const openCovariateEditor = async (record: any, initialTab = 'basic') => {
    setEditingDef(record);
    // Parse options from string if needed
    const parsedRecord = { ...record };
    if (typeof parsedRecord.options === 'string') {
      try { parsedRecord.options = JSON.parse(parsedRecord.options); } catch { parsedRecord.options = []; }
    }
    form.setFieldsValue(parsedRecord);
    const optionAliases = Object.fromEntries(
      (Array.isArray(parsedRecord.options) ? parsedRecord.options : [])
        .filter((option: any) => Array.isArray(option?.agent_aliases) && option.agent_aliases.length > 0)
        .map((option: any) => [String(option.value), option.agent_aliases]),
    );
    form.setFieldValue('agent_option_aliases', Object.keys(optionAliases).length ? JSON.stringify(optionAliases) : '');
    setDrawerTab(initialTab);
    setUnitVariants(record.unit_variants || []);
    setRScriptAliases((record.r_script_aliases || []).map((item: any) => item?.alias_name || item).filter(Boolean));
    loadDerivationRules(record.key);
    // Load model mappings for this covariate
    if (!modelMappings[record.key]) {
      try {
        const data = await covariateAPI.modelsByCovariate(record.key);
        setModelMappings(prev => ({ ...prev, [record.key]: data?.models || [] }));
      } catch { /* ignore */ }
    }
    setDrawerVisible(true);
  };

  const columns = [
    {
      title: '协变量信息', key: 'identity', width: 230,
      render: (_: any, record: any) => (
        <div className="covariate-admin__identity">
          <Avatar className="covariate-admin__avatar" shape="square" size={34} icon={<DatabaseOutlined />} />
          <div className="covariate-admin__identity-copy">
            <span className="covariate-admin__identity-title">{record.label || record.key}</span>
            <span className="covariate-admin__identity-meta">{record.label_en || '未配置英文名'}</span>
            <Text code className="covariate-admin__identity-meta">{record.key}</Text>
          </div>
        </div>
      ),
    },
    {
      title: '类型与分组', key: 'classification', width: 140,
      render: (_: any, record: any) => (
        <div className="covariate-admin__classification">
          <Tag>{record.data_type || '-'}</Tag>
          <Tag color={groupColors[record.group_name] || 'default'}>{GROUP_LABELS[record.group_name] || record.group_name}</Tag>
        </div>
      ),
    },
    {
      title: '单位配置', key: 'units', width: 165,
      render: (_: any, record: any) => {
        const uvs = record.unit_variants || [];
        const std = uvs.find((u: any) => u.is_standard);
        const standardUnit = std?.unit || record.standard_unit || record.unit;
        return (
          <div className="covariate-admin__units">
            <span>{record.unit || standardUnit || '无单位'}</span>
            <Text type="secondary">标准：{standardUnit || '-'}</Text>
            {uvs.length > 0 && <Tag color="cyan">{uvs.length} 套单位</Tag>}
          </div>
        );
      },
    },
    {
      title: '系统关联', key: 'links', width: 145,
      render: (_: any, record: any) => {
        const models = modelMappings[record.key];
        const aliases = record.r_script_aliases || [];
        return (
          <div className="covariate-admin__links">
            <span className="covariate-admin__links-row">R 别名 <Badge count={aliases.length} showZero color={aliases.length ? '#722ED1' : '#98A2B3'} /></span>
            <span className="covariate-admin__links-row">模型引用 {models ? <Badge count={models.length} showZero color={models.length ? '#1677FF' : '#98A2B3'} /> : <Text type="secondary">展开查看</Text>}</span>
          </div>
        );
      },
    },
    { title: 'AI 提取描述', dataIndex: 'extraction_desc', width: 260,
      render: (value: string) => <span className="covariate-admin__description">{value || '未配置提取描述'}</span> },
    {
      title: '', width: 92, fixed: 'right' as const, align: 'right' as const,
      render: (_: any, record: any) => (
        <Space size={4}>
          <Tooltip title="编辑协变量"><Button type="text" size="small" icon={<EditOutlined />} onClick={() => openCovariateEditor(record, 'basic')} /></Tooltip>
          <Popconfirm title="确认删除?" onConfirm={() => handleDelete(record.key)}>
            <Tooltip title="删除"><Button type="text" size="small" danger icon={<DeleteOutlined />} /></Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const conditionFieldSelectOptions = DERIVATION_CONDITION_FIELD_GROUPS.map(group => ({
    label: group.label,
    options: group.options.map(option => ({
      label: `${option.label} (${option.field})`,
      value: option.field,
    })),
  }));

  const renderDerivationConditionValue = (
    row: EditableConditionRow,
    ruleIndex: number,
    conditionIndex: number,
  ) => {
    const config = getConditionFieldConfig(row.field);
    const noValueNeeded = row.op === 'exists' || row.op === 'missing';
    const isListOperator = row.op === 'in' || row.op === 'not_in';

    if (noValueNeeded) {
      return <Text type="secondary">无需填写</Text>;
    }

    if (config.value_type === 'select' || config.value_type === 'boolean') {
      const value = isListOperator
        ? (Array.isArray(row.value) ? row.value : (row.value ? [row.value] : []))
        : (row.value === '' ? undefined : row.value);
      return (
        <Select
          size="small"
          mode={isListOperator ? 'multiple' : undefined}
          value={value}
          options={config.options || []}
          placeholder="选择值"
          style={{ width: '100%' }}
          onChange={value => updateDerivationCondition(ruleIndex, conditionIndex, { value })}
        />
      );
    }

    if (config.value_type === 'number') {
      const numericValue = row.value === '' || row.value === null || row.value === undefined
        ? null
        : Number(row.value);
      return (
        <InputNumber
          size="small"
          value={Number.isFinite(numericValue as number) ? numericValue : null}
          placeholder={config.unit ? `单位: ${config.unit}` : '数值'}
          style={{ width: '100%' }}
          onChange={value => updateDerivationCondition(ruleIndex, conditionIndex, { value: value ?? '' })}
        />
      );
    }

    return (
      <Input
        size="small"
        value={row.value}
        placeholder={isListOperator ? '多个值用逗号分隔' : '条件值'}
        onChange={event => updateDerivationCondition(ruleIndex, conditionIndex, { value: event.target.value })}
      />
    );
  };

  const selectedDerivationRuleIndex = derivationRules.findIndex(rule => rule.ui_id === selectedDerivationRuleId);
  const activeDerivationRuleIndex = selectedDerivationRuleIndex >= 0 ? selectedDerivationRuleIndex : (derivationRules.length > 0 ? 0 : -1);
  const activeDerivationRule = activeDerivationRuleIndex >= 0 ? derivationRules[activeDerivationRuleIndex] : null;

  const handleAddDerivationRule = () => {
    const nextRule = newEditorRule(editingDef?.key || form.getFieldValue('key'), derivationRules.length);
    setDerivationRules(prev => [...prev, nextRule]);
    setSelectedDerivationRuleId(nextRule.ui_id);
  };

  const handleRemoveDerivationRule = (ruleIndex: number) => {
    const removing = derivationRules[ruleIndex];
    const nextRules = derivationRules.filter((_, itemIndex) => itemIndex !== ruleIndex);
    setDerivationRules(nextRules);
    if (removing?.ui_id === activeDerivationRule?.ui_id) {
      setSelectedDerivationRuleId(nextRules[Math.min(ruleIndex, nextRules.length - 1)]?.ui_id || null);
    }
  };

  const sectionStyle: React.CSSProperties = {
    border: '1px solid #E8EEF5',
    borderRadius: 8,
    background: '#FFFFFF',
    padding: 14,
  };

  const sectionTitleStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 12,
  };

  const renderRuleConditionTags = (rule: EditableDerivationRule, maxItems = 3) => {
    const summary = formatRuleConditionSummary(rule, maxItems);
    if (summary.length === 0) return <Tag color="default">通用规则</Tag>;
    return (
      <Space size={[4, 4]} wrap>
        {summary.map((item, itemIndex) => (
          <Tag key={`${rule.ui_id}-summary-${itemIndex}`} color={itemIndex === 0 ? 'blue' : 'default'} style={{ marginInlineEnd: 0 }}>
            {item}
          </Tag>
        ))}
      </Space>
    );
  };

  const renderDerivationConditionEditor = (rule: EditableDerivationRule, ruleIndex: number) => (
    <div style={sectionStyle}>
      <div style={sectionTitleStyle}>
        <Space direction="vertical" size={0}>
          <Text strong>匹配条件</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>用分层条件决定哪条公式命中，例如人群、年龄、性别、SCr 区间。</Text>
        </Space>
        <Select
          size="small"
          value={rule.condition_mode}
          style={{ width: 120 }}
          options={[{ label: '全部满足', value: 'all' }, { label: '任一满足', value: 'any' }]}
          onChange={value => updateDerivationRule(ruleIndex, { condition_mode: value })}
        />
      </div>

      <Space size={[6, 6]} wrap style={{ marginBottom: 12 }}>
        {DERIVATION_CONDITION_PRESETS.map(preset => (
          <Button
            key={preset.key}
            size="small"
            icon={<PlusOutlined />}
            onClick={() => addDerivationCondition(ruleIndex, preset.field)}
          >
            {preset.label}
          </Button>
        ))}
      </Space>

      {rule.conditions.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="无条件时始终匹配" />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          {rule.conditions.map((row, conditionIndex) => {
            const config = getConditionFieldConfig(row.field);
            const isCustomField = config.category === '自定义';
            return (
              <div
                key={row.ui_id}
                style={{
                  border: '1px solid #EEF2F7',
                  borderRadius: 8,
                  padding: 10,
                  background: '#FAFBFC',
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(190px, 1.35fr) minmax(96px, 0.55fr) minmax(150px, 1fr) 74px 32px',
                    gap: 8,
                    alignItems: 'start',
                  }}
                >
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Space size={4}>
                      <Tag color={isCustomField ? 'default' : 'blue'} style={{ marginInlineEnd: 0 }}>{config.category}</Tag>
                      {row.field && <Text type="secondary" style={{ fontSize: 12 }}>{row.field}</Text>}
                    </Space>
                    <Select
                      size="small"
                      showSearch
                      allowClear
                      optionFilterProp="label"
                      value={isCustomField ? undefined : row.field}
                      options={conditionFieldSelectOptions}
                      placeholder="选择字段"
                      style={{ width: '100%' }}
                      onChange={value => {
                        if (!value) {
                          updateDerivationCondition(ruleIndex, conditionIndex, { field: '', op: 'eq', value: '' });
                          return;
                        }
                        const nextRow = newConditionRow(value);
                        updateDerivationCondition(ruleIndex, conditionIndex, {
                          field: nextRow.field,
                          op: nextRow.op,
                          value: nextRow.value,
                        });
                      }}
                    />
                    {isCustomField && (
                      <Input
                        size="small"
                        value={row.field}
                        placeholder="自定义字段 key"
                        onChange={event => {
                          const nextField = event.target.value;
                          const currentConfig = getConditionFieldConfig(row.field);
                          const nextConfig = getConditionFieldConfig(nextField);
                          const patch: Partial<EditableConditionRow> = { field: nextField };
                          if (currentConfig.category === '自定义' && nextConfig.category !== '自定义') {
                            const nextRow = newConditionRow(nextField);
                            patch.op = nextRow.op;
                            patch.value = nextRow.value;
                          }
                          updateDerivationCondition(ruleIndex, conditionIndex, patch);
                        }}
                      />
                    )}
                  </Space>
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>关系</Text>
                    <Select
                      size="small"
                      value={row.op}
                      options={getConditionOperatorOptions(row.field)}
                      style={{ width: '100%' }}
                      onChange={value => updateDerivationCondition(ruleIndex, conditionIndex, { op: value })}
                    />
                  </Space>
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>值</Text>
                    {renderDerivationConditionValue(row, ruleIndex, conditionIndex)}
                  </Space>
                  <Space direction="vertical" size={4} align="center" style={{ width: '100%' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>取反</Text>
                    <Switch
                      size="small"
                      checked={!!row.negated}
                      onChange={checked => updateDerivationCondition(ruleIndex, conditionIndex, { negated: checked })}
                    />
                  </Space>
                  <Button
                    size="small"
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => updateDerivationRule(ruleIndex, {
                      conditions: rule.conditions.filter((_, index) => index !== conditionIndex),
                    })}
                  />
                </div>
              </div>
            );
          })}
        </Space>
      )}
    </div>
  );

  const renderDerivationParamEditor = (rule: EditableDerivationRule, ruleIndex: number) => (
    <div style={sectionStyle}>
      <div style={sectionTitleStyle}>
        <Space direction="vertical" size={0}>
          <Text strong>公式参数</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>把 k、性别系数等可调参数放在这里，表达式中可直接引用。</Text>
        </Space>
        <Button size="small" icon={<PlusOutlined />} onClick={() => updateDerivationRule(ruleIndex, { params_entries: [...rule.params_entries, newParamRow()] })}>
          添加参数
        </Button>
      </div>
      {rule.params_entries.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="无参数" />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          {rule.params_entries.map((row, paramIndex) => (
            <div
              key={row.ui_id}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(160px, 1fr) minmax(180px, 1.2fr) 32px',
                gap: 8,
                alignItems: 'center',
              }}
            >
              <Input
                size="small"
                value={row.key}
                placeholder="参数名，如 k"
                onChange={event => updateDerivationParam(ruleIndex, paramIndex, { key: event.target.value })}
              />
              <Input
                size="small"
                value={row.value}
                placeholder="参数值，如 0.413"
                onChange={event => updateDerivationParam(ruleIndex, paramIndex, { value: event.target.value })}
              />
              <Button
                size="small"
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => updateDerivationRule(ruleIndex, {
                  params_entries: rule.params_entries.filter((_, index) => index !== paramIndex),
                })}
              />
            </div>
          ))}
        </Space>
      )}
    </div>
  );

  const renderDerivationTestPanel = () => (
    <div style={sectionStyle}>
      <div style={sectionTitleStyle}>
        <Space direction="vertical" size={0}>
          <Text strong>规则验证</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>使用当前页面配置直接调用后端规则测试接口，不走前端本地公式。</Text>
        </Space>
        <Button size="small" icon={<FileSearchOutlined />} onClick={handleTestDerivationRules}>测试计算</Button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
        <Form.Item label="测试输入 JSON" style={{ marginBottom: 0 }}>
          <Input.TextArea
            rows={7}
            value={derivationTestInput}
            onChange={(event) => setDerivationTestInput(event.target.value)}
            style={{ fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace' }}
          />
        </Form.Item>
        <Form.Item label="测试结果" style={{ marginBottom: 0 }}>
          <Input.TextArea
            rows={7}
            readOnly
            value={derivationTestResult ? JSON.stringify(derivationTestResult, null, 2) : ''}
            style={{ fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace' }}
          />
        </Form.Item>
      </div>
    </div>
  );

  const renderDerivationRuleEditor = (rule: EditableDerivationRule, ruleIndex: number) => (
    <Space direction="vertical" style={{ width: '100%' }} size={12}>
      <div style={{ ...sectionStyle, borderColor: '#DCE8F8', background: '#FCFDFE' }}>
        <div style={sectionTitleStyle}>
          <Space direction="vertical" size={0}>
            <Space>
              <Text strong style={{ fontSize: 15 }}>{rule.formula_name || rule.rule_key || '未命名规则'}</Text>
              <Tag color={rule.is_active ? 'green' : 'default'}>{rule.is_active ? '启用' : '停用'}</Tag>
              <Tag color="blue">优先级 {rule.priority}</Tag>
            </Space>
            <Text type="secondary" style={{ fontSize: 12 }}>同一目标协变量按优先级从小到大匹配，命中第一条后返回。</Text>
          </Space>
          <Space>
            <Switch size="small" checked={rule.is_active} onChange={checked => updateDerivationRule(ruleIndex, { is_active: checked })} />
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleRemoveDerivationRule(ruleIndex)}>
              删除
            </Button>
          </Space>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
          <Form.Item label="规则 Key" style={{ marginBottom: 0 }}>
            <Input size="small" value={rule.rule_key} placeholder="egfr_schwartz_pediatric" onChange={event => updateDerivationRule(ruleIndex, { rule_key: event.target.value })} />
          </Form.Item>
          <Form.Item label="优先级" style={{ marginBottom: 0 }}>
            <InputNumber size="small" value={rule.priority} style={{ width: '100%' }} onChange={value => updateDerivationRule(ruleIndex, { priority: Number(value ?? 100) })} />
          </Form.Item>
          <Form.Item label="公式名称" style={{ marginBottom: 0 }}>
            <Input size="small" value={rule.formula_name || ''} placeholder="Schwartz" onChange={event => updateDerivationRule(ruleIndex, { formula_name: event.target.value })} />
          </Form.Item>
          <Form.Item label="公式标识" style={{ marginBottom: 0 }}>
            <Input size="small" value={rule.formula_key || ''} placeholder="ckd_epi_2009" onChange={event => updateDerivationRule(ruleIndex, { formula_key: event.target.value })} />
          </Form.Item>
          <Form.Item label="版本" style={{ marginBottom: 0 }}>
            <Input size="small" value={rule.formula_version || ''} placeholder="v1" onChange={event => updateDerivationRule(ruleIndex, { formula_version: event.target.value })} />
          </Form.Item>
          <Form.Item label="输出单位" style={{ marginBottom: 0 }}>
            <Input size="small" value={rule.output_unit || ''} placeholder="mL/min/1.73m2" onChange={event => updateDerivationRule(ruleIndex, { output_unit: event.target.value })} />
          </Form.Item>
          <Form.Item label="小数位" style={{ marginBottom: 0 }}>
            <InputNumber size="small" style={{ width: '100%' }} value={rule.round_digits as number | null} onChange={value => updateDerivationRule(ruleIndex, { round_digits: value as number | null })} />
          </Form.Item>
        </div>
      </div>

      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>
          <Space direction="vertical" size={0}>
            <Text strong>输入与表达式</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>必填输入用于缺失校验；表达式由后端安全解释器执行。</Text>
          </Space>
          <CodeOutlined style={{ color: '#64748B' }} />
        </div>
        <Form.Item label="必填输入" style={{ marginBottom: 10 }}>
          <Select
            mode="tags"
            size="small"
            value={rule.required_inputs}
            tokenSeparators={[',', ' ']}
            placeholder="height, scr_mg_dl"
            style={{ width: '100%' }}
            onChange={values => updateDerivationRule(ruleIndex, { required_inputs: values })}
          />
        </Form.Item>
        <Form.Item label="表达式" style={{ marginBottom: 0 }}>
          <Input.TextArea
            rows={3}
            value={rule.expression}
            placeholder="k * height / scr_mg_dl"
            onChange={event => updateDerivationRule(ruleIndex, { expression: event.target.value })}
            style={{ fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace' }}
          />
        </Form.Item>
      </div>

      {renderDerivationConditionEditor(rule, ruleIndex)}
      {renderDerivationParamEditor(rule, ruleIndex)}

      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>
          <Space direction="vertical" size={0}>
            <Text strong>临床说明</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>用于审计、追溯和 Agent 提示，不参与最终数值计算。</Text>
          </Space>
          <FileTextOutlined style={{ color: '#64748B' }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
          <Form.Item label="提示说明" style={{ marginBottom: 0 }}>
            <Input.TextArea rows={2} value={rule.prompt_hint || ''} onChange={event => updateDerivationRule(ruleIndex, { prompt_hint: event.target.value })} />
          </Form.Item>
          <Form.Item label="参考说明" style={{ marginBottom: 0 }}>
            <Input.TextArea rows={2} value={rule.reference_note || ''} onChange={event => updateDerivationRule(ruleIndex, { reference_note: event.target.value })} />
          </Form.Item>
        </div>
      </div>
      {renderDerivationTestPanel()}
    </Space>
  );

  const renderDerivationRuleWorkbench = () => {
    if (derivationRules.length === 0) {
      return (
        <div style={{ ...sectionStyle, minHeight: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Empty description="暂无派生规则">
            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleAddDerivationRule}>
              添加第一条规则
            </Button>
          </Empty>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', width: '100%', minWidth: 0 }}>
        <div
          style={{
            width: 286,
            flex: '0 0 286px',
            border: '1px solid #E8EEF5',
            borderRadius: 8,
            background: '#F8FAFC',
            padding: 10,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <Space direction="vertical" size={0}>
              <Text strong>规则序列</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>{derivationRules.length} 条分层公式</Text>
            </Space>
            <Button size="small" type="primary" icon={<PlusOutlined />} onClick={handleAddDerivationRule} />
          </div>
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            {derivationRules.map((rule, index) => {
              const selected = index === activeDerivationRuleIndex;
              return (
                <button
                  key={rule.ui_id}
                  type="button"
                  onClick={() => setSelectedDerivationRuleId(rule.ui_id)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    border: `1px solid ${selected ? '#1677FF' : '#E4EAF2'}`,
                    borderRadius: 8,
                    background: selected ? '#F0F7FF' : '#FFFFFF',
                    padding: 10,
                    cursor: 'pointer',
                    boxShadow: selected ? '0 8px 22px rgba(22, 119, 255, 0.08)' : 'none',
                  }}
                >
                  <Space direction="vertical" style={{ width: '100%' }} size={6}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <Text strong ellipsis style={{ maxWidth: 190 }}>{rule.formula_name || rule.rule_key || `规则 ${index + 1}`}</Text>
                      <Tag color={rule.is_active ? 'green' : 'default'} style={{ marginInlineEnd: 0 }}>{rule.is_active ? 'ON' : 'OFF'}</Tag>
                    </div>
                    <Text type="secondary" ellipsis style={{ fontSize: 12 }}>
                      {rule.rule_key || '未填写 rule_key'}
                    </Text>
                    <Space size={4} wrap>
                      <Tag color="blue" style={{ marginInlineEnd: 0 }}>P{rule.priority}</Tag>
                      {rule.output_unit && <Tag style={{ marginInlineEnd: 0 }}>{rule.output_unit}</Tag>}
                    </Space>
                    {renderRuleConditionTags(rule, 2)}
                  </Space>
                </button>
              );
            })}
          </Space>
        </div>
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
          {activeDerivationRule && renderDerivationRuleEditor(activeDerivationRule, activeDerivationRuleIndex)}
        </div>
      </div>
    );
  };
  const covariateSummary = [
    { title: '协变量总数', value: definitions.length },
    { title: '数值型', value: definitions.filter((item) => item.data_type === 'number').length },
    { title: '多单位配置', value: definitions.filter((item) => (item.unit_variants || []).length > 1).length },
    { title: '已配置 R 别名', value: definitions.filter((item) => (item.r_script_aliases || []).length > 0).length },
  ];

  return (
    <>
      <div className="drug-admin covariate-admin">
        <div className="drug-admin__summary">
          {covariateSummary.map((item) => (
            <div className="drug-admin__metric" key={item.title}>
              <span>{item.title}</span><strong>{item.value}</strong>
            </div>
          ))}
        </div>

        <div className="drug-admin__toolbar">
          <Space wrap size={[8, 8]}>
            <Search placeholder="搜索标识、中文或英文标签" allowClear style={{ width: 280 }}
              value={searchText} onChange={e => setSearchText(e.target.value)} />
            <Select placeholder="全部分组" allowClear style={{ width: 160 }} value={filterGroup} onChange={setFilterGroup}
              options={Object.entries(groupStats).map(([g, n]) => ({ label: `${GROUP_LABELS[g] || g} (${n})`, value: g }))} />
          </Space>
          <Space size={6}>
            <Tooltip title={`刷新 · 当前显示 ${filteredDefs.length} / ${definitions.length}`}>
              <Button icon={<ReloadOutlined />} onClick={loadDefs} loading={loading} />
            </Tooltip>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => {
              setEditingDef(null);
              form.resetFields();
              setUnitVariants([]);
              setRScriptAliases([]);
              resetDerivationEditor();
              setDrawerTab('basic');
              setDrawerVisible(true);
            }}>新增协变量</Button>
          </Space>
        </div>

        <Table
          className="drug-admin__table covariate-admin__table"
          dataSource={filteredDefs}
          columns={columns}
          rowKey="key"
          loading={loading}
          size="small"
          scroll={{ x: 960 }}
          pagination={{
            pageSize: 12,
            showSizeChanger: false,
            showTotal: (total) => `共 ${total} 条`,
          }}
          expandable={{
            expandedRowRender: (record: any) => {
              const models = modelMappings[record.key];
              if (!models) return <Spin size="small" />;
              if (models.length === 0) return <Text type="secondary">暂无模型使用此协变量</Text>;
              return (
                <div style={{ padding: '4px 0' }}>
                  <Text strong style={{ fontSize: 12 }}>使用此协变量的模型:</Text>
                  <div style={{ marginTop: 4 }}>
                    {models.map((m: any) => (
                      <Tag key={m.model_id} color="blue" style={{ marginBottom: 4 }}>
                        {m.model_id}: <Text code style={{ fontSize: 11 }}>{m.r_variable_name}</Text>
                        {m.transform_type && <Tag color="geekblue" style={{ fontSize: 10, marginLeft: 4 }}>{TRANSFORM_LABELS[m.transform_type] || m.transform_type}</Tag>}
                        {m.population_tag && <Text type="secondary" style={{ fontSize: 10 }}> ({POPULATION_LABELS[m.population_tag] || m.population_tag})</Text>}
                      </Tag>
                    ))}
                  </div>
                </div>
              );
            },
            onExpand: async (expanded: boolean, record: any) => {
              if (expanded && !modelMappings[record.key]) {
                try {
                  const data = await covariateAPI.modelsByCovariate(record.key);
                  setModelMappings(prev => ({ ...prev, [record.key]: data?.models || [] }));
                } catch {
                  setModelMappings(prev => ({ ...prev, [record.key]: [] }));
                }
              }
            },
          }}
        />
      </div>

      <Modal
        className="drug-editor-modal covariate-editor-modal"
        title={editingDef ? `编辑: ${editingDef.key}` : '新增协变量'}
        open={drawerVisible}
        onCancel={() => setDrawerVisible(false)}
        width={drawerTab === 'derivation_rules' ? 'min(96vw, 1440px)' : 'min(96vw, 1320px)'}
        style={{ top: 12 }}
        okText="保存协变量"
        cancelText="取消"
        onOk={handleSave}
        destroyOnClose
        styles={{ body: { maxHeight: '84vh', overflow: 'hidden', paddingBottom: 8 } }}
      >
        <Tabs
          className="drug-editor-tabs"
          size="small"
          tabPosition={screens.lg ? 'left' : 'top'}
          activeKey={drawerTab}
          onChange={setDrawerTab}
          items={[
          {
            key: 'basic', label: '基本信息', children: (
              <Form form={form} layout="vertical" size="small">
                <Row gutter={16}>
                  {!editingDef && (
                    <Col xs={24} md={8}>
                      <Form.Item name="key" label="标识" rules={[{ required: true }]}>
                        <Input placeholder="如: albumin" />
                      </Form.Item>
                    </Col>
                  )}
                  <Col xs={24} md={editingDef ? 12 : 8}>
                    <Form.Item name="label" label="中文标签" rules={[{ required: true }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={editingDef ? 12 : 8}>
                    <Form.Item name="label_en" label="英文标签">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} md={8}>
                    <Form.Item name="data_type" label="数据类型" rules={[{ required: true }]}>
                      <Select>
                      <Select.Option value="number">数值</Select.Option>
                      <Select.Option value="boolean">布尔</Select.Option>
                      <Select.Option value="select">选择</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="group_name" label="分组" rules={[{ required: true }]}>
                      <Select>
                      <Select.Option value="demographics">人口统计</Select.Option>
                      <Select.Option value="labs">实验室指标</Select.Option>
                      <Select.Option value="conditions">临床状态</Select.Option>
                      <Select.Option value="genetics">遗传基因</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="sort_order" label="排序"><InputNumber style={{ width: '100%' }} /></Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item name="default_value" label="默认值">
                      <Input placeholder="如: 0, false, unknown" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="unit" label="单位">
                      <Input placeholder="如: kg, mg/dL" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item name="standard_unit" label="标准单位" tooltip="由单位变体决定（有变体时只读）">
                      <Input placeholder="如: mg/dL" disabled={unitVariants.length > 0} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="unit_conversion" label="单位换算规则" tooltip="由单位变体决定（有变体时只读）">
                      <Input placeholder="如: 若原文为umol/L请除以88.4" disabled={unitVariants.length > 0} />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="extraction_desc" label="AI提取描述">
                  <Input.TextArea rows={2} placeholder="如: weight(体重,kg)" />
                </Form.Item>
                <Row gutter={16}>
                  <Col xs={24} md={8}><Form.Item name="min_val" label="最小值"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
                  <Col xs={24} md={8}><Form.Item name="max_val" label="最大值"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
                  <Col xs={24} md={8}><Form.Item name="step_val" label="步长"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
                </Row>
                <Form.Item name="placeholder" label="占位文本">
                  <Input />
                </Form.Item>
                <Form.Item name="tooltip" label="提示文本">
                  <Input />
                </Form.Item>
                <Form.Item name="agent_aliases" label="Agent 字段别名" extra="多个用户表达可用回车分隔，保存到后台协变量定义。">
                  <Select mode="tags" tokenSeparators={[',', '，']} placeholder="例如：肌酐、SCr、creatinine" />
                </Form.Item>
                <Form.Item
                  name="agent_option_aliases"
                  label="Agent 选项值别名(JSON)"
                  extra="请输入标准值到别名数组的 JSON 映射。"
                >
                  <Input.TextArea rows={2} placeholder="例如：male 对应 男、男性" />
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(prev, cur) => prev?.data_type !== cur?.data_type}>
                  {({ getFieldValue }) => getFieldValue('data_type') === 'select' ? (
                    <Form.Item label="选项列表">
                      <Form.List name="options">
                        {(fields, { add, remove }) => (
                          <>
                            {fields.map(({ key, name, ...restField }) => (
                              <Space key={key} style={{ display: 'flex', marginBottom: 4 }} align="baseline">
                                <Form.Item {...restField} name={[name, 'label']} rules={[{ required: true, message: '请输入显示名' }]} style={{ marginBottom: 0 }}>
                                  <Input placeholder="显示名" size="small" style={{ width: 140 }} />
                                </Form.Item>
                                <Form.Item {...restField} name={[name, 'value']} rules={[{ required: true, message: '请输入值' }]} style={{ marginBottom: 0 }}>
                                  <Input placeholder="值" size="small" style={{ width: 120 }} />
                                </Form.Item>
                                <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f' }} />
                              </Space>
                            ))}
                            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} size="small">
                              添加选项
                            </Button>
                          </>
                        )}
                      </Form.List>
                    </Form.Item>
                  ) : null}
                </Form.Item>
                <Form.Item name="companions" label="伴随协变量">
                  <Select mode="tags" placeholder="如: scr_unit" />
                </Form.Item>
              </Form>
            ),
          },
          {
            key: 'unit_variants', label: '单位变体', children: (
              <div>
                <Alert type="info" message="为同一协变量定义多套单位配置（如 SCr 同时支持 mg/dL 和 μmol/L），每套有独立的取值范围、步长和 AI 描述。" style={{ marginBottom: 16 }} />
                {unitVariants.map((v, idx) => (
                  <Card key={idx} size="small" style={{ marginBottom: 8 }}
                    title={<Space>{v.is_standard ? <Tag color="green">标准</Tag> : <Tag>备选</Tag>}{v.unit || '未命名'}</Space>}
                    extra={<Button size="small" danger icon={<DeleteOutlined />} onClick={() => {
                      setUnitVariants(prev => prev.filter((_, i) => i !== idx));
                    }} />}
                  >
                    <Row gutter={12}>
                      <Col span={6}>
                        <Form.Item label="单位"><Input value={v.unit} onChange={e => {
                          const arr = [...unitVariants]; arr[idx] = { ...arr[idx], unit: e.target.value }; setUnitVariants(arr);
                        }} /></Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item label="标准单位"><Switch checked={v.is_standard} onChange={checked => {
                          const arr = unitVariants.map((uv, i) => ({ ...uv, is_standard: i === idx ? checked : (checked ? false : uv.is_standard) }));
                          setUnitVariants(arr);
                        }} /></Form.Item>
                      </Col>
                      <Col span={14}>
                        <Form.Item label="AI提取描述"><Input value={v.extraction_desc || ''} onChange={e => {
                          const arr = [...unitVariants]; arr[idx] = { ...arr[idx], extraction_desc: e.target.value }; setUnitVariants(arr);
                        }} /></Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={12}>
                      <Col span={4}><Form.Item label="最小值"><InputNumber style={{ width: '100%' }} value={v.min_val} onChange={val => {
                        const arr = [...unitVariants]; arr[idx] = { ...arr[idx], min_val: val }; setUnitVariants(arr);
                      }} /></Form.Item></Col>
                      <Col span={4}><Form.Item label="最大值"><InputNumber style={{ width: '100%' }} value={v.max_val} onChange={val => {
                        const arr = [...unitVariants]; arr[idx] = { ...arr[idx], max_val: val }; setUnitVariants(arr);
                      }} /></Form.Item></Col>
                      <Col span={4}><Form.Item label="步长"><InputNumber style={{ width: '100%' }} value={v.step_val} onChange={val => {
                        const arr = [...unitVariants]; arr[idx] = { ...arr[idx], step_val: val }; setUnitVariants(arr);
                      }} /></Form.Item></Col>
                      <Col span={4}><Form.Item label="占位符"><Input value={v.placeholder || ''} onChange={e => {
                        const arr = [...unitVariants]; arr[idx] = { ...arr[idx], placeholder: e.target.value }; setUnitVariants(arr);
                      }} /></Form.Item></Col>
                      <Col span={8}><Form.Item label="转换公式" tooltip="转为标准单位的公式，如: value / 88.4">
                        <Input value={v.conversion_formula || ''} disabled={v.is_standard}
                          placeholder={v.is_standard ? '标准单位无需转换' : '如: value / 88.4'}
                          onChange={e => {
                            const arr = [...unitVariants]; arr[idx] = { ...arr[idx], conversion_formula: e.target.value }; setUnitVariants(arr);
                          }} />
                      </Form.Item></Col>
                    </Row>
                  </Card>
                ))}
                <Button type="dashed" block icon={<PlusOutlined />} onClick={() => {
                  setUnitVariants(prev => [...prev, { unit: '', is_standard: prev.length === 0, sort_order: prev.length }]);
                }}>添加单位变体</Button>
              </div>
            ),
          },
          {
            key: 'r_aliases', label: 'R 脚本别名', children: (
              <div>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="这里维护全局 R 名称词典。别名用于生成模型绑定建议；核心计算只接受模型中已确认的精确 R 变量绑定。"
                />
                <Form layout="vertical" size="small">
                  <Form.Item
                    label="R 脚本别名"
                    extra="一个标准协变量可配置多个别名；保存会整体替换该字段原有别名。相同别名不能分配给不同标准字段，模型级 R 变量仍以脚本原始大小写为准。"
                  >
                    <Select
                      mode="tags"
                      value={rScriptAliases}
                      onChange={(values) => {
                        const seen = new Set<string>();
                        const next = (values || [])
                          .map((item) => String(item || '').trim())
                          .filter((alias) => {
                            const normalized = alias.toUpperCase();
                            if (!alias || seen.has(normalized)) return false;
                            seen.add(normalized);
                            return true;
                          });
                        setRScriptAliases(next);
                      }}
                      tokenSeparators={[',', ' ']}
                      placeholder="输入 R 变量名后回车"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Form>
                {!!rScriptAliases.length && (
                  <Space wrap>
                    {rScriptAliases.map((alias) => (
                      <Tag key={alias} color="purple">{alias}</Tag>
                    ))}
                  </Space>
                )}
              </div>
            ),
          },
          {
            key: 'derivation_rules', label: '派生规则', children: (
              <div>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="规则以数据库为准；Agent 与前台计算只提交基础字段，最终派生值统一由后端规则引擎返回。"
                />
                <Spin spinning={derivationRulesLoading}>
                  <Space direction="vertical" style={{ width: '100%', minWidth: 0 }} size={12}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        当前目标：{editingDef?.key || form.getFieldValue('key') || '未填写'}
                      </Text>
                      <Space>
                        <Button size="small" type="primary" icon={<PlusOutlined />} onClick={handleAddDerivationRule}>
                          添加规则
                        </Button>
                        {editingDef && (
                          <Button size="small" icon={<ReloadOutlined />} onClick={() => loadDerivationRules(editingDef.key)} loading={derivationRulesLoading}>
                            重新加载
                          </Button>
                        )}
                      </Space>
                    </div>
                    {renderDerivationRuleWorkbench()}
                  </Space>
                </Spin>
              </div>
            ),
          },
          {
            key: 'model_bindings', label: '模型绑定', disabled: !editingDef, children: editingDef ? (
              <div>
                <Alert type="info" message="显示使用此协变量的所有模型及其 R 变量映射（只读，在模型的「协变量绑定」tab 中编辑）。" style={{ marginBottom: 16 }} />
                <Table
                  dataSource={modelMappings[editingDef?.key] || []}
                  rowKey="model_id"
                  size="small"
                  pagination={false}
                  columns={[
                    { title: '模型ID', dataIndex: 'model_id', ellipsis: true },
                    { title: '已配置 R 变量', dataIndex: 'r_variable_name', width: 120 },
                    { title: '脚本要求', dataIndex: 'expected_r_variable_name', width: 120,
                      render: (v: string) => v || <Text type="secondary">未解析</Text> },
                    { title: '状态', dataIndex: 'binding_status', width: 110,
                      render: (v: string) => {
                        const meta: Record<string, { label: string; color: string }> = {
                          confirmed: { label: '已确认', color: 'green' },
                          alias_suggested: { label: '待确认', color: 'gold' },
                          stale_binding: { label: '大小写/旧绑定', color: 'red' },
                          patient_input_only: { label: '仅患者资料', color: 'blue' },
                          unmapped: { label: '未关联', color: 'red' },
                        };
                        const item = meta[v] || { label: v || '未知', color: 'default' };
                        return <Tag color={item.color}>{item.label}</Tag>;
                      } },
                    { title: '变换类型', dataIndex: 'transform_type', width: 90,
                      render: (v: string) => v ? <Tag>{TRANSFORM_LABELS[v] || v}</Tag> : '-' },
                    { title: '人群', dataIndex: 'population_tag', width: 80,
                      render: (v: string) => v ? <Tag color="blue">{POPULATION_LABELS[v] || v}</Tag> : '-' },
                    { title: '转换公式', dataIndex: 'conversion_formula', width: 120,
                      render: (v: string) => v ? <Text code style={{ fontSize: 11 }}>{v}</Text> : '-' },
                    { title: '默认R值', dataIndex: 'default_r_values', width: 150,
                      render: (v: any) => v ? <Text code style={{ fontSize: 10 }}>{JSON.stringify(v)}</Text> : '-' },
                  ]}
                />
              </div>
            ) : null,
          },
        ]} />
      </Modal>
    </>
  );
};

const ConfigTab: React.FC = () => {
  const [config, setConfig] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [editKey, setEditKey] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, any>>({});
  // Fallback for unrecognized keys
  const [rawEditKey, setRawEditKey] = useState<string | null>(null);
  const [rawEditValue, setRawEditValue] = useState('');
  const [mapFilter, setMapFilter] = useState('');

  useEffect(() => {
    adminAPI.getConfig().then(setConfig).catch(() => message.error('加载配置失败')).finally(() => setLoading(false));
  }, []);


const handleSaveStructured = async (key: string) => {
  setSaving(key);
  try {
    const schema = CONFIG_SCHEMA[key];
    let valueToSave: any;
    if (schema?.fields?.[0]?.key === '_value') {
      valueToSave = editValues._value;
      if (key === 'brand_logo_assets') {
        valueToSave = normalizeBrandLogoAssets(valueToSave);
      }
    } else {
      valueToSave = { ...(typeof config[key] === 'object' ? config[key] : {}), ...editValues };
    }
    await adminAPI.updateConfig(key, valueToSave);
    setConfig((prev) => ({ ...prev, [key]: valueToSave }));
    message.success('已保存');
    setEditKey(null);
  } catch {
    message.error('保存失败');
  } finally {
    setSaving(null);
  }
};

const handleSaveRaw = async () => {
  if (!rawEditKey) return;
  setSaving(rawEditKey);
  try {
    let parsed: any;
    try { parsed = JSON.parse(rawEditValue); } catch { parsed = rawEditValue; }
    await adminAPI.updateConfig(rawEditKey, parsed);
    setConfig((prev) => ({ ...prev, [rawEditKey]: parsed }));
    message.success('已保存');
    setRawEditKey(null);
  } catch {
    message.error('保存失败');
  } finally {
    setSaving(null);
  }
};

if (loading) return <Card loading />;

const knownKeys = Object.keys(CONFIG_SCHEMA);
const unknownKeys = Object.keys(config).filter((k) => !knownKeys.includes(k));

const openEdit = (key: string) => {
  const schema = CONFIG_SCHEMA[key];
  if (!schema) {
    setRawEditKey(key);
    setRawEditValue(typeof config[key] === 'object' ? JSON.stringify(config[key]) : String(config[key] ?? ''));
    return;
  }
  if (schema.fields[0]?.key === '_value') {
    setEditValues({ _value: config[key] ?? '' });
  } else {
    const obj = typeof config[key] === 'object' && config[key] !== null ? config[key] : {};
    setEditValues({ ...obj });
  }
  setEditKey(key);
  setMapFilter('');
};

const setLogoLibrary = (updater: (items: BrandLogoAsset[]) => BrandLogoAsset[]) => {
  setEditValues((prev) => {
    const current = normalizeBrandLogoAssets(prev._value);
    return { ...prev, _value: normalizeBrandLogoAssets(updater(current)) };
  });
};

const updateLogoLibraryItem = (index: number, patch: Partial<BrandLogoAsset>) => {
  setLogoLibrary((items) => items.map((item, itemIndex) => (
    itemIndex === index
      ? {
        ...item,
        ...patch,
        scopes: patch.scopes ? Array.from(new Set(patch.scopes)) as BrandLogoAsset['scopes'] : item.scopes,
      }
      : item
  )));
};

const toggleLogoScopeAssignment = (index: number, scope: BrandLogoScope, checked: boolean) => {
  setLogoLibrary((items) => items.map((item, itemIndex) => {
    const currentScopes = Array.isArray(item.scopes) ? [...item.scopes] : [];
    if (itemIndex === index) {
      return {
        ...item,
        scopes: checked
          ? Array.from(new Set([...currentScopes, scope])) as BrandLogoAsset['scopes']
          : currentScopes.filter((itemScope) => itemScope !== scope) as BrandLogoAsset['scopes'],
      };
    }
    if (!checked || !currentScopes.includes(scope)) {
      return item;
    }
    return {
      ...item,
      scopes: currentScopes.filter((itemScope) => itemScope !== scope) as BrandLogoAsset['scopes'],
    };
  }));
};

const addLogoLibraryItem = () => {
  setLogoLibrary((items) => [
    ...items,
    {
      id: `logo-${Date.now()}`,
      name: `Logo ${items.length + 1}`,
      url: '',
      scopes: [],
    },
  ]);
};

const removeLogoLibraryItem = (index: number) => {
  setLogoLibrary((items) => items.filter((_, itemIndex) => itemIndex !== index));
};

const renderValue = (key: string) => {
  const val = config[key];
  if (val === null || val === undefined || val === '') return <Text type="secondary">未设置</Text>;
  if (Array.isArray(val)) {
    if (val.length === 0) return <Text type="secondary">未设置</Text>;
    return <Tag>{val.length} 项</Tag>;
  }
  if (typeof val === 'object') {
    if (key === 'brand_logo_assets') {
      const assets = normalizeBrandLogoAssets(val);
      const assignedCount = assets.reduce((total, item) => total + item.scopes.length, 0);
      return <Space wrap>
        <Tag color="blue">{assets.length} 套 Logo</Tag>
        <Tag color="processing">{assignedCount} 个页面位</Tag>
        {assets.slice(0, 2).map((item) => <Tag key={item.id}>{item.name}</Tag>)}
        {assets.length > 2 && <Text type="secondary">...</Text>}
      </Space>;
    }
    if ('enabled' in val) {
      return <Space wrap>
        <Tag color={val.enabled ? 'green' : 'default'}>{val.enabled ? '已启用' : '未启用'}</Tag>
        {Object.entries(val).filter(([innerKey]) => innerKey !== 'enabled').slice(0, 2).map(([innerKey, innerValue]) => {
          const summary = String(innerValue);
          return <Tag key={innerKey}>{innerKey}: {summary.length > 20 ? `${summary.slice(0, 20)}…` : summary || '(空)'}</Tag>;
        })}
        {Object.keys(val).length > 3 && <Text type="secondary">...</Text>}
      </Space>;
    }
    const entries = Object.entries(val).slice(0, 3);
    return <Space wrap>{entries.map(([innerKey, innerValue]) => {
      const summary = String(innerValue);
      return <Tag key={innerKey}>{innerKey}: {summary.length > 30 ? `${summary.slice(0, 30)}…` : summary || '(空)'}</Tag>;
    })}{Object.keys(val).length > 3 && <Text type="secondary">...</Text>}</Space>;
  }
  return <Text>{String(val)}</Text>;
};
  const configTableColumns = [
    {
      title: '配置项', dataIndex: 'label', key: 'label', width: 160,
      render: (label: string, r: any) => (
        <div>
          <Text strong>{label}</Text>
          {r.description && <div><Text type="secondary" style={{ fontSize: 11 }}>{r.description}</Text></div>}
        </div>
      ),
    },
    { title: '当前值', dataIndex: 'configKey', key: 'value', render: (_: any, r: any) => renderValue(r.configKey) },
    {
      title: '操作', key: 'ops', width: 80, render: (_: any, r: any) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r.configKey)}>编辑</Button>
      )
    },
  ];

  return (
    <>
      {CONFIG_GROUPS.map(group => {
        const groupKeys = knownKeys.filter(k => CONFIG_SCHEMA[k].group === group.key && !CONFIG_SCHEMA[k].hidden);
        if (groupKeys.length === 0) return null;
        return (
          <Card key={group.key} title={group.label} size="small" style={{ marginBottom: 16 }}>
            <Table
              dataSource={groupKeys.map(k => ({ key: k, configKey: k, label: CONFIG_SCHEMA[k].label, description: CONFIG_SCHEMA[k].description }))}
              columns={configTableColumns}
              rowKey="key" pagination={false} size="small"
            />
          </Card>
        );
      })}

      {/* Raw object/array edit modal */}
      <Modal
        title={rawEditKey ? `编辑: ${rawEditKey}` : ''}
        open={!!rawEditKey && typeof config[rawEditKey!] === 'object' && config[rawEditKey!] !== null}
        onOk={handleSaveRaw}
        onCancel={() => setRawEditKey(null)}
        confirmLoading={!!saving}
        width={650}
      >
        {rawEditKey && (() => {
          const val = config[rawEditKey];
          // Array of objects → table editor
          if (Array.isArray(val)) {
            let rows: any[];
            try { rows = JSON.parse(rawEditValue); } catch { rows = val; }
            if (!Array.isArray(rows)) rows = val;
            const allKeys = Array.from(new Set(rows.flatMap((r: any) => Object.keys(r || {}))));
            if (allKeys.length === 0) allKeys.push('value');
            return (
              <>
                <Table size="small" dataSource={rows.map((r: any, i: number) => ({ ...r, _idx: i }))} rowKey="_idx" pagination={false}
                  columns={[
                    ...allKeys.map(col => ({
                      title: col, dataIndex: col, key: col,
                      render: (v: any, rec: any) => (
                        <Input size="small" value={v ?? ''} onChange={e => {
                          const updated = [...rows];
                          updated[rec._idx] = { ...updated[rec._idx], [col]: e.target.value };
                          setRawEditValue(JSON.stringify(updated));
                        }} />
                      ),
                    })),
                    {
                      title: '', key: '_del', width: 40, render: (_: any, rec: any) => (
                        <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => {
                          const updated = rows.filter((_: any, i: number) => i !== rec._idx);
                          setRawEditValue(JSON.stringify(updated));
                        }} />
                      )
                    },
                  ]}
                />
                <Button size="small" type="dashed" icon={<PlusOutlined />} style={{ marginTop: 8, width: '100%' }}
                  onClick={() => {
                    const empty: any = {};
                    allKeys.forEach(k => { empty[k] = ''; });
                    setRawEditValue(JSON.stringify([...rows, empty]));
                  }}>添加行</Button>
              </>
            );
          }
          // Plain object → key-value table
          if (typeof val === 'object' && val !== null) {
            let obj: Record<string, any>;
            try { obj = JSON.parse(rawEditValue); } catch { obj = val; }
            if (typeof obj !== 'object' || Array.isArray(obj)) obj = val;
            const entries = Object.entries(obj);
            return (
              <>
                <Table size="small" dataSource={entries.map(([k, v], i) => ({ _idx: i, _key: k, _val: v }))} rowKey="_idx" pagination={false}
                  columns={[
                    {
                      title: '键', dataIndex: '_key', key: '_key', width: 180,
                      render: (k: string, rec: any) => (
                        <Input size="small" value={k} onChange={e => {
                          const newEntries = [...entries];
                          newEntries[rec._idx] = [e.target.value, newEntries[rec._idx][1]];
                          setRawEditValue(JSON.stringify(Object.fromEntries(newEntries)));
                        }} />
                      ),
                    },
                    {
                      title: '值', dataIndex: '_val', key: '_val',
                      render: (v: any, rec: any) => {
                        if (typeof v === 'boolean') {
                          return <Switch size="small" checked={v} onChange={checked => {
                            const newEntries = [...entries];
                            newEntries[rec._idx] = [newEntries[rec._idx][0], checked];
                            setRawEditValue(JSON.stringify(Object.fromEntries(newEntries)));
                          }} />;
                        }
                        return <Input size="small" value={typeof v === 'object' ? JSON.stringify(v) : String(v ?? '')} onChange={e => {
                          const newEntries = [...entries];
                          let newVal: any = e.target.value;
                          if (newVal === 'true') newVal = true;
                          else if (newVal === 'false') newVal = false;
                          else if (!isNaN(Number(newVal)) && newVal.trim() !== '') newVal = Number(newVal);
                          newEntries[rec._idx] = [newEntries[rec._idx][0], newVal];
                          setRawEditValue(JSON.stringify(Object.fromEntries(newEntries)));
                        }} />;
                      },
                    },
                    {
                      title: '', key: '_del', width: 40, render: (_: any, rec: any) => (
                        <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => {
                          const newEntries = entries.filter((_: any, i: number) => i !== rec._idx);
                          setRawEditValue(JSON.stringify(Object.fromEntries(newEntries)));
                        }} />
                      )
                    },
                  ]}
                />
                <Button size="small" type="dashed" icon={<PlusOutlined />} style={{ marginTop: 8, width: '100%' }}
                  onClick={() => {
                    const newObj = { ...obj, '': '' };
                    setRawEditValue(JSON.stringify(newObj));
                  }}>添加字段</Button>
              </>
            );
          }
          return null;
        })()}
      </Modal>

      {/* Structured Edit Modal */}
      <Modal
        title={editKey ? `编辑: ${CONFIG_SCHEMA[editKey]?.label || editKey}` : ''}
        open={!!editKey}
        onOk={() => editKey && handleSaveStructured(editKey)}
        onCancel={() => setEditKey(null)}
        confirmLoading={!!saving}
        width={editKey && CONFIG_SCHEMA[editKey]?.fields.some(f => f.type === 'textarea' || f.type === 'kv_table' || f.type === 'model_mapping' || f.type === 'logo_library') ? 880 : 500}
      >
        {editKey && CONFIG_SCHEMA[editKey] && (
          <Form layout="vertical" size="small">
            {CONFIG_SCHEMA[editKey].fields.map(f => (
              <Form.Item label={f.label} key={f.key} style={{ marginBottom: 12 }} extra={f.help}>

{f.type === 'logo_library' ? (() => {
  const items = normalizeBrandLogoAssets(editValues[f.key]);
  const scopeAssignments = items.reduce<Record<string, string>>((acc, item) => {
    item.scopes.forEach((scope) => {
      acc[scope] = item.name;
    });
    return acc;
  }, {});

  return (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="按页面位置分配 Logo"
        description="每个页面位置同时只能使用一个 Logo。勾选新的 Logo 后，系统会自动解除原有占用。"
      />
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {items.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无 Logo 方案，请先新增。" />
        ) : items.map((item, index) => (
          <Card
            key={item.id || index}
            size="small"
            bodyStyle={{ padding: 14 }}
            title={(
              <Space size={8} wrap>
                <Text strong>{item.name || `Logo ${index + 1}`}</Text>
                <Tag color="blue">{item.scopes.length} 个页面位</Tag>
              </Space>
            )}
            extra={(
              <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => removeLogoLibraryItem(index)}>
                删除
              </Button>
            )}
          >
            <Row gutter={[12, 12]}>
              <Col xs={24} md={8}>
                <div style={{
                  height: 112,
                  borderRadius: 12,
                  border: '1px solid #E7EDF4',
                  background: '#F8FAFD',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  padding: 12,
                }}>
                  {item.url ? (
                    <img
                      src={resolvePublicAssetUrl(item.url)}
                      alt={item.name || `Logo ${index + 1}`}
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
                    />
                  ) : (
                    <Text type="secondary">请填写 Logo 地址</Text>
                  )}
                </div>
              </Col>
              <Col xs={24} md={16}>
                <Space direction="vertical" size={10} style={{ width: '100%' }}>
                  <Input
                    value={item.name}
                    placeholder="例如：官网主 Logo / 管理后台图标"
                    onChange={(e) => updateLogoLibraryItem(index, { name: e.target.value })}
                  />
                  <Input
                    value={item.url}
                    placeholder="/images/logo.png ? https://..."
                    onChange={(e) => updateLogoLibraryItem(index, { url: e.target.value })}
                  />
                  <Input
                    value={item.note || ''}
                    placeholder="备注（可选）"
                    onChange={(e) => updateLogoLibraryItem(index, { note: e.target.value })}
                  />
                </Space>
              </Col>
              <Col span={24}>
                <div style={{ fontWeight: 600, color: '#262626', marginBottom: 8 }}>适用页面</div>
                <Row gutter={[8, 8]}>
                  {BRAND_LOGO_SCOPE_OPTIONS.map((scope) => {
                    const checked = item.scopes.includes(scope.value);
                    const assignedName = scopeAssignments[scope.value];
                    const usedByOther = assignedName && assignedName !== item.name;
                    return (
                      <Col xs={24} sm={12} key={scope.value}>
                        <div style={{
                          border: `1px solid ${checked ? '#91Caff' : '#E7EDF4'}`,
                          borderRadius: 10,
                          background: checked ? '#F0F7FF' : '#FFFFFF',
                          padding: '10px 12px',
                          minHeight: 88,
                        }}>
                          <Checkbox
                            checked={checked}
                            onChange={(e) => toggleLogoScopeAssignment(index, scope.value, e.target.checked)}
                          >
                            <span style={{ fontWeight: 600, color: '#262626' }}>{scope.label}</span>
                          </Checkbox>
                          <div style={{ paddingLeft: 24, marginTop: 6 }}>
                            <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.5 }}>
                              {scope.description}
                            </Text>
                            {usedByOther ? (
                              <div style={{ marginTop: 6 }}>
                                <Tag color="orange">当前由 {assignedName} 使用，勾选后会切换到本 Logo</Tag>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </Col>
            </Row>
          </Card>
        ))}
      </Space>
      <Button size="small" type="dashed" icon={<PlusOutlined />} style={{ marginTop: 12, width: '100%' }} onClick={addLogoLibraryItem}>
        新增一套 Logo
      </Button>
    </>
  );
                })() : f.type === 'model_mapping' ? (() => {
                  const mappings: Record<string, string> = (typeof editValues[f.key] === 'object' && editValues[f.key] !== null && !Array.isArray(editValues[f.key]))
                    ? editValues[f.key] : {};
                  const entries = Object.entries(mappings);
                  const filtered = mapFilter
                    ? entries.filter(([k, v]) => k.toLowerCase().includes(mapFilter.toLowerCase()) || String(v).toLowerCase().includes(mapFilter.toLowerCase()))
                    : entries;
                  const updateMappings = (newMappings: Record<string, string>) => {
                    setEditValues(prev => ({ ...prev, [f.key]: newMappings }));
                  };
                  return (
                    <>
                      <Input placeholder="搜索模型ID或脚本文件名..." allowClear size="small"
                        value={mapFilter} prefix={<SearchOutlined />} style={{ marginBottom: 8 }}
                        onChange={e => setMapFilter(e.target.value)} />
                      <Table size="small" dataSource={filtered.map(([k, v], i) => ({ _idx: i, model_id: k, file_base: v }))}
                        rowKey="model_id" pagination={filtered.length > 15 ? { pageSize: 15, size: 'small' } : false}
                        scroll={{ y: 400 }}
                        columns={[
                          {
                            title: '模型ID (元数据)', dataIndex: 'model_id', key: 'mid', width: '48%',
                            render: (v: string, rec: any) => (
                              <Input size="small" value={v} style={{ fontFamily: 'monospace', fontSize: 11 }}
                                onChange={e => {
                                  const newMap = { ...mappings };
                                  const oldKey = v;
                                  const val = newMap[oldKey];
                                  delete newMap[oldKey];
                                  newMap[e.target.value] = val;
                                  updateMappings(newMap);
                                }} />
                            ),
                          },
                          {
                            title: '脚本文件名 (Models/)', dataIndex: 'file_base', key: 'fb', width: '42%',
                            render: (v: string, rec: any) => (
                              <Input size="small" value={v} style={{ fontFamily: 'monospace', fontSize: 11 }}
                                onChange={e => {
                                  const newMap = { ...mappings };
                                  newMap[rec.model_id] = e.target.value;
                                  updateMappings(newMap);
                                }} />
                            ),
                          },
                          {
                            title: '', key: '_del', width: 36, render: (_: any, rec: any) => (
                              <Button size="small" type="text" danger icon={<DeleteOutlined />}
                                onClick={() => {
                                  const newMap = { ...mappings };
                                  delete newMap[rec.model_id];
                                  updateMappings(newMap);
                                }} />
                            )
                          },
                        ]}
                      />
                      <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Button size="small" type="dashed" icon={<PlusOutlined />}
                          onClick={() => updateMappings({ ...mappings, '': '' })}>
                          添加映射
                        </Button>
                        <Tag>{entries.length} 条映射</Tag>
                      </div>
                    </>
                  );
                })() : f.type === 'kv_table' ? (() => {
                  const rows: any[] = Array.isArray(editValues[f.key]) ? editValues[f.key] : [];
                  const cols = f.columns || [{ key: 'key', label: '键' }, { key: 'value', label: '值' }];
                  const dataWithIdx = rows.map((r, i) => ({ ...r, _idx: i }));
                  return (
                    <>
                      <Table size="small" dataSource={dataWithIdx} rowKey="_idx" pagination={false}
                        columns={[
                          ...cols.map(c => ({
                            title: c.label, dataIndex: c.key, key: c.key,
                            render: (val: any, rec: any) => (
                              <Input size="small" value={val ?? ''} onChange={e => {
                                const updated = [...rows];
                                updated[rec._idx] = { ...updated[rec._idx], [c.key]: e.target.value };
                                setEditValues(prev => ({ ...prev, [f.key]: updated }));
                              }} />
                            ),
                          })),
                          {
                            title: '', key: '_ops', width: 40, render: (_: any, rec: any) => (
                              <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => {
                                const updated = rows.filter((_: any, i: number) => i !== rec._idx);
                                setEditValues(prev => ({ ...prev, [f.key]: updated }));
                              }} />
                            )
                          },
                        ]}
                      />
                      <Button size="small" type="dashed" icon={<PlusOutlined />} style={{ marginTop: 8, width: '100%' }}
                        onClick={() => {
                          const empty: any = {};
                          cols.forEach(c => { empty[c.key] = ''; });
                          setEditValues(prev => ({ ...prev, [f.key]: [...rows, empty] }));
                        }}>添加行</Button>
                    </>
                  );
                })() : f.type === 'select' && f.options ? (
                  <Select value={editValues[f.key]} onChange={v => setEditValues(prev => ({ ...prev, [f.key]: v }))} options={f.options} style={{ width: '100%' }} placeholder={f.placeholder} />
                ) : f.type === 'switch' ? (
                  <Switch checked={!!editValues[f.key]} onChange={v => setEditValues(prev => ({ ...prev, [f.key]: v }))} />
                ) : f.type === 'number' ? (
                  <InputNumber value={editValues[f.key]} onChange={v => setEditValues(prev => ({ ...prev, [f.key]: v }))} style={{ width: '100%' }} placeholder={f.placeholder} />
                ) : f.type === 'password' ? (
                  <Input.Password value={editValues[f.key] ?? ''} onChange={e => setEditValues(prev => ({ ...prev, [f.key]: e.target.value }))} placeholder={f.placeholder} />
                ) : f.type === 'textarea' ? (
                  <Input.TextArea value={editValues[f.key] ?? ''} onChange={e => setEditValues(prev => ({ ...prev, [f.key]: e.target.value }))}
                    rows={f.rows || 4} placeholder={f.placeholder} style={{ fontFamily: 'Consolas, Monaco, monospace', fontSize: 12, wordBreak: 'break-all', whiteSpace: 'pre-wrap' }} />
                ) : (
                  <Input value={editValues[f.key] ?? ''} onChange={e => setEditValues(prev => ({ ...prev, [f.key]: e.target.value }))} placeholder={f.placeholder} />
                )}
              </Form.Item>
            ))}
          </Form>
        )}
      </Modal>
    </>
  );
};

// ================================================================
// Tasks Tab (Validation Task Bounty Management)
// ================================================================
const TasksTab: React.FC = () => {
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string | undefined>();

  // Create/Edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<any>(null);
  const [editForm] = Form.useForm();
  const [editSaving, setEditSaving] = useState(false);

  // Applications drawer
  const [appsDrawerOpen, setAppsDrawerOpen] = useState(false);
  const [appsTaskId, setAppsTaskId] = useState<string | null>(null);
  const [applications, setApplications] = useState<any[]>([]);
  const [appsLoading, setAppsLoading] = useState(false);

  // Completion review drawer
  const [reviewDrawerOpen, setReviewDrawerOpen] = useState(false);
  const [reviewTask, setReviewTask] = useState<any>(null);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSaving, setReviewSaving] = useState(false);

  // Model list for select
  const [modelOptions, setModelOptions] = useState<any[]>([]);

  // Reference info for task creation
  const [refInfo, setRefInfo] = useState<any>({});
  const [refLoading, setRefLoading] = useState(false);
  const [refEditing, setRefEditing] = useState(false);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.listTasks({ status: filterStatus, size: 100 });
      setTasks(res.tasks || []);
    } catch { message.error('加载任务失败'); }
    finally { setLoading(false); }
  };

  const loadModelOptions = async () => {
    try {
      const res = await adminAPI.listModelsMeta();
      setModelOptions((res.models || []).map((m: any) => ({
        value: m.model_id,
        label: `${m.name} (${m.model_id})`,
        name: m.name,
      })));
    } catch { }
  };

  useEffect(() => { fetchTasks(); }, [filterStatus]);

  const handleCreate = () => {
    setEditingTask(null);
    editForm.resetFields();
    setRefInfo({});
    setRefEditing(false);
    loadModelOptions();
    setEditOpen(true);
  };

  const handleEditTask = (task: any) => {
    setEditingTask(task);
    loadModelOptions();
    setRefInfo(task.reference_info || {});
    setRefEditing(false);
    editForm.setFieldsValue({
      title: task.title,
      model_id: task.model_id,
      description: task.description,
      requirements: task.requirements,
      deliverables: task.deliverables,
      reward_coins: task.reward_coins,
      deadline: task.deadline ? dayjs(task.deadline) : null,
    });
    setEditOpen(true);
  };

  const handleModelSelect = async (modelId: string) => {
    editForm.setFieldsValue({ model_id: modelId });
    setRefLoading(true);
    try {
      const data = await adminAPI.getModelDetail(modelId);
      const meta = data?.model_metadata || {};
      setRefInfo({
        pmid: meta.pmid || '',
        doi: meta.doi || '',
        journal: meta.journal || '',
        first_author: meta.first_author || '',
        year: meta.publication_year || '',
      });
    } catch {
      // Silently fail - reference info is optional
    } finally { setRefLoading(false); }
  };

  const handleSaveTask = async () => {
    try {
      const values = await editForm.validateFields();
      setEditSaving(true);
      const modelOpt = modelOptions.find((m: any) => m.value === values.model_id);
      const payload = {
        title: values.title,
        model_id: values.model_id,
        model_name: modelOpt?.name || values.model_id,
        description: values.description || '',
        requirements: values.requirements,
        deliverables: values.deliverables || '',
        reward_coins: values.reward_coins,
        deadline: values.deadline ? values.deadline.toISOString() : null,
        reference_info: refInfo,
      };
      if (editingTask) {
        await adminAPI.updateTask(editingTask.id, payload);
        message.success('任务已更新');
      } else {
        await adminAPI.createTask(payload);
        message.success('任务已创建');
      }
      setEditOpen(false);
      fetchTasks();
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.detail || '保存失败');
    } finally { setEditSaving(false); }
  };

  const handlePublish = async (taskId: string) => {
    try { await adminAPI.publishTask(taskId); message.success('任务已发布'); fetchTasks(); }
    catch (err: any) { message.error(err?.response?.data?.detail || '发布失败'); }
  };

  const handleCancel = async (taskId: string) => {
    try { await adminAPI.cancelTask(taskId); message.success('任务已取消'); fetchTasks(); }
    catch (err: any) { message.error(err?.response?.data?.detail || '取消失败'); }
  };

  const openApplications = async (taskId: string) => {
    setAppsTaskId(taskId);
    setAppsDrawerOpen(true);
    setAppsLoading(true);
    try {
      const res = await adminAPI.getApplications(taskId);
      setApplications(res.applications || []);
    } catch { message.error('加载申请失败'); }
    finally { setAppsLoading(false); }
  };

  const handleApproveApp = async (taskId: string, appId: string) => {
    try {
      await adminAPI.approveApplication(taskId, appId);
      message.success('申请已批准');
      openApplications(taskId);
      fetchTasks();
    } catch (err: any) { message.error(err?.response?.data?.detail || '操作失败'); }
  };

  const handleRejectApp = async (taskId: string, appId: string) => {
    try {
      await adminAPI.rejectApplication(taskId, appId);
      message.success('申请已驳回');
      openApplications(taskId);
    } catch (err: any) { message.error(err?.response?.data?.detail || '操作失败'); }
  };

  const openReview = (task: any) => {
    setReviewTask(task);
    setReviewComment('');
    setReviewDrawerOpen(true);
  };

  const handleApproveCompletion = async () => {
    if (!reviewTask) return;
    setReviewSaving(true);
    try {
      await adminAPI.approveCompletion(reviewTask.id, reviewComment);
      message.success('任务已完成，金币已发放');
      setReviewDrawerOpen(false);
      fetchTasks();
    } catch (err: any) { message.error(err?.response?.data?.detail || '操作失败'); }
    finally { setReviewSaving(false); }
  };

  const handleRejectCompletion = async () => {
    if (!reviewTask) return;
    setReviewSaving(true);
    try {
      await adminAPI.rejectCompletion(reviewTask.id, reviewComment);
      message.success('成果已打回');
      setReviewDrawerOpen(false);
      fetchTasks();
    } catch (err: any) { message.error(err?.response?.data?.detail || '操作失败'); }
    finally { setReviewSaving(false); }
  };

  const statusConfig: Record<string, { color: string; label: string }> = {
    draft: { color: 'default', label: '草稿' },
    published: { color: 'blue', label: '招募中' },
    assigned: { color: 'cyan', label: '已指派' },
    in_progress: { color: 'orange', label: '执行中' },
    submitted: { color: 'purple', label: '已提交' },
    reviewing: { color: 'geekblue', label: '审核中' },
    completed: { color: 'green', label: '已完成' },
    cancelled: { color: 'default', label: '已取消' },
    expired: { color: 'default', label: '已过期' },
  };

  const columns = [
    { title: '标题', dataIndex: 'title', key: 'title', width: 200, ellipsis: true },
    { title: '关联模型', dataIndex: 'model_name', key: 'model', width: 160, ellipsis: true },
    {
      title: '奖励', dataIndex: 'reward_coins', key: 'reward', width: 80,
      render: (v: number) => <Text style={{ color: '#C5A55A', fontWeight: 'bold' }}>{v}</Text>
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 80,
      render: (v: string) => {
        const sc = statusConfig[v] || statusConfig.draft;
        return <Tag color={sc.color}>{sc.label}</Tag>;
      }
    },
    { title: '申请数', dataIndex: 'application_count', key: 'apps', width: 60 },
    {
      title: '执行人', dataIndex: 'assigned_user_name', key: 'assigned', width: 100,
      render: (v: string) => v || '-'
    },
    {
      title: '截止', dataIndex: 'deadline', key: 'deadline', width: 100,
      render: (v: string) => v ? formatCN(v, 'YYYY-MM-DD') : '-'
    },
    {
      title: '操作', key: 'ops', width: 280,
      render: (_: any, r: any) => (
        <Space size="small" wrap>
          {r.status === 'draft' && (
            <>
              <Button size="small" icon={<EditOutlined />} onClick={() => handleEditTask(r)}>编辑</Button>
              <Button size="small" type="primary" onClick={() => handlePublish(r.id)}>发布</Button>
            </>
          )}
          {r.status === 'published' && (
            <>
              <Button size="small" icon={<EditOutlined />} onClick={() => handleEditTask(r)}>编辑</Button>
              <Badge count={r.application_count || 0} size="small">
                <Button size="small" onClick={() => openApplications(r.id)}>审核申请</Button>
              </Badge>
            </>
          )}
          {(r.status === 'submitted' || r.status === 'reviewing') && (
            <Button size="small" type="primary" onClick={() => openReview(r)}>审核成果</Button>
          )}
          {r.status === 'assigned' && (
            <Button size="small" onClick={() => openApplications(r.id)}>查看申请</Button>
          )}
          {!['completed', 'cancelled'].includes(r.status) && (
            <Popconfirm title="确认取消此任务?" onConfirm={() => handleCancel(r.id)}>
              <Button size="small" danger>取消</Button>
            </Popconfirm>
          )}
        </Space>
      )
    },
  ];

  return (
    <>
      <Space style={{ marginBottom: 16 }} wrap>
        <Select placeholder="状态筛选" allowClear style={{ width: 140 }} value={filterStatus} onChange={setFilterStatus}
          options={Object.entries(statusConfig).map(([k, v]) => ({ value: k, label: v.label }))} />
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>创建任务</Button>
        <Button icon={<ReloadOutlined />} onClick={fetchTasks} loading={loading}>刷新</Button>
        <Text type="secondary">共 {tasks.length} 个任务</Text>
      </Space>
      <Table columns={columns} dataSource={tasks} rowKey="id" loading={loading}
        pagination={{ pageSize: 20 }} size="small" scroll={{ x: 1100 }} />

      {/* Create/Edit Task Modal */}
      <Modal
        title={editingTask ? '编辑任务' : '创建任务'}
        open={editOpen}
        onOk={handleSaveTask}
        onCancel={() => setEditOpen(false)}
        confirmLoading={editSaving}
        width={640}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入任务标题' }]}>
            <Input placeholder="如: Anderson 2006 万古霉素模型外部验证" />
          </Form.Item>
          <Form.Item name="model_id" label="关联模型" rules={[{ required: true, message: '请选择关联模型' }]}>
            <Select placeholder="选择模型" showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              options={modelOptions}
              onChange={handleModelSelect} />
          </Form.Item>
          {/* Reference info auto-filled from model metadata */}
          {(refInfo.pmid || refInfo.doi || refInfo.journal || refLoading) && (
            <Card size="small" style={{ marginBottom: 16 }}
              title={<Space><LinkOutlined /><span>参考文献</span>{refLoading && <Spin size="small" />}</Space>}
              extra={<Button size="small" type="link" onClick={() => setRefEditing(!refEditing)}>{refEditing ? '收起' : '编辑'}</Button>}
            >
              {!refEditing ? (
                <Descriptions size="small" column={2}>
                  {refInfo.pmid && <Descriptions.Item label="PMID">
                    <a href={`https://pubmed.ncbi.nlm.nih.gov/${refInfo.pmid}`} target="_blank" rel="noreferrer">{refInfo.pmid}</a>
                  </Descriptions.Item>}
                  {refInfo.doi && <Descriptions.Item label="DOI">
                    <a href={`https://doi.org/${refInfo.doi}`} target="_blank" rel="noreferrer">{refInfo.doi}</a>
                  </Descriptions.Item>}
                  {refInfo.journal && <Descriptions.Item label="期刊">{refInfo.journal}</Descriptions.Item>}
                  {refInfo.first_author && <Descriptions.Item label="第一作者">{refInfo.first_author}</Descriptions.Item>}
                  {refInfo.year && <Descriptions.Item label="年份">{refInfo.year}</Descriptions.Item>}
                </Descriptions>
              ) : (
                <Row gutter={[8, 8]}>
                  <Col span={12}><Input size="small" addonBefore="PMID" value={refInfo.pmid || ''} onChange={e => setRefInfo({ ...refInfo, pmid: e.target.value })} /></Col>
                  <Col span={12}><Input size="small" addonBefore="DOI" value={refInfo.doi || ''} onChange={e => setRefInfo({ ...refInfo, doi: e.target.value })} /></Col>
                  <Col span={12}><Input size="small" addonBefore="期刊" value={refInfo.journal || ''} onChange={e => setRefInfo({ ...refInfo, journal: e.target.value })} /></Col>
                  <Col span={12}><Input size="small" addonBefore="作者" value={refInfo.first_author || ''} onChange={e => setRefInfo({ ...refInfo, first_author: e.target.value })} /></Col>
                  <Col span={12}><Input size="small" addonBefore="年份" value={refInfo.year || ''} onChange={e => setRefInfo({ ...refInfo, year: e.target.value })} /></Col>
                </Row>
              )}
            </Card>
          )}
          <Form.Item name="description" label="任务描述">
            <Input.TextArea rows={3} placeholder="任务背景说明..." />
          </Form.Item>
          <Form.Item name="requirements" label="完成要求" rules={[{ required: true, message: '请输入完成要求' }]}>
            <Input.TextArea rows={5} placeholder="详细的验证要求..." />
          </Form.Item>
          <Form.Item name="deliverables" label="交付物说明">
            <Input.TextArea rows={2} placeholder="需要提交的材料..." />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="reward_coins" label="金币奖励" rules={[{ required: true, message: '请输入金币奖励' }]}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="deadline" label="截止日期">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Applications Drawer */}
      <Drawer
        title="竞选申请审核"
        placement="right"
        width={640}
        open={appsDrawerOpen}
        onClose={() => { setAppsDrawerOpen(false); setAppsTaskId(null); }}
        destroyOnClose
      >
        {appsLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
        ) : applications.length === 0 ? (
          <Empty description="暂无申请" />
        ) : (
          applications.map((app: any) => (
            <Card key={app.id} size="small" style={{ marginBottom: 12 }}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Space>
                  <Text strong>{app.user_name || app.user_email}</Text>
                  <Tag color={
                    app.status === 'pending' ? 'orange'
                      : app.status === 'approved' ? 'green'
                        : app.status === 'rejected' ? 'red'
                          : 'default'
                  }>
                    {app.status === 'pending' ? '待审核'
                      : app.status === 'approved' ? '已通过'
                        : app.status === 'rejected' ? '已驳回'
                          : '已撤回'}
                  </Tag>
                  <Text type="secondary" style={{ fontSize: 12 }}>{formatCN(app.created_at, 'YYYY-MM-DD HH:mm')}</Text>
                </Space>
                <div>
                  <Text type="secondary">建模经历:</Text>
                  <Paragraph style={{ whiteSpace: 'pre-wrap', margin: '4px 0' }}>{app.experience}</Paragraph>
                </div>
                {app.credentials && (
                  <div>
                    <Text type="secondary">资质证明:</Text>
                    <Paragraph style={{ whiteSpace: 'pre-wrap', margin: '4px 0' }}>{app.credentials}</Paragraph>
                  </div>
                )}
                {app.status === 'pending' && appsTaskId && (
                  <Space>
                    <Popconfirm title="确认批准此申请? 其余待审申请将自动驳回。" onConfirm={() => handleApproveApp(appsTaskId, app.id)}>
                      <Button type="primary" size="small" icon={<CheckOutlined />}>批准</Button>
                    </Popconfirm>
                    <Popconfirm title="确认驳回此申请?" onConfirm={() => handleRejectApp(appsTaskId, app.id)}>
                      <Button danger size="small" icon={<CloseOutlined />}>驳回</Button>
                    </Popconfirm>
                  </Space>
                )}
              </Space>
            </Card>
          ))
        )}
      </Drawer>

      {/* Completion Review Drawer */}
      <Drawer
        title="成果审核"
        placement="right"
        width={780}
        open={reviewDrawerOpen}
        onClose={() => { setReviewDrawerOpen(false); setReviewTask(null); }}
        destroyOnClose
      >
        {reviewTask && (() => {
          const sr = reviewTask.submission_report || {};
          const gof = sr.gof || {};
          const stab = sr.stability || {};
          const pred = sr.predictive_ability || {};
          const errm = sr.error_metrics || {};
          const pe = sr.parameter_evaluation || {};
          const API_BASE = process.env.REACT_APP_API_URL || '';
          const toFull = (u: string) => u.startsWith('http') ? u : `${API_BASE}${u}`;
          const hasReport = sr.summary || sr.overall_conclusion || gof.dv_vs_pred_description;
          const renderImgs = (imgs?: string[]) => {
            if (!imgs?.length) return null;
            return (
              <Image.PreviewGroup>
                <Space wrap style={{ marginTop: 4 }}>
                  {imgs.map((u, i) => <Image key={i} src={toFull(u)} width={80} height={80} style={{ objectFit: 'cover', borderRadius: 4 }} />)}
                </Space>
              </Image.PreviewGroup>
            );
          };
          const renderFiles = (files?: string[], label?: string) => {
            if (!files?.length) return null;
            return (
              <div style={{ marginBottom: 8 }}>
                {label && <Text type="secondary" style={{ fontSize: 12 }}>{label}:</Text>}
                <Space direction="vertical" size={2} style={{ width: '100%', marginTop: 2 }}>
                  {files.map((u, i) => (
                    <a key={i} href={toFull(u)} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>
                      <DownloadOutlined style={{ marginRight: 4 }} />{u.split('/').pop()}
                    </a>
                  ))}
                </Space>
              </div>
            );
          };
          return (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div>
                <Title level={5} style={{ margin: 0 }}>{reviewTask.title}</Title>
                <Text type="secondary">执行人: {reviewTask.assigned_user_name}</Text>
                {reviewTask.submitted_at && (
                  <Text type="secondary" style={{ fontSize: 12, marginLeft: 12 }}>
                    提交时间: {formatCN(reviewTask.submitted_at, 'YYYY-MM-DD HH:mm')}
                  </Text>
                )}
              </div>

              {hasReport ? (
                <Collapse size="small" defaultActiveKey={['summary', 'gof', 'files']} items={[
                  {
                    key: 'summary', label: '验证总结', children: (
                      <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{sr.summary || reviewTask.submission_text || '-'}</Paragraph>
                    )
                  },
                  {
                    key: 'gof', label: '拟合优度 (GOF)', children: (
                      <>
                        {gof.dv_vs_pred_description && <div><Text strong style={{ fontSize: 12 }}>DV vs PRED:</Text><Paragraph style={{ whiteSpace: 'pre-wrap', margin: '2px 0 8px' }}>{gof.dv_vs_pred_description}</Paragraph></div>}
                        {renderImgs(gof.dv_vs_pred_images)}
                        {gof.dv_vs_ipred_description && <div style={{ marginTop: 8 }}><Text strong style={{ fontSize: 12 }}>DV vs IPRED:</Text><Paragraph style={{ whiteSpace: 'pre-wrap', margin: '2px 0 8px' }}>{gof.dv_vs_ipred_description}</Paragraph></div>}
                        {renderImgs(gof.dv_vs_ipred_images)}
                        {gof.cwres_description && <div style={{ marginTop: 8 }}><Text strong style={{ fontSize: 12 }}>CWRES:</Text><Paragraph style={{ whiteSpace: 'pre-wrap', margin: '2px 0 8px' }}>{gof.cwres_description}</Paragraph></div>}
                        {renderImgs(gof.cwres_images)}
                        {gof.conclusion && <div style={{ marginTop: 8 }}><Text strong style={{ fontSize: 12 }}>结论:</Text><Paragraph style={{ margin: '2px 0' }}>{gof.conclusion}</Paragraph></div>}
                      </>
                    )
                  },
                  {
                    key: 'stability', label: '稳定性评价', children: (
                      <>
                        <Descriptions size="small" column={3}>
                          {stab.bootstrap_n != null && <Descriptions.Item label="Bootstrap次数">{stab.bootstrap_n}</Descriptions.Item>}
                          {stab.bootstrap_success_rate != null && <Descriptions.Item label="成功率">{stab.bootstrap_success_rate}%</Descriptions.Item>}
                          {stab.parameter_bias_percent != null && <Descriptions.Item label="参数偏差">{stab.parameter_bias_percent}%</Descriptions.Item>}
                        </Descriptions>
                        {stab.conclusion && <Paragraph style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>{stab.conclusion}</Paragraph>}
                      </>
                    )
                  },
                  {
                    key: 'pred', label: '预测能力 (VPC/NPDE)', children: (
                      <>
                        {pred.vpc_description && <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{pred.vpc_description}</Paragraph>}
                        {renderImgs(pred.vpc_images)}
                        <Descriptions size="small" column={3} style={{ marginTop: 8 }}>
                          {pred.npde_mean != null && <Descriptions.Item label="NPDE均值">{pred.npde_mean}</Descriptions.Item>}
                          {pred.npde_variance != null && <Descriptions.Item label="NPDE方差">{pred.npde_variance}</Descriptions.Item>}
                          {pred.npde_normality_p != null && <Descriptions.Item label="正态性p值">{pred.npde_normality_p}</Descriptions.Item>}
                        </Descriptions>
                        {pred.conclusion && <Paragraph style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>{pred.conclusion}</Paragraph>}
                      </>
                    )
                  },
                  {
                    key: 'error', label: '误差评价', children: (
                      <>
                        <Descriptions size="small" column={3}>
                          {errm.mpe != null && <Descriptions.Item label="MPE">{errm.mpe}</Descriptions.Item>}
                          {errm.mpe_percent != null && <Descriptions.Item label="MPE%">{errm.mpe_percent}%</Descriptions.Item>}
                          {errm.mae != null && <Descriptions.Item label="MAE">{errm.mae}</Descriptions.Item>}
                          {errm.mape != null && <Descriptions.Item label="MAPE">{errm.mape}%</Descriptions.Item>}
                          {errm.rmse != null && <Descriptions.Item label="RMSE">{errm.rmse}</Descriptions.Item>}
                        </Descriptions>
                        {errm.conclusion && <Paragraph style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>{errm.conclusion}</Paragraph>}
                      </>
                    )
                  },
                  {
                    key: 'param', label: '参数评价', children: (
                      <>
                        {pe.parameters?.length > 0 && (
                          <Table size="small" dataSource={pe.parameters} rowKey={(r: any) => `${r.name}-${pe.parameters.indexOf(r)}`} pagination={false}
                            columns={[
                              { title: '参数', dataIndex: 'name', width: 100 },
                              { title: '估计值', dataIndex: 'estimate', width: 80 },
                              { title: 'RSE%', dataIndex: 'rse_percent', width: 70 },
                              { title: 'Shrinkage%', dataIndex: 'shrinkage_percent', width: 80 },
                            ]} />
                        )}
                        <div style={{ marginTop: 8 }}>
                          {pe.overall_rse_acceptable != null && <Tag color={pe.overall_rse_acceptable ? 'green' : 'red'}>RSE {pe.overall_rse_acceptable ? '可接受' : '偏高'}</Tag>}
                          {pe.overall_shrinkage_acceptable != null && <Tag color={pe.overall_shrinkage_acceptable ? 'green' : 'red'}>Shrinkage {pe.overall_shrinkage_acceptable ? '可接受' : '偏高'}</Tag>}
                        </div>
                        {pe.conclusion && <Paragraph style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>{pe.conclusion}</Paragraph>}
                      </>
                    )
                  },
                  {
                    key: 'conclusion', label: '总体结论', children: (
                      <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{sr.overall_conclusion || '-'}</Paragraph>
                    )
                  },
                  {
                    key: 'files', label: '附件', children: (
                      <>
                        {renderFiles(sr.pk_data_files, 'PK 数据文件')}
                        {renderFiles(sr.model_code_files, '模型代码文件')}
                        {renderFiles(sr.other_attachments, '其他附件')}
                        {!sr.pk_data_files?.length && !sr.model_code_files?.length && !sr.other_attachments?.length && <Text type="secondary">无附件</Text>}
                      </>
                    )
                  },
                ]} />
              ) : reviewTask.submission_text ? (
                <Card size="small" title="提交的成果">
                  <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{reviewTask.submission_text}</Paragraph>
                </Card>
              ) : (
                <Empty description="未提交成果" />
              )}

              <Divider style={{ margin: '8px 0' }} />
              <div>
                <Text strong>审核意见</Text>
                <Input.TextArea
                  rows={3}
                  value={reviewComment}
                  onChange={e => setReviewComment(e.target.value)}
                  placeholder="请输入审核意见..."
                  style={{ marginTop: 4 }}
                />
              </div>
              <Space>
                <Popconfirm title={`确认通过并发放 ${reviewTask.reward_coins} 金币?`} onConfirm={handleApproveCompletion}>
                  <Button type="primary" loading={reviewSaving} icon={<CheckOutlined />}>
                    通过 (发放 {reviewTask.reward_coins} 金币)
                  </Button>
                </Popconfirm>
                <Button danger onClick={handleRejectCompletion} loading={reviewSaving} icon={<CloseOutlined />}>
                  打回修改
                </Button>
              </Space>
            </Space>
          );
        })()}
      </Drawer>
    </>
  );
};

export default AdminPanel;
